import {
  Component,
  StrictMode,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  ClipboardList,
  Clock3,
  Filter,
  LayoutDashboard,
  MapPin,
  Menu,
  Mic,
  Moon,
  Package,
  Route,
  Search,
  Share2,
  Download,
  FileCheck2,
  Settings2,
  Truck,
  TrendingUp,
  Users,
  X,
  Sun,
} from "lucide-react";
import { product3dFor } from "./data/product3d.js";
import "./styles.css";

// Widgets administrativos pesados se cargan solo cuando el Dashboard los
// necesita. Pedidos Web comparte el entrypoint, pero no descarga charts ni
// tablas al iniciar el catálogo.
const AdminWidgets = lazy(() => import("./admin-widgets.jsx"));
const ProductOperationsEditorV2 = lazy(() =>
  import("./ProductOperationsEditor.jsx").then((module) => ({ default: module.ProductOperationsEditor })),
);
const DeliveryDestinationsEditor = lazy(() =>
  import("./DeliveryDestinationsEditor.jsx").then((module) => ({ default: module.DeliveryDestinationsEditor })),
);
const DeliveryServicesEditor = lazy(() =>
  import("./DeliveryServicesEditor.jsx").then((module) => ({ default: module.DeliveryServicesEditor })),
);
const AdminPushNotifications = lazy(() =>
  import("./AdminPushNotifications.jsx").then((module) => ({ default: module.AdminPushNotifications })),
);
const UsersManagement = lazy(() =>
  import("./UsersManagement.jsx").then((module) => ({ default: module.UsersManagement })),
);

function DeferredModule({ children }) {
  return <Suspense fallback={<div className="table-loading">Cargando módulo…</div>}>{children}</Suspense>;
}

const sections = [
  ["resumen", "Resumen", "Centro de mando", LayoutDashboard],
  ["pedidos", "Pedidos", "Confirmados y pendientes", ClipboardList],
  ["lista_espera", "Lista de espera", "Solicitudes por disponibilidad", Clock3],
  ["inventario", "Inventario", "Saldos y alertas", Package],
  ["rutas", "Rutas", "Preparación y reparto", Route],
  ["pagos", "Pagos", "Transferencias y cartera", CircleDollarSign],
  ["envios_whatsapp", "Envíos WhatsApp", "Segmentos de clientes", Share2],
  ["cartera", "Cartera", "Cobros y saldos pendientes", CircleDollarSign],
  ["gastos", "Gastos", "Fletes y operación", CircleDollarSign],
  ["liquidacion", "Liquidación diaria", "Cierre y conciliación", FileCheck2],
  ["auditoria_entregas", "Análisis repartidor", "Tiempos y caja por ruta", Clock3],
  ["configuracion", "Configuración", "Presentaciones y cupos", Settings2],
  ["usuarios", "Usuarios", "Roles y accesos", Users],
  ["estadisticas", "Estadísticas", "Lectura del negocio", BarChart3],
];

const navigationGroups = [
  { id: "inicio", label: "Inicio", items: ["resumen"] },
  { id: "operacion", label: "Operación diaria", items: ["pedidos", "lista_espera", "rutas", "gastos", "liquidacion"] },
  { id: "finanzas", label: "Finanzas", items: ["pagos", "cartera"] },
  { id: "catalogo", label: "Inventario y catálogo", items: ["inventario", "configuracion"] },
  { id: "analisis", label: "Análisis", items: ["estadisticas", "auditoria_entregas"] },
  { id: "comunicacion", label: "Comunicación", items: ["envios_whatsapp"] },
  { id: "administracion", label: "Administración", items: ["usuarios"] },
];

const DEV_BYPASS_AUTH =
  import.meta.env.DEV &&
  import.meta.env.VITE_DASHBOARD_DEV_BYPASS_AUTH === "true";

// Una versión anterior publicó PA01 como referencia de Panela pulverizada.
// La ficha quedó retirada, pero los carritos locales y borradores remotos
// pueden conservarla. El carrito siempre trabaja con la presentación vigente.
const LEGACY_CART_CODE_REPLACEMENTS = Object.freeze({
  PA01: "PP-500",
  "PANELA-PULVERIZADA-500": "PP-500",
});

function normalizarCarritoCodigosHistoricos(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value).reduce((normalizado, [codigo, cantidad]) => {
    const codigoOriginal = String(codigo || "").trim().toUpperCase();
    const codigoVigente = LEGACY_CART_CODE_REPLACEMENTS[codigoOriginal] || codigoOriginal;
    const unidades = Number(cantidad);
    if (!codigoVigente || !Number.isFinite(unidades) || unidades <= 0)
      return normalizado;
    normalizado[codigoVigente] = Number(normalizado[codigoVigente] || 0) + unidades;
    return normalizado;
  }, {});
}

const ADMIN_TOAST_EVENT = "titina-admin-toast";
const ADMIN_CONFIRM_EVENT = "titina-admin-confirm";

// Punto único para las notificaciones visibles del panel. Las pantallas no
// necesitan conocer dónde se muestra el aviso; sólo emiten el resultado de la
// acción. En fases posteriores las operaciones de Pedidos, Rutas e Inventario
// reutilizarán este mismo contrato, sin cambiar el backend.
function notificarAdmin(mensaje, tipo = "info") {
  const texto = String(mensaje || "").trim();
  if (!texto || typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(ADMIN_TOAST_EVENT, {
      detail: { mensaje: texto, tipo },
    }),
  );
}

function confirmarAccionAdmin({ titulo, mensaje, confirmar = "Confirmar", peligro = false, onConfirm }) {
  if (typeof window === "undefined" || typeof onConfirm !== "function") return;
  window.dispatchEvent(
    new CustomEvent(ADMIN_CONFIRM_EVENT, {
      detail: { titulo, mensaje, confirmar, peligro, onConfirm },
    }),
  );
}

function ToastViewport({ avisos, onDismiss }) {
  if (!avisos.length) return null;
  return (
    <section className="admin-toast-viewport" aria-live="polite" aria-label="Notificaciones del sistema">
      {avisos.map((aviso) => {
        const isError = aviso.tipo === "error";
        const isWarning = aviso.tipo === "warning";
        const Icon = isError || isWarning ? AlertTriangle : CheckCircle2;
        return (
          <div className={`admin-toast ${aviso.tipo || "info"}`} key={aviso.id} role={isError ? "alert" : "status"}>
            <Icon size={19} aria-hidden="true" />
            <span>{aviso.mensaje}</span>
            <button type="button" onClick={() => onDismiss(aviso.id)} aria-label="Cerrar notificación">
              <X size={17} aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </section>
  );
}

function ConfirmationDialog({ confirmacion, onClose }) {
  if (!confirmacion) return null;
  return (
    <div className="admin-confirm-backdrop" role="presentation" onClick={onClose}>
      <section className="admin-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="admin-confirm-title" onClick={(event) => event.stopPropagation()}>
        <AlertTriangle size={25} aria-hidden="true" />
        <div>
          <h2 id="admin-confirm-title">{confirmacion.titulo || "Confirmar acción"}</h2>
          <p>{confirmacion.mensaje}</p>
        </div>
        <div className="admin-confirm-actions">
          <button type="button" className="secondary-action" onClick={onClose}>Volver</button>
          <button
            type="button"
            className={confirmacion.peligro ? "danger-action" : "primary-action"}
            onClick={() => {
              const accion = confirmacion.onConfirm;
              onClose();
              accion();
            }}
          >
            {confirmacion.confirmar || "Confirmar"}
          </button>
        </div>
      </section>
    </div>
  );
}

// Safari privado, bloqueadores y políticas de cookies pueden hacer que el
// Storage lance una excepción sincrónica. El acceso web no debe quedar
// bloqueado por esa capacidad opcional del navegador: la sesión principal es
// la cookie HttpOnly y la recuperación alternativa es el número de WhatsApp.
function safeSessionGet(key) {
  try {
    return window.sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function safeSessionSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* continúa sin almacenamiento local */
  }
}

function safeSessionRemove(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* continúa sin almacenamiento local */
  }
}

// El borrador persistido en el ERP es la fuente canónica. Esta copia local no
// decide precios, disponibilidad ni crea pedidos: únicamente evita que una
// recarga inmediata pierda una selección que todavía está viajando al servidor.
function safeLocalGet(key) {
  try {
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function safeLocalSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* continúa sin almacenamiento local */
  }
}

function safeLocalRemove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* continúa sin almacenamiento local */
  }
}

function draftCacheKey(identityId) {
  return `titina_pedidos_draft_cache:${String(identityId || "").trim()}`;
}

function readDraftCache(identityId) {
  const raw = safeLocalGet(draftCacheKey(identityId));
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      !parsed.productos ||
      typeof parsed.productos !== "object"
    )
      return {};
    return Object.fromEntries(
      Object.entries(parsed.productos).filter(
        ([codigo, cantidad]) =>
          String(codigo).trim() &&
          Number.isInteger(Number(cantidad)) &&
          Number(cantidad) > 0,
      ),
    );
  } catch {
    return {};
  }
}

function cacheDraft(identityId, cart) {
  const identity = String(identityId || "").trim();
  if (!identity) return;
  safeLocalSet(
    draftCacheKey(identity),
    JSON.stringify({ productos: cart, updatedAt: Date.now() }),
  );
}

// Correlativo técnico por carga de Pedidos Web. No contiene identidad, enlace,
// teléfono ni contenido comercial; únicamente une tiempos de una sesión.
function createPedidosWebTraceId() {
  try {
    return `pweb-${crypto.randomUUID().replaceAll("-", "")}`;
  } catch {
    return `pweb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function pedidosWebHeaders(traceId, includeJson = false) {
  return {
    ...(includeJson ? { "Content-Type": "application/json" } : {}),
    "X-Pedidos-Web-Trace": traceId,
  };
}

// Instrumentación temporal: captura solo el error técnico y su fase. Nunca
// envía teléfono, token, cliente, carrito ni contenido de formularios.
function reportPedidosWebRuntimeIssue(kind, error, phase, componentStack = "") {
  try {
    const message = String(error?.message || error || "unknown").slice(0, 500);
    const payload = {
      kind: String(kind || "unknown").slice(0, 48),
      phase: String(phase || "unknown").slice(0, 80),
      message,
      component_stack: String(componentStack || "").slice(0, 700),
    };
    fetch("/api/pedidos-web/diagnostico-cliente", {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch {
    // La captura no puede bloquear ni cambiar la experiencia del cliente.
  }
}

const developmentFixtures = {
  resumen: {
    pedidos_confirmados: 8,
    pedidos_entregados: 5,
    transferencias_pendientes: 2,
    valor_ventas: 286400,
    rutas_pendientes: 3,
    inventario_bajo: ["Leche cruda de vaca", "Arepas"],
    ventas_tendencia: [42, 48, 44, 61, 58, 72, 68],
    alertas: [
      {
        prioridad: "alta",
        titulo: "2 transferencias esperan validación",
        detalle: "Requieren revisión administrativa.",
        destino: "pagos",
      },
      {
        prioridad: "media",
        titulo: "Leche de coco bajo mínimo",
        detalle: "4 disponibles · mínimo 8.",
        destino: "inventario",
      },
      {
        prioridad: "baja",
        titulo: "Ruta de mañana por preparar",
        detalle: "5 pedidos confirmados.",
        destino: "rutas",
      },
    ],
    proximos_eventos: [
      {
        hora: "08:30",
        titulo: "Cierre de preparación",
        detalle: "Ruta programada",
      },
      { hora: "10:00", titulo: "Entrega a la medida", detalle: "2 pedidos" },
    ],
  },
  pedidos: {
    pedidos: [
      {
        id: "PED-DEMO-001",
        cliente: "María López",
        estado: "Confirmado",
        total: 42000,
        fecha: "Hoy",
        ruta: "Programada",
      },
      {
        id: "PED-DEMO-002",
        cliente: "Sebastián Ríos",
        estado: "Pendiente de reparto",
        total: 28600,
        fecha: "Hoy",
        ruta: "A la medida",
      },
      {
        id: "PED-DEMO-003",
        cliente: "Sara Gómez",
        estado: "En preparación",
        total: 51600,
        fecha: "Mañana",
        ruta: "Programada",
      },
    ],
  },
  inventario: {
    inventario: [
      {
        producto: "Arepas",
        disponible: 18,
        minimo: 10,
        estado: "Disponible",
        politica: "Capacidad diaria",
      },
      {
        producto: "Leche de coco",
        disponible: 4,
        minimo: 8,
        estado: "Bajo",
        politica: "Inventario estático",
      },
      {
        producto: "Quesito campesino",
        disponible: 12,
        minimo: 6,
        estado: "Disponible",
        politica: "Calendario",
      },
    ],
    movimientos: [
      {
        fecha: "Hoy 08:20",
        producto: "Arepas",
        tipo: "Reserva",
        cantidad: -5,
        referencia: "PED-DEMO-001",
      },
      {
        fecha: "Hoy 07:45",
        producto: "Leche de coco",
        tipo: "Entrada",
        cantidad: 8,
        referencia: "ENT-DEMO-002",
      },
    ],
  },
  rutas: {
    rutas: [
      {
        pedido: "PED-DEMO-001",
        fecha: "2026-08-25",
        cliente: "María López",
        direccion: "Centro, Supía",
        telefono: "3000000001",
        estado: "Pendiente",
        turno: "AM",
        orden: 1,
        repartidor: "",
      },
      {
        pedido: "PED-DEMO-002",
        fecha: "2026-08-25",
        cliente: "Sebastián Ríos",
        direccion: "Barrio La Plazuela, Supía",
        telefono: "3000000002",
        estado: "Pendiente",
        turno: "AM",
        orden: 2,
        repartidor: "",
      },
    ],
  },
  pagos: {
    pagos: [
      {
        referencia: "PAG-DEMO-001",
        pedido: "PED-DEMO-001",
        monto: 42000,
        estado: "Pendiente",
        metodo: "Transferencia",
      },
      {
        referencia: "PAG-DEMO-002",
        pedido: "PED-DEMO-002",
        monto: 28600,
        estado: "Validado",
        metodo: "Efectivo",
      },
    ],
    cartera: [
      {
        cliente: "María López",
        pedido: "PED-DEMO-004",
        saldo: 18000,
        vencimiento: "30 ago",
        estado: "Pendiente",
      },
      {
        cliente: "Sebastián Ríos",
        pedido: "PED-DEMO-005",
        saldo: 9200,
        vencimiento: "02 sep",
        estado: "Al día",
      },
    ],
  },
  estadisticas: {
    ventas_periodo: 286400,
    pedidos_periodo: 8,
    ticket_promedio: 35800,
    producto_mas_vendido: "Arepas",
    ventas_por_dia: [
      { dia: "Lun", valor: 42000 },
      { dia: "Mar", valor: 68000 },
      { dia: "Mié", valor: 51000 },
      { dia: "Jue", valor: 73400 },
      { dia: "Vie", valor: 52000 },
    ],
    ventas: [{ etiqueta: "Ventas", actual: 286400, anterior: 241800 }],
    productos: [
      { nombre: "Arepas", unidades: 32, ingresos: 86400 },
      { nombre: "Quesito campesino", unidades: 12, ingresos: 144000 },
      { nombre: "Leche cruda de vaca", unidades: 18, ingresos: 81000 },
    ],
    clientes: [
      { nombre: "María López", pedidos: 4, valor: 128000 },
      { nombre: "Sebastián Ríos", pedidos: 2, valor: 71600 },
      { nombre: "Sara Gómez", pedidos: 2, valor: 86800 },
    ],
    operacion: [
      { indicador: "Confirmados", valor: 8 },
      { indicador: "Entregados", valor: 5 },
      { indicador: "Pendientes", valor: 3 },
    ],
    inventario: [
      { nombre: "Arepas", reservas: 22, despachos: 18 },
      { nombre: "Leche de coco", reservas: 6, despachos: 4 },
    ],
    pagos: [
      { estado: "Validado", valor: 196000 },
      { estado: "Pendiente", valor: 90400 },
    ],
  },
};

// Las lecturas iguales se comparten para no duplicar consultas, pero nunca se
// comparten entre dos sesiones. Si una sesión anterior respondió tarde con
// 401, su promesa no puede reutilizarse después de que alguien vuelva a
// ingresar al panel.
const dashboardReadInFlight = new Map();
let dashboardReadScope = 0;

function iniciarNuevoAlcanceDashboard() {
  dashboardReadScope += 1;
  dashboardReadInFlight.clear();
  return dashboardReadScope;
}

function readDashboard(operation, body = {}, signal) {
  const scope = dashboardReadScope;
  const key = `${scope}:${operation}:${JSON.stringify(body)}`;
  const existente = dashboardReadInFlight.get(key);
  if (existente) return existente;
  const solicitud = (async () => {
    const response = await fetch(`/api/dashboard/${operation}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
      // Las solicitudes idénticas comparten una única consulta. El AbortSignal
      // no se pasa aquí para que el desmontaje de una vista no cancele la
      // respuesta que otra instancia ya está esperando.
    });
    if (!response.ok) {
      const error = new Error(`La consulta devolvió HTTP ${response.status}`);
      error.status = response.status;
      throw error;
    }
    const payload = await response.json();
    if (!payload.ok)
      throw new Error(payload.mensaje || "No fue posible consultar el dashboard");
    return {
      ...payload,
      data: normalizeDashboardData(operation, payload.data || {}),
    };
  })();
  dashboardReadInFlight.set(key, solicitud);
  solicitud.then(() => {
    if (dashboardReadInFlight.get(key) === solicitud) dashboardReadInFlight.delete(key);
  }, () => {
    if (dashboardReadInFlight.get(key) === solicitud) dashboardReadInFlight.delete(key);
  });
  return solicitud;
}

async function enviarOperacionInventario(solicitud) {
  // Una pérdida transitoria de transporte no significa que el ERP haya
  // rechazado el movimiento. Reutilizamos el mismo operacion_id: Inventario
  // lo trata de forma idempotente y jamás duplica una entrada o ajuste.
  let ultimoError;
  for (let intento = 0; intento < 2; intento += 1) {
    try {
      const response = await fetch("/api/dashboard/inventario/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(solicitud),
      });
      const payload = await response.json().catch(() => null);
      return { response, payload };
    } catch (error) {
      ultimoError = error;
      if (intento === 0 && error instanceof TypeError) {
        await new Promise((resolve) => window.setTimeout(resolve, 500));
        continue;
      }
    }
  }
  throw ultimoError;
}

async function readDashboardProfile(signal) {
  const response = await fetch("/api/dashboard/me", {
    method: "POST",
    credentials: "include",
    signal,
    cache: "no-store",
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok) {
    const error = new Error(
      payload.mensaje ||
        `No fue posible consultar el perfil (HTTP ${response.status})`,
    );
    error.status = response.status;
    throw error;
  }
  return payload.data || {};
}

async function readDashboardProfileWithRetry(signal) {
  // El navegador procesa Set-Cookie antes de resolver el login, pero los PWA
  // instalados pueden reanudar una ventana justo durante ese relevo. Un único
  // reintento breve evita convertir esa carrera transitoria en un cierre de
  // sesión. Un 401 persistente sigue siendo una sesión realmente inválida.
  let lastError;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      return await readDashboardProfile(signal);
    } catch (error) {
      lastError = error;
      if (error?.status !== 401 || attempt === 1) throw error;
      await new Promise((resolve) => window.setTimeout(resolve, 300));
    }
  }
  throw lastError;
}

// El montaje del PWA puede re-ejecutar efectos durante el relevo de sesión.
// Una ruta propia es una lectura idempotente: las vistas concurrentes de la
// misma fecha comparten el mismo viaje HTTP en lugar de saturar PostgreSQL.
const ownDeliveryRouteRequests = new Map();

async function readOwnDeliveryRoute(date) {
  const key = String(date || "");
  const previous = ownDeliveryRouteRequests.get(key);
  if (previous) return previous;
  const request = (async () => {
  const readRoute = fetch("/api/dashboard/rutas/operacion", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      operacion: "obtener_ruta_repartidor",
      datos: { fecha: date },
    }),
  });
  const readCashSummary = fetch("/api/dashboard/rutas/operacion", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      operacion: "resumen_caja_diaria",
      datos: { fecha: date },
    }),
  })
    .then(async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || `No fue posible consultar la caja diaria (HTTP ${response.status})`);
      return payload.data?.resumen || {};
    })
    .catch((error) => ({ fecha: date, error: error.message }));
  const [response, cashSummary] = await Promise.all([readRoute, readCashSummary]);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok) {
    const error = new Error(
      payload.mensaje ||
        `No fue posible consultar tu ruta (HTTP ${response.status})`,
    );
    error.status = response.status;
    throw error;
  }
  const rutas = payload.data?.ruta || payload.data?.rutas || payload.data?.rows || [];
  return {
    ...payload,
    data: normalizeDashboardData("rutas", {
      rutas: (Array.isArray(rutas) ? rutas : []).map((row) => ({
        ...row,
        // Compatibilidad con proyecciones antiguas que llamaban "detalle"
        // a las líneas del pedido y no "productos".
        productos: row.productos || row.Productos || row.detalle || row.Detalle || [],
        valor_total: row.valor_total ?? row.ValorTotal ?? row.Valor ?? row.total,
      })),
      caja_diaria: cashSummary,
      origen_ruta_predefinido: payload.data?.origen_ruta_predefinido || null,
    }),
  };
  })();
  ownDeliveryRouteRequests.set(key, request);
  try {
    return await request;
  } finally {
    if (ownDeliveryRouteRequests.get(key) === request)
      ownDeliveryRouteRequests.delete(key);
  }
}

async function readOwnDeliveryExpenses(date, signal) {
  const response = await fetch("/api/dashboard/gastos/operacion", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ operacion: "listar_propios", datos: { fecha: date } }),
    signal,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok) {
    const error = new Error(payload.mensaje || `No fue posible consultar tus gastos (HTTP ${response.status})`);
    error.status = response.status;
    throw error;
  }
  return {
    ...payload,
    data: normalizeDashboardData("gastos", { gastos: payload.data?.gastos || [] }),
  };
}

async function readOwnDeliveryReceivables(signal) {
  const response = await fetch("/api/dashboard/rutas/operacion", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ operacion: "listar_cartera_repartidor", datos: {} }),
    signal,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok) {
    const error = new Error(payload.mensaje || `No fue posible consultar cartera (HTTP ${response.status})`);
    error.status = response.status;
    throw error;
  }
  return { ...payload, data: { cartera: payload.data?.cartera || [] } };
}

function firstValue(row, keys) {
  for (const key of keys)
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  return undefined;
}

function normalizeRows(rows, aliases) {
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const result = { ...row };
    for (const [target, keys] of Object.entries(aliases)) {
      const value = firstValue(row, [target, ...keys]);
      if (value !== undefined) result[target] = value;
    }
    return result;
  });
}

function normalizeDashboardData(operation, data) {
  if (operation === "pedidos")
    return {
      ...data,
      pedidos: normalizeRows(data.pedidos, {
        id: ["Id Pedido", "PedidoId", "pedido_id", "Pedido"],
        cliente: ["Cliente", "ClienteNombre"],
        estado: ["Estado"],
        fecha: ["Fecha entrega", "FechaEntrega"],
        total: ["Valor", "Total"],
        ruta: ["Ruta"],
      }),
    };
  if (operation === "lista_espera")
    return {
      ...data,
      entradas: normalizeRows(data.entradas, {
        id: ["lista_espera_id", "ListaEsperaId"],
        producto: ["producto", "Producto", "codigo_presentacion"],
        presentacion: ["presentacion", "PresentacionDescripcion"],
        cliente: ["cliente", "Cliente"],
        telefono: ["telefono_contacto", "TelefonoContacto"],
        cantidad: ["cantidad", "CantidadSolicitada"],
        fecha: ["fecha_ingreso", "FechaIngreso"],
        fecha_solicitada: ["fecha_solicitada", "FechaSolicitada"],
        tipo: ["tipo_solicitud"],
        estado: ["estado", "Estado"],
        posicion: ["posicion"],
        politica_disponibilidad: ["politica_disponibilidad", "PoliticaDisponibilidad"],
        precio_referencia: ["precio_referencia", "Precio"],
        unidad_precio: ["unidad_precio", "UnidadPrecio"],
      }),
    };
  if (operation === "inventario") {
    const inventario = normalizeRows(data.inventario, {
      producto: ["Producto", "Nombre", "product_name", "codigo"],
      presentacion: [
        "Presentacion",
        "PresentacionDescripcion",
        "presentation_label",
      ],
      codigo: ["Codigo", "product_code", "CodigoProducto"],
      disponible: ["Disponible", "existencias", "Existencias"],
      existencias: ["Existencias", "on_hand"],
      reservado: ["Reservado", "reserved"],
      minimo: ["PuntoReorden", "punto_reorden", "reorder_point"],
      unidad: ["Unidad", "UnidadVenta", "sale_unit", "unit"],
      costo: ["costo_unitario_promedio", "average_unit_cost", "CostoUnitario"],
      costo_unitario_referencia: [
        "CostoUnitarioReferencia",
        "reference_unit_cost",
      ],
      valor_inventario: ["valor_inventario", "inventory_value"],
      estado: ["Estado", "status"],
      politica: ["Politica", "PoliticaDisponibilidad", "availability_policy"],
      fuente: ["Fuente", "source"],
    }).map((row) => {
      const disponible = Number(row.disponible);
      const costo = Number(row.costo);
      return {
        ...row,
        codigo_visual: shortInventoryCode(row.codigo),
        disponible: Number.isFinite(disponible) ? disponible : null,
        costo: Number.isFinite(costo) ? costo : null,
        valor_inventario:
          row.valor_inventario != null
            ? row.valor_inventario
            : String(row.politica || "").toUpperCase() ===
                  "INVENTARIO_ESTATICO" &&
                Number.isFinite(disponible) &&
                Number.isFinite(costo)
              ? disponible * costo
              : null,
      };
    });
    return { ...data, inventario };
  }
  if (operation === "gastos")
    return {
      ...data,
      gastos: normalizeRows(data.gastos, {
        id: ["gasto_id", "expense_id"],
        tipo: ["Tipo"],
        valor: ["Valor", "amount"],
        fecha: ["Fecha", "occurred_on"],
        descripcion: ["Descripción", "description"],
        estado: ["Estado", "status"],
        fuente_gasto: ["FuenteGasto", "expense_source", "fuente"],
      }),
    };
  if (operation === "rutas")
    return {
      ...data,
      rutas: normalizeRows(data.rutas, {
        pedido: ["Pedido", "PedidoId", "Id Pedido"],
        fecha: ["Fecha", "FechaEntrega", "Fecha entrega"],
        cliente: ["Cliente", "ClienteNombre", "NombreCliente"],
        telefono: ["Telefono", "Teléfono"],
        direccion: ["Direccion", "Dirección", "DireccionEntrega"],
        zona: ["Zona", "ZonaEntrega"],
        repartidor: ["Repartidor", "RepartidorNombre"],
        estado: ["Estado", "EstadoRuta"],
        turno: ["Turno"],
        hora: ["HoraEntrega", "hora_entrega", "Hora entrega"],
        tipo_entrega: ["TipoEntrega", "tipo_entrega"],
        valor_total: ["Valor", "valor_total", "ValorTotal"],
        productos: ["Productos", "productos", "Detalle", "detalle"],
        saldo_pendiente: ["SaldoPendiente", "saldo_pendiente", "Saldo"],
        metodo_pago: ["MetodoPago", "metodo_pago", "Metodo pago"],
        estado_pago: ["EstadoPago", "estado_pago"],
        orden: ["OrdenRuta", "Orden", "OrdenRuta"],
        observacion: ["Observacion", "Observación"],
      }),
    };
  if (operation === "pagos")
    return {
      ...data,
      pagos: normalizeRows(data.pagos, {
        referencia: ["OperacionPagoId", "PagoId"],
        pedido: ["PedidoId", "Pedido"],
        metodo: ["MetodoPago", "Metodo"],
        reportado: ["ValorReportado", "Monto", "Valor"],
        aprobado: ["ValorAprobado", "ValorValidado", "ValorReportado", "Monto", "Valor"],
        cliente: ["Cliente", "ClienteNombre"],
        telefono: ["Telefono", "Teléfono"],
        fecha_entrega: ["FechaEntrega", "Fecha entrega"],
        estado: ["Estado"],
        fecha: ["FechaDecision", "FechaReporte", "Fecha"],
        observacion: ["ObservacionDecision", "Observacion"],
      }),
    };
  if (operation === "envios_whatsapp")
    return {
      ...data,
      clientes: normalizeRows(data.clientes, {
        cliente_id: ["ClienteId", "customer_id"],
        cliente: ["Cliente", "Nombre", "commercial_name"],
        telefono: ["Telefono", "TelefonoContacto", "phone_contact"],
        cantidad_pedidos: ["cantidad_pedidos"],
        valor_facturado: ["valor_facturado"],
        ultima_compra: ["ultima_compra"],
      }),
    };
  if (operation === "cartera")
    return {
      ...data,
      cartera: normalizeRows(data.cartera, {
        id: ["id", "ID", "CarteraId"],
        cliente: ["Cliente", "ClienteNombre"],
        telefono: ["Telefono", "Teléfono"],
        pedido: ["PedidoId", "Pedido"],
        venta: ["Venta", "VentaId"],
        fecha: ["Fecha", "fecha"],
        saldo_pendiente: ["Saldo", "SaldoPendiente", "saldo_pendiente"],
        abonos: ["Abonos", "abonos"],
        valor: ["Valor", "valor"],
        motivo: ["Motivo", "motivo"],
        fecha_vencimiento: ["Fecha de vencimiento", "FechaVencimiento", "fecha_vencimiento"],
        estado: ["Estado"],
      }),
    };
  if (operation === "ventas")
    return {
      ...data,
      ventas: normalizeRows(data.ventas, {
        pedido: ["PedidoId", "Pedido"],
        cliente: ["Cliente", "ClienteNombre"],
        valor: ["Valor", "Total", "Monto"],
        fecha: ["Fecha"],
        estado: ["Estado"],
      }),
    };
  if (operation === "productos")
    return {
      ...data,
      productos: normalizeRows(data.productos, {
        codigo: ["Codigo", "product_code", "CodigoProducto"],
        producto: ["Producto", "Nombre", "product_name"],
        producto_canonico_id: ["ProductoCanonicoId", "catalog_product_id"],
        categoria: ["Categoria", "family_name"],
        presentacion: ["Presentacion", "presentation_label"],
        presentacion_descripcion: [
          "PresentacionDescripcion",
          "presentation_label",
        ],
        unidad: ["Unidad", "UnidadVenta", "sale_unit"],
        minimo: ["Minimo", "minimum_quantity"],
        precio: ["Precio", "price"],
        costo_unitario_referencia: [
          "CostoUnitarioReferencia",
          "reference_unit_cost",
        ],
        costo_producto: ["CostoProducto", "product_unit_cost"],
        costo_adicional: ["CostoAdicional", "additional_unit_cost"],
        descripcion: ["Descripcion", "description"],
        origen: ["Origen", "origin"],
        productor: ["Productor", "producer"],
        beneficios: ["Beneficios", "processing_description"],
        foto: ["Foto", "photo_url", "Imagen", "image_url", "image"],
        video: ["Video", "video_url"],
        contenido_cantidad: ["ContenidoCantidad", "content_value"],
        contenido_unidad: ["ContenidoUnidad", "content_unit"],
        unidades_empaque: ["UnidadesEmpaque", "units_per_package"],
        peso_neto_gramos: ["PesoNetoGramos", "net_weight_g"],
        peso_unidad_gramos: ["PesoUnidadGramos", "unit_weight_g"],
        tipo_empaque: ["TipoEmpaque", "package_type"],
        material_empaque: ["MaterialEmpaque", "package_material"],
        cierre_empaque: ["CierreEmpaque", "closure_type"],
        requiere_refrigeracion: [
          "RequiereRefrigeracion",
          "requires_refrigeration",
        ],
        conservacion: ["Conservacion", "storage"],
        vida_util: ["VidaUtil", "shelf_life_closed"],
        vida_util_cerrado: ["VidaUtilCerrado", "shelf_life_closed"],
        vida_util_abierto: ["VidaUtilAbierto", "shelf_life_opened"],
        ingredientes: ["Ingredientes", "ingredients"],
        caracteristicas: ["Caracteristicas", "relevant_characteristics"],
        recomendacion_consumo: [
          "RecomendacionConsumo",
          "consumption_recommendation",
        ],
        frecuencia_produccion: ["FrecuenciaProduccion", "production_frequency"],
        politica: ["Politica", "PoliticaDisponibilidad", "availability_policy"],
        disponibilidad: [
          "Disponibilidad",
          "EstadoComercial",
          "availability_status",
        ],
        capacidad: ["CodigoCapacidad", "capacity_source_code"],
        unidad_capacidad: ["UnidadCapacidad", "capacity_unit"],
        capacidad_base: ["CapacidadBase", "base_capacity"],
        capacidad_maxima: ["CapacidadMaxima", "maximum_capacity"],
        dias_operacion: ["DiasOperacion", "operating_days"],
        capacidad_activa: ["CapacidadActiva", "capacity_active"],
        visible: ["CatalogoVisible", "catalog_visible"],
      }),
    };
  return data;
}

function numericValue(row, keys) {
  const value = firstValue(row, keys);
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function shortInventoryCode(value) {
  const code = String(value || "").trim();
  if (!code) return "—";
  return code.length <= 18 ? code : `${code.slice(0, 10)}…${code.slice(-5)}`;
}

async function readStatistics(range, signal) {
  const fechaDesde = String(range?.fecha_desde || "");
  const fechaHasta = String(range?.fecha_hasta || todayRouteKey());
  // La primera pantalla de Estadísticas es Ventas. Antes se abrían cuatro
  // informes pesados al mismo tiempo, aunque tres pertenecían a pestañas que
  // nadie había solicitado todavía. El resto se carga al abrir su pestaña.
  const estadisticasVentas = await readDashboard(
    "estadisticas_ventas",
    { fecha_desde: fechaDesde, fecha_hasta: fechaHasta, incluir_costos: false },
    signal,
  );
  const ventasInforme = estadisticasVentas.data || {};
  const resumenVentas = ventasInforme.resumen || {};
  const diasVentas = ventasInforme.dias || [];
  return {
    ok: true,
    data: {
      ventas_periodo: resumenVentas.valor_ventas ?? 0,
      pedidos_periodo: resumenVentas.cantidad_ventas ?? 0,
      pedidos_cancelados_periodo: 0,
      valor_pedidos_periodo: 0,
      ticket_promedio: resumenVentas.ticket_promedio ?? 0,
      producto_mas_vendido: null,
      ventas_por_dia: diasVentas.slice().reverse().map((dia) => ({
        dia: String(dia.fecha || "").slice(5),
        valor: dia.total || 0,
      })),
      ventas: diasVentas,
      productos: ventasInforme.productos || {},
      clientes: ventasInforme.clientes || {},
      operacion: {},
      inventario: {},
      pagos: {},
      cartera: {},
      embudo_web: {},
      productos_mas_consultados: [],
      periodo_analitica: { fecha_desde: fechaDesde || analyticsPeriodStart(fechaHasta, 30), fecha_hasta: fechaHasta },
      periodo_ventas: ventasInforme.periodo || { fecha_desde: fechaDesde, fecha_hasta: fechaHasta },
      ventas_por_medio_pago: ventasInforme.por_medio_pago || [],
      resumen_pagos_visual: ventasInforme.resumen_pagos_visual || {},
      canales_ventas: ventasInforme.por_canal || [],
      caja_presencial: ventasInforme.caja_presencial || { sesiones: [], resumen: {} },
      informe_costos_reales: ventasInforme.informe_costos_reales || {},
      finanzas_por_dia: [],
      finanzas_por_semana: [],
      _fuentes: ["estadisticas_ventas"],
    },
  };
}

async function readStatisticsSupplement(section, range) {
  const fechaDesde = String(range?.fecha_desde || "");
  const fechaHasta = String(range?.fecha_hasta || todayRouteKey());
  if (section === "operativas") {
    const response = await readDashboard("estadisticas_operativas", { fecha_desde: fechaDesde, fecha_hasta: fechaHasta });
    return response.data || {};
  }
  if (section === "finanzas") {
    const response = await readDashboard("estadisticas_financieras", { fecha_desde: fechaDesde, fecha_hasta: fechaHasta });
    return { finanzas_por_dia: response.data?.dias || [], finanzas_por_semana: response.data?.semanas || [] };
  }
  if (section === "analitica") {
    const response = await readDashboard("analitica_pedidos_web", {
      fecha_desde: fechaDesde || analyticsPeriodStart(fechaHasta, 30),
      fecha_hasta: fechaHasta,
    });
    return {
      embudo_web: response.data?.embudo || {},
      productos_mas_consultados: response.data?.productos_mas_consultados || [],
      periodo_analitica: response.data?.periodo || {},
    };
  }
  if (section === "costos") {
    const response = await readDashboard("estadisticas_ventas", {
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
      incluir_costos: true,
    });
    return { informe_costos_reales: response.data?.informe_costos_reales || {} };
  }
  return {};
}

async function readDailyLiquidation(fecha, signal) {
  const response = await fetch("/api/dashboard/liquidacion/operacion", {
    method: "POST",
    credentials: "include",
    signal,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ operacion: "resumen", datos: { fecha } }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok)
    throw Object.assign(new Error(payload.mensaje || `No fue posible cargar la liquidación (HTTP ${response.status})`), { status: response.status });
  return { ok: true, data: payload.data || {} };
}

function App() {
  const initialPushOrderId = new URLSearchParams(window.location.search).get("pedido") || "";
  const initialModule = new URLSearchParams(window.location.search).get("modulo") || "";
  // El panel operativo abre Pedidos por defecto. Resumen es una consulta
  // pesada y solo debe ejecutarse cuando se selecciona explícitamente.
  const initialActiveModule = initialModule !== "resumen" && sections.some(([id]) => id === initialModule)
    ? initialModule
    : "pedidos";
  const [active, setActive] = useState(initialPushOrderId ? "pedidos" : initialActiveModule);
  const [openNavGroups, setOpenNavGroups] = useState(() => Object.fromEntries(navigationGroups.map((group) => [group.id, true])));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [pushOrderId, setPushOrderId] = useState(initialPushOrderId);
  const [payload, setPayload] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [error, setError] = useState("");
  // `null` significa que aún no sabemos si la cookie HttpOnly contiene una
  // sesión vigente. Nunca mostramos el formulario hasta comprobarlo: una
  // recarga del PWA no debe convertir una sesión válida en una pantalla vacía.
  const [authenticated, setAuthenticated] = useState(null);
  const [profile, setProfile] = useState(null);
  // Mientras se recupera la cookie existente, la primera pantalla siempre
  // comunica que está verificando el acceso; no muestra un falso error.
  const [profileState, setProfileState] = useState("loading");
  const [profileError, setProfileError] = useState("");
  const [profileReload, setProfileReload] = useState(0);
  const [reload, setReload] = useState(0);
  const [date, setDate] = useState(() => todayRouteKey());
  const [orderQuery, setOrderQuery] = useState({
    scope: "PROXIMAS",
    estado: "",
    productoCodigo: "",
    productoModo: "TODOS",
    offset: 0,
  });
  const [routeQuery, setRouteQuery] = useState({
    alcance: "PROXIMAS",
    estado: "TODOS",
  });
  const [statisticsRange, setStatisticsRange] = useState(() => {
    const hasta = todayRouteKey();
    return { fecha_desde: analyticsPeriodStart(hasta, 3), fecha_hasta: hasta };
  });
  const [auditRange, setAuditRange] = useState(() => {
    const hasta = todayRouteKey();
    return { fecha_desde: analyticsPeriodStart(hasta, 1), fecha_hasta: hasta };
  });
  const [liquidationDate, setLiquidationDate] = useState(() => todayRouteKey());
  const [pwaUpdate, setPwaUpdate] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const [confirmacion, setConfirmacion] = useState(null);

  useEffect(() => {
    function closeTopmostWindow(event) {
      if (event.key !== "Escape") return;
      const ventanas = Array.from(document.querySelectorAll(".drawer-backdrop, .admin-confirm-backdrop"));
      const ventana = ventanas[ventanas.length - 1];
      if (!ventana) return;
      event.preventDefault();
      // Los drawers escuchan click y la caja administrativa escucha mousedown.
      // Disparamos ambos sobre el fondo, nunca sobre el formulario interno.
      ventana.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
      ventana.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    }
    document.addEventListener("keydown", closeTopmostWindow);
    return () => document.removeEventListener("keydown", closeTopmostWindow);
  }, []);
  // Una actualización solicitada por la persona administradora debe tener un
  // único dueño mientras está en curso. Esto evita lanzar dos lecturas grandes
  // de inventario al pulsar "Actualizar tabla" varias veces.
  const refreshInFlightRef = useRef(null);
  const refreshResolverRef = useRef(null);
  // La generación separa una sesión nueva de todas las respuestas que quedaron
  // en vuelo antes de iniciar/cerrar sesión. Ninguna de esas respuestas puede
  // devolver al acceso a una persona que acaba de autenticarse.
  const authGenerationRef = useRef(0);
  const authRecoveryRef = useRef(null);
  const [accessNotice, setAccessNotice] = useState("");

  function cerrarSesionConfirmada(generation, notice = "") {
    if (generation !== authGenerationRef.current) return false;
    authGenerationRef.current += 1;
    iniciarNuevoAlcanceDashboard();
    authRecoveryRef.current = null;
    setPayload(null);
    setProfile(null);
    setProfileState("idle");
    setProfileError("");
    setIsLoadingData(false);
    setAccessNotice(notice);
    setAuthenticated(false);
    return true;
  }

  async function revalidarSesionTras401(generation) {
    if (generation !== authGenerationRef.current) return false;
    const recovery = authRecoveryRef.current;
    if (recovery?.generation === generation) return recovery.promise;
    const promise = readDashboardProfileWithRetry()
      .then((value) => {
        if (generation !== authGenerationRef.current) return false;
        setProfile(value);
        setProfileState("ready");
        setProfileError("");
        // La cookie sigue vigente: se descarta la lectura que falló y se
        // vuelve a pedir únicamente la vista que la persona estaba viendo.
        iniciarNuevoAlcanceDashboard();
        setReload((value) => value + 1);
        return true;
      })
      .catch((reason) => {
        if (generation !== authGenerationRef.current) return false;
        if (reason?.status === 401) {
          cerrarSesionConfirmada(
            generation,
            "Tu sesión venció. Ingresa nuevamente para continuar.",
          );
          return false;
        }
        const detail = reason?.message || "No pudimos confirmar la sesión. Inténtalo de nuevo.";
        setError(detail);
        notificarAdmin(detail, "error");
        return false;
      })
      .finally(() => {
        if (authRecoveryRef.current?.generation === generation)
          authRecoveryRef.current = null;
      });
    authRecoveryRef.current = { generation, promise };
    return promise;
  }

  useEffect(() => {
    function onToast(event) {
      const detalle = event.detail || {};
      const aviso = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        mensaje: String(detalle.mensaje || ""),
        tipo: ["success", "error", "warning", "info"].includes(detalle.tipo)
          ? detalle.tipo
          : "info",
      };
      if (!aviso.mensaje) return;
      setAvisos((actuales) => [...actuales, aviso].slice(-3));
      window.setTimeout(() => {
        setAvisos((actuales) => actuales.filter((item) => item.id !== aviso.id));
      }, 5500);
    }
    window.addEventListener(ADMIN_TOAST_EVENT, onToast);
    return () => window.removeEventListener(ADMIN_TOAST_EVENT, onToast);
  }, []);

  useEffect(() => {
    function onConfirm(event) {
      const detalle = event.detail || {};
      if (typeof detalle.onConfirm === "function") setConfirmacion(detalle);
    }
    window.addEventListener(ADMIN_CONFIRM_EVENT, onConfirm);
    return () => window.removeEventListener(ADMIN_CONFIRM_EVENT, onConfirm);
  }, []);

  function resolverActualizacion(resultado) {
    const resolver = refreshResolverRef.current;
    refreshResolverRef.current = null;
    refreshInFlightRef.current = null;
    resolver?.(resultado);
  }

  function actualizarVistaActual({ notificar = true } = {}) {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    const solicitud = new Promise((resolve) => {
      refreshResolverRef.current = resolve;
      setReload((value) => value + 1);
    });
    refreshInFlightRef.current = solicitud;
    if (notificar) {
      solicitud.then((resultado) => {
        notificarAdmin(
          resultado?.ok === false
            ? resultado.mensaje || "No fue posible actualizar la información."
            : "Información actualizada.",
          resultado?.ok === false ? "error" : "success",
        );
      });
    }
    return solicitud;
  }

  useEffect(() => {
    function onUpdate(event) {
      setPwaUpdate(() => event.detail?.apply || null);
    }
    window.addEventListener("titina-admin-pwa-update", onUpdate);
    if (typeof window.__titinaAdminPwaUpdate === "function")
      setPwaUpdate(() => window.__titinaAdminPwaUpdate);
    return () =>
      window.removeEventListener("titina-admin-pwa-update", onUpdate);
  }, []);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const authGeneration = authGenerationRef.current;
    if (DEV_BYPASS_AUTH) {
      setAuthenticated(true);
      setProfile({
        nombre: "Vista local",
        roles: ["ADMINISTRADOR"],
        permisos: ["DASHBOARD_CONSULTAR"],
      });
      return () => {
        alive = false;
        controller.abort();
      };
    }
    if (authenticated === false) {
      setProfile(null);
      setProfileState("idle");
      setProfileError("");
      return () => {
        alive = false;
        controller.abort();
      };
    }
    // Después de un login exitoso el backend ya devolvió un perfil RBAC
    // calculado en la misma operación. No volvemos a bloquear la entrada con
    // una segunda llamada de sesión + permisos. Las sesiones ya existentes
    // siguen pasando por la validación unificada de /dashboard/me.
    if (profile) {
      setProfileState("ready");
      setProfileError("");
      return () => {
        alive = false;
        controller.abort();
      };
    }
    setProfileState("loading");
    setProfileError("");
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 12_000);
    readDashboardProfileWithRetry(controller.signal)
      .then((value) => {
        if (!alive || authGeneration !== authGenerationRef.current) return;
        setProfile(value);
        setProfileState("ready");
        setAuthenticated(true);
      })
      .catch((reason) => {
        if (!alive || (reason.name === "AbortError" && !timedOut)) return;
        if (reason.status === 401) {
          // En el arranque, 401 sólo significa que no había una sesión previa.
          // No es un error ni una razón para dejar el PWA bloqueado.
          if (authenticated === null) {
            setProfile(null);
            setProfileState("idle");
            setProfileError("");
            setAuthenticated(false);
            return;
          }
          cerrarSesionConfirmada(
            authGeneration,
            "Tu sesión venció. Ingresa nuevamente para continuar.",
          );
          return;
        }
        const detail = timedOut
          ? "La validación de acceso tardó demasiado. Revisa tu conexión e inténtalo de nuevo."
          : reason.message || "No fue posible consultar tu acceso.";
        setProfileState("error");
        setProfileError(detail);
        setError(detail);
        notificarAdmin(detail, "error");
      })
      .finally(() => window.clearTimeout(timeout));
    return () => {
      alive = false;
      controller.abort();
    };
  }, [authenticated, profile, profileReload]);

  const deliveryOnly = Boolean(
    profile?.roles?.includes("REPARTIDOR") &&
    !profile?.permisos?.includes("DASHBOARD_CONSULTAR"),
  );
  // No mostrar módulos hasta que el perfil RBAC real haya sido resuelto. Si
  // se usa `sections` mientras profile es null, un repartidor ve brevemente
  // todo el panel durante la consulta de permisos.
  const visibleSections = !profile
    ? []
    : deliveryOnly
      ? sections.filter(([id]) => id === "rutas" || id === "gastos" || id === "cartera")
      : sections;
  const visibleSectionIds = new Set(visibleSections.map(([id]) => id));
  const visibleNavigationGroups = deliveryOnly
    ? [{ id: "reparto", label: "Mi operación", items: ["rutas", "cartera", "gastos"] }]
    : navigationGroups;

  useEffect(() => {
    if (deliveryOnly && active !== "rutas" && active !== "gastos" && active !== "cartera") setActive("rutas");
  }, [deliveryOnly, active]);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const authGeneration = authGenerationRef.current;
    let summaryDelay = null;
    setError("");
    // Una actualización operativa no debe desmontar la pantalla ni borrar
    // la información visible mientras llega la respuesta nueva.
    if (!payload) setPayload(null);
    if (DEV_BYPASS_AUTH) {
      setAuthenticated(true);
      setPayload({ ok: true, data: developmentFixtures[active] });
      return () => {
        alive = false;
        controller.abort();
      };
    }
    if (authenticated === false || !profile || profile.requiere_cambio_password)
      return () => {
        alive = false;
        controller.abort();
      };
    setIsLoadingData(true);
    const esperaResumen = active === "resumen"
      ? new Promise((resolve) => {
          summaryDelay = setTimeout(resolve, 350);
        })
      : Promise.resolve();
    const consulta = esperaResumen.then(() => deliveryOnly
      ? active === "gastos"
        ? readOwnDeliveryExpenses(todayRouteKey(), controller.signal)
        : active === "cartera"
          ? readOwnDeliveryReceivables(controller.signal)
          : readOwnDeliveryRoute(todayRouteKey())
      : active === "usuarios"
        ? Promise.resolve({ ok: true, data: {} })
          : active === "estadisticas"
            ? readStatistics(statisticsRange, controller.signal)
          : active === "auditoria_entregas"
            ? readDashboard("auditoria_entregas", auditRange, controller.signal)
          : active === "liquidacion"
            ? readDailyLiquidation(liquidationDate, controller.signal)
          : active === "pedidos"
            ? readDashboard("pedidos", pedidoDashboardRequest(orderQuery), controller.signal)
            : active === "rutas"
              ? readDashboard("rutas", routeQuery, controller.signal)
            : readDashboard(
                active === "configuracion" ? "productos" : active,
                active === "resumen" ? { fecha: date } : {},
                controller.signal,
              ));
    consulta
      .then((value) => {
        if (!alive || authGeneration !== authGenerationRef.current) return;
        setPayload(value);
        setIsLoadingData(false);
        resolverActualizacion({ ok: true });
      })
      .catch(async (reason) => {
        if (!alive || reason.name === "AbortError") return;
        if (reason.status === 401) {
          const sessionRecovered = await revalidarSesionTras401(authGeneration);
          if (!alive || authGeneration !== authGenerationRef.current) return;
          setIsLoadingData(false);
          resolverActualizacion({
            ok: sessionRecovered,
            mensaje: sessionRecovered ? "" : "No fue posible confirmar tu sesión.",
          });
          return;
        }
        setIsLoadingData(false);
        {
          setError(reason.message);
          notificarAdmin(reason.message || "No fue posible actualizar la vista.", "error");
        }
        resolverActualizacion({
          ok: false,
          mensaje: reason.message || "No fue posible actualizar la vista.",
        });
      });
    return () => {
      alive = false;
      controller.abort();
      if (summaryDelay !== null) clearTimeout(summaryDelay);
    };
  }, [active, authenticated, profile, deliveryOnly, reload, date, statisticsRange, auditRange, liquidationDate, orderQuery, routeQuery]);

  function actualizarConsultaPedidos(changes) {
    setOrderQuery((current) => ({
      ...current,
      ...changes,
      // Un filtro nuevo siempre vuelve a la primera página. La paginación
      // conserva su offset únicamente cuando el propio paginador lo cambia.
      offset: Object.prototype.hasOwnProperty.call(changes || {}, "offset")
        ? Math.max(0, Number(changes.offset) || 0)
        : 0,
    }));
  }

  function actualizarConsultaRutas(changes) {
    setRouteQuery((current) => ({ ...current, ...changes }));
  }

  async function iniciarSesion(username, password) {
    const response = await fetch("/api/dashboard/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      cache: "no-store",
      body: JSON.stringify({ username, password }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.ok) {
      throw new Error(
        result.mensaje ||
          `No fue posible iniciar sesión (HTTP ${response.status})`,
      );
    }
    authGenerationRef.current += 1;
    iniciarNuevoAlcanceDashboard();
    authRecoveryRef.current = null;
    setPayload(null);
    setAccessNotice("");
    setError("");
    setProfileError("");
    const perfil = result?.data?.perfil;
    if (perfil && typeof perfil === "object" && perfil.usuario_operativo_id) {
      setProfile(perfil);
      setProfileState("ready");
    } else {
      setProfile(null);
      setProfileState("loading");
    }
    setAuthenticated(true);
  }

  async function cerrarSesion() {
    authGenerationRef.current += 1;
    iniciarNuevoAlcanceDashboard();
    authRecoveryRef.current = null;
    if (!DEV_BYPASS_AUTH)
      await fetch("/api/dashboard/logout", {
        method: "POST",
        credentials: "include",
      });
    setPayload(null);
    setProfile(null);
    setProfileState("idle");
    setProfileError("");
    setIsLoadingData(false);
    setAccessNotice("");
    setAuthenticated(false);
  }

  function reintentarPerfil() {
    setError("");
    setProfileError("");
    setProfileState("loading");
    setProfileReload((value) => value + 1);
  }

  async function cambiarPassword(passwordActual, passwordNueva) {
    const response = await fetch("/api/dashboard/password", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        password_actual: passwordActual,
        password_nueva: passwordNueva,
      }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.ok)
      throw new Error(
        result.mensaje || "No fue posible actualizar la contraseña.",
      );
    authGenerationRef.current += 1;
    iniciarNuevoAlcanceDashboard();
    authRecoveryRef.current = null;
    setPayload(null);
    setProfile(null);
    setError("");
    setProfileState("loading");
    setProfileReload((value) => value + 1);
  }

  if (authenticated === false) return <Login onSubmit={iniciarSesion} notice={accessNotice} />;
  if (!DEV_BYPASS_AUTH && profile?.requiere_cambio_password)
    return (
      <ChangePassword onSubmit={cambiarPassword} onLogout={cerrarSesion} />
    );
  if (!profile)
    return (
      <ProfileBootstrap
        state={profileState}
        error={profileError}
        onRetry={reintentarPerfil}
        onLogout={cerrarSesion}
      />
    );
  const data = payload?.data || {};
  const activeSection =
    visibleSections.find(([id]) => id === active) || visibleSections[0];
  const mobileNavItems = (deliveryOnly ? ["rutas", "cartera", "gastos"] : ["pedidos", "estadisticas", "auditoria_entregas"])
    .map((id) => sections.find(([sectionId]) => sectionId === id))
    .filter((section) => section && visibleSectionIds.has(section[0]));
  return (
    <>
      <main className={`shell${deliveryOnly ? " delivery-shell" : ""}`}>
        {mobileNavOpen && <button className="mobile-nav-backdrop" type="button" aria-label="Cerrar menú" onClick={() => setMobileNavOpen(false)} />}
        <aside className={`sidebar${mobileNavOpen ? " mobile-open" : ""}`}>
          <div className="brand">
            <span className="brand-mark">T</span>
            <div>
              <strong>Titina</strong>
              <small>
                {deliveryOnly ? "Mi ruta de reparto" : "Panel operativo"}
              </small>
            </div>
          </div>
          <nav className="main-navigation" aria-label="Módulos">
            {visibleNavigationGroups.map((group) => {
              const items = group.items
                .map((id) => sections.find(([sectionId]) => sectionId === id))
                .filter((section) => section && visibleSectionIds.has(section[0]));
              if (!items.length) return null;
              const expanded = openNavGroups[group.id] !== false || items.some(([id]) => id === active);
              return (
                <div className="nav-group" key={group.id}>
                  <button
                    type="button"
                    className="nav-group-toggle"
                    aria-expanded={expanded}
                    onClick={() => setOpenNavGroups((current) => ({ ...current, [group.id]: !expanded }))}
                  >
                    <span>{group.label}</span><b aria-hidden="true">{expanded ? "−" : "+"}</b>
                  </button>
                  {expanded && <div className="nav-group-items">
                    {items.map(([id, label, hint, Icon]) => (
                      <button
                        className={active === id ? "nav-item active" : "nav-item"}
                        key={id}
                        onClick={() => {
                          setOpenNavGroups((current) => ({ ...current, [group.id]: true }));
                          setActive(id);
                          setMobileNavOpen(false);
                        }}
                      >
                        <Icon size={17} strokeWidth={1.8} />
                        <span><b>{label}</b><small>{hint}</small></span>
                      </button>
                    ))}
                  </div>}
                </div>
              );
            })}
          </nav>
          <div className="sidebar-profile">
            <span className="sidebar-profile-dot" aria-hidden="true" />
            <div>
              <small>{deliveryOnly ? "Sesión de reparto" : "Sesión operativa"}</small>
              <b>{profile?.nombre || (DEV_BYPASS_AUTH ? "Vista local" : "Usuario conectado")}</b>
            </div>
          </div>
        </aside>
        <section className="content">
          <header className="topbar">
            <div>
              <p className="eyebrow">
                {deliveryOnly ? "REPARTO" : "OPERACIÓN DIARIA"}
              </p>
              <h1>{deliveryOnly ? "Mi ruta de hoy" : activeSection?.[1]}</h1>
            </div>
            <div className="topbar-actions">
              <button className="mobile-nav-trigger" type="button" aria-label="Abrir menú" onClick={() => setMobileNavOpen(true)}>
                <Menu size={21} />
              </button>
              <span
                className={DEV_BYPASS_AUTH ? "status development" : "status"}
              >
                <i />
                {DEV_BYPASS_AUTH
                  ? "Datos de desarrollo"
                  : deliveryOnly
                    ? "Ruta asignada"
                    : "Fuente operativa conectada"}
              </span>
              <button className="logout" onClick={cerrarSesion}>
                Cerrar sesión
              </button>
            </div>
          </header>
          {error && (
            <div className="notice error">
              <strong>La vista no pudo cargarse.</strong>
              <span>{error}</span>
            </div>
          )}
          {!error && !profile && (
            <PanelLoadingState message="Verificando tu acceso…" />
          )}
          {!error && profile && !payload && (
            <PanelLoadingState message="Preparando tu panel operativo…" />
          )}
          {!error && payload && (
            <div className="dashboard-content-stage">
              {isLoadingData && <div className="panel-refresh-indicator" role="status" aria-live="polite"><span className="panel-refresh-spinner" aria-hidden="true" /><span>{active === "pedidos" ? "Aplicando filtro…" : "Actualizando información…"}</span></div>}
              <Overview
                data={data}
                active={active}
                onNavigate={setActive}
                date={date}
                onDateChange={setDate}
                statisticsRange={statisticsRange}
                onStatisticsRangeChange={setStatisticsRange}
                auditRange={auditRange}
                onAuditRangeChange={setAuditRange}
                liquidationDate={liquidationDate}
                onLiquidationDateChange={setLiquidationDate}
                orderQuery={orderQuery}
                onOrderQueryChange={actualizarConsultaPedidos}
                routeQuery={routeQuery}
                onRouteQueryChange={actualizarConsultaRutas}
                onRefresh={actualizarVistaActual}
                deliveryOnly={deliveryOnly}
                pushOrderId={pushOrderId}
                onPushOrderOpened={() => {
                  setPushOrderId("");
                  const url = new URL(window.location.href);
                  url.searchParams.delete("pedido");
                  window.history.replaceState({}, "", url);
                }}
              />
            </div>
          )}
        </section>
      </main>
      <nav className={`mobile-bottom-nav${deliveryOnly ? " delivery-mobile-nav" : ""}`} aria-label="Accesos principales">
        {mobileNavItems.map(([id, label, , Icon]) => (
          <button type="button" className={active === id ? "active" : ""} key={id} onClick={() => setActive(id)}>
            <Icon size={19} /><span>{label}</span>
          </button>
        ))}
        <button type="button" className={mobileNavOpen ? "active" : ""} onClick={() => setMobileNavOpen(true)}>
          <Menu size={20} /><span>Más</span>
        </button>
      </nav>
      {pwaUpdate && (
        <div className="pwa-update-notice" role="status">
          <span>Hay una nueva versión de Titina Admin.</span>
          <button type="button" onClick={() => pwaUpdate()}>
            Actualizar ahora
          </button>
        </div>
      )}
      <ToastViewport
        avisos={avisos}
        onDismiss={(id) => setAvisos((actuales) => actuales.filter((aviso) => aviso.id !== id))}
      />
      <ConfirmationDialog confirmacion={confirmacion} onClose={() => setConfirmacion(null)} />
    </>
  );
}

function AccessBrandPanel({ eyebrow = "PANEL OPERATIVO", title = "La operación diaria, en un solo lugar.", description = "Pedidos, rutas, caja y control operativo para Titina." }) {
  return <section className="access-brand-panel">
    <div className="access-brand-content">
      <div className="access-brand-lockup">
        <span className="brand-mark">T</span>
        <span><strong>Titina</strong><small>Panel operativo</small></span>
      </div>
      <div className="access-brand-copy">
        <p>{eyebrow}</p>
        <h1>{title}</h1>
        <span>{description}</span>
      </div>
    </div>
    <small className="access-brand-footer">Operación conectada y protegida</small>
  </section>;
}

function PanelLoadingState({ message }) {
  return <section className="panel-loading-state" role="status" aria-live="polite">
    <span className="panel-loading-spinner" aria-hidden="true" />
    <div><strong>{message}</strong><span>Estamos conectando la información necesaria.</span></div>
  </section>;
}

function Login({ onSubmit, notice = "" }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  async function submit(event) {
    event.preventDefault();
    setError("");
    try {
      await onSubmit(username, password);
    } catch (reason) {
      setError(reason.message);
    }
  }
  return (
    <main className="access-shell">
      <AccessBrandPanel />
      <section className="access-form-panel">
      <form className="access-form" onSubmit={submit}>
        <p className="access-kicker">ACCESO SEGURO</p>
        <h2>Bienvenido</h2>
        <p className="access-description">Ingresa con tus credenciales para continuar.</p>
        {notice && <div className="notice warning" role="status">{notice}</div>}
        <label>
          Usuario
          <input
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            required
          />
        </label>
        <label>
          Contraseña
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        {error && <div className="notice error">{error}</div>}
        <button className="login-submit" type="submit">
          Entrar al panel
        </button>
      </form>
      </section>
    </main>
  );
}

function ProfileBootstrap({ state, error, onRetry, onLogout }) {
  const waiting = state === "loading";
  return (
    <main className="access-shell profile-bootstrap">
      <AccessBrandPanel />
      <section className="access-form-panel">
      <section className="access-form" aria-live="polite">
        <p className="access-kicker">ACCESO SEGURO</p>
        <h2>{waiting ? "Preparando tu panel" : "No pudimos abrir tu panel"}</h2>
        <p className="access-description">
          {waiting
            ? "Estamos verificando tu sesión y los permisos de este usuario."
            : error || "Tu sesión no pudo validarse por ahora."}
        </p>
        {waiting ? (
          <span className="profile-bootstrap-progress">Validando sesión…</span>
        ) : (
          <div className="profile-bootstrap-actions">
            <button type="button" className="login-submit" onClick={onRetry}>
              Reintentar
            </button>
            <button type="button" className="secondary-action" onClick={onLogout}>
              Cerrar sesión
            </button>
          </div>
        )}
      </section>
      </section>
    </main>
  );
}

function ChangePassword({ onSubmit, onLogout }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  async function submit(event) {
    event.preventDefault();
    setError("");
    if (next !== repeat) {
      setError("Las contraseñas nuevas no coinciden.");
      return;
    }
    if (next.length < 6) {
      setError("La nueva contraseña debe tener al menos 6 caracteres.");
      return;
    }
    setSaving(true);
    try {
      await onSubmit(current, next);
    } catch (reason) {
      setError(reason.message || "No fue posible actualizar la contraseña.");
    } finally {
      setSaving(false);
    }
  }
  return (
    <main className="access-shell">
      <AccessBrandPanel eyebrow="ACCESO PERSONAL" title="Tu cuenta también protege la operación." description="Crea una contraseña propia antes de continuar al panel." />
      <section className="access-form-panel">
      <form className="access-form" onSubmit={submit}>
        <p className="access-kicker">PRIMER INGRESO</p>
        <h2>Crea tu contraseña</h2>
        <p className="access-description">Reemplaza la clave temporal antes de ver tu panel.</p>
        <label>
          Contraseña temporal
          <input
            type="password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <label>
          Nueva contraseña
          <input
            type="password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            autoComplete="new-password"
            minLength="6"
            required
          />
        </label>
        <label>
          Repite la nueva contraseña
          <input
            type="password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            autoComplete="new-password"
            minLength="6"
            required
          />
        </label>
        {error && <div className="notice error">{error}</div>}
        <button className="login-submit" type="submit" disabled={saving}>
          {saving ? "Guardando…" : "Guardar y continuar"}
        </button>
        <button className="login-secondary" type="button" onClick={onLogout}>
          Cerrar sesión
        </button>
      </form>
      </section>
    </main>
  );
}

function Overview({
  data,
  active,
  onNavigate,
  date,
  onDateChange,
  statisticsRange,
  onStatisticsRangeChange,
  auditRange,
  onAuditRangeChange,
  liquidationDate,
  onLiquidationDateChange,
  orderQuery,
  onOrderQueryChange,
  routeQuery,
  onRouteQueryChange,
  onRefresh,
  deliveryOnly = false,
  pushOrderId = "",
  onPushOrderOpened,
}) {
  if (active === "resumen")
    return (
      <Summary
        data={data}
        onNavigate={onNavigate}
        date={date}
        onDateChange={onDateChange}
        onRefresh={onRefresh}
      />
    );
  if (active === "estadisticas") return <Statistics data={data} range={statisticsRange} onRangeChange={onStatisticsRangeChange} />;
  if (active === "auditoria_entregas") return <DeliveryAudit data={data} range={auditRange} onRangeChange={onAuditRangeChange} />;
  if (active === "liquidacion") return <DailyLiquidation data={data} date={liquidationDate} onDateChange={onLiquidationDateChange} />;
  if (active === "cartera") return deliveryOnly
    ? <RouteReceivablesModule rows={data.cartera || []} onRefresh={onRefresh} />
    : <CarteraModule rows={data.cartera || []} onRefresh={onRefresh} />;
  if (active === "pagos") return <TransferenciasModule rows={data.pagos || []} onRefresh={onRefresh} />;
  if (active === "envios_whatsapp") return <WhatsAppShipmentsModule data={data} />;
  if (active === "gastos" && deliveryOnly)
    return <RouteExpensesModule expenses={data.gastos || []} onRefresh={onRefresh} />;
  if (active === "configuracion")
    return <ConfigurationCenter products={data.productos || []} onRefresh={onRefresh} />;
  if (active === "usuarios") return <DeferredModule><UsersManagement /></DeferredModule>;
  return (
    <>
      {active === "inventario" && (
        <InventoryActions
          inventory={data.inventario || []}
          onRefresh={onRefresh}
        />
      )}
      {active === "gastos" && !deliveryOnly && <ExpenseActions onChanged={onRefresh} expenses={data.gastos || []} />}
      {
        <OperationalModule
          active={active}
          data={data}
          onRefresh={onRefresh}
          deliveryOnly={deliveryOnly}
          orderQuery={orderQuery}
          onOrderQueryChange={onOrderQueryChange}
          routeQuery={routeQuery}
          onRouteQueryChange={onRouteQueryChange}
          pushOrderId={pushOrderId}
          onPushOrderOpened={onPushOrderOpened}
        />
      }
    </>
  );
}

function DeliveryAudit({ data = {}, range, onRangeChange }) {
  const routes = Array.isArray(data.rutas) ? data.rutas : [];
  const summary = data.resumen || {};
  const maps = data.maps || {};
  const [repartidorId, setRepartidorId] = useState("");
  const repartidores = [...new Map(
    routes
      .map((route) => [route.repartidor_id || route.repartidor, visibleRepartidorName(route.repartidor || route.repartidor_id)])
      .filter(([id]) => id),
  ).entries()];
  const visibleRoutes = routes.filter((route) => !repartidorId || route.repartidor_id === repartidorId);
  const displayedSummary = repartidorId
    ? visibleRoutes.reduce((acc, route) => ({
        rutas: acc.rutas + 1,
        paradas_programadas: acc.paradas_programadas + Number(route.paradas_programadas || 0),
        entregas_registradas: acc.entregas_registradas + Number(route.entregas_registradas || 0),
        efectivo: acc.efectivo + Number(route.efectivo || 0),
        transferencia: acc.transferencia + Number(route.transferencia || 0),
        pendiente: acc.pendiente + Number(route.pendiente || 0),
      }), { rutas: 0, paradas_programadas: 0, entregas_registradas: 0, efectivo: 0, transferencia: 0, pendiente: 0 })
    : summary;
  const updateRange = (key, value) => onRangeChange?.((actual) => ({ ...actual, [key]: value }));
  const money = (value) => formatCurrency(value || 0);
  const duration = (value) => value == null ? "—" : `${value} min`;
  const legClass = (status) => ({
    DENTRO_TOLERANCIA: "on-time",
    ANTICIPADO: "early",
    RETRASO: "late",
    SIN_COORDENADAS: "missing",
  }[status] || "pending");
  const legStatus = (leg) => {
    if (leg.estado_comparacion === "DENTRO_TOLERANCIA") return "A tiempo";
    if (leg.estado_comparacion === "ANTICIPADO") return "Antes de lo esperado";
    if (leg.estado_comparacion === "RETRASO") return `Retraso ${Math.max(0, Number(leg.diferencia_minutos || 0)).toFixed(1)} min`;
    if (leg.estado_comparacion === "SIN_COORDENADAS") return "Sin coordenadas";
    if (leg.estado_comparacion === "MAPS_SIN_CONFIGURAR") return "Maps sin configurar";
    if (leg.estado_comparacion === "PENDIENTE_LLEGADA") return "Esperando llegada";
    return "Pendiente Maps";
  };

  return (
    <div className="delivery-audit-module">
      <section className="filter-bar delivery-audit-filter">
        <div>
          <p className="eyebrow">CONTROL OPERATIVO</p>
          <h2>Auditoría de entregas</h2>
          <small>Lectura basada en horas de entrega registradas. No modifica rutas ni pedidos.</small>
        </div>
        <div className="stats-date-range" aria-label="Rango de auditoría">
          <label>Desde<input type="date" value={range?.fecha_desde || ""} max={range?.fecha_hasta || undefined} onChange={(event) => updateRange("fecha_desde", event.target.value)} /></label>
          <label>Hasta<input type="date" value={range?.fecha_hasta || ""} min={range?.fecha_desde || undefined} onChange={(event) => updateRange("fecha_hasta", event.target.value)} /></label>
        </div>
        <label className="delivery-audit-driver-filter">Repartidor
          <select value={repartidorId} onChange={(event) => setRepartidorId(event.target.value)}>
            <option value="">Todos</option>
            {repartidores.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </label>
      </section>

      <section className="delivery-audit-kpis">
        <div><span>Rutas</span><strong>{displayedSummary.rutas || 0}</strong></div>
        <div><span>Entregas registradas</span><strong>{displayedSummary.entregas_registradas || 0}</strong></div>
        <div><span>Efectivo</span><strong>{money(displayedSummary.efectivo)}</strong></div>
        <div className="audit-transfer"><span>Transferencias</span><strong>{money(displayedSummary.transferencia)}</strong></div>
        <div className="audit-pending"><span>Pendiente</span><strong>{money(displayedSummary.pendiente)}</strong></div>
        <div className="audit-on-time"><span>Trayectos a tiempo</span><strong>{displayedSummary.trayectos_a_tiempo || 0}</strong></div>
        <div className="audit-late"><span>Trayectos con retraso</span><strong>{displayedSummary.trayectos_retrasados || 0}</strong></div>
      </section>

      <p className="delivery-audit-note">{maps.configurado ? "La comparación usa la salida registrada, el tiempo de Google Maps y una tolerancia de 3 minutos. Sin salida y llegada registradas, no se califica el trayecto." : "Google Maps aún no está configurado. El panel conserva los eventos operativos, pero no califica puntualidad ni realiza llamadas externas."}</p>

      {visibleRoutes.length ? visibleRoutes.map((route) => (
        <article className="delivery-audit-route" key={`${route.fecha}-${route.repartidor_id}`}>
          <header>
            <div><p className="eyebrow">{formatSalesDate(route.fecha)}</p><h3>{visibleRepartidorName(route.repartidor)}</h3><small>{route.paradas_programadas} programadas · {route.entregas_registradas} con hora registrada</small></div>
            <div className="delivery-audit-route-summary"><b>{route.inicio_ruta || "—"} → {route.ultima_entrega || "—"}</b><span>Hora local Bogotá · {duration(route.duracion_total_minutos)}</span></div>
          </header>
          <div className="delivery-audit-money"><span>Efectivo <b>{money(route.efectivo)}</b></span><span>Transferencia <b>{money(route.transferencia)}</b></span><span>Pendiente <b>{money(route.pendiente)}</b></span></div>
          <div className="delivery-audit-timings"><div><span>Inicio de ruta</span><b>{route.inicio_ruta || "Sin datos"}</b><small>{route.inicio_ruta_fuente === "EVENTO_INICIO_RUTA" ? "Registrado" : route.inicio_ruta_fuente === "PRIMERA_ENTREGA_ESTIMADA" ? "Estimado" : "Pendiente"}</small></div><div><span>Primera entrega</span><b>{route.primera_entrega || "Sin datos"}</b><small>Hora de llegada</small></div><div><span>Última entrega</span><b>{route.ultima_entrega || "Sin datos"}</b><small>Hora de llegada</small></div></div>
          <div className="delivery-audit-meta"><span>{route.eventos_operativos || 0} evento(s) operativo(s)</span><span>{route.pausas_registradas || 0} pausa(s)</span><span>{route.novedades_registradas || 0} novedad(es)</span></div>
          {route.trayectos?.length ? <div className="delivery-audit-legs">{route.trayectos.map((leg, index) => <div className={`delivery-audit-leg ${legClass(leg.estado_comparacion)}`} key={`${leg.origen_pedido_id}-${leg.destino_pedido_id}-${index}`}><div><span className="delivery-audit-stop-number">Tramo {index + 1}</span><b>{leg.origen_cliente} → {leg.destino_cliente}</b><small>{leg.fuente_tiempo === "EVENTOS_RUTA" ? `${leg.hora_salida_registrada} → ${leg.hora_llegada_registrada} · Desplazamiento registrado: ${duration(leg.tiempo_desplazamiento_minutos)}` : `${leg.hora_salida_estimada} → ${leg.hora_entrega_siguiente} · Tiempo entre confirmaciones: ${duration(leg.tiempo_operativo_minutos)}`}{leg.tiempo_maps_minutos != null ? ` · Maps: ${duration(leg.tiempo_maps_minutos)}${leg.distancia_metros != null ? ` · ${(leg.distancia_metros / 1000).toFixed(1)} km` : ""}` : ""}{leg.hora_esperada ? ` · Esperada: ${leg.hora_esperada}` : ""}</small></div><strong>{legStatus(leg)}</strong></div>)}</div> : <p className="delivery-audit-empty">No hay dos entregas con hora registrada para comparar.</p>}
        </article>
      )) : <div className="empty-state">No hay rutas para el rango seleccionado.</div>}
    </div>
  );
}

function ConfigurationCenter({ products, onRefresh }) {
  const [activeTab, setActiveTab] = useState("productos");
  const [openModules, setOpenModules] = useState({ productos: true, entregas: true, destinos: true, notificaciones: true });
  const modules = [
    { id: "productos", label: "Productos", detail: "Presentaciones, precios y disponibilidad", icon: Package },
    { id: "entregas", label: "Entregas", detail: "Rutas, horarios y cortes", icon: Clock3 },
    { id: "destinos", label: "Destinos", detail: "Municipios, transporte y fechas", icon: Truck },
    { id: "notificaciones", label: "Notificaciones", detail: "Avisos privados del panel", icon: CheckCircle2 },
  ];
  function toggleModule(id) {
    setOpenModules((current) => ({ ...current, [id]: !current[id] }));
  }
  return <section className="configuration-center">
    <header className="module-panel configuration-center-intro">
      <div><p className="eyebrow">CENTRO DE CONFIGURACIÓN</p><h2>Configura Titina por módulos</h2><p>Cada pestaña concentra una parte de la operación. Los cambios se guardan desde el formulario de su propio módulo.</p></div>
      <span className="configuration-save-note">Los formularios conservan sus cambios al cambiar de pestaña.</span>
    </header>
    <nav className="configuration-tabs" role="tablist" aria-label="Secciones de configuración">
      {modules.map(({ id, label, detail, icon: Icon }) => <button type="button" role="tab" aria-selected={activeTab === id} className={activeTab === id ? "configuration-tab active" : "configuration-tab"} key={id} onClick={() => setActiveTab(id)}><Icon size={18} /><span><b>{label}</b><small>{detail}</small></span></button>)}
    </nav>
    <div className="configuration-workspace">
      <div className="configuration-tab-content" role="tabpanel" hidden={activeTab !== "productos"}>
        <ConfigurationMiniModule id="productos" title="Productos, presentaciones y disponibilidad" detail="Administra el catálogo comercial y operativo desde aquí." open={openModules.productos} onToggle={toggleModule}>
          <DeferredModule><ProductOperationsEditorV2 products={products} onChanged={onRefresh} /></DeferredModule>
        </ConfigurationMiniModule>
      </div>
      <div className="configuration-tab-content" role="tabpanel" hidden={activeTab !== "entregas"}>
        <ConfigurationMiniModule id="entregas" title="Servicios de entrega" detail="Configura la Ruta AM y Sopas mediodía sin mezclar sus horarios." open={openModules.entregas} onToggle={toggleModule}>
          <DeferredModule><DeliveryServicesEditor /></DeferredModule>
        </ConfigurationMiniModule>
      </div>
      <div className="configuration-tab-content" role="tabpanel" hidden={activeTab !== "destinos"}>
        <ConfigurationMiniModule id="destinos" title="Destinos de entrega" detail="Define dónde se reciben pedidos y qué verá el cliente." open={openModules.destinos} onToggle={toggleModule}>
          <DeferredModule><DeliveryDestinationsEditor /></DeferredModule>
        </ConfigurationMiniModule>
      </div>
      <div className="configuration-tab-content" role="tabpanel" hidden={activeTab !== "notificaciones"}>
        <ConfigurationMiniModule id="notificaciones" title="Notificaciones del panel" detail="Activa o desactiva los avisos push en este dispositivo." open={openModules.notificaciones} onToggle={toggleModule}>
          <DeferredModule><AdminPushNotifications /></DeferredModule>
        </ConfigurationMiniModule>
      </div>
    </div>
  </section>;
}

function ConfigurationMiniModule({ id, title, detail, open, onToggle, children }) {
  return <section className={`configuration-mini-module ${open ? "is-open" : "is-closed"}`}>
    <button type="button" className="configuration-mini-heading" aria-expanded={open} aria-controls={`configuration-panel-${id}`} onClick={() => onToggle(id)}>
      <span><b>{title}</b><small>{detail}</small></span><span className="configuration-mini-action">{open ? "Ocultar" : "Abrir"}<ChevronDown size={17} /></span>
    </button>
    <div id={`configuration-panel-${id}`} className="configuration-mini-body" hidden={!open}>{children}</div>
  </section>;
}

async function dashboardUserOperation(operacion, datos = {}) {
  const response = await fetch("/api/dashboard/usuarios/operacion", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ operacion, datos }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok)
    throw new Error(
      payload.mensaje ||
        `No fue posible completar la operación (HTTP ${response.status}).`,
    );
  return payload;
}

function UsersManagementLegacy() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [name, setName] = useState("");
  const [initialRole, setInitialRole] = useState("REPARTIDOR");
  const [roleByUser, setRoleByUser] = useState({});
  const [credentialByUser, setCredentialByUser] = useState({});
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState("");

  async function load() {
    setBusy("loading");
    try {
      const payload = await dashboardUserOperation("listar");
      setUsers(payload.data?.usuarios || []);
      setRoles(payload.data?.roles_disponibles || []);
    } catch (error) {
      setStatus(error.message || "No fue posible consultar los usuarios.");
    } finally {
      setBusy("");
    }
  }
  useEffect(() => {
    load();
  }, []);
  async function execute(key, operation, datos, success) {
    setBusy(key);
    setStatus("");
    try {
      const payload = await dashboardUserOperation(operation, datos);
      setStatus(payload.mensaje || success);
      await load();
    } catch (error) {
      setStatus(error.message || "No fue posible completar la operación.");
    } finally {
      setBusy("");
    }
  }
  async function create(event) {
    event.preventDefault();
    await execute(
      "create",
      "crear",
      { nombre: name, codigo_rol: initialRole },
      "Usuario creado correctamente.",
    );
    setName("");
  }
  async function configureCredential(user) {
    const credential = credentialByUser[user.usuario_operativo_id] || {};
    if (!credential.username || String(credential.password || "").length < 6) {
      setStatus(
        "Define un usuario y una contraseña temporal de mínimo 6 caracteres.",
      );
      return;
    }
    await execute(
      `credential:${user.usuario_operativo_id}`,
      "configurar_credencial",
      {
        usuario_operativo_id: user.usuario_operativo_id,
        username: credential.username,
        password: credential.password,
      },
      "Acceso configurado. La persona deberá cambiar la contraseña al ingresar.",
    );
    setCredentialByUser((current) => ({
      ...current,
      [user.usuario_operativo_id]: {
        username: credential.username,
        password: "",
      },
    }));
  }
  return (
    <section className="users-management">
      <header className="module-panel users-intro">
        <div>
          <p className="eyebrow">ACCESOS OPERATIVOS</p>
          <h2>Usuarios y roles</h2>
          <p>
            Los permisos pertenecen a los roles canónicos del ERP. Aquí decides
            quién está activo y qué rol cumple cada persona.
          </p>
        </div>
        <span className="record-count">
          {users.filter((user) => user.estado === "ACTIVO").length} activos
        </span>
      </header>
      <section className="module-panel user-create">
        <div>
          <h3>Crear usuario</h3>
          <p>
            Primero crea a la persona y asígnale su función. Las credenciales de
            acceso se configurarán en el siguiente sprint.
          </p>
        </div>
        <form onSubmit={create}>
          <label>
            Nombre completo
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Ej.: María González"
              required
            />
          </label>
          <label>
            Rol inicial
            <select
              value={initialRole}
              onChange={(event) => setInitialRole(event.target.value)}
            >
              {roles.map((role) => (
                <option key={role.codigo} value={role.codigo}>
                  {role.nombre}
                </option>
              ))}
            </select>
          </label>
          <button
            className="primary-action"
            disabled={Boolean(busy) || !roles.length}
          >
            {busy === "create" ? "Creando…" : "Crear usuario"}
          </button>
        </form>
      </section>
      {status && (
        <p
          className={`action-status ${/no fue|no puedes|obligatorio|inválido|invalido/i.test(status) ? "error" : "success"}`}
          role="status"
        >
          {status}
        </p>
      )}
      <section className="module-panel users-list">
        <div className="module-heading">
          <div>
            <p className="eyebrow">EQUIPO</p>
            <h2>Usuarios operativos</h2>
          </div>
          <button
            className="secondary-action"
            onClick={load}
            disabled={Boolean(busy)}
          >
            {busy === "loading" ? "Actualizando…" : "Actualizar"}
          </button>
        </div>
        {busy === "loading" && !users.length ? (
          <div className="table-loading">Cargando usuarios…</div>
        ) : (
          <div className="user-cards">
            {users.map((user) => {
              const selectedRole = roleByUser[user.usuario_operativo_id] || "";
              const active = user.estado === "ACTIVO";
              return (
                <article
                  className={`user-card ${active ? "" : "inactive"}`}
                  key={user.usuario_operativo_id}
                >
                  <div className="user-card-heading">
                    <div>
                      <b>{user.nombre || "Usuario sin nombre"}</b>
                      <small>{user.tipo_usuario || "INTERNO"}</small>
                    </div>
                    <span
                      className={`status-pill ${active ? "activo" : "inactivo"}`}
                    >
                      {active ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                  <div className="user-role-list">
                    {(user.roles || []).map((role) => (
                      <span className="user-role" key={role}>
                        {role.replaceAll("_", " ")}
                        <button
                          type="button"
                          aria-label={`Retirar ${role}`}
                          disabled={
                            Boolean(busy) || (user.roles || []).length <= 1
                          }
                          onClick={() => confirmarAccionAdmin({
                            titulo: "Retirar rol",
                            mensaje: `¿Retirar el rol ${role.replaceAll("_", " ")} de ${user.nombre}?`,
                            confirmar: "Retirar rol",
                            peligro: true,
                            onConfirm: () => execute(
                              `remove:${user.usuario_operativo_id}:${role}`,
                              "retirar_rol",
                              {
                                usuario_operativo_id: user.usuario_operativo_id,
                                codigo_rol: role,
                              },
                              "Rol retirado.",
                            ),
                          })}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="user-role-add">
                    <select
                      aria-label={`Agregar rol a ${user.nombre}`}
                      value={selectedRole}
                      onChange={(event) =>
                        setRoleByUser((current) => ({
                          ...current,
                          [user.usuario_operativo_id]: event.target.value,
                        }))
                      }
                    >
                      <option value="">Agregar rol…</option>
                      {roles
                        .filter(
                          (role) => !(user.roles || []).includes(role.codigo),
                        )
                        .map((role) => (
                          <option key={role.codigo} value={role.codigo}>
                            {role.nombre}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="secondary-action"
                      disabled={!selectedRole || Boolean(busy)}
                      onClick={() =>
                        execute(
                          `role:${user.usuario_operativo_id}`,
                          "asignar_rol",
                          {
                            usuario_operativo_id: user.usuario_operativo_id,
                            codigo_rol: selectedRole,
                          },
                          "Rol asignado.",
                        )
                      }
                    >
                      Asignar
                    </button>
                  </div>
                  <div className="user-card-actions">
                    <button
                      type="button"
                      className={active ? "danger-action" : "primary-action"}
                      disabled={Boolean(busy)}
                      onClick={() => {
                        const next = active ? "INACTIVO" : "ACTIVO";
                        confirmarAccionAdmin({
                          titulo: active ? "Desactivar usuario" : "Activar usuario",
                          mensaje: `${active ? "¿Desactivar" : "¿Activar"} a ${user.nombre}?`,
                          confirmar: active ? "Desactivar" : "Activar",
                          peligro: active,
                          onConfirm: () => execute(
                            `state:${user.usuario_operativo_id}`,
                            "cambiar_estado",
                            {
                              usuario_operativo_id: user.usuario_operativo_id,
                              estado: next,
                            },
                            active
                              ? "Usuario desactivado."
                              : "Usuario activado.",
                          ),
                        });
                      }}
                    >
                      {active ? "Desactivar" : "Activar"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </section>
  );
}

const CAPACITY_POLICIES = new Set(["CAPACIDAD_DIARIA"]);
const WEEK_DAYS = [
  [0, "Lun"],
  [1, "Mar"],
  [2, "Mié"],
  [3, "Jue"],
  [4, "Vie"],
  [5, "Sáb"],
  [6, "Dom"],
];

function ProductOperationsEditor({ products }) {
  const groups = useMemo(
    () =>
      Object.values(
        (products || []).reduce((all, row) => {
          const id = row.producto_canonico_id || row.codigo;
          if (!id) return all;
          if (!all[id])
            all[id] = { id, name: row.producto || "Producto", rows: [] };
          all[id].rows.push(row);
          return all;
        }, {}),
      ).sort((a, b) => a.name.localeCompare(b.name, "es")),
    [products],
  );
  const [selectedId, setSelectedId] = useState("");
  const [tab, setTab] = useState("presentaciones");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [capacity, setCapacity] = useState({
    unit: "",
    base: "",
    maximum: "",
    days: [],
  });
  const [newPresentation, setNewPresentation] = useState({
    presentacion: "",
    unidad: "",
    precio: "",
    minimo: "1",
    politica: "INVENTARIO_ESTATICO",
    disponibilidad: "DISPONIBLE",
  });
  const selected = groups.find((group) => group.id === selectedId);
  const rows = selected?.rows || [];
  const daily =
    rows.length > 0 &&
    rows.every((row) =>
      CAPACITY_POLICIES.has(String(row.politica || "").toUpperCase()),
    );
  function choose(id) {
    setSelectedId(id);
    setStatus("");
    const row = groups.find((group) => group.id === id)?.rows[0];
    setCapacity({
      unit: row?.unidad_capacidad || "",
      base: row?.capacidad_base || "",
      maximum: row?.capacidad_maxima || "",
      days: Array.isArray(row?.dias_operacion) ? row.dias_operacion : [],
    });
  }
  async function call(operation, datos) {
    const response = await fetch("/api/dashboard/productos/operacion", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ operacion, datos }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.ok)
      throw new Error(
        payload.mensaje || `No fue posible guardar (HTTP ${response.status}).`,
      );
    return payload;
  }
  async function saveAvailability(row, cambios) {
    setBusy(true);
    setStatus("");
    try {
      await call("configurar_disponibilidad", { codigo: row.codigo, cambios });
      setStatus(
        "Disponibilidad actualizada. Recarga la vista para ver el cambio.",
      );
    } catch (error) {
      setStatus(error.message);
    } finally {
      setBusy(false);
    }
  }
  async function createPresentation(event) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    setStatus("");
    try {
      await call("crear_presentacion", {
        producto_canonico_id: selected.id,
        presentacion: {
          ...newPresentation,
          precio: Number(newPresentation.precio),
          minimo: Number(newPresentation.minimo),
        },
      });
      setStatus(
        "Presentación creada. Recarga la vista para verla en el producto.",
      );
      setNewPresentation({
        presentacion: "",
        unidad: "",
        precio: "",
        minimo: "1",
        politica: "INVENTARIO_ESTATICO",
        disponibilidad: "DISPONIBLE",
      });
    } catch (error) {
      setStatus(error.message);
    } finally {
      setBusy(false);
    }
  }
  async function retire(row) {
    confirmarAccionAdmin({
      titulo: "Retirar presentación",
      mensaje: `¿Retirar ${row.presentacion_descripcion || row.presentacion || row.codigo} del catálogo? Se conservará su historial.`,
      confirmar: "Retirar presentación",
      peligro: true,
      onConfirm: () => confirmarRetiro(row),
    });
  }
  async function confirmarRetiro(row) {
    setBusy(true);
    setStatus("");
    try {
      await call("retirar_presentacion", { codigo: row.codigo });
      setStatus(
        "Presentación retirada del catálogo. Recarga la vista para actualizar la lista.",
      );
    } catch (error) {
      setStatus(error.message);
    } finally {
      setBusy(false);
    }
  }
  async function saveCapacity(event) {
    event.preventDefault();
    if (!selected || !daily) return;
    setBusy(true);
    setStatus("");
    try {
      await call("configurar_operacion_canonica", {
        producto_canonico_id: selected.id,
        configuracion: {
          unidad_capacidad: capacity.unit,
          capacidad_base: Number(capacity.base),
          capacidad_maxima: Number(capacity.maximum || capacity.base),
          dias_operacion: capacity.days,
          activo: true,
        },
      });
      setStatus(
        "Capacidad guardada. Los cupos se crean automáticamente cuando un pedido los necesita.",
      );
    } catch (error) {
      setStatus(error.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="product-ops">
      <header className="module-panel product-ops-intro">
        <p className="eyebrow">CONFIGURACIÓN SEGURA</p>
        <h2>Productos, presentaciones y disponibilidad</h2>
        <p>
          Elige un producto una sola vez. Aquí administras lo que el cliente
          puede comprar y cómo se entrega, sin códigos internos ni fechas
          técnicas.
        </p>
      </header>
      <section className="module-panel product-editor">
        <label className="product-picker">
          Producto
          <select
            value={selectedId}
            onChange={(event) => choose(event.target.value)}
          >
            <option value="">Selecciona un producto</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name} · {group.rows.length} presentación(es)
              </option>
            ))}
          </select>
        </label>
        {selected && (
          <>
            <div className="product-editor-heading">
              <div>
                <p className="eyebrow">{selected.name.toUpperCase()}</p>
                <h2>Configura este producto</h2>
              </div>
              <span>{rows.length} presentación(es) vendible(s)</span>
            </div>
            <nav className="editor-tabs">
              <button
                className={tab === "presentaciones" ? "active" : ""}
                onClick={() => setTab("presentaciones")}
              >
                Presentaciones
              </button>
              <button
                className={tab === "disponibilidad" ? "active" : ""}
                onClick={() => setTab("disponibilidad")}
              >
                Disponibilidad y entrega
              </button>
              {daily && (
                <button
                  className={tab === "capacidad" ? "active" : ""}
                  onClick={() => setTab("capacidad")}
                >
                  Capacidad diaria
                </button>
              )}
            </nav>
            {tab === "presentaciones" && (
              <div className="editor-section">
                <div className="presentation-list">
                  {rows.map((row) => (
                    <article className="presentation-row" key={row.codigo}>
                      <div>
                        <b>
                          {row.presentacion_descripcion ||
                            row.presentacion ||
                            "Presentación"}
                        </b>
                        <small>
                          {row.unidad || "Unidad no configurada"} · $
                          {Number(row.precio || 0).toLocaleString("es-CO")}
                        </small>
                      </div>
                      <button
                        type="button"
                        className="danger-link"
                        disabled={busy}
                        onClick={() => retire(row)}
                      >
                        Retirar
                      </button>
                    </article>
                  ))}
                </div>
                <form
                  className="presentation-form"
                  onSubmit={createPresentation}
                >
                  <h3>Agregar presentación</h3>
                  <label>
                    Presentación
                    <input
                      required
                      value={newPresentation.presentacion}
                      placeholder="Ej.: botella de 1 litro"
                      onChange={(e) =>
                        setNewPresentation({
                          ...newPresentation,
                          presentacion: e.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Unidad de venta
                    <input
                      required
                      value={newPresentation.unidad}
                      placeholder="Ej.: botella"
                      onChange={(e) =>
                        setNewPresentation({
                          ...newPresentation,
                          unidad: e.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Precio de venta
                    <input
                      required
                      type="number"
                      min="0"
                      value={newPresentation.precio}
                      onChange={(e) =>
                        setNewPresentation({
                          ...newPresentation,
                          precio: e.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Política
                    <select
                      value={newPresentation.politica}
                      onChange={(e) =>
                        setNewPresentation({
                          ...newPresentation,
                          politica: e.target.value,
                        })
                      }
                    >
                      <option value="INVENTARIO_ESTATICO">Inventario</option>
                      <option value="CAPACIDAD_DIARIA">Capacidad diaria</option>
                      <option value="CALENDARIO">Días programados</option>
                      <option value="LOTE_VARIABLE">
                        Reserva con precio por definir
                      </option>
                    </select>
                  </label>
                  <button disabled={busy}>Agregar presentación</button>
                </form>
              </div>
            )}
            {tab === "disponibilidad" && (
              <div className="editor-section">
                <p className="editor-help">
                  La disponibilidad se decide por cada presentación. Cambiarla
                  no altera otras presentaciones.
                </p>
                {rows.map((row) => (
                  <article className="availability-row" key={row.codigo}>
                    <div>
                      <b>{row.presentacion_descripcion || row.presentacion}</b>
                      <small>
                        Estado actual: {row.disponibilidad || "Sin configurar"}{" "}
                        · {String(row.politica || "").replaceAll("_", " ")}
                      </small>
                    </div>
                    <div className="availability-actions">
                      <button
                        disabled={busy}
                        onClick={() =>
                          saveAvailability(row, {
                            disponibilidad: "DISPONIBLE",
                            catalogo_visible: true,
                          })
                        }
                      >
                        Disponible
                      </button>
                      <button
                        disabled={busy}
                        onClick={() =>
                          saveAvailability(row, { disponibilidad: "AGOTADO" })
                        }
                      >
                        Agotado
                      </button>
                      <button
                        disabled={busy}
                        onClick={() =>
                          saveAvailability(row, {
                            disponibilidad: "INACTIVO",
                            catalogo_visible: false,
                          })
                        }
                      >
                        Ocultar
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
            {tab === "capacidad" && (
              <form
                className="editor-section capacity-form"
                onSubmit={saveCapacity}
              >
                <p className="editor-help">
                  Todas estas presentaciones consumen el mismo cupo. El sistema
                  abre el cupo para una fecha sólo cuando un pedido lo solicita:
                  no debes abrir días manualmente.
                </p>
                <div className="capacity-grid">
                  <label>
                    Unidad de capacidad
                    <input
                      required
                      value={capacity.unit}
                      placeholder="litros o paquetes"
                      onChange={(e) =>
                        setCapacity({ ...capacity, unit: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Capacidad diaria
                    <input
                      required
                      type="number"
                      min="0"
                      step="0.001"
                      value={capacity.base}
                      onChange={(e) =>
                        setCapacity({ ...capacity, base: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Máximo diario
                    <input
                      required
                      type="number"
                      min="0"
                      step="0.001"
                      value={capacity.maximum}
                      onChange={(e) =>
                        setCapacity({ ...capacity, maximum: e.target.value })
                      }
                    />
                  </label>
                </div>
                <fieldset>
                  <legend>Días en que se entrega</legend>
                  <div className="day-options">
                    {WEEK_DAYS.map(([day, label]) => (
                      <label key={day}>
                        <input
                          type="checkbox"
                          checked={capacity.days.includes(day)}
                          onChange={() =>
                            setCapacity({
                              ...capacity,
                              days: capacity.days.includes(day)
                                ? capacity.days.filter((value) => value !== day)
                                : [...capacity.days, day].sort(),
                            })
                          }
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <button disabled={busy}>
                  {busy ? "Guardando..." : "Guardar capacidad del producto"}
                </button>
              </form>
            )}
            {status && (
              <p className="action-status product-editor-status">{status}</p>
            )}
          </>
        )}
      </section>
    </section>
  );
}

function KpiCard({
  label,
  value,
  trend,
  tone = "green",
  icon: Icon,
  spark = [3, 5, 4, 6],
}) {
  return (
    <article className={`card kpi-card ${tone}`}>
      <div className="kpi-top">
        <span>{label}</span>
        {Icon && <Icon size={17} />}
      </div>
      <strong>{value}</strong>
      <div className="kpi-bottom">
        <span className="trend-up">{trend || "Fuente ERP"}</span>
        <span>vs. periodo anterior</span>
      </div>
      <Sparkline values={spark} />
    </article>
  );
}

function Sparkline({ values }) {
  return (
    <Suspense fallback={<div className="sparkline" aria-hidden="true" />}>
      <AdminWidgets type="sparkline" values={values} />
    </Suspense>
  );
}

function SummaryLegacy({ data, onNavigate, date, onDateChange }) {
  const setDate = onDateChange;
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const alerts = [...(data.alertas || [])].sort(
    (a, b) =>
      ({ alta: 0, media: 1, baja: 2 })[a.prioridad] -
      { alta: 0, media: 1, baja: 2 }[b.prioridad],
  );
  const cards = [
    [
      "Pedidos confirmados",
      data.pedidos_confirmados ?? "—",
      "Hoy",
      ClipboardList,
    ],
    [
      "Ventas del día",
      data.valor_ventas == null
        ? "—"
        : `$${Number(data.valor_ventas).toLocaleString("es-CO")}`,
      "Fuente ERP",
      CircleDollarSign,
    ],
    [
      "Costo de mercancía",
      data.costo_mercancia_vendida == null
        ? "—"
        : `$${Number(data.costo_mercancia_vendida).toLocaleString("es-CO")}`,
      data.ventas_con_costo_pendiente
        ? `${data.ventas_con_costo_pendiente} venta(s) sin costo configurado`
        : "Costo congelado al entregar",
      Package,
    ],
    [
      "Utilidad bruta",
      data.utilidad_bruta == null
        ? "Pendiente"
        : `$${Number(data.utilidad_bruta).toLocaleString("es-CO")}`,
      data.margen_bruto == null ? "Completa costos para calcular" : `${Number(data.margen_bruto).toFixed(1)}% margen`,
      TrendingUp,
    ],
    [
      "Pendientes de pago",
      data.transferencias_pendientes ?? "—",
      "Requieren revisión",
      Clock3,
    ],
    ["Rutas por atender", data.rutas_pendientes ?? "—", "Preparación", Truck],
  ];
  return (
    <>
      <section className="filter-bar">
        <label className="date-filter">
          Fecha principal
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
        <button
          className="secondary-filter"
          onClick={() => setSecondaryOpen(!secondaryOpen)}
        >
          <Filter size={16} />
          Filtros secundarios
          <ChevronDown size={16} />
        </button>
        {secondaryOpen && (
          <div className="filter-popover">
            <label>
              Estado
              <select>
                <option>Todos</option>
                <option>Confirmados</option>
                <option>Pendientes</option>
              </select>
            </label>
            <label>
              Ruta
              <select>
                <option>Todas</option>
                <option>Programada</option>
                <option>A la medida</option>
              </select>
            </label>
            <label>
              Pago
              <select>
                <option>Todos</option>
                <option>Transferencia</option>
                <option>Efectivo</option>
              </select>
            </label>
          </div>
        )}
      </section>
      <div className="command-intro">
        <div>
          <p className="eyebrow">CENTRO DE MANDO</p>
          <h2>Así marcha la operación del {date}.</h2>
        </div>
        <span className="source-note">Las prioridades provienen del ERP</span>
      </div>
      <div className="cards">
        {cards.map(([label, value, trend, Icon]) => (
          <KpiCard
            key={label}
            label={label}
            value={value}
            trend={trend}
            icon={Icon}
          />
        ))}
      </div>
      <div className="summary-grid">
        <section className="module-panel alerts-panel">
          <div className="module-heading">
            <div>
              <p className="eyebrow">PRIORIZADO</p>
              <h2>Alertas de operación</h2>
            </div>
            <span className="record-count">{alerts.length} activas</span>
          </div>
          <div className="alert-list">
            {alerts.map((alert) => (
              <button
                className={`alert-row ${alert.prioridad}`}
                key={alert.titulo}
                onClick={() => onNavigate(alert.destino)}
              >
                <span className="alert-icon">
                  {alert.prioridad === "alta" ? (
                    <AlertTriangle size={17} />
                  ) : alert.prioridad === "media" ? (
                    <Clock3 size={17} />
                  ) : (
                    <CheckCircle2 size={17} />
                  )}
                </span>
                <span>
                  <b>{alert.titulo}</b>
                  <small>{alert.detalle}</small>
                </span>
                <span className="priority-label">{alert.prioridad}</span>
              </button>
            ))}
          </div>
        </section>
        <section className="module-panel events-panel">
          <div className="module-heading">
            <div>
              <p className="eyebrow">AGENDA</p>
              <h2>Próximos eventos</h2>
            </div>
          </div>
          <div className="event-list">
            {(data.proximos_eventos || []).map((event) => (
              <div className="event-row" key={event.hora}>
                <strong>{event.hora}</strong>
                <span>
                  <b>{event.titulo}</b>
                  <small>{event.detalle}</small>
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
      <section className="module-panel trend-panel">
        <div className="module-heading">
          <div>
            <p className="eyebrow">TENDENCIA</p>
            <h2>Ventas recientes</h2>
          </div>
          <span className="record-count">Lectura informativa</span>
        </div>
        <Suspense
          fallback={
            <div className="large-chart" aria-label="Cargando tendencia" />
          }
        >
          <AdminWidgets.AdminTrendChart values={data.ventas_tendencia || []} />
        </Suspense>
      </section>
    </>
  );
}

function Summary({ data, onNavigate, date, onDateChange, onRefresh }) {
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const [cajaAbierta, setCajaAbierta] = useState(false);
  const priority = { alta: 0, media: 1, baja: 2 };
  const alerts = [...(data.alertas || [])].sort(
    (a, b) => (priority[a.prioridad] ?? 9) - (priority[b.prioridad] ?? 9),
  );
  const cards = [
    ["Pedidos realizados", data.pedidos_confirmados ?? "—", ClipboardList],
    [
      "Valor de pedidos",
      data.valor_pedidos == null
        ? "—"
        : `$${Number(data.valor_pedidos).toLocaleString("es-CO")}`,
      CircleDollarSign,
    ],
    ["Pendientes de pago", data.transferencias_pendientes ?? "—", Clock3],
    ["Rutas por atender", data.rutas_pendientes ?? "—", Truck],
  ];
  const caja = data.caja_repartidores || { total: 0, cantidad_pagos: 0, repartidores: [] };
  return (
    <>
      <section className="filter-bar">
        <label className="date-filter">
          Fecha principal
          <input
            type="date"
            value={date}
            onChange={(event) => onDateChange(event.target.value)}
          />
        </label>
        <button
          className="secondary-filter"
          onClick={() => setSecondaryOpen((open) => !open)}
        >
          <Filter size={16} />
          Filtros secundarios
          <ChevronDown size={16} />
        </button>
        {secondaryOpen && (
          <div className="filter-popover">
            <label>
              Estado
              <select>
                <option>Todos</option>
                <option>Confirmados</option>
                <option>Pendientes</option>
              </select>
            </label>
            <label>
              Ruta
              <select>
                <option>Todas</option>
                <option>Programada</option>
                <option>A la medida</option>
              </select>
            </label>
          </div>
        )}
      </section>
      <div className="command-intro">
        <div>
          <p className="eyebrow">CENTRO DE MANDO</p>
          <h2>Así marcha la operación del {date}.</h2>
        </div>
        <span className="source-note">Las prioridades provienen del ERP</span>
      </div>
      <div className="cards">
        {cards.map(([label, value, Icon]) => (
          <KpiCard key={label} label={label} value={value} icon={Icon} />
        ))}
        <button type="button" className="card kpi-card green cash-box-card" onClick={() => setCajaAbierta(true)}>
          <div className="kpi-top"><span>Caja del repartidor</span><CircleDollarSign size={17} /></div>
          <strong>{formatCurrency(caja.total || 0)}</strong>
          <div className="kpi-bottom"><span className="trend-up">{caja.cantidad_pagos || 0} movimiento(s) de caja</span><span>Ver detalle</span></div>
          <Sparkline values={[0, caja.cantidad_pagos || 0, caja.total || 0, caja.total || 0]} />
        </button>
      </div>
      {cajaAbierta && <CashBoxDialog caja={caja} onClose={() => setCajaAbierta(false)} onClosed={() => { setCajaAbierta(false); onRefresh?.(); }} />}
      <div className="summary-grid">
        <section className="module-panel alerts-panel">
          <div className="module-heading">
            <div>
              <p className="eyebrow">PRIORIZADO</p>
              <h2>Alertas de operación</h2>
            </div>
            <span className="record-count">{alerts.length} activas</span>
          </div>
          <div className="alert-list">
            {alerts.map((alert) => (
              <button
                className={`alert-row ${alert.prioridad}`}
                key={alert.titulo}
                onClick={() => onNavigate(alert.destino)}
              >
                <span className="alert-icon">
                  {alert.prioridad === "alta" ? (
                    <AlertTriangle size={17} />
                  ) : alert.prioridad === "media" ? (
                    <Clock3 size={17} />
                  ) : (
                    <CheckCircle2 size={17} />
                  )}
                </span>
                <span>
                  <b>{alert.titulo}</b>
                  <small>{alert.detalle}</small>
                </span>
                <span className="priority-label">{alert.prioridad}</span>
              </button>
            ))}
          </div>
        </section>
        <section className="module-panel events-panel">
          <div className="module-heading">
            <div>
              <p className="eyebrow">AGENDA</p>
              <h2>Próximos eventos</h2>
            </div>
          </div>
          <div className="event-list">
            {(data.proximos_eventos || []).map((event) => (
              <div className="event-row" key={event.hora}>
                <strong>{event.hora}</strong>
                <span>
                  <b>{event.titulo}</b>
                  <small>{event.detalle}</small>
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
      <section className="module-panel trend-panel">
        <div className="module-heading">
          <div>
            <p className="eyebrow">TENDENCIA</p>
            <h2>Ventas recientes</h2>
          </div>
          <span className="record-count">Lectura informativa</span>
        </div>
        <Suspense
          fallback={
            <div className="large-chart" aria-label="Cargando tendencia" />
          }
        >
          <AdminWidgets type="trend" values={data.ventas_tendencia || []} />
        </Suspense>
      </section>
    </>
  );
}

function CashBoxDialog({ caja, onClose, onClosed }) {
  const [recibidos, setRecibidos] = useState({});
  const [observaciones, setObservaciones] = useState({});
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState("");
  const fecha = caja.fecha ? new Date(`${caja.fecha}T12:00:00`).toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "numeric" }) : "Fecha seleccionada";
  const fechaInicio = (caja.repartidores || [])[0]?.fecha_inicio || caja.fecha;
  const fechaInicioTexto = fechaInicio ? new Date(`${fechaInicio}T12:00:00`).toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "numeric" }) : fecha;
  async function cerrarCaja(grupo) {
    const valor = recibidos[grupo.repartidor_id] ?? grupo.total;
    setBusy(grupo.repartidor_id);
    setStatus("");
    try {
      const response = await fetch("/api/dashboard/caja/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "cerrar", datos: { fecha: caja.fecha, repartidor_id: grupo.repartidor_id, valor_recibido: valor, observacion: observaciones[grupo.repartidor_id] || "" } }) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible cerrar la caja.");
      notificarAdmin(payload.mensaje || "Caja cerrada correctamente.", "success");
      onClosed?.();
    } catch (error) {
      setStatus(error.message || "No fue posible cerrar la caja.");
      notificarAdmin(error.message || "No fue posible cerrar la caja.", "error");
    } finally { setBusy(""); }
  }
  return <div className="admin-confirm-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="admin-confirm-dialog cash-box-dialog" role="dialog" aria-modal="true" aria-label="Caja del repartidor" onMouseDown={(event) => event.stopPropagation()}>
      <p className="eyebrow">EFECTIVO RECAUDADO</p>
      <h2>Caja del repartidor</h2>
      <p>Saldo activo desde el {fechaInicioTexto}. Incluye efectivo recibido y descuenta gastos pagados en efectivo y retiros registrados.</p>
      {(caja.repartidores || []).length ? caja.repartidores.map((grupo) => <section className="cash-box-group" key={grupo.repartidor_id || grupo.repartidor}>
        <header><b>{visibleRepartidorName(grupo.repartidor)}</b><strong>{formatCurrency(grupo.total || 0)}</strong></header>
        <p className="cash-box-breakdown">Efectivo recibido {formatCurrency(grupo.ingresos_efectivo || 0)} · gastos en efectivo {formatCurrency(grupo.gastos_efectivo || 0)}</p>
        <div className="cash-box-detail-scroll"><div className="cash-box-days">{(grupo.dias || []).map((dia) => <section className="cash-box-day" key={dia.fecha}><header><b>{new Date(`${dia.fecha}T12:00:00`).toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" })}</b><strong>{formatCurrency(dia.total || 0)}</strong></header><div className="cash-box-lines">{(dia.pedidos || []).map((pedido, index) => <div className="cash-box-line" key={`${dia.fecha}-${pedido.pedido_id}-${index}`}><span><b>{pedido.cliente}</b><small>{pedido.origen || "Movimiento en efectivo"} · {pedido.pedido_id}</small></span><strong>{formatCurrency(pedido.valor || 0)}</strong></div>)}</div></section>)}</div></div>
        {grupo.repartidor_id && (() => { const cierre = (caja.cierres || []).find((item) => item.delivery_user_id === grupo.repartidor_id); return cierre ? <p className="cash-box-closed">Caja cerrada · recibido {formatCurrency(cierre.received_value || 0)} · diferencia {formatCurrency(cierre.difference_value || 0)}</p> : <div className="cash-box-close"><label>Efectivo recibido por administración<input type="number" min="0" step="0.01" value={recibidos[grupo.repartidor_id] ?? grupo.total ?? 0} onChange={(event) => setRecibidos((actual) => ({ ...actual, [grupo.repartidor_id]: event.target.value }))} /></label><label>Observación<input value={observaciones[grupo.repartidor_id] || ""} onChange={(event) => setObservaciones((actual) => ({ ...actual, [grupo.repartidor_id]: event.target.value }))} placeholder="Opcional" /></label><button type="button" className="secondary-action" disabled={busy === grupo.repartidor_id} onClick={() => cerrarCaja(grupo)}>{busy === grupo.repartidor_id ? "Cerrando…" : "Cerrar caja"}</button></div>; })()}
      </section>) : <p className="cash-box-empty">No hay pagos en efectivo aplicados para esta fecha.</p>}
      {status && <p className="push-status warning">{status}</p>}
      <footer className="admin-confirm-actions"><strong>Disponible {formatCurrency(caja.total || 0)}</strong><button type="button" className="primary-action" onClick={onClose}>Cerrar</button></footer>
    </section>
  </div>;
}

const moduleConfig = {
  pedidos: {
    title: "Pedidos recientes",
    key: "pedidos",
    tabs: ["Todos", "Pendientes", "En ruta", "Parciales", "Entregados", "Cancelados"],
    columns: [
      { accessorKey: "cliente", header: "Cliente" },
      { accessorKey: "estado", header: "Estado" },
      { accessorKey: "fecha", header: "Fecha" },
      { accessorKey: "total", header: "Total" },
    ],
  },
  lista_espera: {
    title: "Lista de espera",
    key: "entradas",
    tabs: [],
    columns: [
      { accessorKey: "producto", header: "Producto" },
      { accessorKey: "cantidad", header: "Cantidad" },
      { accessorKey: "cliente", header: "Cliente" },
      { accessorKey: "telefono", header: "Teléfono" },
      { accessorKey: "tipo", header: "Solicitud" },
      { accessorKey: "fecha", header: "Ingreso" },
      { accessorKey: "estado", header: "Estado" },
    ],
  },
  inventario: {
    title: "Inventario operativo",
    key: "inventario",
    tabs: ["Saldos", "Movimientos"],
    columns: [
      { accessorKey: "producto", header: "Producto" },
      { accessorKey: "codigo_visual", header: "Código" },
      { accessorKey: "politica", header: "Política" },
      { accessorKey: "disponible", header: "Disponible" },
      { accessorKey: "reservado", header: "Reservado" },
      { accessorKey: "costo", header: "Costo promedio" },
      { accessorKey: "valor_inventario", header: "Valor inventario" },
      { accessorKey: "estado", header: "Estado" },
    ],
  },
  rutas: { title: "Rutas operativas", key: "rutas", tabs: [], columns: [] },
  pagos: {
    title: "Pagos y cartera",
    key: "pagos",
    tabs: ["Transferencias"],
    columns: [
      { accessorKey: "referencia", header: "Referencia" },
      { accessorKey: "pedido", header: "Pedido" },
      { accessorKey: "monto", header: "Monto" },
      { accessorKey: "metodo", header: "Método" },
      { accessorKey: "estado", header: "Estado" },
    ],
  },
  gastos: {
    title: "Gastos operativos",
    key: "gastos",
    tabs: ["Todos"],
    columns: [
      { accessorKey: "fecha", header: "Fecha" },
      { accessorKey: "tipo", header: "Tipo" },
      { accessorKey: "descripcion", header: "Descripción" },
      { accessorKey: "valor", header: "Valor" },
      { accessorKey: "estado", header: "Estado" },
    ],
  },
};

function pedidoTabEstado(estado) {
  const normalizado = String(estado || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  if (normalizado === "cancelado") return "Cancelados";
  if (normalizado === "entregado") return "Entregados";
  if (normalizado === "parcial") return "Parciales";
  if (normalizado === "en ruta") return "En ruta";
  // "Pendiente" es el estado de un pedido confirmado que aún espera reparto.
  return "Pendientes";
}

function adminDateKey(value) {
  const text = String(value || "").trim();
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (/^hoy/i.test(text)) return base.getTime();
  if (/^mañana/i.test(text)) return base.getTime() + 86400000;
  const iso = text.match(/\d{4}-\d{2}-\d{2}/)?.[0];
  if (iso) return new Date(`${iso}T00:00:00`).getTime();
  const local = text.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (local)
    return new Date(
      Number(local[3]),
      Number(local[2]) - 1,
      Number(local[1]),
    ).getTime();
  return Number.NEGATIVE_INFINITY;
}

function orderDateMatches(value, filter) {
  if (filter === "TODAS") return true;
  const key = adminDateKey(value);
  if (!Number.isFinite(key)) return false;
  const now = new Date();
  const today = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  if (filter === "HOY") return key === today;
  if (filter === "MANANA") return key === today + 86400000;
  if (filter === "PROXIMAS") return key >= today;
  if (filter === "SEMANA") return key >= today && key < today + 7 * 86400000;
  return true;
}

function pedidoEstadoServidor(tab) {
  return ({
    Pendientes: "Pendiente",
    "En ruta": "En ruta",
    Parciales: "Parcial",
    Entregados: "Entregado",
    Cancelados: "Cancelado",
  })[tab] || "";
}

function pedidoDashboardRequest(query = {}) {
  const scope = String(query.scope || "PROXIMAS").toUpperCase();
  const limite = Math.min(80, Math.max(10, Number(query.limite) || 50));
  const request = {
    vista_resumida: true,
    incluir_necesidades: false,
    limite,
    offset: Math.max(0, Number(query.offset) || 0),
  };
  if (query.estado) request.estado = query.estado;
  if (query.productoCodigo && ["CON", "SIN"].includes(query.productoModo)) {
    request.producto_codigo = query.productoCodigo;
    request.producto_modo = query.productoModo;
  }
  if (scope === "TODAS") {
    request.mostrar_todos = true;
  } else if (scope === "HOY") {
    request.fecha = todayRouteKey();
  } else if (scope === "MANANA") {
    request.fecha = todayRouteKey(1);
  } else if (scope === "SEMANA") {
    request.fecha_desde = todayRouteKey();
    request.fecha_hasta = todayRouteKey(6);
  } else {
    request.fecha_desde = todayRouteKey();
  }
  return request;
}

function AdminOrderDrawer({ order, onClose, onChanged }) {
  const [detail, setDetail] = useState(order);
  const [lines, setLines] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [showProductAdder, setShowProductAdder] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [newProduct, setNewProduct] = useState("");
  const [newQuantity, setNewQuantity] = useState(1);
  const [address, setAddress] = useState(
    order.direccion || order.direccion_entrega || "",
  );
  const [observation, setObservation] = useState(order.observaciones || "");
  const [hour, setHour] = useState(order.hora_entrega_solicitada || "");
  const [date, setDate] = useState(order.fecha || "");
  const [status, setStatus] = useState("Cargando pedido…");
  const [busy, setBusy] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Efectivo");
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [deliveryLines, setDeliveryLines] = useState([]);
  const [pendingDate, setPendingDate] = useState(
    operationalDateKey(1),
  );
  const pedidoId = String(
    order.id ||
      order["Id Pedido"] ||
      order.PedidoId ||
      order.pedido_id ||
      order.pedido ||
      "",
  ).trim();

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const response = await fetch("/api/dashboard/pedidos/operacion", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            operacion: "detalle",
            datos: { pedido_id: pedidoId },
          }),
        });
        const payload = await response.json();
        if (!response.ok || !payload.ok)
          throw new Error(
            payload.mensaje ||
              `No fue posible cargar el pedido (HTTP ${response.status}).`,
          );
        const responseData = payload.data || payload;
        const current = responseData.pedido || {};
        const detailRows = responseData.detalle || current.detalle || [];
        if (!active) return;
        setDetail(current);
        const normalizedLines = detailRows.map((line) => ({
            ...line,
            codigo:
              line.codigo_producto ||
              line.codigo ||
              line.CodigoProducto ||
              line.producto ||
              "",
            cantidad: Number(line.cantidad_solicitada || line.cantidad || 1),
            cantidadEntregada: Number(line.cantidad_entregada || line.CantidadEntregada || 0),
            pesoKg: Number(line.peso_kg || line.PesoKg || 0),
            precioKg: Number(line.precio_kg || line.PrecioKg || 0),
            pricingStatus: line.pricing_status || line.PricingStatus || "CONFIRMADO",
          }));
        setLines(normalizedLines);
        setDeliveryLines(normalizedLines.map((line) => ({
          codigo: line.codigo,
          producto: line.producto || line.descripcion || line.codigo,
          solicitada: Number(line.cantidad || 0),
          entregada: Number(line.cantidadEntregada || 0),
          pendiente: Math.max(0, Number(line.cantidad || 0) - Number(line.cantidadEntregada || 0)),
          cantidad: Math.max(0, Number(line.cantidad || 0) - Number(line.cantidadEntregada || 0)),
        })));
        setAddress(current.direccion_entrega || current.DireccionEntrega || "");
        setObservation(current.observaciones || current.Observaciones || "");
        setHour(
          current.hora_entrega_solicitada ||
            current.HoraEntregaSolicitada ||
            "",
        );
        setDate(current.fecha_entrega || current["Fecha entrega"] || "");
        setStatus("");
      } catch (error) {
        if (active) setStatus(error.message);
      }
    }
    if (pedidoId) load();
    return () => {
      active = false;
    };
  }, [pedidoId]);

  useEffect(() => {
    if (!showProductAdder || productOptions.length) return undefined;
    let active = true;
    setProductsLoading(true);
    readDashboard("productos")
      .then((payload) => {
        if (active) setProductOptions(payload.data?.productos || []);
      })
      .catch(() => {
        if (active) setProductOptions([]);
      })
      .finally(() => {
        if (active) setProductsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [showProductAdder, productOptions.length]);

  async function execute(operation, datos = {}) {
    setBusy(true);
    setStatus("Guardando cambios del pedido…");
    try {
      const response = await fetch("/api/dashboard/pedidos/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: operation,
          datos: {
            pedido_id: pedidoId,
            operacion_id: createOperationId("ADM-PED"),
            ...datos,
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(
          payload.mensaje ||
            `No fue posible guardar (HTTP ${response.status}).`,
        );
      const mensaje = payload.mensaje || "Pedido actualizado correctamente.";
      setStatus(mensaje);
      notificarAdmin(mensaje, "success");
      if (payload.data) {
        const data = payload.data;
        if (Array.isArray(data.productos)) {
          setLines(
            data.productos.map((item) => ({
              ...item,
              precio: Number(item.precio || 0),
              valor: Number(item.valor || 0),
            })),
          );
        }
        setDetail((current) => ({
          ...(current || {}),
          ...(data.pedido_actualizado || {}),
          ...(data.valor_productos != null
            ? { valor_productos: data.valor_productos }
            : {}),
          ...(data.recargo_entrega != null
            ? { recargo_entrega: data.recargo_entrega }
            : {}),
          ...(data.valor_total != null
            ? { valor_total: data.valor_total }
            : data.valor != null
              ? { valor_total: data.valor }
              : {}),
        }));
      }
      onChanged?.({ notificar: false });
    } catch (error) {
      const mensaje = error.message || "No fue posible guardar el pedido.";
      setStatus(mensaje);
      notificarAdmin(mensaje, "error");
    } finally {
      setBusy(false);
    }
  }

  const gestion = detail || {};
  const puedeEditar = gestion.puede_editarse !== false;
  const puedeCancelar = gestion.puede_cancelarse !== false;
  // Un pedido pendiente cuya fecha venció no admite cambios comerciales,
  // pero sí debe poder trasladarse a una nueva fecha. El backend conserva
  // esta misma regla para reprogramar y mover su reserva/ruta de forma
  // atómica; no usemos ``puede_editarse`` para bloquear esa única acción.
  const estadoParaReprogramar = String(
    gestion.estado || gestion.Estado || "",
  )
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  const puedeReprogramar = ![
    "en preparacion",
    "en ruta",
    "entregado",
    "cancelado",
  ].includes(estadoParaReprogramar);
  function addProduct() {
    if (!newProduct || !puedeEditar) return;
    const product = productOptions.find(
      (item) => String(item.codigo || "") === newProduct,
    );
    setLines((current) => {
      const exists = current.find(
        (line) => String(line.codigo || "") === newProduct,
      );
      return exists
        ? current.map((line) =>
            line === exists
              ? {
                  ...line,
                  cantidad: Number(line.cantidad || 0) + Number(newQuantity || 1),
                  precio: Number(line.precio || product?.precio || 0),
                  valor:
                    (Number(line.cantidad || 0) + Number(newQuantity || 1)) *
                    Number(line.precio || product?.precio || 0),
                }
              : line,
          )
        : [
            ...current,
            {
              codigo: newProduct,
              producto:
                product?.producto || product?.presentacion || newProduct,
              cantidad: Number(newQuantity || 1),
              precio: Number(product?.precio || 0),
              valor: Number(product?.precio || 0) * Number(newQuantity || 1),
            },
          ];
    });
    setNewProduct("");
    setNewQuantity(1);
  }

  function saveLines() {
    execute("modificar", {
      cambios: {
        productos: lines
          .map((line) => ({
            codigo: line.codigo || line.producto,
            cantidad: Number(line.cantidad || 0),
          }))
          .filter((line) => line.cantidad > 0),
        fecha_entrega: date,
        direccion: address,
        observacion: observation,
        hora_entrega: hour,
      },
    });
  }

  const subtotal = Number(detail?.valor_productos ?? 0);
  const recargo = Number(detail?.recargo_entrega ?? 0);
  const total = Number(
    detail?.valor_total ?? detail?.valor ?? order.total ?? 0,
  );
  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside
        className="drawer order-admin-drawer"
        onClick={(event) => event.stopPropagation()}
      >
        <button className="drawer-close" onClick={onClose}>
          <X size={18} />
        </button>
        <p className="eyebrow">GESTIÓN DE PEDIDO</p>
        <h2>{pedidoId}</h2>
        <p className="drawer-subtitle">
          {detail?.cliente || order.cliente || "Cliente sin nombre"} ·{" "}
          {detail?.estado_operativo || order.estado || "Estado pendiente"}
        </p>
        {status === "Cargando pedido…" && (
          <p className="action-status progress" role="status">
            Cargando productos y valores del pedido…
          </p>
        )}
        <div className="order-admin-summary">
          <span>Entrega</span>
          <b>{date || "Por programar"}</b>
          <span>Productos</span>
          <b>{formatCurrency(subtotal)}</b>
          {recargo > 0 && (
            <>
              <span>Domicilio</span>
              <b>{formatCurrency(recargo)}</b>
            </>
          )}
          <span>Total</span>
          <b>{formatCurrency(total)}</b>
        </div>
        <div className="admin-order-lines">
          <h3>Productos del pedido</h3>
          {lines.length ? (
            lines.map((line, index) => (
              <div
                className="admin-order-line"
                key={`${line.producto || line.codigo || "linea"}-${index}`}
              >
                <span>
                  {line.producto ||
                    line.descripcion ||
                    line.codigo ||
                    "Producto"}
                  <small>
                    {Number(line.cantidad || 0)} ×{" "}
                    {formatCurrency(line.precio || 0)} ·{" "}
                    {formatCurrency(line.valor || 0)}
                    {Number(line.cantidadEntregada || 0) > 0 &&
                      ` · Entregado: ${Number(line.cantidadEntregada || 0)}`}
                  </small>
                  {Number(line.pesoKg || 0) > 0 && (
                    <small>
                      {`${(Number(line.pesoKg) * 2.20462262).toLocaleString("es-CO", { maximumFractionDigits: 2 })} lb por unidad · ${formatCurrency(Number(line.precioKg || 0) * 0.45359237)} por libra · ${line.pricingStatus === "CONFIRMADO" ? "Valor confirmado" : "Valor pendiente"}`}
                    </small>
                  )}
                  {Number(line.cantidadEntregada || 0) >= Number(line.cantidad || 0) ? (
                    <em className="delivery-line-status delivered">Entregado completo</em>
                  ) : Number(line.cantidadEntregada || 0) > 0 ? (
                    <em className="delivery-line-status partial">Parcial: faltan {Number(line.cantidad || 0) - Number(line.cantidadEntregada || 0)}</em>
                  ) : (
                    <em className="delivery-line-status pending">Pendiente de entrega</em>
                  )}
                </span>
                <input
                  aria-label={`Cantidad ${line.producto || line.codigo || index + 1}`}
                  type="number"
                  min="0"
                  value={line.cantidad || 0}
                  disabled={!puedeEditar}
                  onChange={(event) =>
                    setLines((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index
                          ? { ...item, cantidad: event.target.value }
                          : item,
                      ),
                    )
                  }
                />
                <button
                  type="button"
                  className="secondary-action"
                  disabled={!puedeEditar}
                  onClick={() =>
                    setLines((current) =>
                      current.filter((_, itemIndex) => itemIndex !== index),
                    )
                  }
                >
                  Quitar
                </button>
              </div>
            ))
          ) : (
            <small>Este pedido no tiene líneas visibles.</small>
          )}
          {puedeEditar && (
            <>
              <button
                type="button"
                className="secondary-action"
                onClick={() => setShowProductAdder((value) => !value)}
              >
                {showProductAdder ? "Cerrar selector" : "Agregar producto"}
              </button>
              {showProductAdder && (
                <div className="admin-order-line-add">
                  <select
                    value={newProduct}
                    onChange={(event) => setNewProduct(event.target.value)}
                    disabled={productsLoading}
                  >
                    <option value="">
                      {productsLoading
                        ? "Cargando productos…"
                        : "Selecciona una presentación"}
                    </option>
                    {productOptions
                      .filter(isOrderProductSelectable)
                      .map((item) => (
                        <option key={item.codigo} value={item.codigo}>
                          {orderProductLabel(item)}
                        </option>
                      ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    value={newQuantity}
                    onChange={(event) => setNewQuantity(event.target.value)}
                  />
                  <button
                    type="button"
                    className="secondary-action"
                    disabled={!newProduct}
                    onClick={addProduct}
                  >
                    Agregar
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <div className="admin-order-form">
          <label>
            Dirección de entrega
            <input
              value={address}
              disabled={!puedeEditar}
              onChange={(event) => setAddress(event.target.value)}
            />
          </label>
          <label>
            Observación
            <textarea
              value={observation}
              disabled={!puedeEditar}
              onChange={(event) => setObservation(event.target.value)}
              rows="3"
            />
          </label>
          <label>
            Hora solicitada
            <input
              type="time"
              value={hour}
              disabled={!puedeEditar}
              onChange={(event) => setHour(event.target.value)}
            />
          </label>
          <label>
            Fecha de entrega
            <input
              type="date"
              value={date}
              disabled={!puedeEditar && !puedeReprogramar}
              onChange={(event) => setDate(event.target.value)}
            />
          </label>
        </div>
        <div className="order-admin-actions">
          <button
            type="button"
            className="primary-action"
            disabled={busy || !puedeEditar || !lines.length}
            onClick={saveLines}
          >
            {busy ? "Guardando…" : "Guardar cambios del pedido"}
          </button>
          <button
            type="button"
            className="secondary-action"
            disabled={busy || !puedeReprogramar || !date}
            onClick={() => execute("reprogramar", { fecha_entrega: date })}
          >
            Reprogramar entrega
          </button>
          <button
            type="button"
            className="secondary-action"
            disabled={busy || detail?.estado_pago === "Pagado"}
            onClick={() => setPaymentOpen(true)}
          >
            {detail?.estado_pago === "Pagado"
              ? "Pago registrado"
              : "Registrar pago"}
          </button>
          <button
            type="button"
            className="primary-action"
            disabled={busy || detail?.estado === "Entregado" || detail?.Estado === "Entregado" || !deliveryLines.some((line) => line.pendiente > 0)}
            onClick={() => setDeliveryOpen(true)}
          >
            {detail?.estado === "Entregado" || detail?.Estado === "Entregado" ? "Pedido entregado" : "Registrar entrega"}
          </button>
          <button
            type="button"
            className="danger-action"
            disabled={busy || !puedeCancelar}
            onClick={() => confirmarAccionAdmin({
              titulo: "Cancelar pedido",
              mensaje: "¿Cancelar este pedido y liberar su disponibilidad?",
              confirmar: "Cancelar pedido",
              peligro: true,
              onConfirm: () => execute("cancelar"),
            })}
          >
            Cancelar pedido
          </button>
        </div>
        {!puedeEditar && (
          <p className="admin-order-warning">
            {puedeReprogramar
              ? "Los productos y datos de entrega están bloqueados, pero puedes reprogramar la fecha."
              : detail?.motivo_no_edicion ||
                "Este pedido no admite edición en su estado actual."}
          </p>
        )}
        {status && status !== "Cargando pedido…" && (
          <p
            className={`action-status ${status.includes("No fue") || status.includes("HTTP") ? "error" : "success"}`}
            role="status"
            aria-live="polite"
          >
            {status}
          </p>
        )}
        {paymentOpen && (
          <div className="admin-payment-panel">
            <h3>Registrar pago total</h3>
            <p>{formatCurrency(total)} · quedará asociado a este pedido.</p>
            <label>
              Forma de pago
              <select
                value={paymentMethod}
                onChange={(event) => setPaymentMethod(event.target.value)}
              >
                <option>Efectivo</option>
                <option>Transferencia</option>
              </select>
            </label>
            <div className="order-admin-actions">
              <button
                type="button"
                className="secondary-action"
                disabled={busy}
                onClick={() => setPaymentOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="primary-action"
                disabled={busy}
                onClick={() => {
                  setPaymentOpen(false);
                  execute("registrar_pago", {
                    metodo_pago: paymentMethod,
                    valor_recibido: total,
                    operacion_pago_id: createOperationId("PAGO-ADM"),
                  });
                }}
              >
                {busy ? "Registrando…" : "Confirmar pago"}
              </button>
            </div>
          </div>
        )}
        {deliveryOpen && (
          <div className="admin-payment-panel">
            <h3>Registrar productos entregados</h3>
            <p>Marca la cantidad entregada de cada producto. Los que ya se entregaron quedan identificados y no se vuelven a cobrar.</p>
            <div className="delivery-lines-status-list">
              {deliveryLines.map((line, index) => (
                <div className={`delivery-line-status-row ${line.pendiente <= 0 ? "is-delivered" : "is-pending"}`} key={`${line.codigo}-${index}`}>
                  <div>
                    <strong>{line.producto}</strong>
                    <small>
                      {line.pendiente <= 0
                        ? `Entregado: ${line.entregada} de ${line.solicitada}`
                        : `Pendiente: ${line.pendiente} de ${line.solicitada}`}
                    </small>
                  </div>
                  {line.pendiente > 0 ? (
                    <input
                      aria-label={`Cantidad entregada de ${line.producto}`}
                      type="number"
                      min="0"
                      max={line.pendiente}
                      step="0.001"
                      value={line.cantidad}
                      onChange={(event) => setDeliveryLines((current) => current.map((item) =>
                        item.codigo === line.codigo ? { ...item, cantidad: event.target.value } : item,
                      ))}
                    />
                  ) : (
                    <span className="delivery-line-check">Entregado</span>
                  )}
                </div>
              ))}
            </div>
            <label>
              Fecha alternativa para el saldo pendiente
              <input type="date" value={pendingDate} onChange={(event) => setPendingDate(event.target.value)} />
            </label>
            <small className="delivery-route-date-help">Si la ruta de hoy sigue activa, el saldo conserva automáticamente la fecha de hoy y permanece en la misma ruta.</small>
            <div className="order-admin-actions">
              <button type="button" className="secondary-action" disabled={busy} onClick={() => setDeliveryOpen(false)}>Cancelar</button>
              <button
                type="button"
                className="primary-action"
                disabled={busy || !deliveryLines.some((line) => Number(line.cantidad || 0) > 0)}
                onClick={() => {
                  setDeliveryOpen(false);
                  execute("registrar_entrega", {
                    productos_entregados: deliveryLines.filter((line) => Number(line.cantidad || 0) > 0).map((line) => ({ codigo: line.codigo, producto: line.producto, cantidad: Number(line.cantidad) })),
                    fecha_pendiente: pendingDate,
                  });
                }}
              >
                {busy ? "Registrando…" : "Confirmar entrega"}
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function WaitlistEntryDrawer({ entry, onClose, onChanged }) {
  const [telefono, setTelefono] = useState(entry.telefono || "");
  const [cantidad, setCantidad] = useState(entry.cantidad || 1);
  const [fechaSolicitada, setFechaSolicitada] = useState(String(entry.fecha_solicitada || "").slice(0, 10));
  const [observacion, setObservacion] = useState(entry.observacion || "");
  const [conversionOpen, setConversionOpen] = useState(false);
  const [nombre, setNombre] = useState("");
  const [direccion, setDireccion] = useState("");
  const [municipioEntrega, setMunicipioEntrega] = useState("Supía, Caldas");
  const [fechaEntrega, setFechaEntrega] = useState(String(entry.fecha_solicitada || "").slice(0, 10));
  const [observacionPedido, setObservacionPedido] = useState("");
  const esPesoVariable = String(entry.politica_disponibilidad || "").toUpperCase() === "LOTE_VARIABLE";
  const precioReferencia = Number(entry.precio_referencia || 0);
  const precioPorLibraReferencia = entry.unidad_precio === "kg"
    ? precioReferencia * 0.45359237
    : precioReferencia;
  const [libras, setLibras] = useState("");
  const [precioPorLibra, setPrecioPorLibra] = useState(
    precioPorLibraReferencia > 0 ? String(Math.round(precioPorLibraReferencia)) : "",
  );
  const librasNumero = Number(libras || 0);
  const precioLibraNumero = Number(precioPorLibra || 0);
  const subtotalReferencia = esPesoVariable
    ? librasNumero * precioLibraNumero * Number(cantidad || 0)
    : Number(cantidad || 0) * precioReferencia;
  const pesoVariableCompleto = librasNumero > 0 && precioLibraNumero > 0;
  // Conserva la misma llave si la red falla y la persona reintenta sin cerrar.
  const conversionOperationId = useRef(createOperationId("LEE-PEDIDO"));
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  useEffect(() => {
    const telefonoInicial = String(entry.telefono || "").trim();
    if (!telefonoInicial) return undefined;
    let active = true;
    fetch("/api/dashboard/pedidos/operacion", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ operacion: "buscar_cliente", datos: { telefono: telefonoInicial } }),
    })
      .then((response) => response.json())
      .then((payload) => {
        const cliente = payload?.data?.cliente;
        if (!active || !payload?.ok || !cliente) return;
        setNombre((actual) => actual || cliente.nombre || "");
        setDireccion((actual) => actual || cliente.direccion || "");
        setMunicipioEntrega((actual) => actual === "Supía, Caldas" ? (cliente.municipio || actual) : actual);
      })
      .catch(() => {
        // La edición manual sigue disponible si el perfil no puede leerse.
      });
    return () => { active = false; };
  }, [entry.id, entry.telefono]);
  async function ejecutar(operacion) {
    if (operacion === "convertir_en_pedido" && (!nombre.trim() || !direccion.trim())) {
      setStatus("Escribe el nombre y la dirección antes de crear el pedido.");
      return;
    }
    if (operacion === "convertir_en_pedido" && esPesoVariable && !pesoVariableCompleto) {
      setStatus("Registra las libras por unidad y el precio por libra antes de crear el pedido.");
      return;
    }
    setBusy(true); setStatus("");
    try {
      const datos = { lista_espera_id: entry.id };
      if (operacion === "actualizar") Object.assign(datos, { telefono_contacto: telefono, cantidad: Number(cantidad), fecha_solicitada: fechaSolicitada, observacion });
      if (operacion === "convertir_en_pedido") Object.assign(datos, {
        operacion_id: conversionOperationId.current,
        nombre: nombre.trim(), direccion: direccion.trim(), municipio_entrega: municipioEntrega.trim(),
        fecha_entrega: fechaEntrega, observaciones: observacionPedido.trim(),
      });
      if (operacion === "convertir_en_pedido" && esPesoVariable) Object.assign(datos, {
        libras_por_unidad: librasNumero,
        precio_por_libra: precioLibraNumero,
      });
      const response = await fetch("/api/dashboard/lista-espera/operacion", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ operacion, datos }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible gestionar la solicitud.");
      const totalConfirmado = operacion === "convertir_en_pedido" ? Number(payload.data?.valor_total) : NaN;
      const mensajeConfirmacion = Number.isFinite(totalConfirmado)
        ? `${payload.mensaje || "Pedido creado correctamente."} Total a cobrar: ${formatCurrency(totalConfirmado)}.`
        : (payload.mensaje || "Solicitud actualizada.");
      notificarAdmin(mensajeConfirmacion, "success");
      onChanged?.(); onClose();
    } catch (error) {
      const mensaje = error.message || "No fue posible gestionar la solicitud.";
      setStatus(mensaje); notificarAdmin(mensaje, "error");
    } finally { setBusy(false); }
  }
  function confirmarSalida(operacion) {
    const atender = operacion === "atender";
    confirmarAccionAdmin({
      titulo: atender ? "Marcar solicitud atendida" : "Cancelar solicitud",
      mensaje: atender ? "La solicitud dejará de estar activa. Esto no crea un pedido." : "La solicitud dejará de estar activa y no crea ningún pedido.",
      confirmar: atender ? "Marcar atendida" : "Cancelar solicitud", peligro: !atender,
      onConfirm: () => ejecutar(operacion),
    });
  }
  function confirmarConversion() {
    confirmarAccionAdmin({
      titulo: "Crear pedido desde Lista de espera",
      mensaje: "Se validarán disponibilidad, precio, fecha y cobertura. Solo si todo es válido se crea el pedido y se cierra esta solicitud.",
      confirmar: "Validar y crear pedido",
      onConfirm: () => ejecutar("convertir_en_pedido"),
    });
  }
  return <div className="drawer-backdrop" onClick={onClose}>
    <aside className="drawer order-admin-drawer" onClick={(event) => event.stopPropagation()}>
      <button className="drawer-close" onClick={onClose} aria-label="Cerrar">×</button>
      <p className="eyebrow">LISTA DE ESPERA</p>
      <h2>{entry.producto || entry.codigo_presentacion}</h2>
      <p className="drawer-subtitle">{entry.tipo || "LISTA_ESPERA"} · Posición FIFO {entry.posicion || "—"}</p>
      <div className="detail-list">
        <div><span>Referencia</span><b>{entry.id}</b></div>
        <div><span>Cliente</span><b>{entry.cliente || "No registrado"}</b></div>
        <div className="waitlist-contact-row"><span>WhatsApp de contacto</span><b>{formatPhoneVisible(entry.telefono) || "No registrado"}</b>{formatPhoneHref(entry.telefono) && <a href={formatPhoneHref(entry.telefono)} target="_blank" rel="noreferrer">Abrir WhatsApp</a>}</div>
        <div><span>Presentación</span><b>{entry.presentacion || entry.codigo_presentacion || "—"}</b></div>
        <div><span>Ingreso</span><b>{entry.fecha || "—"}</b></div>
      </div>
      <div className="admin-order-form">
        <label>Teléfono de contacto<input value={telefono} onChange={(event) => setTelefono(event.target.value)} /></label>
        <label>Cantidad solicitada<input type="number" min="1" step="1" value={cantidad} onChange={(event) => setCantidad(event.target.value)} /></label>
        <label>Fecha solicitada<input type="date" value={fechaSolicitada} onChange={(event) => setFechaSolicitada(event.target.value)} /></label>
        <label>Observación<textarea rows="3" value={observacion} onChange={(event) => setObservacion(event.target.value)} /></label>
        <button type="button" className="primary-action" disabled={busy} onClick={() => ejecutar("actualizar")}>{busy ? "Guardando…" : "Guardar cambios"}</button>
        <button type="button" className="secondary-action" disabled={busy} onClick={() => setConversionOpen((visible) => !visible)}>{conversionOpen ? "Cerrar creación de pedido" : "Convertir en pedido"}</button>
        {conversionOpen && <section className="admin-payment-panel">
          <h3>Datos para crear el pedido</h3>
          <p>La solicitud se conserva activa si falta cupo, producto o cobertura.</p>
          <label>Nombre del cliente<input value={nombre} onChange={(event) => setNombre(event.target.value)} /></label>
          <label>Dirección de entrega<input value={direccion} onChange={(event) => setDireccion(event.target.value)} /></label>
          <label>Municipio de entrega<input value={municipioEntrega} onChange={(event) => setMunicipioEntrega(event.target.value)} /></label>
          <label>Fecha de entrega<input type="date" value={fechaEntrega} onChange={(event) => setFechaEntrega(event.target.value)} /></label>
          {esPesoVariable ? <>
            <p>Producto por peso. Registra el peso y el precio para revisar el valor que se cobrará.</p>
            <label>Libras reales por unidad<input type="number" min="0.01" step="0.01" value={libras} onChange={(event) => setLibras(event.target.value)} placeholder="Ej.: 8" /></label>
            <label>Precio por libra<input type="number" min="0" step="1" value={precioPorLibra} onChange={(event) => setPrecioPorLibra(event.target.value)} placeholder="Valor por libra" /></label>
            <div className="detail-list">
              <div><span>Subtotal del producto</span><b>{pesoVariableCompleto ? formatCurrency(subtotalReferencia) : "Completa libras y precio"}</b></div>
              <div><span>Transporte y domicilio</span><b>Se valida según destino</b></div>
              <div><span>Total a cobrar</span><b>Disponible al confirmar el valor</b></div>
            </div>
            <p className="admin-order-warning">El peso y el precio se guardarán junto con el pedido. El total definitivo incluirá el transporte y domicilio que valide el destino.</p>
          </> : <div className="detail-list">
            <div><span>Precio de referencia</span><b>{precioReferencia > 0 ? formatCurrency(precioReferencia) : "Pendiente de validar"}</b></div>
            <div><span>Subtotal de productos</span><b>{precioReferencia > 0 ? formatCurrency(subtotalReferencia) : "Pendiente de validar"}</b></div>
            <div><span>Transporte y domicilio</span><b>Se valida según destino</b></div>
          </div>}
          <label>Observación del pedido<textarea rows="2" value={observacionPedido} onChange={(event) => setObservacionPedido(event.target.value)} /></label>
          <button type="button" className="primary-action" disabled={busy || !nombre.trim() || !direccion.trim() || (esPesoVariable && !pesoVariableCompleto)} onClick={confirmarConversion}>{busy ? "Validando…" : "Validar y crear pedido"}</button>
        </section>}
        <button type="button" className="secondary-action" disabled={busy} onClick={() => confirmarSalida("atender")}>Marcar atendida sin pedido</button>
        <button type="button" className="danger-action" disabled={busy} onClick={() => confirmarSalida("cancelar")}>Cancelar solicitud</button>
      </div>
      {status && <p className="action-status error">{status}</p>}
      <p className="admin-order-warning">Marcar como atendida o cancelar no crea un pedido ni reserva producto.</p>
    </aside>
  </div>;
}

function ManualOrderModal({ onClose, onCreated }) {
  const [products, setProducts] = useState([]);
  const [phone, setPhone] = useState("");
  const [customer, setCustomer] = useState(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [saleMode, setSaleMode] = useState("PROGRAMADO");
  const [paid, setPaid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Efectivo");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [municipality, setMunicipality] = useState("Supía, Caldas");
  const [date, setDate] = useState(
    operationalDateKey(1),
  );
  const [sameDayDelivery, setSameDayDelivery] = useState(false);
  const [deliveryType, setDeliveryType] = useState("Ruta normal");
  const [hour, setHour] = useState("08:00");
  const [observation, setObservation] = useState("");
  const [code, setCode] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [lines, setLines] = useState([]);
  const [status, setStatus] = useState("Cargando productos…");
  const [busy, setBusy] = useState(false);
  // Una creación es una sola operación aunque el navegador deba reintentarla.
  // La llave vive durante todo el ciclo de este modal y se descarta al cerrarlo.
  const creationOperationId = useRef(null);
  const lineUnitPrice = (product, fallback = 0) => Number(product?.precio ?? product?.precio_venta ?? product?.Precio ?? fallback ?? 0);
  const orderProductsTotal = lines.reduce((sum, line) => {
    const product = products.find(
      (item) => String(item.codigo || "") === String(line.codigo || ""),
    );
    return (
      sum +
      lineUnitPrice(product, line.precio) *
        Number(line.cantidad || 0)
    );
  }, 0);
  const deliverySurcharge = deliveryType === "Personalizada" ? 3000 : deliveryType === "Domicilio" ? 1000 : 0;
  const orderTotal = orderProductsTotal + deliverySurcharge;

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    readDashboard("productos")
      .then((payload) => {
        if (active) {
          setProducts(payload.data?.productos || []);
          setStatus("");
        }
      })
      .catch((error) => {
        if (active) setStatus(error.message);
      });
    return () => {
      active = false;
    };
  }, []);

  async function searchCustomers(event) {
    event?.preventDefault();
    const q = customerQuery.trim();
    if (q.length < 2) {
      setCustomerResults([]);
      return;
    }
    setCustomerSearching(true);
    try {
      const response = await fetch("/api/dashboard/pedidos/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ operacion: "buscar_clientes", datos: { q } }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || "No fue posible buscar clientes.");
      setCustomerResults(payload.data?.clientes || []);
    } catch (error) {
      setCustomerResults([]);
      setStatus(error.message || "No fue posible buscar clientes.");
    } finally {
      setCustomerSearching(false);
    }
  }

  function chooseCustomer(client) {
    setCustomer(client);
    setPhone(client.telefono || "");
    setName(client.nombre || "");
    setAddress(client.direccion || "");
    setCustomerQuery(client.nombre || client.telefono || "");
    setCustomerResults([]);
    setStatus(
      "Cliente existente asociado. Verifica los datos y confirma el pedido.",
    );
  }

  async function resolveCustomerByPhone() {
    const requestedPhone = phone.trim();
    if (requestedPhone.replace(/\D/g, "").length < 7) return;
    setCustomerSearching(true);
    try {
      const response = await fetch("/api/dashboard/pedidos/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ operacion: "buscar_cliente", datos: { telefono: requestedPhone } }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || "No fue posible consultar el cliente.");
      const found = payload.data?.cliente;
      if (found) chooseCustomer({ ...found, telefono: found.telefono || payload.data?.telefono || requestedPhone });
      else setStatus("Cliente nuevo. Completa sus datos para registrar el pedido.");
    } catch (error) {
      setStatus(error.message || "No fue posible consultar el cliente.");
    } finally {
      setCustomerSearching(false);
    }
  }

  function addLine() {
    const product = products.find((item) => item.codigo === code);
    addProductToLines(product, Number(quantity));
  }

  function addProductToLines(product, amount = 1) {
    if (!product || !isOrderProductSelectable(product) || !Number.isFinite(Number(amount)) || Number(amount) < 1) {
      setCode("");
      setStatus("Ese producto ya no está disponible para nuevos pedidos.");
      return;
    }
    const productCode = product.codigo || product.Codigo;
    setLines((current) => {
      const exists = current.find((item) => item.codigo === productCode);
      return exists
        ? current.map((item) =>
            item.codigo === productCode
              ? { ...item, cantidad: item.cantidad + Number(amount) }
              : item,
          )
        : [
            ...current,
            {
              codigo: productCode,
              cantidad: Number(amount),
              nombre: orderProductLabel(product),
              servicio_entrega: product.servicio_entrega || product.ServicioEntrega || "RUTA_AM",
            },
          ];
    });
    setCode("");
    setQuantity(1);
  }

  async function create(event) {
    event.preventDefault();
    const anonymous = saleMode === "ANONIMA" || saleMode === "SITIO";
    // La compra en sitio/anónima se registra primero como venta pagada.
    // Solo el pedido de un cliente conocido usa el cierre inmediato; así una
    // falla de entrega o ruta nunca borra la venta anónima recién creada.
    const immediate = saleMode === "CLIENTE";
    if (
      (anonymous && !paid) ||
      (!anonymous && (!phone.trim() || !name.trim() || !address.trim())) ||
      !date ||
      !lines.length ||
      (deliveryType === "Personalizada" && !hour)
    ) {
      const mensaje =
        anonymous && !paid
          ? "La venta anónima debe estar pagada."
          : "Completa WhatsApp, nombre, fecha y al menos un producto.";
      setStatus(mensaje);
      notificarAdmin(mensaje, "warning");
      return;
    }
    setBusy(true);
    setStatus("Validando y creando pedido…");
    try {
      const operationId =
        creationOperationId.current ||
        (creationOperationId.current = createOperationId("ADM-MANUAL"));
      const response = await fetch("/api/dashboard/pedidos/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: "crear_manual",
          datos: {
            operacion_id: operationId,
            telefono: phone,
            nombre: name,
            direccion: address,
            municipio_entrega: municipality,
            fecha_entrega: date,
            tipo_entrega: deliveryType,
            hora_entrega_solicitada:
              deliveryType === "Personalizada" ? hour : "",
            observaciones: observation,
            productos: lines.map(({ codigo, cantidad }) => ({
              codigo,
              cantidad,
            })),
            entrega_mismo_dia: sameDayDelivery,
            venta_anonima: anonymous,
            entregado_inmediatamente: immediate,
            pago_realizado: paid,
            metodo_pago: paymentMethod,
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(
          payload.mensaje ||
            `No fue posible crear el pedido (HTTP ${response.status}).`,
        );
      const mensaje =
        anonymous
          ? "Venta anónima pagada registrada. Puedes marcar la entrega después desde Pedidos."
          : immediate
            ? "Pedido pagado y entregado; resumen enviado al cliente."
            : paid
              ? "Pedido creado como pagado; no se enviaron datos bancarios."
              : "Pedido creado y confirmación enviada a la cola de WhatsApp.";
      setStatus(mensaje);
      notificarAdmin(mensaje, "success");
      onCreated?.();
    } catch (error) {
      const mensaje = error.message || "No fue posible crear el pedido.";
      setStatus(mensaje);
      notificarAdmin(mensaje, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside
        className="drawer manual-order-drawer"
        onClick={(event) => event.stopPropagation()}
      >
        <button className="drawer-close" onClick={onClose}>
          <X size={18} />
        </button>
        <p className="eyebrow">NUEVO PEDIDO</p>
        <h2>Registrar pedido del cliente</h2>
        <p className="drawer-subtitle">
          Usa las mismas reglas de disponibilidad, cobertura y reserva del canal
          principal.
        </p>
        <section className="manual-sale-mode">
          <label>
            Tipo de registro
            <select
              value={saleMode}
              onChange={(event) => {
                const mode = event.target.value;
                setSaleMode(mode);
                if (mode !== "PROGRAMADO") {
                  setPaid(true);
                  setDate(operationalDateKey());
                }
              }}
            >
              <option value="PROGRAMADO">Pedido programado</option>
              <option value="CLIENTE">
                Pedido de cliente · pagado y entregado
              </option>
              <option value="SITIO">Compra en sitio · pagada</option>
              <option value="ANONIMA">Venta anónima · pagada</option>
            </select>
          </label>
          <label>
            <input
              type="checkbox"
              checked={paid}
              disabled={saleMode !== "PROGRAMADO"}
              onChange={(event) => setPaid(event.target.checked)}
            />{" "}
            Pago realizado
          </label>
          {paid && (
            <label>
              Forma de pago
              <select
                value={paymentMethod}
                onChange={(event) => setPaymentMethod(event.target.value)}
              >
                <option>Efectivo</option>
                <option>Transferencia</option>
              </select>
            </label>
          )}
          {(saleMode === "ANONIMA" || saleMode === "SITIO") && (
            <small>
              Se asignará al cliente fijo “Compra en sitio”; no se crea cliente
              ni se envía WhatsApp.
            </small>
          )}
        </section>
        {saleMode !== "ANONIMA" && saleMode !== "SITIO" && (
          <section className="manual-customer-finder">
            <form onSubmit={searchCustomers}>
              <label>
                Buscar cliente
                <input
                  type="search"
                  value={customerQuery}
                  onChange={(event) => setCustomerQuery(event.target.value)}
                  placeholder="🔎 Celular, nombre o dirección"
                  aria-label="Buscar cliente por celular, nombre o dirección"
                />
              </label>
              <button
                type="submit"
                className="secondary-action"
                disabled={customerSearching}
              >
                {customerSearching ? "Buscando…" : "Buscar"}
              </button>
            </form>
            {customerResults.length > 0 && (
              <div className="manual-customer-results">
                {customerResults.map((client) => (
                  <button
                    type="button"
                    className="manual-customer-result"
                    key={client.cliente_id || client.telefono || client.nombre}
                    onClick={() => chooseCustomer(client)}
                  >
                    <b>{client.nombre || "Cliente sin nombre"}</b>
                    <small>
                      {[client.telefono, client.direccion]
                        .filter(Boolean)
                        .join(" · ") || "Sin teléfono o dirección"}
                    </small>
                  </button>
                ))}
              </div>
            )}
            {customerQuery.trim().length >= 2 &&
              !customerSearching &&
              customerResults.length === 0 && (
                <small>No encontramos clientes con esa búsqueda.</small>
              )}
          </section>
        )}
        <form className="admin-order-form" noValidate onSubmit={create}>
          {saleMode === "ANONIMA" || saleMode === "SITIO" ? (
            <p className="customer-found">
              Venta asignada a Compra en sitio. No se solicitarán datos del
              comprador.
            </p>
          ) : (
            <>
              <label>
                WhatsApp del cliente
                <input
                  value={phone}
                  onChange={(event) => {
                    setPhone(event.target.value);
                    setCustomer(null);
                  }}
                  onBlur={resolveCustomerByPhone}
                  placeholder="3001234567"
                  inputMode="tel"
                />
              </label>
              {customer && (
                <p className="customer-found">
                  Cliente existente identificado. Sus datos ya están
                  precargados.
                </p>
              )}
              <label>
                Nombre
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label>
                Dirección de entrega
                <input value={address} onChange={(event) => setAddress(event.target.value)} />
              </label>
              <label className="manual-municipality-field">
                Municipio de entrega
                <select value={municipality} onChange={(event) => setMunicipality(event.target.value)}>
                  <option value="Supía, Caldas">Supía, Caldas</option>
                </select>
              </label>
            </>
          )}
          {lines.length > 0 && (
            <p className="delivery-service-notice">
              {lines.some((line) => line.servicio_entrega === "RUTA_SOPAS_MEDIODIA") && lines.some((line) => line.servicio_entrega !== "RUTA_SOPAS_MEDIODIA")
                ? "Este carrito contiene productos de mañana y sopas de mediodía. Al confirmar, Titina creará un pedido operativo para cada ruta."
                : ""}
            </p>
          )}
          <fieldset className="manual-product-picker">
            <legend>Productos del pedido</legend>
            <div className="order-product-card-toolbar">
              <label>Cantidad para agregar<input type="number" min="1" value={quantity} onChange={(event) => setQuantity(event.target.value)} /></label>
              <span>Selecciona una tarjeta para agregarla</span>
            </div>
            <OrderProductCards products={products} selectedCode={code} lines={lines} onSelect={(product) => setCode(product.codigo)} onAdd={(product) => addProductToLines(product, Number(quantity))} />
          </fieldset>
          <OrderDetailFloatingButton lines={lines} />
          {lines.length > 0 && (
            <EditableOrderSummary
              lines={lines}
              products={products}
              setLines={setLines}
              priceFor={(line, catalog) => lineUnitPrice(catalog.find((item) => String(item.codigo) === String(line.codigo)), line.precio)}
              title="Detalle editable del pedido"
              extra={deliverySurcharge > 0 ? <div className="manual-order-total"><span>{deliveryType === "Domicilio" ? "Domicilio" : "Entrega personalizada"}</span><b>{formatCurrency(deliverySurcharge)}</b></div> : null}
              totalExtra={deliverySurcharge}
            />
          )}
          <label>
            Fecha de entrega
            <input
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </label>
          {saleMode === "PROGRAMADO" && (
            <label className="same-day-delivery-option">
              <input
                type="checkbox"
                checked={sameDayDelivery}
                onChange={(event) => {
                  const enabled = event.target.checked;
                  setSameDayDelivery(enabled);
                  if (enabled) setDate(todayRouteKey());
                }}
              />
              Entregar hoy y asignar directamente a David Romero
            </label>
          )}
          <label>
            Tipo de entrega
            <select
              value={deliveryType}
              onChange={(event) => setDeliveryType(event.target.value)}
            >
              <option>Ruta normal</option>
              <option>Domicilio</option>
              <option>Personalizada</option>
            </select>
          </label>
          {deliveryType === "Domicilio" && <p className="field-help">Domicilio: $1.000 por pedido.</p>}
          {deliveryType === "Personalizada" && (
            <label>
              Hora solicitada
              <input
                type="time"
                value={hour}
                onChange={(event) => setHour(event.target.value)}
              />
            </label>
          )}
          <label>
            Observaciones
            <textarea
              rows="3"
              value={observation}
              onChange={(event) => setObservation(event.target.value)}
            />
          </label>
          <button className="primary-action" type="submit" disabled={busy}>
            {busy ? "Creando…" : "Crear pedido y notificar"}
          </button>
        </form>
        {status && (
          <p
            className={`action-status ${status.includes("No fue") || status.includes("HTTP") ? "error" : "success"}`}
            role="status"
            aria-live="polite"
          >
            {status}
          </p>
        )}
      </aside>
    </div>
  );
}

function RouteOrderModal({ sourceOrder, onClose, onCreated }) {
  const [products, setProducts] = useState([]);
  const [code, setCode] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [lines, setLines] = useState([]);
  const [deliveryDate, setDeliveryDate] = useState(() => todayRouteKey());
  const [deliveryType, setDeliveryType] = useState("Ruta normal");
  const [observation, setObservation] = useState("");
  const [status, setStatus] = useState("Cargando catálogo…");
  const [busy, setBusy] = useState(false);
  const creationOperationId = useRef(null);

  useEffect(() => {
    let active = true;
    fetch("/api/dashboard/pedidos/operacion", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ operacion: "catalogo_desde_ruta", datos: {} }),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok)
          throw new Error(payload.mensaje || "No fue posible cargar el catálogo.");
        return payload;
      })
      .then((payload) => {
        if (!active) return;
        setProducts(payload.data?.productos || []);
        setStatus("");
      })
      .catch((error) => {
        if (active) setStatus(error.message || "No fue posible cargar el catálogo.");
      });
    return () => { active = false; };
  }, []);

  const total = lines.reduce((sum, line) => sum + Number(line.precio || 0) * Number(line.cantidad || 0), 0);
  const deliveryIsToday = deliveryDate === todayRouteKey();

  function addLine() {
    const product = products.find((item) => String(item.codigo || "") === String(code));
    const amount = Number(quantity);
    addProductToLines(product, amount);
  }

  function addProductToLines(product, amount = 1) {
    if (!product || !isOrderProductSelectable(product) || !Number.isFinite(Number(amount)) || Number(amount) <= 0) {
      setCode("");
      setStatus("Ese producto ya no está disponible para nuevos pedidos.");
      return;
    }
    const productCode = product.codigo || product.Codigo;
    setLines((current) => {
      const existing = current.find((item) => item.codigo === productCode);
      return existing
        ? current.map((item) => item.codigo === productCode ? { ...item, cantidad: item.cantidad + Number(amount) } : item)
        : [{
            codigo: productCode,
            cantidad: Number(amount),
            nombre: orderProductLabel(product),
            servicio_entrega: product.servicio_entrega || product.ServicioEntrega || "RUTA_AM",
            precio: Number(product.precio || 0),
          }, ...current];
    });
    setCode("");
    setQuantity("1");
  }

  async function create(event) {
    event.preventDefault();
    if (!lines.length) {
      setStatus("Agrega al menos un producto al pedido.");
      notificarAdmin("Agrega al menos un producto al pedido.", "warning");
      return;
    }
    setBusy(true);
    setStatus("Validando disponibilidad y programando el pedido…");
    try {
      const response = await fetch("/api/dashboard/pedidos/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: "crear_desde_ruta",
          datos: {
            operacion_id:
              creationOperationId.current ||
              (creationOperationId.current = createOperationId("RUTA-NUEVO")),
            pedido_origen_id: sourceOrder.pedido,
            productos: lines.map(({ codigo, cantidad }) => ({ codigo, cantidad })),
            fecha_entrega: deliveryDate,
            tipo_entrega: deliveryType,
            entrega_mismo_dia: deliveryIsToday,
            observaciones: observation,
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || `No fue posible crear el pedido (HTTP ${response.status}).`);
      const pedido = payload.data?.pedido || payload.data?.pedido_id || "nuevo pedido";
      const mensaje = deliveryIsToday
        ? `Pedido ${pedido} creado para hoy y agregado a la ruta de David Romero.`
        : `Pedido ${pedido} programado para ${routeDateLabel(deliveryDate)} en la ruta de David Romero.`;
      setStatus(mensaje);
      notificarAdmin(mensaje, "success");
      await onCreated?.();
      onClose?.();
    } catch (error) {
      const mensaje = error.message || "No fue posible crear el pedido.";
      setStatus(mensaje);
      notificarAdmin(mensaje, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="drawer-backdrop" onClick={() => !busy && onClose()}>
      <aside className="drawer route-order-drawer" onClick={(event) => event.stopPropagation()}>
        <button className="drawer-close" onClick={onClose} disabled={busy} aria-label="Cerrar"><X size={18} /></button>
        <p className="eyebrow">NUEVO PEDIDO EN RUTA</p>
        <h2>Pedido para {sourceOrder.cliente || "este cliente"}</h2>
        <p className="drawer-subtitle">Usaremos la dirección y destino de esta parada. Elige si se entrega hoy o en una ruta de otro día.</p>
        <div className="route-order-origin"><span>Parada de origen</span><b>{sourceOrder.pedido}</b><small>{sourceOrder.direccion || "Dirección registrada"}</small></div>
        <form className="admin-order-form" noValidate onSubmit={create}>
          <fieldset className="manual-product-picker">
            <legend>Productos para programar</legend>
            <div className="order-product-card-toolbar"><label>Cantidad para agregar<input type="number" min="0.001" step="any" inputMode="decimal" value={quantity} onChange={(event) => setQuantity(event.target.value)} disabled={busy} /></label><span>Toca una tarjeta para agregarla</span></div>
            <OrderProductCards products={products} selectedCode={code} lines={lines} onSelect={(product) => setCode(product.codigo)} onAdd={(product) => addProductToLines(product, Number(quantity))} disabled={busy} />
          </fieldset>
          <OrderDetailFloatingButton lines={lines} />
          {lines.length > 0 && (
            <EditableOrderSummary lines={lines} products={products} setLines={setLines} priceFor={(line) => line.precio} title="Pedido nuevo · editable" extra={deliveryType === "Domicilio" ? <div className="manual-order-total"><span>Domicilio</span><b>{formatCurrency(1000)}</b></div> : null} totalExtra={deliveryType === "Domicilio" ? 1000 : 0} />
          )}
          <label>
            Fecha de entrega
            <input type="date" min={todayRouteKey()} value={deliveryDate} onChange={(event) => setDeliveryDate(event.target.value)} disabled={busy} required />
          </label>
          <label>
            Tipo de entrega
            <select value={deliveryType} onChange={(event) => setDeliveryType(event.target.value)} disabled={busy}>
              <option value="Ruta normal">Ruta programada</option>
              <option value="Domicilio">Domicilio · $1.000</option>
            </select>
          </label>
          <label>Observación para preparación<textarea rows="3" value={observation} onChange={(event) => setObservation(event.target.value)} disabled={busy} placeholder="Ej.: confirmar presentación con el cliente" /></label>
          {lines.length > 0 && lines.some((line) => line.servicio_entrega === "RUTA_SOPAS_MEDIODIA") && lines.some((line) => line.servicio_entrega !== "RUTA_SOPAS_MEDIODIA") && <p className="delivery-service-notice">Este carrito se organizará en dos pedidos: entrega de mañana y sopas al mediodía.</p>}
          <p className="route-order-help">La disponibilidad se valida para la fecha elegida. El pedido queda pendiente de pago y no modifica la entrega actual.</p>
          <button className="primary-action" type="submit" disabled={busy || !lines.length || !deliveryDate}>{busy ? "Programando…" : deliveryIsToday ? "Crear para hoy" : "Programar pedido"}</button>
        </form>
        {status && <p className={`action-status ${status.startsWith("No fue") || status.startsWith("Agrega") ? "error" : "success"}`} role="status" aria-live="polite">{status}</p>}
      </aside>
    </div>
  );
}

function NewRouteDeliveryModal({ onClose, onCreated }) {
  const [products, setProducts] = useState([]);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [municipality, setMunicipality] = useState("Supía, Caldas");
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(() => todayRouteKey());
  const [deliveryType, setDeliveryType] = useState("Ruta normal");
  const [observation, setObservation] = useState("");
  const [code, setCode] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [lines, setLines] = useState([]);
  const [location, setLocation] = useState(null);
  const [status, setStatus] = useState("Cargando catálogo…");
  const [busy, setBusy] = useState(false);
  // Evita que un segundo toque, reintento de red o espera de respuesta cree
  // otra orden: la misma operación se reutiliza hasta cerrar el formulario.
  const creationOperationId = useRef(null);

  useEffect(() => {
    let active = true;
    fetch("/api/dashboard/pedidos/operacion", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ operacion: "catalogo_domicilio_desde_ruta", datos: {} }),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok)
          throw new Error(payload.mensaje || "No fue posible cargar el catálogo.");
        return payload;
      })
      .then((payload) => {
        if (!active) return;
        setProducts(payload.data?.productos || []);
        setStatus("");
      })
      .catch((error) => {
        if (active) setStatus(error.message || "No fue posible cargar el catálogo.");
      });
    return () => { active = false; };
  }, []);

  const total = lines.reduce((sum, line) => sum + Number(line.precio || 0) * Number(line.cantidad || 0), 0);
  const deliveryIsToday = deliveryDate === todayRouteKey();

  function chooseCustomer(client) {
    setPhone(client.telefono || "");
    setName(client.nombre || "");
    setAddress(client.direccion || "");
    setCustomerQuery(client.nombre || client.telefono || "");
    setCustomerResults([]);
    setCustomerFound(true);
    setStatus("Cliente existente identificado. Verifica sus datos y continúa con el pedido.");
  }

  async function searchCustomers(event) {
    event?.preventDefault();
    const q = customerQuery.trim();
    if (q.length < 2) {
      setCustomerResults([]);
      return;
    }
    setCustomerSearching(true);
    try {
      const response = await fetch("/api/dashboard/pedidos/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ operacion: "buscar_clientes", datos: { q } }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || "No fue posible buscar clientes.");
      setCustomerResults(payload.data?.clientes || []);
    } catch (error) {
      setCustomerResults([]);
      setStatus(error.message || "No fue posible buscar clientes.");
    } finally {
      setCustomerSearching(false);
    }
  }

  async function resolveCustomerByPhone() {
    const requestedPhone = phone.trim();
    if (requestedPhone.replace(/\D/g, "").length < 7) return;
    setCustomerSearching(true);
    try {
      const response = await fetch("/api/dashboard/pedidos/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ operacion: "buscar_cliente", datos: { telefono: requestedPhone } }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || "No fue posible consultar el cliente.");
      const found = payload.data?.cliente;
      if (found) chooseCustomer({ ...found, telefono: found.telefono || payload.data?.telefono || requestedPhone });
      else {
        setCustomerFound(false);
        setStatus("Cliente nuevo. Completa sus datos para crear el domicilio.");
      }
    } catch (error) {
      setStatus(error.message || "No fue posible consultar el cliente.");
    } finally {
      setCustomerSearching(false);
    }
  }

  function addLine() {
    const product = products.find((item) => String(item.codigo || "") === String(code));
    const amount = Number(quantity);
    addProductToLines(product, amount);
  }

  function addProductToLines(product, amount = 1) {
    if (!product || !isOrderProductSelectable(product) || !Number.isFinite(Number(amount)) || Number(amount) <= 0) {
      setCode("");
      setStatus("Ese producto ya no está disponible para nuevos pedidos.");
      return;
    }
    const productCode = product.codigo || product.Codigo;
    setLines((current) => {
      const existing = current.find((item) => item.codigo === productCode);
      return existing
        ? current.map((item) => item.codigo === productCode ? { ...item, cantidad: item.cantidad + Number(amount) } : item)
        : [{
            codigo: productCode,
            cantidad: Number(amount),
            nombre: orderProductLabel(product),
            servicio_entrega: product.servicio_entrega || product.ServicioEntrega || "RUTA_AM",
            precio: Number(product.precio || 0),
          }, ...current];
    });
    setCode("");
    setQuantity("1");
  }

  function captureLocation() {
    if (!navigator.geolocation) {
      setStatus("Este dispositivo no permite compartir ubicación.");
      return;
    }
    setStatus("Obteniendo ubicación de entrega…");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({ latitud: position.coords.latitude, longitud: position.coords.longitude });
        setStatus("Ubicación de entrega agregada.");
      },
      () => setStatus("No fue posible obtener la ubicación. Puedes continuar con la dirección escrita."),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
    );
  }

  async function create(event) {
    event.preventDefault();
    if (!phone.trim() || !name.trim() || !address.trim() || !lines.length) {
      setStatus("Completa WhatsApp, nombre, dirección y al menos un producto.");
      notificarAdmin("Completa WhatsApp, nombre, dirección y al menos un producto.", "warning");
      return;
    }
    setBusy(true);
    setStatus("Validando cobertura, disponibilidad y creando el domicilio…");
    try {
      const response = await fetch("/api/dashboard/pedidos/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: "crear_domicilio_desde_ruta",
          datos: {
            operacion_id:
              creationOperationId.current ||
              (creationOperationId.current = createOperationId("RUTA-DOMICILIO")),
            telefono: phone,
            nombre: name,
            direccion: address,
            municipio_entrega: municipality,
            productos: lines.map(({ codigo, cantidad }) => ({ codigo, cantidad })),
            fecha_entrega: deliveryDate,
            tipo_entrega: deliveryType,
            entrega_mismo_dia: deliveryIsToday,
            observaciones: observation,
            ubicacion_entrega: location || undefined,
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || `No fue posible crear el domicilio (HTTP ${response.status}).`);
      const pedido = payload.data?.pedido || payload.data?.pedido_id || "nuevo pedido";
      const mensaje = deliveryIsToday
        ? `Domicilio ${pedido} creado para hoy y agregado a la ruta de David Romero.`
        : `Domicilio ${pedido} programado para ${routeDateLabel(deliveryDate)} en la ruta de David Romero.`;
      setStatus(mensaje);
      notificarAdmin(mensaje, "success");
      await onCreated?.();
      onClose?.();
    } catch (error) {
      const mensaje = error.message || "No fue posible crear el domicilio.";
      setStatus(mensaje);
      notificarAdmin(mensaje, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="drawer-backdrop" onClick={() => !busy && onClose()}>
      <aside className="drawer route-order-drawer" onClick={(event) => event.stopPropagation()}>
        <button className="drawer-close" onClick={onClose} disabled={busy} aria-label="Cerrar"><X size={18} /></button>
        <p className="eyebrow">NUEVO DOMICILIO</p>
        <h2>Crear pedido desde cero</h2>
        <p className="drawer-subtitle">Este pedido es independiente: no cambia el cliente, la dirección ni la asignación de una parada existente.</p>
        <form className="admin-order-form" noValidate onSubmit={create}>
          <fieldset className="manual-product-picker route-new-customer">
            <legend>Datos del cliente</legend>
            <div className="manual-customer-finder">
              <div className="manual-customer-search-row">
                <label>
                  Buscar cliente
                  <input type="search" value={customerQuery} onChange={(event) => setCustomerQuery(event.target.value)} disabled={busy} placeholder="Celular, nombre o dirección" aria-label="Buscar cliente por celular, nombre o dirección" />
                </label>
                <button type="button" className="secondary-action" onClick={searchCustomers} disabled={busy || customerSearching}>{customerSearching ? "Buscando…" : "Buscar"}</button>
              </div>
              {customerResults.length > 0 && (
                <div className="manual-customer-results">
                  {customerResults.map((client) => (
                    <button type="button" className="manual-customer-result" key={client.cliente_id || client.telefono || client.nombre} onClick={() => chooseCustomer(client)}>
                      <b>{client.nombre || "Cliente sin nombre"}</b>
                      <small>{[client.telefono, client.direccion].filter(Boolean).join(" · ") || "Sin teléfono o dirección"}</small>
                    </button>
                  ))}
                </div>
              )}
              {customerQuery.trim().length >= 2 && !customerSearching && customerResults.length === 0 && <small>No encontramos clientes con esa búsqueda.</small>}
            </div>
            <label>WhatsApp<input type="tel" inputMode="tel" value={phone} onChange={(event) => { setPhone(event.target.value); setCustomerFound(false); }} onBlur={resolveCustomerByPhone} disabled={busy} placeholder="300 000 0000" /></label>
            {customerFound && <p className="customer-found">Cliente existente identificado. Sus datos fueron precargados.</p>}
            <label>Nombre<input value={name} onChange={(event) => setName(event.target.value)} disabled={busy} placeholder="Nombre del cliente" /></label>
            <label>Dirección<input value={address} onChange={(event) => setAddress(event.target.value)} disabled={busy} placeholder="Dirección y referencia" /></label>
            <label>Municipio<select value={municipality} onChange={(event) => setMunicipality(event.target.value)} disabled={busy}><option>Supía, Caldas</option></select></label>
            <button type="button" className="secondary-action" onClick={captureLocation} disabled={busy}>{location ? "Ubicación agregada" : "Agregar ubicación"}</button>
          </fieldset>
          <fieldset className="manual-product-picker">
            <legend>Productos</legend>
            <div className="order-product-card-toolbar"><label>Cantidad para agregar<input type="number" min="0.001" step="any" inputMode="decimal" value={quantity} onChange={(event) => setQuantity(event.target.value)} disabled={busy} /></label><span>Toca una tarjeta para agregarla</span></div>
            <OrderProductCards products={products} selectedCode={code} lines={lines} onSelect={(product) => setCode(product.codigo)} onAdd={(product) => addProductToLines(product, Number(quantity))} disabled={busy} />
          </fieldset>
          <OrderDetailFloatingButton lines={lines} />
          {lines.length > 0 && <EditableOrderSummary lines={lines} products={products} setLines={setLines} priceFor={(line) => line.precio} title="Resumen editable" extra={deliveryType === "Domicilio" ? <div className="manual-order-total"><span>Domicilio</span><b>{formatCurrency(1000)}</b></div> : null} totalExtra={deliveryType === "Domicilio" ? 1000 : 0} />}
          <label>Fecha de entrega<input type="date" min={todayRouteKey()} value={deliveryDate} onChange={(event) => setDeliveryDate(event.target.value)} disabled={busy} required /></label>
          <label>
            Tipo de entrega
            <select value={deliveryType} onChange={(event) => setDeliveryType(event.target.value)} disabled={busy}>
              <option value="Ruta normal">Ruta programada</option>
              <option value="Domicilio">Domicilio · $1.000</option>
            </select>
          </label>
          {lines.length > 0 && lines.some((line) => line.servicio_entrega === "RUTA_SOPAS_MEDIODIA") && lines.some((line) => line.servicio_entrega !== "RUTA_SOPAS_MEDIODIA") && <p className="delivery-service-notice">Este carrito se organizará en dos pedidos: entrega de mañana y sopas al mediodía.</p>}
          <label>Observaciones<textarea rows="3" value={observation} onChange={(event) => setObservation(event.target.value)} disabled={busy} placeholder="Ej.: llamar antes de entregar" /></label>
          <p className="route-order-help">El sistema valida cobertura y disponibilidad para la fecha elegida. Si se confirma, queda pendiente de pago y asignado a David Romero.</p>
          <button className="primary-action" type="submit" disabled={busy || !lines.length || !deliveryDate}>{busy ? "Creando…" : deliveryIsToday ? "Crear para hoy" : "Programar pedido"}</button>
        </form>
        {status && <p className={`action-status ${status.startsWith("No fue") || status.startsWith("Completa") ? "error" : "success"}`} role="status" aria-live="polite">{status}</p>}
      </aside>
    </div>
  );
}

function routeDateKey(value) {
  const text = String(value || "").trim();
  const iso = text.match(/\d{4}-\d{2}-\d{2}/)?.[0];
  if (iso) return iso;
  const local = text.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (local)
    return `${local[3]}-${String(local[2]).padStart(2, "0")}-${String(local[1]).padStart(2, "0")}`;
  return "";
}

function routeDateLabel(value) {
  const key = routeDateKey(value);
  if (!key) return "Fecha por confirmar";
  const date = new Date(`${key}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? key
    : date.toLocaleDateString("es-CO", {
        weekday: "long",
        day: "numeric",
        month: "long",
      });
}

function operationalDateKey(offset = 0) {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Bogota",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(new Date()).reduce((result, part) => {
    if (part.type !== "literal") result[part.type] = part.value;
    return result;
  }, {});
  const date = new Date(
    Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)),
  );
  date.setUTCDate(date.getUTCDate() + Number(offset || 0));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}

function todayRouteKey(offset = 0) {
  return operationalDateKey(offset);
}

function routeServiceDescriptor(row) {
  // El servicio es la fuente de verdad comercial. El turno solo conserva
  // compatibilidad con rutas creadas antes de que la API expusiera el campo.
  const service = String(
    row?.servicio_entrega || row?.ServicioEntrega || "",
  ).trim().toUpperCase();
  const shift = String(row?.turno || "").trim().toUpperCase();
  if (service === "RUTA_SOPAS_MEDIODIA" || shift === "SOPAS_MEDIODIA") {
    return {
      service: "RUTA_SOPAS_MEDIODIA",
      shift: "SOPAS_MEDIODIA",
      title: "Pedidos Sopas · Mediodía",
      routeLabel: "Ruta Sopas · Mediodía",
    };
  }
  if (shift === "PM") {
    return {
      service: "RUTA_PM",
      shift: "PM",
      title: "Pedidos PM",
      routeLabel: "Ruta PM",
    };
  }
  return {
    service: "RUTA_AM",
    shift: "AM",
    title: "Pedidos AM",
    routeLabel: "Ruta AM",
  };
}

function analyticsPeriodStart(endDate, days = 30) {
  const date = new Date(`${String(endDate || "").slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return todayRouteKey(-(days - 1));
  date.setDate(date.getDate() - Math.max(0, days - 1));
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function routeCoordinate(row) {
  const latitud = Number(row?.ubicacion?.latitud);
  const longitud = Number(row?.ubicacion?.longitud);
  if (!Number.isFinite(latitud) || !Number.isFinite(longitud)) return null;
  if (latitud < -90 || latitud > 90 || longitud < -180 || longitud > 180) return null;
  return { latitud, longitud };
}

function routeOriginCoordinate(origin) {
  const latitud = Number(origin?.latitud);
  const longitud = Number(origin?.longitud);
  if (!Number.isFinite(latitud) || !Number.isFinite(longitud)) return null;
  if (latitud < -90 || latitud > 90 || longitud < -180 || longitud > 180) return null;
  return { latitud, longitud };
}

function routeDistanceKm(origin, destination) {
  const earthRadiusKm = 6371;
  const radians = (value) => (value * Math.PI) / 180;
  const deltaLat = radians(destination.latitud - origin.latitud);
  const deltaLong = radians(destination.longitud - origin.longitud);
  const originLat = radians(origin.latitud);
  const destinationLat = radians(destination.latitud);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(originLat) * Math.cos(destinationLat) * Math.sin(deltaLong / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(Math.max(0, 1 - a)));
}

function routeStopIsClosed(row) {
  return /(entregado|cancelado|no entregado)/i.test(String(row?.estado || ""));
}

function routeStopIsPersonalized(row) {
  return String(row?.tipo_entrega || "").toLowerCase().includes("medida");
}

function previewRouteGroup(rows, origin = null) {
  // El grupo ya llega ordenado como se ve operativamente en la ruta: se
  // conserva ese orden como desempate y como punto inicial de la sugerencia.
  const currentOrder = [...rows];
  const locked = currentOrder.filter(routeStopIsClosed);
  const fixed = currentOrder.filter(
    (row) => !routeStopIsClosed(row) && routeStopIsPersonalized(row),
  );
  const flexible = currentOrder.filter(
    (row) => !routeStopIsClosed(row) && !routeStopIsPersonalized(row),
  );
  const located = flexible.filter((row) => routeCoordinate(row));
  const missing = flexible.filter((row) => !routeCoordinate(row));
  const start = routeOriginCoordinate(origin);
  if (located.length < 2) {
    return {
      ordered: located,
      missing,
      fixed,
      locked,
      suggested: [...fixed, ...located, ...missing],
      distanceKm: start && located[0] ? routeDistanceKm(start, routeCoordinate(located[0])) : 0,
      estimatedMinutes: 0,
      anchor: located[0] || null,
    };
  }

  // Si el repartidor comparte su ubicación, esta determina la primera parada.
  // Si no existe una ubicación válida, se mantiene la primera parada actual
  // como ancla para que una vista previa nunca invente una posición.
  const ordered = [];
  const remaining = [...located];
  let distanceKm = 0;
  let anchor = start;
  if (!anchor) {
    const first = remaining.shift();
    ordered.push(first);
    anchor = routeCoordinate(first);
  }
  while (remaining.length) {
    const origin = anchor;
    let nextIndex = 0;
    let nextDistance = routeDistanceKm(origin, routeCoordinate(remaining[0]));
    for (let index = 1; index < remaining.length; index += 1) {
      const candidateDistance = routeDistanceKm(origin, routeCoordinate(remaining[index]));
      if (
        candidateDistance < nextDistance ||
        (candidateDistance === nextDistance && Number(remaining[index].orden ?? 999999) < Number(remaining[nextIndex].orden ?? 999999))
      ) {
        nextIndex = index;
        nextDistance = candidateDistance;
      }
    }
    distanceKm += nextDistance;
    const next = remaining.splice(nextIndex, 1)[0];
    ordered.push(next);
    anchor = routeCoordinate(next);
  }
  return {
    ordered,
    missing,
    fixed,
    locked,
    suggested: [...fixed, ...ordered, ...missing],
    distanceKm,
    // 25 km/h is only a transparent planning assumption; this is not road
    // distance, traffic or turn-by-turn navigation.
    estimatedMinutes: Math.round((distanceKm / 25) * 60 + Math.max(0, ordered.length - 1) * 5),
    anchor: ordered[0],
  };
}

function orderRouteGroupFromBaseOrigin(rows, origin) {
  const baseOrigin = routeOriginCoordinate(origin);
  const currentOrder = [...rows];
  if (!baseOrigin) return currentOrder;

  // Las entregas cerradas son historial y las personalizadas preservan la
  // prioridad comercial. Solo se ordenan las paradas programadas flexibles.
  const closed = currentOrder.filter(routeStopIsClosed);
  const fixed = currentOrder.filter(
    (row) => !routeStopIsClosed(row) && routeStopIsPersonalized(row),
  );
  const flexible = currentOrder.filter(
    (row) => !routeStopIsClosed(row) && !routeStopIsPersonalized(row),
  );
  const located = flexible.filter((row) => routeCoordinate(row));
  const missing = flexible.filter((row) => !routeCoordinate(row));
  if (located.length < 2) return [...fixed, ...located, ...missing, ...closed];

  const ordered = [];
  const remaining = [...located];
  let anchor = baseOrigin;
  while (remaining.length) {
    let nextIndex = 0;
    let nextDistance = routeDistanceKm(anchor, routeCoordinate(remaining[0]));
    for (let index = 1; index < remaining.length; index += 1) {
      const candidateDistance = routeDistanceKm(anchor, routeCoordinate(remaining[index]));
      if (
        candidateDistance < nextDistance ||
        (candidateDistance === nextDistance &&
          Number(remaining[index].orden ?? 999999) < Number(remaining[nextIndex].orden ?? 999999))
      ) {
        nextIndex = index;
        nextDistance = candidateDistance;
      }
    }
    const next = remaining.splice(nextIndex, 1)[0];
    ordered.push(next);
    anchor = routeCoordinate(next);
  }
  return [...fixed, ...ordered, ...missing, ...closed];
}

// Google Maps URLs admite como máximo tres paradas intermedias en navegadores
// móviles. Cada bloque contiene hasta cuatro entregas nuevas: la aplicación
// conserva el final del bloque anterior como origen del siguiente.
const GOOGLE_MAPS_MOBILE_STOPS_PER_BLOCK = 4;

function mapsCoordinateValue(row) {
  const coordinate = routeCoordinate(row);
  return coordinate ? `${coordinate.latitud},${coordinate.longitud}` : "";
}

function buildGoogleMapsRoutePlan(rows) {
  const active = (rows || []).filter((row) => !routeStopIsClosed(row));
  const located = active.filter((row) => routeCoordinate(row));
  const missing = active.filter((row) => !routeCoordinate(row));
  const blocks = [];
  for (let cursor = 0; cursor < located.length; cursor += GOOGLE_MAPS_MOBILE_STOPS_PER_BLOCK) {
    const stops = located.slice(cursor, cursor + GOOGLE_MAPS_MOBILE_STOPS_PER_BLOCK);
    const destination = stops[stops.length - 1];
    const params = new URLSearchParams({
      api: "1",
      destination: mapsCoordinateValue(destination),
      travelmode: "driving",
    });
    if (cursor > 0) params.set("origin", mapsCoordinateValue(located[cursor - 1]));
    const waypoints = stops.slice(0, -1).map(mapsCoordinateValue).filter(Boolean);
    if (waypoints.length) params.set("waypoints", waypoints.join("|"));
    blocks.push({
      index: blocks.length + 1,
      stops,
      url: `https://www.google.com/maps/dir/?${params.toString()}`,
    });
  }
  return { active, located, missing, blocks };
}

function RouteDailyCashCard({ summary }) {
  const caja = summary || {};
  const hasError = Boolean(caja.error);
  const movimientos = Number(caja.movimientos || 0);
  const efectivoDomicilios = Number(caja.efectivo_domicilios || 0);
  const efectivoCartera = Number(caja.efectivo_cartera || 0);
  const transferenciasDomicilios = Number(caja.transferencias_domicilios || 0);
  const transferenciasCartera = Number(caja.transferencias_cartera || 0);
  const pendienteDomicilios = Number(caja.pendiente_domicilios ?? caja.pendiente ?? 0);
  const gastosDomicilios = Number(caja.gastos_domicilios_efectivo || 0);
  const valorAConsignar = Number(caja.valor_a_consignar || 0);
  const saldoGastosPorCompensar = Number(caja.saldo_gastos_por_compensar || 0);
  const cardRef = useRef(null);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const updateCompactState = () => {
      const top = cardRef.current?.getBoundingClientRect().top;
      setCompact((current) => {
        const next = Number.isFinite(top) && top <= 1;
        return current === next ? current : next;
      });
    };
    updateCompactState();
    document.addEventListener("scroll", updateCompactState, { passive: true, capture: true });
    window.addEventListener("resize", updateCompactState);
    return () => {
      document.removeEventListener("scroll", updateCompactState, true);
      window.removeEventListener("resize", updateCompactState);
    };
  }, []);

  return (
    <div className={`route-daily-cash-anchor${compact ? " is-compact" : ""}`}>
      <section
        ref={cardRef}
        className={`route-daily-cash${compact ? " is-compact" : ""}`}
        aria-label="Resumen diario de dinero"
      >
      <header className="route-daily-cash-header">
        <div>
          <p className="eyebrow">DINERO DEL DÍA</p>
          <h3>Mi caja de hoy</h3>
          <span>Ingresos del día, gastos de domicilios y efectivo para consignar.</span>
        </div>
        <div className="route-daily-cash-remittance-total"><span>Valor a consignar</span><b>{formatCurrency(valorAConsignar)}</b></div>
      </header>
      <div className="route-daily-cash-grid">
        <div className="route-daily-cash-metric cash">
          <span>Efectivo</span>
          <b>{formatCurrency(caja.efectivo)}</b>
          <small>Domicilios {formatCurrency(efectivoDomicilios)} · Cartera {formatCurrency(efectivoCartera)}</small>
        </div>
        <div className="route-daily-cash-metric transfer">
          <span>Transferencias</span>
          <b>{formatCurrency(caja.transferencias)}</b>
          <small>Domicilios {formatCurrency(transferenciasDomicilios)} · Cartera {formatCurrency(transferenciasCartera)}</small>
        </div>
        <div className="route-daily-cash-metric pending">
          <span>Pendiente</span>
          <b>{formatCurrency(caja.pendiente)}</b>
          <small>Domicilios {formatCurrency(pendienteDomicilios)} · Por cobrar</small>
        </div>
      </div>
      <div className={`route-daily-cash-remittance${saldoGastosPorCompensar > 0 ? " has-shortfall" : ""}`}>
        <div><span>Efectivo recibido</span><b>{formatCurrency(caja.efectivo)}</b></div>
        <span aria-hidden="true">−</span>
        <div><span>Gastos de domicilios</span><b>{formatCurrency(gastosDomicilios)}</b></div>
        <span aria-hidden="true">=</span>
        <div className="total"><span>Valor a consignar</span><b>{formatCurrency(valorAConsignar)}</b></div>
      </div>
      {saldoGastosPorCompensar > 0 && <p className="route-daily-cash-shortfall">Los gastos superan el efectivo recibido por {formatCurrency(saldoGastosPorCompensar)}. Administración debe revisarlo.</p>}
      <p className={`route-daily-cash-note${hasError ? " error" : ""}`}>
        {hasError
          ? "No pudimos actualizar el resumen de dinero. Tu ruta sigue disponible."
          : movimientos
            ? `${movimientos} movimiento${movimientos === 1 ? "" : "s"} registrado${movimientos === 1 ? "" : "s"} hoy.`
            : "Aún no hay movimientos registrados en tu caja de hoy."}
      </p>
      </section>
    </div>
  );
}

function SwipeReveal({ revealed = "", onReveal, onClose, leftAction = null, rightAction = null, className = "", children }) {
  const gesture = useRef(null);
  function iniciar(event) {
    if (event.pointerType !== "touch") return;
    const target = event.target;
    if (target instanceof Element && target.closest("[data-swipe-reveal-action]")) return;
    gesture.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      horizontal: false,
    };
  }
  function mover(event) {
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - current.startX;
    const deltaY = event.clientY - current.startY;
    if (!current.horizontal && Math.abs(deltaY) > 12 && Math.abs(deltaY) > Math.abs(deltaX)) {
      gesture.current = null;
      return;
    }
    if (Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY)) current.horizontal = true;
  }
  function terminar(event) {
    const current = gesture.current;
    gesture.current = null;
    if (!current || current.pointerId !== event.pointerId || !current.horizontal) return;
    event.preventDefault();
    const deltaX = event.clientX - current.startX;
    if (deltaX <= -64 && rightAction) onReveal?.("left");
    else if (deltaX >= 64 && leftAction) onReveal?.("right");
    else onClose?.();
  }
  return <div
    className={`swipe-reveal ${className}${revealed ? ` is-revealed-${revealed}` : ""}`}
    onPointerDown={iniciar}
    onPointerMove={mover}
    onPointerUp={terminar}
    onPointerCancel={() => { gesture.current = null; }}
  >
    {leftAction && <button type="button" className="swipe-reveal-action swipe-reveal-action-left" data-swipe-reveal-action onClick={() => { onClose?.(); leftAction.onClick(); }}>
      {leftAction.icon}<span>{leftAction.label}</span>
    </button>}
    {rightAction && <button type="button" className="swipe-reveal-action swipe-reveal-action-right" data-swipe-reveal-action onClick={() => { onClose?.(); rightAction.onClick(); }}>
      {rightAction.icon}<span>{rightAction.label}</span>
    </button>}
    <div className="swipe-reveal-content">{children}</div>
  </div>;
}

function RouteGuide({ rows, onRefresh, deliveryOnly = false, defaultRouteOrigin = null, routeQuery = {}, onRouteQueryChange }) {
  // El repartidor inicia en la jornada actual y las próximas; nunca en todo
  // su historial operativo. El servidor aplica el mismo alcance por seguridad.
  const [filter, setFilter] = useState("PROXIMAS");
  const [statusFilter, setStatusFilter] = useState("TODOS");
  const serverControlledFilters = !deliveryOnly && typeof onRouteQueryChange === "function";
  const activeFilter = serverControlledFilters ? (routeQuery.alcance || "PROXIMAS") : filter;
  const activeStatusFilter = serverControlledFilters ? (routeQuery.estado || "TODOS") : statusFilter;
  const [deliveryFilter, setDeliveryFilter] = useState("TODAS");
  const [routeProductFilter, setRouteProductFilter] = useState("");
  const [routeProductMode, setRouteProductMode] = useState("SIN");
  const [routeFiltersOpen, setRouteFiltersOpen] = useState(false);
  const [routeDarkMode, setRouteDarkMode] = useState(() => {
    try { return window.localStorage.getItem("titina-route-dark-mode") === "true"; } catch (_) { return false; }
  });
  const [selected, setSelected] = useState(null);
  const [partialDeliveryOpen, setPartialDeliveryOpen] = useState(false);
  const [deliverySelection, setDeliverySelection] = useState({});
  const partialDeliveryRef = useRef(null);
  const [locationBusy, setLocationBusy] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [locationMessage, setLocationMessage] = useState("");
  const [routeOrderSource, setRouteOrderSource] = useState(null);
  const [routeOptimizationPreview, setRouteOptimizationPreview] = useState(null);
  const [routeViewOrder, setRouteViewOrder] = useState("ACTUAL");
  const [routeOptimizationBusy, setRouteOptimizationBusy] = useState(false);
  const [routeOptimizationOrigin, setRouteOptimizationOrigin] = useState(null);
  const [routeProjections, setRouteProjections] = useState({});
  const [routeProjectionLocks, setRouteProjectionLocks] = useState({});
  const [routePlanningOpen, setRoutePlanningOpen] = useState({});
  const [routeProjectionBusy, setRouteProjectionBusy] = useState("");
  const [routeProjectionFeedback, setRouteProjectionFeedback] = useState({});
  const projectionRequestsInFlight = useRef(new Set());
  const storedProjectionRequestsInFlight = useRef(new Set());
  const [mapsRoutePlan, setMapsRoutePlan] = useState(null);
  const [routeEventBusy, setRouteEventBusy] = useState("");
  const [routeEventMessage, setRouteEventMessage] = useState("");
  const routeEventOperationIds = useRef({});
  const routeEventRequestsInFlight = useRef(new Set());
  const [newRouteDelivery, setNewRouteDelivery] = useState(false);
  const [paymentPrompt, setPaymentPrompt] = useState(null);
  const [quickCashPrompt, setQuickCashPrompt] = useState(null);
  const [swipedRouteAction, setSwipedRouteAction] = useState(null);
  const [busy, setBusy] = useState("");
  // React actualiza el estado después del evento. Este candado síncrono evita
  // que dos toques consecutivos creen dos POST antes de que el botón quede
  // visualmente deshabilitado.
  const deliveryRequestsInFlight = useRef(new Set());
  const deliveryOperationIds = useRef({});
  const [message, setMessage] = useState("");
  const [repartidores, setRepartidores] = useState([]);
  const [repartidorPorRuta, setRepartidorPorRuta] = useState({});
  const [ordenBorrador, setOrdenBorrador] = useState({});
  const today = todayRouteKey();
  const tomorrow = todayRouteKey(1);
  const weekEnd = todayRouteKey(7);
  useEffect(() => {
    setRouteOptimizationPreview(null);
    setRouteViewOrder("ACTUAL");
    setRouteOptimizationOrigin(null);
    setRouteFiltersOpen(false);
  }, [activeFilter, activeStatusFilter, deliveryFilter, routeProductFilter, routeProductMode]);
  useEffect(() => {
    try { window.localStorage.setItem("titina-route-dark-mode", String(routeDarkMode)); } catch (_) { /* preferencia local no bloquea Ruta */ }
  }, [routeDarkMode]);
  function cambiarAlcanceRuta(alcance) {
    if (serverControlledFilters) onRouteQueryChange({ alcance });
    else setFilter(alcance);
  }
  function cambiarEstadoRuta(estado) {
    if (serverControlledFilters) onRouteQueryChange({ estado });
    else setStatusFilter(estado);
  }
  function cerrarDetalleRuta() {
    setSelected(null);
    setPendingLocation(null);
    setLocationMessage("");
  }
  function puedeCobroRapido(row) {
    if (!deliveryOnly || Boolean(busy)) return false;
    const estado = String(row?.estado || "").toLowerCase();
    const total = Number(row?.valor_total || 0);
    const productos = Array.isArray(row?.productos) ? row.productos : [];
    const tieneEntregaParcial = productos.some((item) => {
      const solicitada = Number(item?.cantidad || 0);
      const entregada = Number(item?.cantidad_entregada || 0);
      return entregada > 0 && entregada < solicitada;
    });
    return Boolean(row?.pedido) && total > 0 && !tieneEntregaParcial && !/(entregado|cancelado|no entregado)/.test(estado);
  }
  function abrirCobroRapido(row) {
    if (!puedeCobroRapido(row)) return;
    setSwipedRouteAction(null);
    setQuickCashPrompt({
      pedido: row.pedido,
      grupo_entrega_id: row.grupo_entrega_id || "",
      cliente: row.cliente || "Cliente",
      total: Number(row.valor_total || 0),
      error: "",
      operacion_pago_id: createOperationId(`ENTREGA-${row.pedido}`),
    });
  }
  function abrirPagoPendienteRapido(row) {
    if (!puedeCobroRapido(row)) return;
    setSwipedRouteAction(null);
    setPaymentPrompt({
      pedido: row.pedido,
      grupo_entrega_id: row.grupo_entrega_id || "",
      total: Number(row.valor_total || 0),
      choice: "PENDIENTE",
      amount: "",
      error: "",
      operacion_pago_id: createOperationId(`ENTREGA-${row.pedido}`),
    });
  }

  function abrirPlanGoogleMaps(group, rowsForDisplay) {
    const plan = buildGoogleMapsRoutePlan(rowsForDisplay);
    if (!plan.blocks.length) {
      setMessage("Las paradas visibles no tienen coordenadas disponibles para abrir Google Maps.");
      return;
    }
    setMapsRoutePlan({
      ...plan,
      date: group.date,
      shift: group.shift,
    });
  }
  function projectionSignature(rowsForDisplay) {
    return (rowsForDisplay || [])
      .filter((row) => !routeStopIsClosed(row))
      .map((row) => String(row.pedido_proyeccion || row.pedido || "").trim())
      .filter(Boolean)
      .join("|");
  }
  function projectionTime(value) {
    if (!value) return "—";
    const instant = new Date(value);
    if (Number.isNaN(instant.getTime())) return "—";
    return instant.toLocaleTimeString("es-CO", {
      timeZone: "America/Bogota", hour: "2-digit", minute: "2-digit", hour12: false,
    });
  }
  function projectionDuration(seconds) {
    const minutes = Math.max(0, Math.round(Number(seconds || 0) / 60));
    if (minutes < 60) return `${minutes} min`;
    return `${Math.floor(minutes / 60)} h ${minutes % 60 ? `${minutes % 60} min` : ""}`.trim();
  }
  function obtenerUbicacionParaProyeccion() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Este dispositivo no permite compartir ubicación."));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitud = Number(position.coords?.latitude);
          const longitud = Number(position.coords?.longitude);
          if (!Number.isFinite(latitud) || !Number.isFinite(longitud)) {
            reject(new Error("No recibimos una ubicación válida."));
            return;
          }
          resolve({ latitud, longitud });
        },
        () => reject(new Error("No pudimos usar tu ubicación. Revisa el permiso e inténtalo de nuevo.")),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
      );
    });
  }
  async function calcularProyeccionRuta(group, rowsForDisplay, usarUbicacionActual = false) {
    const paradasSinCoordenadas = (rowsForDisplay || []).filter((row) => !ubicacionMaps(row));
    const pedidos = (rowsForDisplay || [])
      .filter((row) => !routeStopIsClosed(row))
      .map((row) => String(row.pedido_proyeccion || row.pedido || "").trim())
      .filter(Boolean);
    const signature = pedidos.join("|");
    if (!pedidos.length) {
      setRouteProjectionFeedback((current) => ({ ...current, [group.key]: "No hay paradas activas para proyectar." }));
      return;
    }
    const busyKey = `${group.key}:${usarUbicacionActual ? "ACTUAL" : "BASE"}`;
    const requestKey = `${busyKey}:${signature}`;
    // El efecto automático y un toque manual pueden coincidir antes de que
    // React pinte el estado ocupado. No enviamos dos POST para la misma ruta.
    if (projectionRequestsInFlight.current.has(requestKey)) return;
    projectionRequestsInFlight.current.add(requestKey);
    setRouteProjectionBusy(busyKey);
    setRouteProjectionFeedback((current) => ({ ...current, [group.key]: "" }));
    try {
      const origen = usarUbicacionActual ? await obtenerUbicacionParaProyeccion() : undefined;
      const response = await fetch("/api/dashboard/rutas/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: "proyectar_ruta",
          datos: {
            fecha: group.date,
            repartidor_id: rowsForDisplay?.[0]?.repartidor_id || "",
            pedidos,
            paradas_bloqueadas: routeProjectionLocks[group.key] || [],
            ...(origen ? { origen } : {}),
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible calcular el tiempo de la ruta.");
      setRouteProjections((current) => ({
        ...current,
        [group.key]: { signature, data: payload.data?.proyeccion || {} },
      }));
      // Una proyección se solicita deliberadamente: al terminar la mostramos
      // de inmediato para que el repartidor vea qué calculó el motor local.
      setRoutePlanningOpen((current) => ({ ...current, [group.key]: true }));
      setRouteProjectionLocks((current) => ({
        ...current,
        [group.key]: payload.data?.proyeccion?.paradas_bloqueadas || current[group.key] || [],
      }));
      const sinCoordenadas = payload.data?.proyeccion?.pedidos_sin_coordenadas || paradasSinCoordenadas.map((row) => row.pedido);
      setRouteProjectionFeedback((current) => ({
        ...current,
        [group.key]: `${payload.data?.reutilizada ? "Estimación recuperada" : "Estimación calculada"}. ${sinCoordenadas.length ? `${sinCoordenadas.length} parada(s) sin coordenadas quedaron al final y marcadas en rojo.` : "La ruta y los pedidos no fueron modificados."}`,
      }));
    } catch (error) {
      setRouteProjectionFeedback((current) => ({
        ...current,
        [group.key]: error.message || "No fue posible calcular el tiempo de la ruta.",
      }));
    } finally {
      projectionRequestsInFlight.current.delete(requestKey);
      setRouteProjectionBusy("");
    }
  }
  async function recuperarProyeccionGuardada(group, rowsForDisplay) {
    const pedidos = (rowsForDisplay || [])
      .filter((row) => !routeStopIsClosed(row))
      .map((row) => String(row.pedido_proyeccion || row.pedido || "").trim())
      .filter(Boolean);
    const signature = pedidos.join("|");
    if (!signature || routeProjections[group.key]?.signature === signature) return;
    const requestKey = `${group.key}:GUARDADA:${signature}`;
    if (storedProjectionRequestsInFlight.current.has(requestKey)) return;
    storedProjectionRequestsInFlight.current.add(requestKey);
    try {
      const response = await fetch("/api/dashboard/rutas/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: "consultar_proyeccion_guardada",
          datos: {
            fecha: group.date,
            repartidor_id: rowsForDisplay?.[0]?.repartidor_id || "",
            pedidos,
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      // Esta lectura nunca debe mostrar un error ni impedir trabajar en Ruta:
      // si no existe una proyección compatible, el botón queda disponible.
      if (!response.ok || !payload.ok || !payload.data?.vigente || !payload.data?.proyeccion) return;
      setRouteProjections((current) => ({
        ...current,
        [group.key]: { signature, data: payload.data.proyeccion },
      }));
      setRouteProjectionLocks((current) => ({
        ...current,
        [group.key]: payload.data?.proyeccion?.paradas_bloqueadas || current[group.key] || [],
      }));
      setRouteProjectionFeedback((current) => ({
        ...current,
        [group.key]: "Estimación guardada de esta ruta.",
      }));
    } catch (_) {
      // La guía de reparto no depende de una estimación guardada.
    } finally {
      storedProjectionRequestsInFlight.current.delete(requestKey);
    }
  }
  function alternarBloqueoProyeccion(groupKey, pedido) {
    const id = String(pedido || "").trim();
    if (!id) return;
    setRouteProjectionLocks((current) => {
      const existentes = new Set(current[groupKey] || []);
      if (existentes.has(id)) existentes.delete(id);
      else existentes.add(id);
      return { ...current, [groupKey]: [...existentes] };
    });
    setRouteProjectionFeedback((current) => ({
      ...current,
      [groupKey]: "Bloqueo actualizado. Pulsa Actualizar para recalcular conservando esta parada en su posición.",
    }));
  }
  function abrirDetalleRuta(row) {
    setSelected(row);
    setPendingLocation(null);
    setLocationMessage("");
    setRouteEventMessage("");
  }
  async function registrarEventoOperativo(tipo, { pedido = "", fecha = "", turno = "", observacion = "" } = {}) {
    const scope = pedido || fecha || "ruta";
    const busyKey = `${tipo}:${scope}`;
    if (routeEventRequestsInFlight.current.has(busyKey)) return;
    routeEventRequestsInFlight.current.add(busyKey);
    const operationKey = `${tipo}:${scope}`;
    const operationId = tipo === "NOVEDAD" || tipo === "NO_APERTURA"
      ? createOperationId(`RUTA-${tipo}-${scope}`)
      : routeEventOperationIds.current[operationKey] || (routeEventOperationIds.current[operationKey] = createOperationId(`RUTA-${tipo}-${scope}`));
    setRouteEventBusy(busyKey);
    setRouteEventMessage("");
    try {
      const response = await fetch("/api/dashboard/rutas/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: "registrar_evento_operativo",
          datos: {
            tipo,
            pedido,
            fecha,
            turno,
            observacion,
            operacion_id: operationId,
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || `No fue posible registrar el evento (HTTP ${response.status}).`);
      const mensaje = payload.data?.idempotente
        ? "Este evento ya estaba registrado."
        : payload.data?.mensaje_visible || payload.mensaje || "Evento operativo registrado.";
      setRouteEventMessage(mensaje);
      notificarAdmin(mensaje, "success");
    } catch (error) {
      const detail = error.message || "No fue posible registrar el evento.";
      setRouteEventMessage(detail);
      notificarAdmin(detail, "error");
    } finally {
      setRouteEventBusy("");
      routeEventRequestsInFlight.current.delete(busyKey);
    }
  }
  function ubicacionMaps(row) {
    const latitud = Number(row?.ubicacion?.latitud);
    const longitud = Number(row?.ubicacion?.longitud);
    if (!row?.ubicacion?.disponible || !Number.isFinite(latitud) || !Number.isFinite(longitud)) return null;
    return {
      latitud,
      longitud,
      url: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${latitud},${longitud}`)}&travelmode=driving`,
    };
  }
  function solicitarUbicacionActual() {
    if (!selected?.pedido) return;
    if (!navigator.geolocation) {
      setLocationMessage("Este dispositivo no permite compartir ubicación. Puedes continuar con la entrega.");
      return;
    }
    setLocationBusy(true);
    setLocationMessage("Buscando tu ubicación actual…");
    setPendingLocation(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords || {};
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          setLocationMessage("No pudimos obtener una ubicación válida. Puedes continuar con la entrega.");
          setLocationBusy(false);
          return;
        }
        setPendingLocation({ latitud: latitude, longitud: longitude, precision: accuracy });
        setLocationMessage("");
        setLocationBusy(false);
      },
      () => {
        setLocationMessage("No pudimos obtener la ubicación. Revisa el permiso del navegador o continúa con la entrega.");
        setLocationBusy(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }
  async function guardarUbicacionActual() {
    if (!selected?.pedido || !pendingLocation) return;
    const pedido = selected.pedido;
    setLocationBusy(true);
    setLocationMessage("Guardando ubicación de esta dirección…");
    try {
      const response = await fetch("/api/dashboard/rutas/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: "guardar_ubicacion_entrega",
          pedido_id: pedido,
          datos: pendingLocation,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) {
        throw new Error(payload.mensaje || `No fue posible guardar la ubicación (HTTP ${response.status}).`);
      }
      const direccion = payload.data?.direccion || {};
      setSelected((current) =>
        current?.pedido === pedido
          ? {
              ...current,
              direccion_id: direccion.id || current.direccion_id,
              ubicacion: {
                disponible: Boolean(direccion.latitud != null && direccion.longitud != null),
                latitud: direccion.latitud,
                longitud: direccion.longitud,
                capturada_en: direccion.ubicacion_capturada_en,
                capturada_por: direccion.ubicacion_capturada_por,
                precision: direccion.precision_ubicacion,
              },
            }
          : current,
      );
      setPendingLocation(null);
      setLocationMessage("Ubicación guardada. El pedido y la entrega no fueron modificados.");
      notificarAdmin("Ubicación GPS guardada para la dirección del cliente.", "success");
      onRefresh?.({ notificar: false });
    } catch (error) {
      const detail = error.message || "No fue posible guardar la ubicación.";
      setLocationMessage(`${detail} Puedes continuar con la entrega.`);
      notificarAdmin(detail, "error");
    } finally {
      setLocationBusy(false);
    }
  }
  function abrirEntregaParcial() {
    if (selected?.grupo_entrega_id) {
      setMessage("Una parada con pedidos unificados se entrega completa. Sus pedidos originales permanecen separados para pago, cartera e inventario.");
      return;
    }
    const pendientes = (selected?.productos || []).filter(
      (item) => Number(item.cantidad_pendiente ?? item.cantidad ?? 0) > 0,
    );
    if (!pendientes.length) {
      setMessage("Este pedido no tiene productos pendientes para entregar parcialmente.");
      return;
    }
    const selection = Object.fromEntries(
      pendientes.map((item) => [String(item.codigo || item.producto || ""), false]),
    );
    setDeliverySelection(selection);
    setPartialDeliveryOpen(true);
  }
  useEffect(() => {
    if (!partialDeliveryOpen) return undefined;
    const frame = window.requestAnimationFrame(() => {
      partialDeliveryRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [partialDeliveryOpen]);
  function alternarProductoEntrega(codigo) {
    setDeliverySelection((current) => ({
      ...current,
      [codigo]: !current[codigo],
    }));
  }
  function confirmarEntregaParcial() {
    const productos = (selected?.productos || [])
      .filter((item) => deliverySelection[String(item.codigo || item.producto || "")])
      .map((item) => ({
        codigo: item.codigo || item.producto,
        producto: item.producto,
        cantidad: Number(item.cantidad_pendiente ?? item.cantidad ?? 0),
      }))
      .filter((item) => item.cantidad > 0);
    if (!productos.length) {
      setMessage("Selecciona al menos un producto entregado.");
      return;
    }
    setPartialDeliveryOpen(false);
    run("registrar_entrega", {
      pedido: selected.pedido,
      datos: {
        productos_entregados: productos,
        // Sprint 2 registra únicamente la entrega; el pago se conserva sin
        // cambios hasta implementar el flujo de transferencia/efectivo.
        tipo_cobro: "PENDIENTE",
      },
    });
  }
  useEffect(() => {
    if (deliveryOnly) return undefined;
    let active = true;
    fetch("/api/dashboard/rutas/operacion", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ operacion: "listar_repartidores", datos: {} }),
    })
      .then(async (response) => ({
        response,
        payload: await response.json().catch(() => ({})),
      }))
      .then(({ response, payload }) => {
        if (active && response.ok && payload.ok)
          setRepartidores((payload.data || {}).repartidores || []);
      })
      .catch(() => {
        /* La guía sigue siendo usable aunque no haya repartidores configurados. */
      });
    return () => {
      active = false;
    };
  }, [deliveryOnly]);
  const visibleBeforeProductFilter = (rows || [])
    .filter((row) => {
      const estado = String(row.estado || "Pendiente").toLowerCase();
      const tipo = String(row.tipo_entrega || "").toLowerCase();
      // Una parada cancelada es sólo historial operativo: nunca puede
      // mostrarse como disponible ni entrar en una asignación de ruta.
      if (/(cancelado|no entregado)/.test(estado)) return false;
      if (!serverControlledFilters &&
        activeStatusFilter === "PENDIENTES" &&
        /(entregado|cancelado|no entregado|parcial)/.test(estado)
      )
        return false;
      if (!serverControlledFilters && activeStatusFilter === "PARCIALES" && !estado.includes("parcial"))
        return false;
      if (!serverControlledFilters && activeStatusFilter === "ENTREGADAS" && !estado.includes("entregado"))
        return false;
      if (deliveryFilter === "MEDIDA" && !tipo.includes("medida")) return false;
      if (deliveryFilter === "PROGRAMADA" && tipo.includes("medida"))
        return false;
      const key = routeDateKey(row.fecha);
      if (serverControlledFilters) return true;
      if (activeFilter === "TODAS") return true;
      if (activeFilter === "HOY") return key === today;
      if (activeFilter === "MANANA") return key === tomorrow;
      if (activeFilter === "SEMANA") return key >= today && key <= weekEnd;
      if (activeFilter === "PASADAS") return Boolean(key) && key < today;
      return Boolean(key) && key >= today;
    })
    .sort(
      (a, b) =>
        routeDateKey(a.fecha).localeCompare(routeDateKey(b.fecha)) ||
        Number(ordenBorrador[a.pedido] ?? a.orden ?? 999999) -
          Number(ordenBorrador[b.pedido] ?? b.orden ?? 999999),
    );
  const routeProductOptions = (() => {
    const options = new Map();
    for (const row of visibleBeforeProductFilter) {
      for (const item of row.productos || []) {
        const codigo = String(item.codigo || "").trim();
        if (!codigo || options.has(codigo)) continue;
        options.set(codigo, {
          codigo,
          producto: String(item.producto || codigo).trim(),
          presentacion: String(item.presentacion || "").trim(),
        });
      }
    }
    return [...options.values()].sort((left, right) =>
      `${left.producto} ${left.presentacion}`.localeCompare(
        `${right.producto} ${right.presentacion}`,
        "es",
      ),
    );
  })();
  const visible = routeProductFilter
    ? visibleBeforeProductFilter.filter((row) => {
        const contiene = (row.productos || []).some(
          (item) => String(item.codigo || "").trim() === routeProductFilter,
        );
        return routeProductMode === "CON" ? contiene : !contiene;
      })
    : visibleBeforeProductFilter;
  const groups = visible.reduce((all, row) => {
    const date = routeDateKey(row.fecha) || "SIN_FECHA";
    const service = routeServiceDescriptor(row);
    const key = `${date}|${service.service}`;
    if (!all[key]) all[key] = { date, ...service, rows: [] };
    all[key].rows.push(row);
    return all;
  }, {});
  Object.values(groups).forEach((group) => {
    group.rows.sort(
      (left, right) =>
        Number(
          Boolean(
            String(right.tipo_entrega || "")
              .toLowerCase()
              .includes("medida"),
          ),
        ) -
          Number(
            Boolean(
              String(left.tipo_entrega || "")
                .toLowerCase()
                .includes("medida"),
            ),
          ) || Number(left.orden || 0) - Number(right.orden || 0),
    );
    // El orden base se aplica después de todos los filtros activos y solo en
    // la presentación inicial. Un ajuste manual en curso no se pisa.
    const hasDraftOrder = group.rows.some((row) => ordenBorrador[row.pedido] !== undefined);
    if (!hasDraftOrder) {
      group.rows = orderRouteGroupFromBaseOrigin(group.rows, defaultRouteOrigin);
    }
  });
  const orderedRouteGroups = Object.entries(groups).sort(([, left], [, right]) => {
    const dateOrder = String(left.date).localeCompare(String(right.date));
    if (dateOrder) return dateOrder;
    const priority = { RUTA_AM: 0, RUTA_SOPAS_MEDIODIA: 1, RUTA_PM: 2 };
    return Number(priority[left.service] ?? 99) - Number(priority[right.service] ?? 99);
  });
  // La próxima parada se deriva estrictamente de la guía visible: respeta
  // fecha, servicio (AM/Sopas), estado, filtro de producto y orden vigente.
  // Nunca modifica la ruta ni adelanta un pedido cerrado.
  const nextRouteStop = deliveryOnly
    ? orderedRouteGroups.reduce((candidate, [key, group]) => {
        if (candidate) return candidate;
        const sourceRows = group.rows || [];
        const projection = routeProjections[group.key || key];
        const projectionMatches = projection?.signature === projectionSignature(sourceRows);
        const byPedido = new Map(sourceRows.map((item) => [String(item.pedido_proyeccion || item.pedido || ""), item]));
        const projected = projectionMatches
          ? (projection.data?.paradas || [])
              .map((item) => byPedido.get(String(item.pedido_id || "")))
              .filter(Boolean)
          : [];
        const row = [...projected, ...sourceRows].find((item) => !routeStopIsClosed(item));
        return row ? { key, group, row } : null;
      }, null)
    : null;
  const nextRouteStopMaps = nextRouteStop ? ubicacionMaps(nextRouteStop.row) : null;
  // Al abrir Rutas se permite leer una estimación ya guardada, pero jamás se
  // consulta Google Maps automáticamente. Las consultas externas quedan
  // exclusivamente detrás de los botones de actualización.
  const storedProjectionKey = deliveryOnly
    ? orderedRouteGroups
        .map(([key, group]) => `${key}:${projectionSignature(group.rows)}`)
        .join("|")
    : "";
  useEffect(() => {
    if (!deliveryOnly || !storedProjectionKey) return undefined;
    let cancelled = false;
    const recuperarTiemposGuardados = async () => {
      for (const [key, group] of orderedRouteGroups) {
        if (cancelled) return;
        const rowsForProjection = group.rows || [];
        const signature = projectionSignature(rowsForProjection);
        if (!signature || routeProjections[key]?.signature === signature) continue;
        await recuperarProyeccionGuardada({ ...group, key }, rowsForProjection);
      }
    };
    recuperarTiemposGuardados();
    return () => {
      cancelled = true;
    };
  }, [deliveryOnly, storedProjectionKey]);
  function construirPrevisualizacionRuta(origin) {
    const previews = orderedRouteGroups.map(([key, group]) => ({
      key,
      date: group.date,
      shift: group.shift,
      ...previewRouteGroup(group.rows, origin),
    }));
    setRouteOptimizationPreview(previews);
    setRouteViewOrder("ACTUAL");
  }
  function generarPrevisualizacionRuta() {
    if (!navigator.geolocation) {
      setMessage("Este dispositivo no permite compartir su ubicación para sugerir el orden.");
      return;
    }
    setRouteOptimizationBusy(true);
    setMessage("Buscando tu ubicación actual para sugerir el orden de entrega…");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const origin = {
          latitud: position.coords?.latitude,
          longitud: position.coords?.longitude,
        };
        if (!routeOriginCoordinate(origin)) {
          setMessage("No recibimos una ubicación válida. Revisa el permiso e inténtalo de nuevo.");
          setRouteOptimizationBusy(false);
          return;
        }
        setRouteOptimizationOrigin(origin);
        construirPrevisualizacionRuta(origin);
        setMessage("");
        setRouteOptimizationBusy(false);
      },
      () => {
        setRouteOptimizationBusy(false);
        setMessage("No pudimos usar tu ubicación. Autoriza el permiso de ubicación e inténtalo de nuevo.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  }
  function paradasActivasDelGrupo(group) {
    return (rows || [])
      .filter((row) => {
        const date = routeDateKey(row.fecha) || "SIN_FECHA";
        const service = routeServiceDescriptor(row);
        return date === group.date && service.service === group.service && !routeStopIsClosed(row);
      })
      .sort(
        (left, right) =>
          Number(routeStopIsPersonalized(right)) - Number(routeStopIsPersonalized(left)) ||
          Number(left.orden ?? 999999) - Number(right.orden ?? 999999),
      );
  }
  function guardarOrdenSugerido(group, preview) {
    if (!preview) return;
    const activas = paradasActivasDelGrupo(group);
    const personalizadas = activas.filter(routeStopIsPersonalized);
    const idsSugeridos = new Set(
      preview.suggested
        .filter((row) => !routeStopIsPersonalized(row))
        .map((row) => String(row.pedido || "").trim())
        .filter(Boolean),
    );
    const sugeridas = preview.suggested.filter(
      (row) => !routeStopIsPersonalized(row) && idsSugeridos.has(String(row.pedido || "").trim()),
    );
    const restantes = activas.filter(
      (row) => !routeStopIsPersonalized(row) && !idsSugeridos.has(String(row.pedido || "").trim()),
    );
    const ordenFinal = [...personalizadas, ...sugeridas, ...restantes];
    if (!ordenFinal.length) {
      setMessage("No hay paradas activas para guardar en esta ruta.");
      return;
    }
    if (new Set(ordenFinal.map((row) => String(row.pedido || "").trim())).size !== ordenFinal.length) {
      setMessage("No fue posible guardar: la ruta contiene una parada repetida.");
      return;
    }
    confirmarAccionAdmin({
      titulo: "Guardar orden sugerido",
      mensaje: `Se guardará el orden de ${ordenFinal.length} parada(s) activas para ${routeDateLabel(group.date)} · ${group.routeLabel}. Las entregas a tu medida conservan prioridad y los pedidos sin coordenadas quedan después de los ubicados.`,
      confirmar: "Guardar orden",
      onConfirm: () => run("ordenar_ruta", {
        datos: {
          ordenes: ordenFinal.map((row, index) => ({ pedido: row.pedido, orden: index + 1 })),
        },
      }),
    });
  }
  const states = visible.reduce((all, row) => {
    const state = row.estado || "Pendiente";
    all[state] = (all[state] || 0) + 1;
    return all;
  }, {});
  async function run(operation, { pedido = "", datos = {} } = {}) {
    if (operation === "registrar_entrega" && !datos.tipo_cobro) {
      const grupoEntregaId = selected?.grupo_entrega_id || "";
      setPaymentPrompt({
        pedido,
        grupo_entrega_id: grupoEntregaId,
        total: Number(selected?.valor_total || 0),
        choice: "",
        amount: "",
        error: "",
        // El reintento debe conservar la misma clave: si el servidor guardó
        // la entrega pero el navegador perdió la respuesta, la operación se
        // recupera sin cobrar ni crear una venta por segunda vez.
        operacion_pago_id: createOperationId(`ENTREGA-${pedido}`),
      });
      return;
    }
    const isGroupDelivery = operation === "registrar_entrega_grupo_operativo";
    const isDeliveryOperation = operation === "registrar_entrega" || isGroupDelivery;
    let deliveryRequestKey = "";
    if (isDeliveryOperation) {
      deliveryRequestKey = String(datos.grupo_entrega_id || pedido || "").trim();
      if (deliveryRequestsInFlight.current.has(deliveryRequestKey)) {
        setMessage("La entrega ya se está registrando. Espera la confirmación.");
        return;
      }
      deliveryRequestsInFlight.current.add(deliveryRequestKey);
      const operationId = datos.operacion_entrega_id || deliveryOperationIds.current[deliveryRequestKey] || createOperationId(`ENTREGA-${deliveryRequestKey}`);
      deliveryOperationIds.current[deliveryRequestKey] = operationId;
      datos = { ...datos, operacion_entrega_id: operationId };
    }
    setBusy(`${operation}:${pedido || datos.fecha || "ruta"}`);
    setMessage("");
    if (isDeliveryOperation) {
      setPaymentPrompt((current) => current ? { ...current, error: "" } : current);
    }
    try {
      if (isDeliveryOperation) {
        setMessage("Registrando la entrega y verificando el estado del pago…");
        console.info("RUTAS_DELIVERY_REQUEST", {
          pedido_id: pedido,
          tipo_cobro: datos.tipo_cobro || "PENDIENTE",
          operacion_entrega_id: datos.operacion_entrega_id || "",
          operacion_pago_id: datos.operacion_pago_id || "",
          started_at: new Date().toISOString(),
        });
      }
      const requestStartedAt = performance.now();
      const response = await fetch("/api/dashboard/rutas/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: isGroupDelivery ? "registrar_entrega_grupo_operativo" : operation === "registrar_entrega" ? "registrar_entrega_con_cobro" : operation,
          ...(isGroupDelivery ? {} : { pedido_id: pedido }),
          datos:
            isDeliveryOperation
              ? { ...datos, tipo_cobro: datos.tipo_cobro || "PENDIENTE" }
              : datos,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (isDeliveryOperation) {
        console.info("RUTAS_DELIVERY_RESPONSE", {
          pedido_id: pedido,
          tipo_cobro: datos.tipo_cobro || "PENDIENTE",
          http_status: response.status,
          ok: Boolean(response.ok && payload.ok),
          codigo: payload.codigo || "",
          elapsed_ms: Math.round(performance.now() - requestStartedAt),
          received_at: new Date().toISOString(),
        });
      }
      if (!response.ok || !payload.ok)
        throw new Error(
          `${payload.codigo ? `${payload.codigo}: ` : ""}${payload.mensaje || `No fue posible actualizar la ruta (HTTP ${response.status}).`}`,
        );
      const mensaje = payload.mensaje || "Ruta actualizada correctamente.";
      setMessage(mensaje);
      notificarAdmin(mensaje, "success");
      if (operation === "ordenar_ruta") {
        setOrdenBorrador({});
        setRouteOptimizationPreview(null);
        setRouteViewOrder("ACTUAL");
      }
      if (isDeliveryOperation) {
        setPaymentPrompt(null);
        setQuickCashPrompt(null);
        setSwipedRouteAction(null);
      }
      if (
        isDeliveryOperation ||
        operation === "registrar_no_entrega"
      )
        setSelected(null);
      // La operación ya está confirmada por el servidor. La recarga de toda
      // la ruta (que puede tardar varios segundos) no debe mantener el botón
      // de entrega bloqueado ni ocultar el resultado al repartidor.
      Promise.resolve(onRefresh?.({ notificar: false }))
        .then((refreshed) => {
          if (refreshed?.ok === false) {
            const mensaje = "Entrega guardada correctamente. Actualiza la ruta para ver el estado más reciente.";
            setMessage(mensaje);
            notificarAdmin(mensaje, "warning");
          }
        })
        .catch(() => {
          // La entrega ya fue aceptada; un refresco visual fallido no cambia
          // su estado ni debe volver a abrir el formulario de cobro.
        });
    } catch (error) {
      const detail = error.message || "No fue posible actualizar la ruta.";
      setMessage(detail);
      notificarAdmin(detail, "error");
      if (isDeliveryOperation) {
        setPaymentPrompt((current) => current ? { ...current, error: detail } : current);
        setQuickCashPrompt((current) => current ? { ...current, error: detail } : current);
      }
    } finally {
      if (deliveryRequestKey) deliveryRequestsInFlight.current.delete(deliveryRequestKey);
      setBusy("");
    }
  }
  function moverParada(group, row, direction) {
    const ordered = [...group.rows].sort(
      (left, right) =>
        Number(ordenBorrador[left.pedido] ?? left.orden ?? 999999) -
        Number(ordenBorrador[right.pedido] ?? right.orden ?? 999999),
    );
    const index = ordered.findIndex((item) => item.pedido === row.pedido);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ordered.length) return;
    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    setOrdenBorrador((current) => ({
      ...current,
      ...Object.fromEntries(
        ordered.map((item, position) => [item.pedido, position + 1]),
      ),
    }));
  }
  function guardarOrden(group) {
    const ordenes = [...group.rows]
      .sort(
        (left, right) =>
          Number(ordenBorrador[left.pedido] ?? left.orden ?? 999999) -
          Number(ordenBorrador[right.pedido] ?? right.orden ?? 999999),
      )
      .map((row, index) => ({ pedido: row.pedido, orden: index + 1 }));
    run("ordenar_ruta", { datos: { ordenes } });
  }
  function resumenProductoRuta(item) {
    const total = Number(item.cantidad || 0);
    const producto = item.producto || "Producto";
    const descripcion = [producto, item.presentacion].filter(Boolean).join(" ");
    const valor = item.total == null ? "" : ` · ${formatCurrency(item.total)}`;
    return `${total} ${descripcion}${valor}`;
  }
  function estadoProductoRuta(item) {
    const total = Number(item.cantidad || 0);
    const entregada = Number(item.cantidad_entregada || 0);
    const pendiente = Number(item.cantidad_pendiente ?? Math.max(total - entregada, 0));
    if (entregada >= total && total > 0) return { label: "Entregado completo", kind: "delivered" };
    if (entregada > 0) return { label: `Parcial · faltan ${pendiente}`, kind: "partial" };
    return { label: `Pendiente · ${pendiente}`, kind: "pending" };
  }
  const selectedDeliverySummary = (() => {
    const products = selected?.productos || [];
    const summary = products.reduce(
      (result, item) => {
        const total = Number(item.cantidad || 0);
        const delivered = Math.min(Number(item.cantidad_entregada || 0), total);
        const pending = Math.max(Number(item.cantidad_pendiente ?? total - delivered), 0);
        result.totalLines += total > 0 ? 1 : 0;
        result.deliveredLines += total > 0 && delivered >= total ? 1 : 0;
        result.pendingLines += total > delivered ? 1 : 0;
        result.totalUnits += total;
        result.deliveredUnits += delivered;
        result.pendingUnits += pending;
        return result;
      },
      { totalLines: 0, deliveredLines: 0, pendingLines: 0, totalUnits: 0, deliveredUnits: 0, pendingUnits: 0 },
    );
    const complete = summary.totalLines > 0 && summary.pendingLines === 0;
    const partial = summary.deliveredUnits > 0 && summary.pendingUnits > 0;
    return { ...summary, complete, partial, kind: complete ? "complete" : partial ? "partial" : "pending" };
  })();
  return (
    <section className={`route-guide${routeDarkMode ? " route-guide-dark" : ""}`}>
      <div className="route-summary">
        <div className="route-summary-primary">
          <span>{deliveryOnly ? "Paradas asignadas" : "Pedidos en guía"}</span>
          <strong>{visible.length}</strong>
        </div>
        <div className="route-summary-pending">
          <span>Pendientes</span>
          <strong>{states.Pendiente || 0}</strong>
        </div>
        <div className="route-summary-active">
          <span>En ruta</span>
          <strong>{states["En ruta"] || states.EnRuta || 0}</strong>
        </div>
        <div className="route-summary-partial">
          <span>Parciales</span>
          <strong>{states.Parcial || 0}</strong>
        </div>
        <div className="route-summary-delivered">
          <span>Entregados</span>
          <strong>{states.Entregado || 0}</strong>
        </div>
      </div>
      <div className="route-quick-tools">
        <button
          type="button"
          className={`route-filters-toggle${routeFiltersOpen ? " is-open" : ""}`}
          aria-expanded={routeFiltersOpen}
          onClick={() => setRouteFiltersOpen((current) => !current)}
        >
          <Filter size={15} aria-hidden="true" />
          <span>Filtros</span>
          <small>{visible.length} pedidos visibles</small>
          <ChevronDown size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="route-theme-toggle"
          aria-pressed={routeDarkMode}
          onClick={() => setRouteDarkMode((current) => !current)}
          title={routeDarkMode ? "Usar modo claro" : "Usar modo oscuro"}
        >
          {routeDarkMode ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
          <span>{routeDarkMode ? "Claro" : "Oscuro"}</span>
        </button>
      </div>
      {routeFiltersOpen && (
        <div className="route-filter-panel">
      <div
        className="route-filters"
        role="group"
        aria-label="Filtrar guía de reparto"
      >
        <div className="route-filter-group">
          <span>Fecha</span>
          <div>
            {[
              ["PROXIMAS", "Próximas"],
              ["HOY", "Hoy"],
              ["MANANA", "Mañana"],
              ["SEMANA", "Esta semana"],
              ["PASADAS", "Pasadas"],
              ["TODAS", "Todas"],
            ].map(([value, label]) => (
              <button type="button" className={activeFilter === value ? "active" : ""} key={value} onClick={() => cambiarAlcanceRuta(value)}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="route-filter-group">
          <span>Estado</span>
          <div>
            {[
              ["TODOS", "Todos"],
              ["PENDIENTES", "Pendientes"],
              ["PARCIALES", "Parciales"],
              ["ENTREGADAS", "Entregadas"],
            ].map(([value, label]) => (
              <button type="button" className={activeStatusFilter === value ? "active" : ""} key={value} onClick={() => cambiarEstadoRuta(value)}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="route-filter-group">
          <span>Tipo de entrega</span>
          <div>
            {[
              ["TODAS", "Todas"],
              ["PROGRAMADA", "Ruta programada"],
              ["MEDIDA", "A tu medida"],
            ].map(([value, label]) => (
              <button type="button" className={deliveryFilter === value ? "active" : ""} key={value} onClick={() => setDeliveryFilter(value)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="route-product-filters" role="group" aria-label="Filtrar ruta por producto">
        <label>
          Producto de la ruta
          <select
            value={routeProductFilter}
            onChange={(event) => setRouteProductFilter(event.target.value)}
          >
            <option value="">Todos los productos</option>
            {routeProductOptions.map((item) => (
              <option value={item.codigo} key={item.codigo}>
                {[item.producto, item.presentacion].filter(Boolean).join(" · ")}
              </option>
            ))}
          </select>
        </label>
        <div className="route-product-mode" role="group" aria-label="Modo del filtro de producto">
          <span>Mostrar:</span>
          <button
            type="button"
            className={routeProductMode === "SIN" ? "active" : ""}
            disabled={!routeProductFilter}
            onClick={() => setRouteProductMode("SIN")}
          >
            Sin este producto
          </button>
          <button
            type="button"
            className={routeProductMode === "CON" ? "active" : ""}
            disabled={!routeProductFilter}
            onClick={() => setRouteProductMode("CON")}
          >
            Con este producto
          </button>
        </div>
        <span className="route-product-filter-summary">
          {routeProductFilter
            ? `${visible.length} parada(s) en esta vista · no modifica la ruta`
            : "Selecciona un producto para priorizar entregas"}
        </span>
      </div>
        </div>
      )}
      <div className="route-optimization-tools">
        <div>
          <strong>Orden sugerido por ubicación</strong>
          <span>Usa tu ubicación actual y solo las paradas visibles. No guarda cambios.</span>
        </div>
        <button
          type="button"
          className="secondary-action"
          disabled={visible.length < 2 || Boolean(busy) || routeOptimizationBusy}
          onClick={generarPrevisualizacionRuta}
        >
          {routeOptimizationBusy ? "Ubicando…" : "Sugerir desde mi ubicación"}
        </button>
      </div>
      {routeOptimizationPreview && (
        <section className="route-optimization-preview" aria-live="polite">
          <div className="route-optimization-heading">
            <div>
              <p className="eyebrow">PREVISUALIZACIÓN · SIN CAMBIOS EN LA RUTA</p>
              <h3>Orden sugerido para las paradas filtradas</h3>
              <p>
                {routeOptimizationOrigin
                  ? "Calculado desde tu ubicación actual. Estimación en línea recta; no usa tráfico ni reemplaza Google Maps."
                  : "Estimación en línea recta; no usa tráfico ni reemplaza Google Maps."}
              </p>
            </div>
            <div className="route-optimization-heading-actions">
              <button
                type="button"
                className={routeViewOrder === "OPTIMIZADA" ? "primary-action" : "secondary-action"}
                onClick={() => setRouteViewOrder((current) => current === "OPTIMIZADA" ? "ACTUAL" : "OPTIMIZADA")}
              >
                {routeViewOrder === "OPTIMIZADA" ? "Ver orden guardado" : "Mostrar en la guía"}
              </button>
              <button type="button" className="secondary-action" onClick={() => { setRouteOptimizationPreview(null); setRouteViewOrder("ACTUAL"); }}>
                Cerrar vista previa
              </button>
            </div>
          </div>
          <div className="route-optimization-groups">
            {routeOptimizationPreview.map((preview) => (
              <article className="route-optimization-group" key={preview.key}>
                <div className="route-optimization-group-heading">
                  <strong>
                    {preview.date === "SIN_FECHA" ? "Fecha por confirmar" : routeDateLabel(preview.date)} · {preview.shift}
                  </strong>
                  <div className="route-optimization-group-tools">
                    <span>
                      {preview.ordered.length} optimizadas · {preview.fixed.length} a tu medida · {preview.missing.length} sin ubicación · {preview.distanceKm.toFixed(1)} km · ~{preview.estimatedMinutes} min
                    </span>
                    {!deliveryOnly && (
                      <button
                        type="button"
                        className="secondary-action"
                        disabled={!preview.suggested.length || Boolean(busy)}
                        onClick={() => guardarOrdenSugerido(groups[preview.key], preview)}
                      >
                        Guardar este orden
                      </button>
                    )}
                  </div>
                </div>
                {preview.suggested.length ? (
                  <ol>
                    {preview.suggested.map((row, index) => (
                      <li key={row.pedido || `${preview.key}-${index}`}>
                        <b>{index + 1}</b>
                        <span>
                          <strong>{row.cliente || "Cliente sin nombre"}</strong>
                          <small>{row.direccion || "Dirección pendiente"}</small>
                        </span>
                        <em>
                          {routeStopIsPersonalized(row)
                            ? "Horario fijo"
                            : !routeCoordinate(row)
                              ? "Sin ubicación"
                              : index === preview.fixed.length
                                ? "Ancla actual"
                                : `Actual: ${row.orden ?? "—"}`}
                        </em>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="route-optimization-empty">No hay paradas con coordenadas en este grupo.</p>
                )}
                {preview.missing.length > 0 && (
                  <div className="route-optimization-missing">
                    <strong>Fuera del orden sugerido por falta de coordenadas:</strong>
                    <span>{preview.missing.map((row) => row.cliente || row.pedido || "Cliente").join(" · ")}</span>
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>
      )}
      <button type="button" className="primary-action route-new-delivery" disabled={Boolean(busy)} onClick={() => setNewRouteDelivery(true)}>
        Crear nuevo domicilio
      </button>
      {!visible.length && (
        <div className="route-empty">
          No hay pedidos con fecha de entrega para este filtro.
        </div>
      )}
      {message && (
        <p
          className={`action-status ${message.startsWith("No fue") ? "error" : "success"}`}
          role="status"
        >
          {message}
        </p>
      )}
      {nextRouteStop && (
        <SwipeReveal
          className="route-next-stop-swipe"
          revealed={swipedRouteAction?.pedido === nextRouteStop.row.pedido ? swipedRouteAction.direccion : ""}
          onReveal={(direccion) => setSwipedRouteAction({ pedido: nextRouteStop.row.pedido, direccion })}
          onClose={() => setSwipedRouteAction(null)}
          leftAction={puedeCobroRapido(nextRouteStop.row) ? {
            label: "Pago pendiente",
            icon: <Clock3 size={19} aria-hidden="true" />,
            onClick: () => abrirPagoPendienteRapido(nextRouteStop.row),
          } : null}
          rightAction={puedeCobroRapido(nextRouteStop.row) ? {
            label: "Cobrar efectivo",
            icon: <CircleDollarSign size={19} aria-hidden="true" />,
            onClick: () => abrirCobroRapido(nextRouteStop.row),
          } : null}
        >
          <section
            className={`route-next-stop${nextRouteStopMaps ? "" : " route-next-stop-missing-location"}`}
            aria-label="Próxima entrega de la ruta"
          >
            <div className="route-next-stop-heading">
              <div>
                <p className="eyebrow">PRÓXIMA ENTREGA</p>
                <div className="route-next-stop-name-line">
                  <h3>{nextRouteStop.row.cliente || "Cliente sin nombre"}</h3>
                  <strong>{formatCurrency(nextRouteStop.row.valor_total || 0)}</strong>
                </div>
                <p>{nextRouteStop.row.direccion || "Dirección pendiente"}</p>
              </div>
              <div className="route-next-stop-status">
                <span>{nextRouteStop.group.routeLabel}</span>
              </div>
            </div>
            <div className="route-next-stop-products">
              {(nextRouteStop.row.productos || []).length
                ? nextRouteStop.row.productos.map((item, index) => (
                  <span key={`${nextRouteStop.row.pedido || "pedido"}-next-${index}`}>{resumenProductoRuta(item)}</span>
                ))
                : <span>Productos disponibles al abrir el pedido</span>}
            </div>
            <div className="route-next-stop-actions">
              <button type="button" className="primary-action" onClick={() => abrirDetalleRuta(nextRouteStop.row)}>
                Registrar entrega
              </button>
              {nextRouteStopMaps ? (
                <a className="route-maps-action" href={nextRouteStopMaps.url} target="_blank" rel="noreferrer">
                  <MapPin size={15} aria-hidden="true" />
                  Abrir en Google Maps
                </a>
              ) : (
                <button type="button" className="route-maps-action is-disabled" disabled>
                  <MapPin size={15} aria-hidden="true" />
                  Sin coordenadas GPS
                </button>
              )}
              {nextRouteStop.row.telefono && (
                <a
                  className="route-whatsapp"
                  href={`https://wa.me/${String(nextRouteStop.row.telefono).replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  WhatsApp
                </a>
              )}
            </div>
          </section>
        </SwipeReveal>
      )}
      <div className="route-days">
        {orderedRouteGroups.map(([key, group]) => {
          const previewForGroup = routeOptimizationPreview?.find((item) => item.key === key);
          const sourceRowsForDisplay = routeViewOrder === "OPTIMIZADA" && previewForGroup
            ? [...previewForGroup.suggested, ...previewForGroup.locked]
            : group.rows;
          const elegido = repartidorPorRuta[key] || "";
          const assigned =
            group.rows.find((row) => row.repartidor)?.repartidor || "";
          const ordenModificado = group.rows.some(
            (row) =>
              Number(ordenBorrador[row.pedido] ?? row.orden ?? 0) !==
              Number(row.orden ?? 0),
          );
          const usaOrdenBase =
            routeViewOrder === "ACTUAL" &&
            !ordenModificado &&
            Boolean(routeOriginCoordinate(defaultRouteOrigin));
          const projectionKey = group.key || key;
          const signature = projectionSignature(sourceRowsForDisplay);
          const projectionRecord = routeProjections[projectionKey];
          const projection = projectionRecord?.signature === signature ? projectionRecord.data : null;
          const projectionOrder = (projection?.paradas || [])
            .map((stop) => String(stop.pedido_id || ""))
            .filter(Boolean);
          const rowsByProjectionId = new Map(
            sourceRowsForDisplay.map((row) => [String(row.pedido_proyeccion || row.pedido || ""), row]),
          );
          const projectedRows = projectionOrder
            .map((pedido) => rowsByProjectionId.get(pedido))
            .filter(Boolean);
          const projectedIds = new Set(projectionOrder);
          const rowsForDisplay = projection && routeViewOrder === "ACTUAL"
            ? [
              ...projectedRows,
              ...sourceRowsForDisplay.filter(
                (row) => !projectedIds.has(String(row.pedido_proyeccion || row.pedido || "")),
              ),
            ]
            : sourceRowsForDisplay;
          const projectionByOrder = new Map((projection?.paradas || []).map((stop) => [String(stop.pedido_id || ""), stop]));
          const projectionBusyBase = routeProjectionBusy === `${projectionKey}:BASE`;
          const projectionBusyCurrent = routeProjectionBusy === `${projectionKey}:ACTUAL`;
          const isSoupRoute = String(group.service || group.shift || "").toUpperCase().includes("SOPA");
          return (
            <section className={`route-day route-service-${String(group.service || group.shift || "AM").toLowerCase()}${isSoupRoute ? " route-day-soup" : " route-day-am"}`} key={key}>
              <div className="route-day-heading">
                <div className="route-day-title">
                  <p className="route-service-badge">{isSoupRoute ? "SOPAS · ENTREGA MEDIODÍA" : "PEDIDOS AM · ENTREGA MAÑANA"}</p>
                  <h3>
                    {group.date === "SIN_FECHA"
                      ? "Fecha por confirmar"
                      : routeDateLabel(group.date)}
                  </h3>
                  <div className="route-day-context">
                    <span
                      className={`route-assignment ${assigned ? "assigned" : "unassigned"}`}
                    >
                      {assigned
                      ? `Asignada a ${visibleRepartidorName(assigned)}`
                        : "Sin repartidor asignado"}
                    </span>
                    <span className="route-day-count">
                      {rowsForDisplay.length} pedido(s)
                      {routeViewOrder === "OPTIMIZADA" && previewForGroup ? " · orden sugerido" : ""}
                      {projection && routeViewOrder === "ACTUAL" ? " · orden OSRM" : ""}
                      {usaOrdenBase ? " · desde Sebastiano" : ""}
                    </span>
                  </div>
                </div>
                <div className="route-day-actions">
                  <button
                    type="button"
                    className="route-open-maps"
                    disabled={!buildGoogleMapsRoutePlan(rowsForDisplay).blocks.length}
                    onClick={() => abrirPlanGoogleMaps(group, rowsForDisplay)}
                    title="Abrir las paradas visibles en Google Maps"
                  >
                    <MapPin size={15} aria-hidden="true" />
                    Abrir ruta en Maps
                  </button>
                  {deliveryOnly && (
                    <>
                      <button
                        type="button"
                        className="primary-action route-start-action"
                        disabled={Boolean(routeEventBusy)}
                        onClick={() => registrarEventoOperativo("INICIO_RUTA", { fecha: group.date, turno: group.shift })}
                      >
                        {routeEventBusy === `INICIO_RUTA:${group.date}` ? "Guardando…" : "Iniciar ruta"}
                      </button>
                      <details className="route-day-more-actions">
                        <summary>Más acciones</summary>
                        <div className="route-operational-events" aria-label="Bitácora operativa de ruta">
                          <button
                            type="button"
                            className="secondary-action"
                            disabled={Boolean(routeEventBusy)}
                            onClick={() => registrarEventoOperativo("INICIO_PAUSA", { fecha: group.date, turno: group.shift })}
                          >
                            Pausa
                          </button>
                          <button
                            type="button"
                            className="secondary-action"
                            disabled={Boolean(routeEventBusy)}
                            onClick={() => registrarEventoOperativo("FIN_PAUSA", { fecha: group.date, turno: group.shift })}
                          >
                            Reanudar
                          </button>
                        </div>
                      </details>
                    </>
                  )}
                  {!deliveryOnly && (
                    <>
                      <button
                        type="button"
                        className="primary-action route-start-action"
                        disabled={Boolean(busy)}
                        onClick={() =>
                          run("iniciar_ruta", {
                            datos: { fecha: group.date, turno: group.shift },
                          })
                        }
                      >
                        {busy === `iniciar_ruta:${group.date}`
                          ? "Iniciando…"
                          : "Iniciar ruta"}
                      </button>
                      <details className="route-day-more-actions">
                        <summary>Gestionar ruta</summary>
                        <div className="route-admin-actions">
                          {repartidores.length > 0 && (
                            <div className="route-admin-assignment">
                              <label>
                                Repartidor
                                <select
                                  aria-label="Repartidor de la ruta"
                                  value={elegido}
                                  onChange={(event) =>
                                    setRepartidorPorRuta((current) => ({
                                      ...current,
                                      [key]: event.target.value,
                                    }))
                                  }
                                >
                                  <option value="">Asignar repartidor…</option>
                                  {repartidores.map((person) => (
                                    <option
                                      key={person.usuario_operativo_id}
                                      value={person.usuario_operativo_id}
                                    >
                                      {visibleRepartidorName(person.nombre)}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <button
                                type="button"
                                className="secondary-action"
                                disabled={!elegido || Boolean(busy)}
                                onClick={() =>
                                  run("asignar_repartidor", {
                                    datos: {
                                      fecha: group.date,
                                      turno: group.shift,
                                      repartidor_id: elegido,
                                    },
                                  })
                                }
                              >
                                {busy === `asignar_repartidor:${group.date}`
                                  ? "Asignando…"
                                  : "Asignar ruta"}
                              </button>
                            </div>
                          )}
                          <div className="route-admin-order-actions">
                            <button
                              type="button"
                              className="secondary-action"
                              disabled={!ordenModificado || Boolean(busy) || routeViewOrder === "OPTIMIZADA"}
                              onClick={() => guardarOrden(group)}
                            >
                              {busy === "ordenar_ruta:ruta"
                                ? "Guardando…"
                                : "Guardar orden"}
                            </button>
                            {routeViewOrder === "OPTIMIZADA" && previewForGroup && (
                              <button
                                type="button"
                                className="primary-action"
                                disabled={!previewForGroup.suggested.length || Boolean(busy)}
                                onClick={() => guardarOrdenSugerido(group, previewForGroup)}
                              >
                                {busy === "ordenar_ruta:ruta" ? "Guardando…" : "Guardar orden sugerido"}
                              </button>
                            )}
                          </div>
                        </div>
                      </details>
                    </>
                  )}
                </div>
              </div>
              <section className={`route-time-estimate ${projection ? "has-projection" : "is-empty"}`} aria-live="polite">
                <div className="route-time-estimate-heading">
                  <div>
                    <p className="eyebrow">PLANIFICACIÓN DE RUTA</p>
                    <h4>Proyección de esta ruta</h4>
                    <span>{projection ? `Desde ${projection.origen || "el punto de salida"} · ${group.routeLabel} · ${projection.proveedor === "OSRM" ? "OSRM local, sin tráfico" : "motor de rutas"} · hora Colombia` : `Calcula las paradas visibles de ${group.routeLabel}; las que no tengan GPS quedan al final.`}</span>
                  </div>
                  <div className="route-time-estimate-actions">
                    <button
                      type="button"
                      className="secondary-action"
                      disabled={Boolean(busy) || Boolean(routeProjectionBusy) || !signature}
                      onClick={() => calcularProyeccionRuta({ ...group, key: projectionKey }, group.rows)}
                    >
                      {projectionBusyBase ? "Calculando…" : projection ? "Actualizar desde Sebastiano" : "Calcular desde Sebastiano"}
                    </button>
                    <button
                      type="button"
                      className="secondary-action"
                      disabled={Boolean(busy) || Boolean(routeProjectionBusy) || !signature}
                      onClick={() => calcularProyeccionRuta({ ...group, key: projectionKey }, group.rows, true)}
                    >
                      {projectionBusyCurrent ? "Ubicando…" : "Actualizar desde mi ubicación"}
                    </button>
                  </div>
                </div>
                {projection && (
                  <>
                    <div className="route-time-estimate-grid">
                      <span><small>Conducción</small><b>{projectionDuration(projection.tiempo_conduccion_segundos)}</b></span>
                      <span><small>Ruta completa</small><b>{projectionDuration(projection.tiempo_operativo_estimado_segundos)}</b></span>
                      <span><small>Distancia</small><b>{(Number(projection.distancia_metros || 0) / 1000).toFixed(1)} km</b></span>
                      <span><small>Final aprox.</small><b>{projectionTime(projection.llegada_final_estimada)}</b></span>
                    </div>
                    <details
                      className="route-projection-itinerary"
                      open={Boolean(routePlanningOpen[projectionKey])}
                      onToggle={(event) => setRoutePlanningOpen((current) => ({
                        ...current,
                        [projectionKey]: event.currentTarget.open,
                      }))}
                    >
                      <summary>
                        <span>
                          <b>Ver recorrido y horarios</b>
                          <small>{(projection.paradas || []).length} parada(s) calculadas{(projection.paradas_bloqueadas || []).length ? ` · ${(projection.paradas_bloqueadas || []).length} con posición fija` : ""}</small>
                        </span>
                        <ChevronDown size={17} aria-hidden="true" />
                      </summary>
                      <div className="route-projection-itinerary-note">
                        <Route size={15} aria-hidden="true" />
                        <span>Los horarios son una guía de desplazamiento. “Posición fija” conserva esa parada en su lugar al recalcular.</span>
                      </div>
                      <ol className="route-projection-stops">
                        {(projection.paradas || []).map((stop, stopIndex) => {
                          const isLocked = (projection.paradas_bloqueadas || []).includes(String(stop.pedido_id || ""));
                          return (
                            <li className={isLocked ? "is-locked" : ""} key={`${stop.pedido_id || "parada"}-${stopIndex}`}>
                              <span className="route-projection-order">{stopIndex + 1}</span>
                              <span className="route-projection-stop-main">
                                <b>{stop.cliente || "Cliente"}</b>
                                <small>{isLocked ? "Posición fija" : "Orden sugerido"}</small>
                              </span>
                              <span className="route-projection-stop-time">
                                <b>{projectionTime(stop.ventana_desde)}–{projectionTime(stop.ventana_hasta)}</b>
                                <small>{projectionDuration(stop.duracion_tramo_segundos)} · {(Number(stop.distancia_tramo_metros || 0) / 1000).toFixed(1)} km</small>
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                    </details>
                  </>
                )}
                {routeProjectionFeedback[projectionKey] && <p className={`route-time-estimate-feedback ${routeProjectionFeedback[projectionKey].startsWith("No ") || routeProjectionFeedback[projectionKey].startsWith("Hay ") ? "error" : ""}`}>{routeProjectionFeedback[projectionKey]}</p>}
              </section>
              <div className="route-stop-list">
                {rowsForDisplay.map((row, index) => (
                  <SwipeReveal
                    className="route-stop-swipe"
                    key={`${row.pedido || "pedido"}-${index}`}
                    revealed={swipedRouteAction?.pedido === row.pedido ? swipedRouteAction.direccion : ""}
                    onReveal={(direccion) => setSwipedRouteAction({ pedido: row.pedido, direccion })}
                    onClose={() => setSwipedRouteAction(null)}
                    leftAction={puedeCobroRapido(row) ? {
                      label: "Pago pendiente",
                      icon: <Clock3 size={19} aria-hidden="true" />,
                      onClick: () => abrirPagoPendienteRapido(row),
                    } : null}
                    rightAction={puedeCobroRapido(row) ? {
                      label: "Cobrar efectivo",
                      icon: <CircleDollarSign size={19} aria-hidden="true" />,
                      onClick: () => abrirCobroRapido(row),
                    } : null}
                  >
                    <article
                      className={`route-stop${ubicacionMaps(row) ? "" : " route-stop-missing-location"}`}
                      aria-label={`${row.cliente || "Cliente"}. ${puedeCobroRapido(row) ? "Desliza a la izquierda para cobrar en efectivo o a la derecha para dejar pago pendiente." : ""}`}
                    >
                      <span className="route-order">
                        {ordenBorrador[row.pedido] ?? (usaOrdenBase ? index + 1 : row.orden ?? index + 1)}
                      </span>
                      <button
                        type="button"
                        className="route-stop-open"
                        onClick={() => {
                          if (swipedRouteAction?.pedido === row.pedido) {
                            setSwipedRouteAction(null);
                            return;
                          }
                          abrirDetalleRuta(row);
                        }}
                      >
                        <span className="route-stop-main">
                          <span className="route-stop-title-row">
                            <b className="route-stop-client">{row.cliente || "Cliente sin nombre"}</b>
                            <strong className="route-stop-total">{formatCurrency(row.valor_total || 0)}</strong>
                          </span>
                          <small className="route-stop-address">{row.direccion || "Dirección pendiente"}</small>
                          <span className="route-stop-products">
                            {(row.productos || []).length
                              ? row.productos.map((item, productIndex) => (
                                <small key={`${row.pedido || "pedido"}-producto-${productIndex}`}>
                                  {resumenProductoRuta(item)}
                                </small>
                              ))
                              : <small>Productos en el detalle</small>}
                          </span>
                        </span>
                        <span className="route-stop-meta">
                          {!ubicacionMaps(row) && <strong className="route-location-alert">Sin coordenadas GPS</strong>}
                          <em
                            className={`status-pill ${String(row.estado || "")
                              .toLowerCase()
                              .replaceAll(" ", "-")}`}
                          >
                            {row.estado || "Pendiente"}
                          </em>
                          <small>{row.hora || group.routeLabel}</small>
                          {projectionByOrder.get(String(row.pedido_proyeccion || row.pedido || "")) && (
                            <small className="route-stop-eta">
                              Llegada aprox. {projectionTime(projectionByOrder.get(String(row.pedido_proyeccion || row.pedido || "")).ventana_desde)}–{projectionTime(projectionByOrder.get(String(row.pedido_proyeccion || row.pedido || "")).ventana_hasta)}
                            </small>
                          )}
                          {puedeCobroRapido(row) && <small className="route-swipe-hint">Desliza para cobrar o dejar pendiente</small>}
                        </span>
                      </button>
                      <div className="route-stop-tools" aria-label={`Acciones para ${row.cliente || "esta parada"}`}>
                          {row.telefono && (
                            <a
                              className="route-whatsapp"
                              href={`https://wa.me/${String(row.telefono).replace(/\D/g, "")}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              WhatsApp
                            </a>
                          )}
                          {(() => {
                            const maps = ubicacionMaps(row);
                            return maps ? (
                              <a
                                className="route-maps-action"
                                href={maps.url}
                                target="_blank"
                                rel="noreferrer"
                                title={`Abrir coordenadas ${maps.latitud}, ${maps.longitud} en Google Maps`}
                              >
                                <MapPin size={14} aria-hidden="true" />
                                <span>Google Maps</span>
                              </a>
                            ) : (
                              <button
                                type="button"
                                className="route-maps-action is-disabled"
                                disabled
                                title="Esta dirección no tiene coordenadas guardadas"
                              >
                                <MapPin size={14} aria-hidden="true" />
                                <span>Sin ubicación</span>
                              </button>
                            );
                          })()}
                          {!/[Cc]ancelado|[Ee]ntregado/.test(String(row.estado || "")) && (
                            <button
                              type="button"
                              className="route-stop-new-order"
                              disabled={Boolean(busy)}
                              onClick={() => setRouteOrderSource(row)}
                            >
                              Crear pedido
                            </button>
                          )}
                      </div>
                      {!deliveryOnly && routeViewOrder !== "OPTIMIZADA" && (
                        <span className="route-order-actions">
                          <button type="button" aria-label={`Subir ${row.cliente || row.pedido || "parada"}`} disabled={index === 0 || Boolean(busy)} onClick={() => moverParada(group, row, -1)}>↑</button>
                          <button type="button" aria-label={`Bajar ${row.cliente || row.pedido || "parada"}`} disabled={index === group.rows.length - 1 || Boolean(busy)} onClick={() => moverParada(group, row, 1)}>↓</button>
                        </span>
                      )}
                    </article>
                  </SwipeReveal>
                ))}
              </div>
            </section>
          );
        })}
      </div>
      {selected && (
        <div className="drawer-backdrop" onClick={cerrarDetalleRuta}>
          <aside
            className="drawer route-detail-drawer"
            onClick={(event) => event.stopPropagation()}
          >
            <button className="drawer-close" onClick={cerrarDetalleRuta}>
              <X size={18} />
            </button>
            <p className="eyebrow">PARADA DE RUTA</p>
            <h2>{selected.cliente || "Cliente"}</h2>
            <p className="drawer-subtitle">
              {selected.grupo_entrega_id
                ? `${selected.cantidad_pedidos_unificados || 0} pedidos unificados · ${routeDateLabel(selected.fecha)}`
                : `${selected.pedido || "Pedido"} · ${routeDateLabel(selected.fecha)}`}
            </p>
            <div className={`route-delivery-summary ${selectedDeliverySummary.kind}`}>
              <div className="route-delivery-summary-heading">
                <span className="route-delivery-summary-icon" aria-hidden="true">
                  {selectedDeliverySummary.complete ? "✓" : selectedDeliverySummary.partial ? "!" : "○"}
                </span>
                <div>
                  <strong>
                    {selectedDeliverySummary.complete
                      ? "Pedido entregado completamente"
                      : selectedDeliverySummary.partial
                        ? "Pedido con entrega parcial"
                        : "Pedido pendiente de entrega"}
                  </strong>
                  <small>
                    {selectedDeliverySummary.complete
                      ? `${selectedDeliverySummary.deliveredLines} producto(s) completado(s)`
                      : selectedDeliverySummary.partial
                        ? `${selectedDeliverySummary.deliveredUnits} entregado(s) · ${selectedDeliverySummary.pendingUnits} pendiente(s)`
                        : `${selectedDeliverySummary.pendingUnits || selectedDeliverySummary.totalUnits} unidad(es) pendientes`}
                  </small>
                </div>
              </div>
              <div className="route-delivery-progress" aria-label="Avance de entrega">
                <span style={{ width: `${selectedDeliverySummary.totalUnits > 0 ? Math.min(100, Math.round((selectedDeliverySummary.deliveredUnits / selectedDeliverySummary.totalUnits) * 100)) : 0}%` }} />
              </div>
            </div>
            <div className="detail-list">
              <div>
                <span>Dirección</span>
                <b>{selected.direccion || "Pendiente"}</b>
              </div>
              <div>
                <span>Teléfono</span>
                <b>{selected.telefono || "—"}</b>
              </div>
              <div>
                <span>Entrega</span>
                <b>
                  {selected.tipo_entrega === "Personalizada"
                    ? `A tu medida · ${selected.hora || "Hora pendiente"}`
                    : "Ruta programada"}
                </b>
              </div>
              <div>
                <span>Orden</span>
                <b>{selected.orden || "—"}</b>
              </div>
              <div>
                <span>Estado</span>
                <b>{selected.estado || "Pendiente"}</b>
              </div>
              <div>
                <span>Repartidor</span>
                <b>{visibleRepartidorName(selected.repartidor) || "Sin asignar"}</b>
              </div>
              <div>
                <span>Total</span>
                <b>
                  {selected.valor_total == null
                    ? "—"
                    : `$${Number(selected.valor_total).toLocaleString("es-CO")}`}
                </b>
              </div>
              <div>
                <span>Saldo pendiente</span>
                <b>
                  {selected.saldo_pendiente == null
                    ? "—"
                    : `$${Number(selected.saldo_pendiente).toLocaleString("es-CO")}`}
                </b>
              </div>
              <div>
                <span>Pago</span>
                <b>
                  {selected.estado_pago || "Pendiente de registrar"}
                  {selected.metodo_pago ? ` · ${selected.metodo_pago}` : ""}
                </b>
              </div>
              <div>
                <span>Observación</span>
                <b>{selected.observacion || "—"}</b>
              </div>
            </div>
            <section
              className={`route-location-card ${selected.ubicacion?.disponible ? "is-saved" : pendingLocation ? "is-ready" : "is-missing"}`}
              aria-live="polite"
            >
              <div className="route-location-heading">
                <span className="route-location-icon" aria-hidden="true">⌖</span>
                <div>
                  <strong>
                    {selected.ubicacion?.disponible
                      ? "Ubicación GPS guardada"
                      : pendingLocation
                        ? "Ubicación lista para guardar"
                        : "Ubicación GPS no registrada"}
                  </strong>
                  <small>
                    {selected.ubicacion?.disponible
                      ? "Esta dirección ya tiene un punto de referencia para próximas rutas."
                      : pendingLocation
                        ? `Precisión aproximada: ${Math.round(Number(pendingLocation.precision || 0)) || "sin dato"}${Number(pendingLocation.precision || 0) ? " m" : ""}. Confirma antes de guardarla.`
                        : "Al entregar aquí, comparte la ubicación para facilitar próximas rutas."}
                  </small>
                </div>
              </div>
              {selected.ubicacion?.disponible && (
                <p className="route-location-meta">
                  {selected.ubicacion?.precision != null
                    ? `Precisión registrada: ${Math.round(Number(selected.ubicacion.precision))} m`
                    : "Ubicación guardada sin dato de precisión"}
                </p>
              )}
              {!selected.ubicacion?.disponible && (
                <div className="route-location-actions">
                  {pendingLocation ? (
                    <>
                      <button type="button" className="secondary-action" disabled={locationBusy} onClick={solicitarUbicacionActual}>
                        Tomar de nuevo
                      </button>
                      <button type="button" className="primary-action" disabled={locationBusy} onClick={guardarUbicacionActual}>
                        {locationBusy ? "Guardando…" : "Guardar ubicación"}
                      </button>
                    </>
                  ) : (
                    <button type="button" className="secondary-action" disabled={locationBusy} onClick={solicitarUbicacionActual}>
                      {locationBusy ? "Buscando ubicación…" : "Guardar ubicación actual"}
                    </button>
                  )}
                </div>
              )}
              {locationMessage && <p className="route-location-message">{locationMessage}</p>}
            </section>
            <div className="route-products">
              <div className="route-products-heading">
                <h3>Productos</h3>
                {selectedDeliverySummary.partial && <span>Verde: entregado · Ámbar: pendiente</span>}
              </div>
              {(selected.productos || []).length ? (
                selected.productos.map((item, index) => {
                  const estadoProducto = estadoProductoRuta(item);
                  return (
                    <div className={`route-product route-product-${estadoProducto.kind}`} key={`${item.producto || "producto"}-${index}`}>
                      <span>
                        <strong>{item.producto || "Producto"}{item.presentacion ? ` · ${item.presentacion}` : ""}</strong>
                        <small className="route-product-quantity">{estadoProducto.label}</small>
                      </span>
                      <span className="route-product-status">
                        <b>{Number(item.cantidad_entregada || 0)} / {Number(item.cantidad || 0)}</b>
                        <small>{estadoProducto.kind === "delivered" ? "Listo" : estadoProducto.kind === "partial" ? "Falta entregar" : "Por entregar"}</small>
                      </span>
                    </div>
                  );
                })
              ) : (
                <p>No hay productos registrados.</p>
              )}
            </div>
            {!/[Cc]ancelado|[Ee]ntregado/.test(
              String(selected.estado || ""),
            ) && (
              <div className="route-detail-actions">
                {deliveryOnly && !selected.grupo_entrega_id && (
                  <>
                    <button
                      type="button"
                      className="secondary-action"
                      disabled={Boolean(busy) || Boolean(routeEventBusy)}
                      onClick={() => confirmarAccionAdmin({
                        titulo: "Registrar no apertura",
                        mensaje: "¿No fue posible contactar al cliente? El pedido seguirá pendiente y no se modificará pago, cartera, inventario, capacidad ni fecha de entrega. El aviso al cliente quedará preparado, pero no se enviará ni se pondrá en cola.",
                        confirmar: "Registrar no apertura",
                        onConfirm: () => registrarEventoOperativo("NO_APERTURA", {
                          pedido: selected.pedido,
                          fecha: routeDateKey(selected.fecha),
                          turno: selected.turno,
                        }),
                      })}
                    >
                      {routeEventBusy === `NO_APERTURA:${selected.pedido}` ? "Guardando…" : "No apertura"}
                    </button>
                  </>
                )}
                {deliveryOnly && !selected.grupo_entrega_id && (
                  <button
                    type="button"
                    className="secondary-action"
                    disabled={Boolean(busy)}
                    onClick={() => setRouteOrderSource(selected)}
                  >
                    Crear nuevo pedido
                  </button>
                )}
                {deliveryOnly && !selected.grupo_entrega_id && (
                  <button
                    type="button"
                    className="secondary-action"
                    disabled={Boolean(busy)}
                    onClick={abrirEntregaParcial}
                  >
                    Registrar entrega parcial
                  </button>
                )}
                <button
                  type="button"
                  className="primary-action"
                  disabled={Boolean(busy)}
                  onClick={() =>
                    run("registrar_entrega", { pedido: selected.pedido })
                  }
                >
                  {busy === `registrar_entrega:${selected.pedido}`
                    ? "Registrando…"
                    : "Registrar entrega"}
                </button>
                {!selected.grupo_entrega_id && <button
                  type="button"
                  className="danger-action"
                  disabled={Boolean(busy)}
                  onClick={() => confirmarAccionAdmin({
                    titulo: "Marcar pedido como no entregado",
                    mensaje: `¿Registrar como no entregado el pedido ${selected.pedido}? Se liberará la disponibilidad reservada.`,
                    confirmar: "Marcar no entregado",
                    peligro: true,
                    onConfirm: () => run("registrar_no_entrega", {
                        pedido: selected.pedido,
                        datos: { estado: "No entregado" },
                    }),
                  })}
                >
                  No entregado
                </button>}
                {message && (
                  <p className="route-detail-message" role="alert">
                    {message}
                  </p>
                )}
                {routeEventMessage && <p className="route-detail-message route-event-message" role="status">{routeEventMessage}</p>}
              </div>
            )}
            {partialDeliveryOpen && (
              <div ref={partialDeliveryRef} className="route-partial-delivery" role="region" aria-label="Seleccionar productos entregados" tabIndex={-1}>
                <p className="eyebrow">ENTREGA PARCIAL</p>
                <h3>¿Qué productos entregaste?</h3>
                <p>Marca los productos entregados hoy. Los demás quedarán pendientes de entrega.</p>
                <div className="route-partial-products">
                  {(selected.productos || [])
                    .filter((item) => Number(item.cantidad_pendiente ?? item.cantidad ?? 0) > 0)
                    .map((item, index) => {
                      const codigo = String(item.codigo || item.producto || `producto-${index}`);
                      return (
                        <label className="route-partial-product" key={codigo}>
                          <input
                            type="checkbox"
                            checked={deliverySelection[codigo] === true}
                            onChange={() => alternarProductoEntrega(codigo)}
                          />
                          <span>
                            <strong>{item.producto || "Producto"}</strong>
                            <small>{item.presentacion || ""} · cantidad: {Number(item.cantidad_pendiente ?? item.cantidad ?? 0)}</small>
                          </span>
                        </label>
                      );
                    })}
                </div>
                <div className="route-partial-actions">
                  <button type="button" className="secondary-action" disabled={Boolean(busy)} onClick={() => setPartialDeliveryOpen(false)}>Cancelar</button>
                  <button type="button" className="primary-action" disabled={Boolean(busy) || !Object.values(deliverySelection).some(Boolean)} onClick={confirmarEntregaParcial}>Confirmar entrega parcial</button>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
      {routeOrderSource && (
        <RouteOrderModal
          sourceOrder={routeOrderSource}
          onClose={() => setRouteOrderSource(null)}
          onCreated={async () => {
            const refreshed = await onRefresh?.({ notificar: false });
            if (refreshed?.ok === false)
              setMessage("Pedido programado correctamente. Actualiza la ruta para ver la agenda más reciente.");
          }}
        />
      )}
      {newRouteDelivery && (
        <NewRouteDeliveryModal
          onClose={() => setNewRouteDelivery(false)}
          onCreated={async () => {
            const refreshed = await onRefresh?.({ notificar: false });
            if (refreshed?.ok === false)
              setMessage("Domicilio creado correctamente. Actualiza la ruta para ver la agenda más reciente.");
          }}
        />
      )}
      {mapsRoutePlan && (
        <RouteMapsPlanModal
          plan={mapsRoutePlan}
          onClose={() => setMapsRoutePlan(null)}
        />
      )}
      {quickCashPrompt && (
        <div
          className="drawer-backdrop"
          role="presentation"
          onClick={() => !busy && setQuickCashPrompt(null)}
        >
          <section
            className="route-quick-cash-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="route-quick-cash-title"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="eyebrow">COBRO RÁPIDO</p>
            <h2 id="route-quick-cash-title">¿Recibiste el pago completo?</h2>
            <p>Se registrará la entrega de <strong>{quickCashPrompt.cliente}</strong>{quickCashPrompt.grupo_entrega_id ? " para todos sus pedidos unificados" : ""} y el efectivo entrará a tu caja de hoy.</p>
            <div className="route-quick-cash-total">
              <span>Total recibido en efectivo</span>
              <strong>{formatCurrency(quickCashPrompt.total || 0)}</strong>
            </div>
            {quickCashPrompt.error && <p className="delivery-payment-error" role="alert">{quickCashPrompt.error}</p>}
            <div className="delivery-payment-actions">
              <button type="button" className="secondary-action" disabled={Boolean(busy)} onClick={() => setQuickCashPrompt(null)}>
                Volver
              </button>
              <button
                type="button"
                className="primary-action"
                disabled={Boolean(busy)}
                onClick={() => run(quickCashPrompt.grupo_entrega_id ? "registrar_entrega_grupo_operativo" : "registrar_entrega", {
                  pedido: quickCashPrompt.pedido,
                  datos: {
                    ...(quickCashPrompt.grupo_entrega_id ? { grupo_entrega_id: quickCashPrompt.grupo_entrega_id } : {}),
                    tipo_cobro: "EFECTIVO",
                    valor_recibido: Number(quickCashPrompt.total || 0),
                    operacion_pago_id: quickCashPrompt.operacion_pago_id,
                    metodo_pago: "Efectivo",
                  },
                })}
              >
                {busy === `registrar_entrega:${quickCashPrompt.pedido}` ? "Registrando…" : "Confirmar pago total"}
              </button>
            </div>
          </section>
        </div>
      )}
      {paymentPrompt && (
        <div
          className="drawer-backdrop"
          role="presentation"
          onClick={() => !busy && setPaymentPrompt(null)}
        >
          <div
            className="delivery-payment-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delivery-payment-title"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="eyebrow">CIERRE DE ENTREGA</p>
            <h2 id="delivery-payment-title">¿Cómo quedó el pago?</h2>
            <p className="delivery-payment-help">
              Registra el estado del dinero antes de marcar {paymentPrompt.grupo_entrega_id ? "todos los pedidos de esta parada" : "el pedido"} como entregado.
            </p>
            <div className="delivery-payment-options">
              <button
                type="button"
                className={
                  paymentPrompt.choice === "PENDIENTE"
                    ? "delivery-payment-option active"
                    : "delivery-payment-option"
                }
                onClick={() => {
                  console.info("RUTAS_PAYMENT_METHOD_SELECTED", {
                    pedido_id: paymentPrompt.pedido,
                    metodo: "PENDIENTE",
                    selected_at: new Date().toISOString(),
                  });
                  setPaymentPrompt((current) => ({
                    ...current,
                    choice: "PENDIENTE",
                    amount: "",
                    error: "",
                  }));
                }}
              >
                <strong>Pago pendiente</strong>
                <span>No se recibió dinero en la entrega</span>
              </button>
              <button
                type="button"
                className={
                  paymentPrompt.choice === "TOTAL"
                    ? "delivery-payment-option active"
                    : "delivery-payment-option"
                }
                onClick={() => {
                  console.info("RUTAS_PAYMENT_METHOD_SELECTED", {
                    pedido_id: paymentPrompt.pedido,
                    metodo: "TOTAL",
                    selected_at: new Date().toISOString(),
                  });
                  setPaymentPrompt((current) => ({
                    ...current,
                    choice: "TOTAL",
                    amount: String(current.total || ""),
                    error: "",
                  }));
                }}
              >
                <strong>Pago total</strong>
                <span>Se recibió el valor completo del pedido</span>
              </button>
              {!paymentPrompt.grupo_entrega_id && <button
                type="button"
                className={
                  paymentPrompt.choice === "PARCIAL"
                    ? "delivery-payment-option active"
                    : "delivery-payment-option"
                }
                onClick={() => {
                  console.info("RUTAS_PAYMENT_METHOD_SELECTED", {
                    pedido_id: paymentPrompt.pedido,
                    metodo: "PARCIAL",
                    selected_at: new Date().toISOString(),
                  });
                  setPaymentPrompt((current) => ({
                    ...current,
                    choice: "PARCIAL",
                    amount: current.amount || "",
                    error: "",
                  }));
                }}
              >
                <strong>Pago parcial</strong>
                  <span>Se recibió solo una parte</span>
                </button>}
              <button
                type="button"
                className={
                  paymentPrompt.choice === "TRANSFERENCIA"
                    ? "delivery-payment-option active"
                    : "delivery-payment-option"
                }
                onClick={() => {
                  console.info("RUTAS_PAYMENT_METHOD_SELECTED", {
                    pedido_id: paymentPrompt.pedido,
                    metodo: "TRANSFERENCIA",
                    selected_at: new Date().toISOString(),
                  });
                  setPaymentPrompt((current) => ({
                    ...current,
                    choice: "TRANSFERENCIA",
                    amount: String(current.total || ""),
                    error: "",
                  }));
                }}
              >
                <strong>Transferencia</strong>
                <span>Notificar al administrador para validar el pago</span>
              </button>
            </div>
            {paymentPrompt.choice === "PARCIAL" && (
              <label className="delivery-payment-amount">
                Valor recibido
                <input
                  type="number"
                  min="0"
                  max={paymentPrompt.total || undefined}
                  value={paymentPrompt.amount}
                  onChange={(event) =>
                    setPaymentPrompt((current) => ({
                      ...current,
                      amount: event.target.value,
                    }))
                  }
                  placeholder="Ej.: 20.000"
                  autoFocus
                />
              </label>
            )}
            {paymentPrompt.choice === "TOTAL" && (
              <p className="delivery-payment-total">
                Total del pedido:{" "}
                <strong>
                  ${Number(paymentPrompt.total || 0).toLocaleString("es-CO")}
                </strong>
              </p>
            )}
            {paymentPrompt.error && (
              <p className="delivery-payment-error" role="alert">
                {paymentPrompt.error}
              </p>
            )}
            <div className="delivery-payment-actions">
              <button
                type="button"
                className="secondary-action"
                disabled={Boolean(busy)}
                onClick={() => setPaymentPrompt(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="primary-action"
                disabled={
                  Boolean(busy) ||
                  !paymentPrompt.choice ||
                  (paymentPrompt.choice === "PARCIAL" &&
                    (!Number.isFinite(Number(paymentPrompt.amount)) ||
                      Number(paymentPrompt.amount) <= 0 ||
                      Number(paymentPrompt.amount) >
                        Number(paymentPrompt.total || 0)))
                }
                onClick={() =>
                  run(paymentPrompt.grupo_entrega_id ? "registrar_entrega_grupo_operativo" : "registrar_entrega", {
                    pedido: paymentPrompt.pedido,
                    datos:
                      paymentPrompt.choice === "PENDIENTE"
                        ? { tipo_cobro: "PENDIENTE", ...(paymentPrompt.grupo_entrega_id ? { grupo_entrega_id: paymentPrompt.grupo_entrega_id } : {}) }
                        : paymentPrompt.choice === "TRANSFERENCIA"
                          ? {
                              ...(paymentPrompt.grupo_entrega_id ? { grupo_entrega_id: paymentPrompt.grupo_entrega_id } : {}),
                              tipo_cobro: "TRANSFERENCIA",
                              valor_reportado: Number(paymentPrompt.total || 0),
                              operacion_pago_id: paymentPrompt.operacion_pago_id,
                              comprobante_recibido: false,
                            }
                        : {
                            ...(paymentPrompt.grupo_entrega_id ? { grupo_entrega_id: paymentPrompt.grupo_entrega_id } : {}),
                            tipo_cobro: "EFECTIVO",
                            valor_recibido:
                              paymentPrompt.choice === "TOTAL"
                                ? Number(paymentPrompt.total || 0)
                                : Number(paymentPrompt.amount),
                            operacion_pago_id: paymentPrompt.operacion_pago_id,
                            metodo_pago: "Efectivo",
                          },
                  })
                }
              >
                {busy === `registrar_entrega:${paymentPrompt.pedido}`
                  ? "Registrando…"
                  : "Confirmar entrega"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function RouteMapsPlanModal({ plan, onClose }) {
  const title = `${plan.date === "SIN_FECHA" ? "Fecha por confirmar" : routeDateLabel(plan.date)} · ${plan.shift}`;
  return (
    <div className="drawer-backdrop route-maps-plan-backdrop" onClick={onClose}>
      <section className="route-maps-plan" role="dialog" aria-modal="true" aria-labelledby="route-maps-plan-title" onClick={(event) => event.stopPropagation()}>
        <button className="drawer-close" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        <p className="eyebrow">RUTA EN GOOGLE MAPS</p>
        <h2 id="route-maps-plan-title">{title}</h2>
        <p className="route-maps-plan-intro">
          {plan.located.length} parada(s) con ubicación serán enviadas en el orden visible de la guía.
        </p>
        {plan.blocks.length > 1 && (
          <p className="route-maps-plan-help">
            Google Maps móvil admite hasta cuatro entregas por enlace. Abre cada bloque al terminar el anterior; el siguiente empieza desde la última parada del bloque previo.
          </p>
        )}
        <div className="route-maps-plan-blocks">
          {plan.blocks.map((block) => (
            <a key={block.index} className={block.index === 1 ? "primary-action" : "secondary-action"} href={block.url} target="_blank" rel="noreferrer">
              <MapPin size={16} aria-hidden="true" />
              <span>
                <strong>{plan.blocks.length === 1 ? "Abrir ruta en Google Maps" : `Abrir bloque ${block.index} de ${plan.blocks.length}`}</strong>
                <small>{block.stops.map((row) => row.cliente || row.pedido || "Parada").join(" · ")}</small>
              </span>
            </a>
          ))}
        </div>
        {plan.missing.length > 0 && (
          <div className="route-maps-plan-missing">
            <strong>{plan.missing.length} parada(s) sin coordenadas no se enviaron:</strong>
            <span>{plan.missing.map((row) => row.cliente || row.pedido || "Cliente").join(" · ")}</span>
          </div>
        )}
      </section>
    </div>
  );
}

function PushOrderFocus({ orderId, rows, onOpen }) {
  const openedRef = useRef("");
  useEffect(() => {
    if (!orderId || openedRef.current === orderId) return;
    const order = rows.find((row) => String(row.id || row.pedido || row.PedidoId || "") === orderId);
    if (!order) return;
    openedRef.current = orderId;
    onOpen(order);
  }, [orderId, rows, onOpen]);
  return null;
}

function WhatsAppShipmentsModule({ data }) {
  const [draft, setDraft] = useState({ tipo_filtro: "UNA_COMPRA", cantidad: 1, limite: 5, fecha_desde: "", fecha_hasta: "" });
  const [applied, setApplied] = useState(draft);
  const [segmentData, setSegmentData] = useState(data || {});
  const [query, setQuery] = useState("");
  const [exclusionReason, setExclusionReason] = useState("");
  const [showCampaign, setShowCampaign] = useState(false);
  const [campaign, setCampaign] = useState({ nombre: "", mensaje: "", imagen: "" });
  const [campaignStatus, setCampaignStatus] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [whatsappState, setWhatsappState] = useState({ estado: "DESCONECTADO", conectado: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    readDashboard("envios_whatsapp", applied, controller.signal)
      .then((payload) => {
        if (active) setSegmentData(payload.data || {});
      })
      .catch((reason) => {
        if (active && reason.name !== "AbortError") setError(reason.message || "No fue posible consultar el segmento.");
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; controller.abort(); };
  }, [applied]);
  const loadCampaigns = useCallback(async () => {
    try {
      const response = await fetch("/api/dashboard/envios-whatsapp/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "listar", datos: {} }) });
      const payload = await response.json();
      if (response.ok && payload.ok) setCampaigns(payload.data?.campanas || []);
    } catch (_) { /* Un borrador no bloquea la segmentación. */ }
  }, []);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);
  useEffect(() => {
    let active = true;
    const check = () => fetch("/api/dashboard/envios-whatsapp/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "estado", datos: {} }) }).then((response) => response.json()).then((payload) => active && setWhatsappState(payload.data || {})).catch(() => active && setWhatsappState({ estado: "NO DISPONIBLE", conectado: false }));
    check(); const timer = window.setInterval(check, 15000); return () => { active = false; window.clearInterval(timer); };
  }, []);

  const rows = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("es-CO");
    return (segmentData.clientes || []).filter((row) => !needle || [row.cliente, row.telefono, row.cliente_id]
      .some((value) => String(value || "").toLocaleLowerCase("es-CO").includes(needle)));
  }, [query, segmentData.clientes]);
  const summary = segmentData.segmento || {};
  const exclusions = summary.exclusiones || {};
  const filterLabels = {
    TODOS_CLIENTES: "Todos los contactos (todos los clientes)",
    UNA_COMPRA: "Clientes con una sola compra",
    COMPRAS_EXACTAS: "Clientes con exactamente N compras",
    COMPRAS_MINIMAS: "Clientes con al menos N compras",
    TOP_FACTURACION: "Top clientes por facturación",
    TOP_COMPRAS: "Top clientes por cantidad de compras",
  };
  function updateDraft(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }
  function changeFilter(value) {
    setDraft((current) => ({
      ...current,
      tipo_filtro: value,
      ...(value === "TODOS_CLIENTES" ? { fecha_desde: "", fecha_hasta: "" } : {}),
    }));
    setExclusionReason("");
  }
  function apply(event) {
    event.preventDefault();
    const desde = draft.fecha_desde || "";
    const hasta = draft.fecha_hasta || "";
    setApplied({
      ...draft,
      fecha_desde: desde && hasta && desde > hasta ? hasta : desde,
      fecha_hasta: desde && hasta && desde > hasta ? desde : hasta,
      cantidad: Number(draft.cantidad || 1),
      limite: Number(draft.limite || 5),
    });
  }
  function selectImage(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size > 4 * 1024 * 1024) {
      setCampaignStatus("La imagen debe ser JPG, PNG o WEBP y pesar máximo 4 MB."); return;
    }
    const reader = new FileReader();
    reader.onload = () => setCampaign((current) => ({ ...current, imagen: String(reader.result || "") }));
    reader.readAsDataURL(file);
  }
  async function sendAdminTest(campanaId, nombre) {
    if (!campanaId || !whatsappState.conectado) return;
    const confirmed = window.confirm(`Enviar la prueba de “${nombre || "esta campaña"}” únicamente a los administradores activos con WhatsApp configurado. No se enviará a clientes. ¿Continuar?`);
    if (!confirmed) return;
    setCampaignStatus("Enviando prueba solo a administradores…");
    try {
      const response = await fetch("/api/dashboard/envios-whatsapp/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "probar_administradores", datos: { campana_id: campanaId } }) });
      const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible enviar la prueba.");
      const data = payload.data || {};
      setCampaignStatus(`${payload.mensaje} Aceptados por el gateway: ${data.aceptados || 0}. Fallidos: ${data.fallidos || 0}.`);
      loadCampaigns();
    } catch (error) { setCampaignStatus(error.message || "No fue posible enviar la prueba."); }
  }
  async function createCampaign(event) {
    event.preventDefault();
    setCampaignStatus("Guardando borrador…");
    try {
      const response = await fetch("/api/dashboard/envios-whatsapp/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "crear", datos: { nombre: campaign.nombre, mensaje: campaign.mensaje, imagen: campaign.imagen, segmento: { ...summary, filtro: applied }, destinatarios: rows.map((row) => ({ cliente_id: row.cliente_id, telefono: row.telefono, cliente: row.cliente })) } }) });
      const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible guardar la campaña.");
      setCampaignStatus("Borrador guardado. WhatsApp sigue desconectado; no se enviaron mensajes.");
      setCampaign({ nombre: "", mensaje: "", imagen: "" }); setShowCampaign(false); loadCampaigns();
    } catch (error) { setCampaignStatus(error.message || "No fue posible guardar la campaña."); }
  }
  async function controlCampaign(operacion, campanaId) {
    setCampaignStatus("Actualizando campaña…");
    try {
      const response = await fetch("/api/dashboard/envios-whatsapp/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion, datos: { campana_id: campanaId } }) });
      const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible actualizar la campaña.");
      setCampaignStatus(payload.mensaje); loadCampaigns();
    } catch (error) { setCampaignStatus(error.message || "No fue posible actualizar la campaña."); }
  }
  async function prepareCampaignApproval(campanaId, nombre) {
    setCampaignStatus("Calculando los destinatarios reales que se congelarán…");
    try {
      const preparedResponse = await fetch("/api/dashboard/envios-whatsapp/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "preparar_aprobacion", datos: { campana_id: campanaId } }) });
      const preparedPayload = await preparedResponse.json();
      if (!preparedResponse.ok || !preparedPayload.ok) throw new Error(preparedPayload.mensaje || "No fue posible revisar la campaña.");
      const prepared = preparedPayload.data || {};
      const confirmed = window.confirm(`Vas a congelar exactamente ${prepared.destinatarios || 0} destinatarios para “${nombre || "esta campaña"}”. No se enviará ningún mensaje todavía y el filtro no podrá cambiar esta campaña. ¿Confirmas?`);
      if (!confirmed) { setCampaignStatus("La campaña continúa como borrador; no se congeló ningún destinatario."); return; }
      setCampaignStatus("Congelando destinatarios y preparando cola…");
      const response = await fetch("/api/dashboard/envios-whatsapp/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "aprobar", datos: { campana_id: campanaId, huella: prepared.huella } }) });
      const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible congelar los destinatarios.");
      setCampaignStatus(payload.mensaje);
      loadCampaigns();
    } catch (error) { setCampaignStatus(error.message || "No fue posible preparar la campaña."); }
  }
  async function startCampaign(campanaId, nombre, pending) {
    const confirmed = window.confirm(`Vas a iniciar el envío de “${nombre || "esta campaña"}” a ${pending || 0} destinatarios congelados. Se enviarán como máximo 2 mensajes por minuto y podrás pausarlo. ¿Confirmas?`);
    if (!confirmed) return;
    setCampaignStatus("Iniciando despacho controlado…");
    try {
      const response = await fetch("/api/dashboard/envios-whatsapp/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "iniciar", datos: { campana_id: campanaId } }) });
      const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible iniciar el envío.");
      setCampaignStatus(payload.mensaje);
      loadCampaigns();
    } catch (error) { setCampaignStatus(error.message || "No fue posible iniciar el envío."); }
  }
  return <section className="module-panel whatsapp-shipments-module">
    <header className="module-heading whatsapp-shipments-heading">
      <div>
        <p className="eyebrow">SEGMENTACIÓN SEGURA</p>
        <h2>Envíos WhatsApp</h2>
        <span className="record-count">Pruebas internas habilitadas · los envíos a clientes siguen bloqueados.</span>
      </div>
      <span className={`whatsapp-safety-pill ${whatsappState.conectado ? "connected" : String(whatsappState.estado || "").includes("DESCONECTADO") ? "disconnected" : ""}`}><span /> WhatsApp {String(whatsappState.estado || "DESCONECTADO").toLowerCase()}</span>
    </header>
    <div className="whatsapp-safety-note">
      <strong>Modo lectura y preparación</strong>
      <span>Solo las pruebas explícitas llegan a administradores. Los clientes solo reciben mensajes después de congelar el filtro y confirmar “Iniciar envío”.</span>
    </div>
    <form className="whatsapp-segment-form" onSubmit={apply}>
      <label>Segmento
        <select value={draft.tipo_filtro} onChange={(event) => changeFilter(event.target.value)}>
          {Object.entries(filterLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </select>
      </label>
      {(draft.tipo_filtro === "COMPRAS_EXACTAS" || draft.tipo_filtro === "COMPRAS_MINIMAS") && <label>Cantidad de compras
        <input type="number" min="1" max="10000" value={draft.cantidad} onChange={(event) => updateDraft("cantidad", event.target.value)} />
      </label>}
      {(draft.tipo_filtro === "TOP_FACTURACION" || draft.tipo_filtro === "TOP_COMPRAS") && <label>Cantidad del top
        <input type="number" min="1" max="100" value={draft.limite} onChange={(event) => updateDraft("limite", event.target.value)} />
      </label>}
      <label>Desde
        <input type="date" disabled={draft.tipo_filtro === "TODOS_CLIENTES"} value={draft.fecha_desde} max={draft.fecha_hasta || undefined} onChange={(event) => updateDraft("fecha_desde", event.target.value)} />
      </label>
      <label>Hasta
        <input type="date" disabled={draft.tipo_filtro === "TODOS_CLIENTES"} value={draft.fecha_hasta} min={draft.fecha_desde || undefined} onChange={(event) => updateDraft("fecha_hasta", event.target.value)} />
      </label>
      <small className="whatsapp-date-help">{draft.tipo_filtro === "TODOS_CLIENTES" ? "Este segmento incluye todos los contactos; las fechas no aplican." : "Las fechas filtran las compras del segmento e incluyen ambos días."}</small>
      <button className="primary-action" type="submit" disabled={loading}>{loading ? "Consultando…" : "Aplicar filtro"}</button>
    </form>
    {error && <p className="action-status error" role="alert">{error}</p>}
    <div className="whatsapp-segment-summary">
      <div><small>Incluidos</small><strong>{summary.total_incluidos ?? 0}</strong><span>Con teléfono válido y único</span></div>
      <div><small>Con compra</small><strong>{summary.total_clientes_con_compra ?? 0}</strong><span>Coinciden con el período</span></div>
      <div><small>Excluidos</small><strong>{summary.total_excluidos ?? 0}</strong><span>No entran en este segmento</span></div>
    </div>
    <div className="whatsapp-segment-toolbar">
      <div><p className="eyebrow">FILTRO ACTIVO</p><h3>{summary.etiqueta || "Preparando segmento…"}</h3></div>
      <div className="whatsapp-toolbar-actions"><button type="button" className="primary-action" disabled={!rows.length} onClick={() => { setShowCampaign(true); setCampaignStatus(""); }}>Crear campaña con este filtro</button><label className="table-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar dentro del segmento" /></label></div>
    </div>
    {showCampaign && <form className="whatsapp-campaign-editor" onSubmit={createCampaign}>
      <div><p className="eyebrow">NUEVA CAMPAÑA</p><h3>{rows.length} destinatarios del filtro actual</h3><small>Guarda primero el borrador. La prueba posterior se enviará solo a administradores, nunca a este filtro.</small></div>
      <label>Nombre interno<input required value={campaign.nombre} onChange={(event) => setCampaign((current) => ({ ...current, nombre: event.target.value }))} placeholder="Promoción semanal" /></label>
      <label>Mensaje<textarea required rows="5" value={campaign.mensaje} onChange={(event) => setCampaign((current) => ({ ...current, mensaje: event.target.value }))} placeholder="Hola {{nombre}}, esta semana tenemos una promoción." /><small>Usa <button type="button" className="inline-token" onClick={() => setCampaign((current) => ({ ...current, mensaje: `${current.mensaje}{{nombre}}` }))}>{"{{nombre}}"}</button> para insertar el nombre guardado del cliente.</small></label>
      <label>Imagen opcional<input type="file" accept="image/jpeg,image/png,image/webp" onChange={selectImage} /></label>
      {campaign.imagen && <img className="whatsapp-campaign-preview" src={campaign.imagen} alt="Vista previa de la campaña" />}
      <div className="whatsapp-message-preview"><small>VISTA PREVIA</small><p>{(campaign.mensaje || "Hola {{nombre}}, aquí aparecerá tu mensaje.").replaceAll("{{nombre}}", rows[0]?.cliente || "María")}</p></div>
      <div className="whatsapp-campaign-actions"><button type="button" className="secondary-action" onClick={() => setShowCampaign(false)}>Cancelar</button><button type="submit" className="primary-action">Guardar borrador</button></div>
    </form>}
    {campaignStatus && <p className="action-status" role="status">{campaignStatus}</p>}
    {!!campaigns.length && <section className="whatsapp-campaign-list"><p className="eyebrow">CAMPAÑAS</p>{campaigns.map((item) => { const total = Number(item.recipient_count || 0); const sent = Number(item.enviados || 0); const pending = Number(item.pendientes || 0); const progress = total ? Math.round((sent / total) * 100) : 0; const remaining = Math.ceil(pending / 2); const acceptedTests = Number(item.pruebas_aceptadas || 0); const failedTests = Number(item.pruebas_fallidas || 0); const ready = item.status === "LISTA_PARA_ENVIO"; const running = item.status === "EN_COLA"; return <article key={item.campaign_id}><div><b>{item.name}</b><span>{total} contactos · {ready ? "LISTA PARA ENVÍO" : running ? "ENVIANDO" : item.status}</span></div><div className="campaign-progress"><span style={{ width: `${progress}%` }} /></div><small>{sent} aceptados · {pending} pendientes · {item.fallidos || 0} fallidos · estimado {remaining} min a 2/min</small>{ready && <small>Destinatarios congelados. No se enviará hasta que confirmes “Iniciar envío”.</small>}{running && <small>Despacho activo: máximo 2 mensajes por minuto.</small>}{(acceptedTests || failedTests) ? <small>Pruebas internas: {acceptedTests} aceptadas por el gateway · {failedTests} fallidas</small> : <small>Aún no se ha enviado una prueba a administradores.</small>}<div className="whatsapp-campaign-actions">{item.status === "BORRADOR" && <><button type="button" className="secondary-action" onClick={() => sendAdminTest(item.campaign_id, item.name)} disabled={!whatsappState.conectado}>Enviar prueba a administradores</button><button type="button" className="primary-action" onClick={() => prepareCampaignApproval(item.campaign_id, item.name)}>Aprobar y preparar envío</button></>}{ready && <button type="button" className="primary-action" onClick={() => startCampaign(item.campaign_id, item.name, pending)} disabled={!whatsappState.conectado || !pending}>Iniciar envío</button>}{item.status === "PAUSADA" && <button type="button" className="secondary-action" onClick={() => controlCampaign("reanudar", item.campaign_id)} disabled={!whatsappState.conectado}>Reanudar</button>}{running && <button type="button" className="secondary-action" onClick={() => controlCampaign("pausar", item.campaign_id)}>Pausar</button>}{!['CANCELADA','COMPLETADA'].includes(item.status) && <button type="button" className="danger-action" onClick={() => controlCampaign("cancelar", item.campaign_id)}>Cancelar</button>}</div></article>; })}</section>}
    <div className="whatsapp-exclusions">
      {[
        ["sin_pedidos", `${exclusions.sin_pedidos || 0} sin compras`],
        ["no_cumple_filtro", `${exclusions.no_cumple_filtro || 0} fuera del filtro`],
        ["sin_telefono", `${exclusions.sin_telefono || 0} sin teléfono válido`],
        ["telefono_duplicado", `${exclusions.telefono_duplicado || 0} teléfono(s) duplicado(s)`],
      ].map(([reason, label]) => <button type="button" className={exclusionReason === reason ? "active" : ""} key={reason} onClick={() => setExclusionReason(exclusionReason === reason ? "" : reason)}>{label}</button>)}
    </div>
    {exclusionReason && <div className="whatsapp-exclusion-detail">
      <strong>Detalle de la exclusión</strong>
      {(exclusions.detalle?.[exclusionReason] || []).map((row, index) => <span key={row.cliente_id || row.telefono || index}>{row.cliente || "Cliente sin nombre"} · {row.telefono || "Sin teléfono"}</span>)}
      {!exclusions.detalle?.[exclusionReason]?.length && <span>No hay contactos en esta razón.</span>}
    </div>}
    <div className="whatsapp-customer-list">
      {rows.map((row, index) => <article className="whatsapp-customer-card" key={row.cliente_id || row.telefono || index}>
        <div className="whatsapp-customer-main"><span className="whatsapp-customer-index">{index + 1}</span><div><strong>{row.cliente || "Cliente sin nombre"}</strong><small>{row.telefono} · {row.cliente_id || "Sin ID canónico"}</small></div></div>
        <div className="whatsapp-customer-metrics"><span><small>Compras</small><b>{row.cantidad_pedidos}</b></span><span><small>Facturado</small><b>{formatCurrency(row.valor_facturado)}</b></span><span><small>Última compra</small><b>{row.ultima_compra || "—"}</b></span></div>
      </article>)}
      {!rows.length && <div className="whatsapp-empty">No hay clientes para este segmento o búsqueda.</div>}
    </div>
  </section>;
}

function TransferenciasModule({ rows, onRefresh }) {
  const [statusFilter, setStatusFilter] = useState("TODAS");
  const [methodFilter, setMethodFilter] = useState("TRANSFERENCIA");
  const [query, setQuery] = useState("");
  const transferOrderIds = [
    "PED-DE018F4C7A56456FABCAAAEA04E7FFF6",
    "PED-D2CD82B78508406DAC26EB84A356BC29",
    "PED-917AA2A4347F4E86A0D2F04D59422876",
    "PED-8B618F9846684B469017B06C7B787A9A",
  ];
  const transferOrderPosition = new Map(transferOrderIds.map((id, index) => [id, index]));
  const statuses = ["TODAS", "PENDIENTE_VALIDACION", "APLICADA", "RECHAZADO"];
  const scopedRows = useMemo(() => {
    const selected = rows.filter((row) =>
      transferOrderPosition.has(String(row.pedido || "").trim()) &&
      (methodFilter === "TODOS" || String(row.metodo || "").toUpperCase() === methodFilter)
    );
    const byOrder = new Map();
    selected.forEach((row) => {
      const orderId = String(row.pedido || "").trim();
      const current = byOrder.get(orderId);
      const priority = (value) => String(value || "").toUpperCase() === "APLICADA" ? 2 : String(value || "").toUpperCase() === "PENDIENTE_VALIDACION" ? 1 : 0;
      if (!current || priority(row.estado) > priority(current.estado)) byOrder.set(orderId, row);
    });
    return [...byOrder.values()].sort((left, right) =>
      (transferOrderPosition.get(String(left.pedido || "").trim()) ?? Number.MAX_SAFE_INTEGER) -
      (transferOrderPosition.get(String(right.pedido || "").trim()) ?? Number.MAX_SAFE_INTEGER)
    );
  }, [methodFilter, rows]);
  const visible = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("es-CO");
    return [...scopedRows]
      .filter((row) => statusFilter === "TODAS" || String(row.estado || "").toUpperCase() === statusFilter)
      .filter((row) => !needle || [row.cliente, row.telefono, row.pedido, row.referencia]
        .some((value) => String(value || "").toLocaleLowerCase("es-CO").includes(needle)))
      .sort((left, right) => String(right.fecha || "").localeCompare(String(left.fecha || "")));
  }, [query, scopedRows, statusFilter]);
  const pending = scopedRows.filter((row) => /PENDIENTE/i.test(String(row.estado || "")));
  const approvedTotal = scopedRows.reduce((total, row) => total + Number(row.aprobado || 0), 0);
  const pendingTotal = pending.reduce((total, row) => total + Number(row.reportado || 0), 0);
  const statusLabel = (value) => {
    const normalized = String(value || "").toUpperCase();
    if (normalized === "PENDIENTE_VALIDACION") return "Pendiente de validar";
    if (normalized === "APLICADA") return "Aplicada";
    if (normalized === "RECHAZADO") return "Rechazada";
    return value || "Sin estado";
  };
  return <section className="module-panel operational-panel transferencias-module">
    <div className="module-heading">
      <div><p className="eyebrow">CONTROL DE PAGOS</p><h2>Transferencias</h2><span className="record-count">Revisa el estado y el valor de cada operación recibida.</span></div>
      <button type="button" className="route-refresh" onClick={() => onRefresh?.()}>Actualizar</button>
    </div>
    <div className="transfer-kpis">
      <div><span>{methodFilter === "TRANSFERENCIA" ? "Transferencias" : "Pagos"}</span><strong>{scopedRows.length}</strong></div>
      <div className="warning"><span>Pendiente de validar</span><strong>{pending.length}</strong><small>{formatCurrency(pendingTotal)}</small></div>
      <div className="success"><span>Total aprobado</span><strong>{formatCurrency(approvedTotal)}</strong></div>
    </div>
    <div className="transfer-toolbar">
      <label className="table-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar cliente, celular, pedido o referencia" /></label>
      <div className="transfer-status-filters" role="group" aria-label="Filtrar tipo de pago">
        <button type="button" className={methodFilter === "TRANSFERENCIA" ? "active" : ""} onClick={() => setMethodFilter("TRANSFERENCIA")}>Transferencias</button>
        <button type="button" className={methodFilter === "TODOS" ? "active" : ""} onClick={() => setMethodFilter("TODOS")}>Todos los pagos</button>
      </div>
      <div className="transfer-status-filters" role="group" aria-label="Filtrar estado de transferencias">
        {statuses.map((value) => <button type="button" key={value} className={statusFilter === value ? "active" : ""} onClick={() => setStatusFilter(value)}>{value === "TODAS" ? "Todas" : value === "PENDIENTE_VALIDACION" ? "Pendientes" : value === "APLICADA" ? "Aplicadas" : "Rechazadas"}</button>)}
      </div>
    </div>
    <div className="transfer-table-intro"><span>{visible.length} pedido(s) visibles</span><small>Se conserva una operación por pedido; los demás registros permanecen en el historial.</small></div>
    <div className="transfer-list" aria-label="Listado de transferencias">
      {visible.map((row) => {
        const status = String(row.estado || "").toUpperCase();
        const statusClass = status === "APLICADA" ? "applied" : status === "RECHAZADO" ? "rejected" : "pending";
        return <article className={`transfer-card ${statusClass}`} key={row.referencia || `${row.pedido}-${row.fecha}`}>
          <header className="transfer-card-header">
            <div className="transfer-card-person">
              <strong>{row.cliente || "Cliente sin nombre"}</strong>
              <span>{formatPhoneVisible(row.telefono) || "Teléfono no registrado"}</span>
            </div>
            <span className={`transfer-state ${statusClass}`}>{statusLabel(row.estado)}</span>
          </header>
          <div className="transfer-card-meta">
            <div><small>Pedido</small><b>{row.pedido || "—"}</b></div>
            <div><small>Fecha</small><b>{String(row.fecha || "—").replace("T", " ").slice(0, 16)}</b></div>
            <div><small>Referencia</small><b>{row.referencia || "—"}</b></div>
          </div>
          <div className="transfer-card-amounts">
            <div><span>Valor reportado</span><strong>{formatCurrency(row.reportado || 0)}</strong></div>
            <div><span>Valor aprobado</span><strong>{formatCurrency(row.aprobado || 0)}</strong></div>
          </div>
          <footer className="transfer-card-footer">
            <span><b>Método:</b> {row.metodo || "Transferencia"}</span>
            {row.fecha_entrega && <span><b>Entrega:</b> {String(row.fecha_entrega).slice(0, 10)}</span>}
          </footer>
        </article>;
      })}
      {!visible.length && <div className="transfer-empty">No hay transferencias para los filtros seleccionados.</div>}
    </div>
  </section>;
}

function SwipeableCarteraCard({ row, selected = false, onOpen, onQuickCash, children, cardClassName = "" }) {
  const [revealed, setRevealed] = useState("");
  const saldo = Number(row?.saldo_pendiente || 0);
  return <SwipeReveal
    className="cartera-swipe"
    revealed={revealed}
    onReveal={setRevealed}
    onClose={() => setRevealed("")}
    rightAction={saldo > 0 ? {
      label: "Cobrar efectivo",
      icon: <CircleDollarSign size={19} aria-hidden="true" />,
      onClick: () => onQuickCash?.(row),
    } : null}
  >
    <button
      type="button"
      className={`cartera-card${selected ? " selected" : ""}${cardClassName}`}
      onClick={() => {
        if (revealed) { setRevealed(""); return; }
        onOpen?.(row);
      }}
      aria-label={`${row?.cliente || "Cliente"}. ${saldo > 0 ? "Desliza a la izquierda para cobrar en efectivo." : ""}`}
    >
      {children}
    </button>
  </SwipeReveal>;
}

function carteraClientKey(row) {
  return String(row?.cliente_id || row?.clienteId || row?.telefono || row?.cliente || "")
    .trim()
    .toLocaleLowerCase("es-CO");
}

function RouteReceivablesModule({ rows, onRefresh }) {
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [selected, setSelected] = useState(null);
  const [valor, setValor] = useState("");
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [observacion, setObservacion] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const paymentPanelRef = useRef(null);
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("es-CO");
    return [...rows]
      .filter((row) => !fechaDesde || String(row.fecha || "").slice(0, 10) >= fechaDesde)
      .filter((row) => !fechaHasta || String(row.fecha || "").slice(0, 10) <= fechaHasta)
      .filter((row) => !needle || [row.cliente, row.telefono, row.pedido]
        .some((value) => String(value || "").toLocaleLowerCase("es-CO").includes(needle)))
      .sort((left, right) => String(right.fecha || "").localeCompare(String(left.fecha || "")));
  }, [rows, query, fechaDesde, fechaHasta]);
  const saldoVisible = filtered.reduce((total, row) => total + Number(row.saldo_pendiente || 0), 0);
  const saldoSeleccionado = Number(selected?.saldo_pendiente || 0);
  const cuentasDelCliente = useMemo(() => {
    if (!selected) return [];
    const key = carteraClientKey(selected);
    return rows.filter((row) => carteraClientKey(row) === key && Number(row.saldo_pendiente || 0) > 0);
  }, [rows, selected]);
  const saldoCliente = cuentasDelCliente.reduce((total, row) => total + Number(row.saldo_pendiente || 0), 0);

  useEffect(() => {
    if (!selected) return;
    const updated = rows.find((row) => row.id === selected.id);
    if (!updated) setSelected(null);
    else setSelected(updated);
  }, [rows]);

  async function registrar(monto) {
    if (!selected) return;
    if (!Number.isFinite(monto) || monto <= 0 || monto > saldoSeleccionado) {
      setStatus("Indica un valor mayor a cero y no superior al saldo pendiente.");
      return;
    }
    setBusy(true); setStatus("Registrando abono…");
    try {
      const response = await fetch("/api/dashboard/rutas/operacion", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: "registrar_abono_cartera_repartidor",
          datos: {
            cartera_id: selected.id,
            valor: monto,
            metodo_pago: metodoPago,
            observacion,
            operacion_cartera_id: createOperationId(`ABONO-RUTA-${selected.id}`),
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible registrar el abono.");
      const saldo = Number(payload.data?.saldo_pendiente || 0);
      setStatus(saldo === 0 ? "Pago total registrado. La cuenta quedó en cero." : `Abono registrado. Saldo pendiente: ${formatCurrency(saldo)}.`);
      setValor(""); setObservacion("");
      onRefresh?.({ notificar: false });
    } catch (error) {
      setStatus(error.message || "No fue posible registrar el abono.");
    } finally { setBusy(false); }
  }

  function prepararCobroRapido(row) {
    const saldo = Number(row?.saldo_pendiente || 0);
    if (!Number.isFinite(saldo) || saldo <= 0) return;
    setSelected(row);
    setValor(String(saldo));
    setMetodoPago("EFECTIVO");
    setObservacion("");
    setStatus("Cobro total en efectivo listo para confirmar.");
    window.requestAnimationFrame(() => paymentPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }));
  }

  return <section className="module-panel cartera-module route-receivables-module">
    <div className="module-heading">
      <div><p className="eyebrow">COBROS EN RUTA</p><h2>Cartera</h2><span className="record-count">Registra únicamente el dinero que recibes tú. El efectivo y las transferencias quedan en tu caja de hoy.</span></div>
      <div className="cartera-total"><span>Por cobrar visible</span><strong>{formatCurrency(saldoVisible)}</strong></div>
    </div>
    <div className="cartera-toolbar route-receivables-toolbar">
      <label className="table-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por cliente o celular" /></label>
      <button type="button" className={`cartera-filters-toggle${filtersOpen ? " is-open" : ""}`} onClick={() => setFiltersOpen((open) => !open)} aria-expanded={filtersOpen}><Filter size={16} /> Filtros{fechaDesde || fechaHasta ? " activos" : ""}</button>
      <button type="button" className="route-refresh" onClick={() => onRefresh?.({ notificar: false })}>Actualizar</button>
    </div>
    {filtersOpen && <div className="cartera-filter-panel"><label>Desde<input type="date" value={fechaDesde} onChange={(event) => setFechaDesde(event.target.value)} /></label><label>Hasta<input type="date" value={fechaHasta} onChange={(event) => setFechaHasta(event.target.value)} /></label><button type="button" className="text-action" onClick={() => { setFechaDesde(""); setFechaHasta(""); }}>Limpiar fechas</button></div>}
    <div className="cartera-list-heading"><span>{filtered.length} cuenta(s) pendiente(s)</span><small>Toca una cuenta para registrar un abono o pago total.</small></div>
    <div className="cartera-cards">
      {filtered.map((row) => <SwipeableCarteraCard key={row.id} row={row} selected={selected?.id === row.id} onOpen={(item) => { setSelected(item); setValor(""); setStatus(""); }} onQuickCash={prepararCobroRapido}>
        <div className="cartera-card-top"><div><b>{row.cliente || "Cliente sin nombre"}</b><small>{formatPhoneVisible(row.telefono) || "Teléfono no registrado"}</small></div><span className="cartera-state pending">Pendiente</span></div>
        <div className="cartera-card-values"><span><small>Saldo pendiente</small><strong>{formatCurrency(row.saldo_pendiente || 0)}</strong></span><span><small>Última venta</small><b>{row.fecha || "—"}</b></span></div>
      </SwipeableCarteraCard>)}
      {!filtered.length && <div className="cartera-empty"><CircleDollarSign size={24} /><b>No hay cartera pendiente para este filtro.</b><span>Prueba con otra fecha, nombre o número.</span></div>}
    </div>
    {selected && <section className="route-receivables-payment" ref={paymentPanelRef} aria-live="polite">
      <div><p className="eyebrow">REGISTRAR COBRO</p><h3>{selected.cliente || "Cliente"}</h3><span>Saldo actual: <b>{formatCurrency(saldoSeleccionado)}</b></span></div>
      {cuentasDelCliente.length > 1 && <div className="cartera-client-summary route-client-summary"><span>Cartera activa del cliente</span><strong>{formatCurrency(saldoCliente)}</strong><small>{cuentasDelCliente.length} cuentas pendientes. Selecciona la cuenta a cobrar:</small><div className="cartera-client-accounts">{cuentasDelCliente.map((cuenta) => <button type="button" key={cuenta.id} className={cuenta.id === selected.id ? "is-active" : ""} onClick={() => { setSelected(cuenta); setValor(""); setStatus(""); }}> {cuenta.pedido || "Cuenta"} · {formatCurrency(cuenta.saldo_pendiente || 0)}</button>)}</div></div>}
      <label>Valor recibido<input type="number" inputMode="decimal" min="1" max={saldoSeleccionado} step="0.01" value={valor} onChange={(event) => setValor(event.target.value)} placeholder={`Máximo ${formatCurrency(saldoSeleccionado)}`} /></label>
      <label>Medio de pago<select value={metodoPago} onChange={(event) => setMetodoPago(event.target.value)}><option value="EFECTIVO">Efectivo</option><option value="TRANSFERENCIA">Transferencia</option></select></label>
      <label>Observación<input value={observacion} onChange={(event) => setObservacion(event.target.value)} placeholder="Opcional" /></label>
      <div className="route-receivables-actions"><button type="button" className="secondary-action" disabled={busy} onClick={() => setSelected(null)}>Cancelar</button><button type="button" className="primary-action" disabled={busy} onClick={() => registrar(Number(valor))}>{busy ? "Registrando…" : "Registrar abono"}</button><button type="button" className="primary-action" disabled={busy} onClick={() => registrar(saldoSeleccionado)}>Pago total {formatCurrency(saldoSeleccionado)}</button></div>
      {status && <p className="action-status">{status}</p>}
    </section>}
  </section>;
}

function CarteraModule({ rows, onRefresh }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [quickCashId, setQuickCashId] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [clientAccountsBusy, setClientAccountsBusy] = useState(false);
  const [showPaid, setShowPaid] = useState(false);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [audit, setAudit] = useState(null);
  const [auditBusy, setAuditBusy] = useState(false);
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("es-CO");
    return [...rows]
      .filter((row) => showPaid || !/pagada/i.test(String(row.estado || "")))
      .filter((row) => !fechaDesde || String(row.fecha || "").slice(0, 10) >= fechaDesde)
      .filter((row) => !fechaHasta || String(row.fecha || "").slice(0, 10) <= fechaHasta)
      .filter((row) => !needle || [row.cliente, row.telefono, row.pedido, row.venta]
        .some((value) => String(value || "").toLocaleLowerCase("es-CO").includes(needle)))
      .sort((left, right) => Number(right.saldo_pendiente || 0) - Number(left.saldo_pendiente || 0));
  }, [query, rows, showPaid, fechaDesde, fechaHasta]);
  const totalPendiente = filtered.reduce((total, row) => total + Number(row.saldo_pendiente || 0), 0);
  const totalAbonado = filtered.reduce((total, row) => total + Number(row.abonos || 0), 0);
  const cuentasPendientes = filtered.filter((row) => Number(row.saldo_pendiente || 0) > 0).length;
  useEffect(() => {
    if (!selected) { setClientAccounts([]); setClientAccountsBusy(false); return; }
    const clienteId = selected.cliente_id || selected.clienteId;
    const fallback = rows.filter((row) => carteraClientKey(row) === carteraClientKey(selected) && Number(row.saldo_pendiente || 0) > 0);
    setClientAccounts(fallback.length ? fallback : [selected]);
    if (!clienteId) return;
    let active = true;
    setClientAccountsBusy(true);
    fetch("/api/dashboard/cartera/operacion", {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ operacion: "consultar_cartera_cliente", datos: { cliente_id: clienteId } }),
    })
      .then(async (response) => ({ response, payload: await response.json().catch(() => ({})) }))
      .then(({ response, payload }) => {
        if (!active || !response.ok || !payload.ok) return;
        const cuentas = (payload.data?.cartera || []).filter((row) => Number(row.saldo_pendiente || 0) > 0 && !/anulada/i.test(String(row.estado || "")));
        if (cuentas.length) setClientAccounts(cuentas);
      })
      .catch(() => { /* La cuenta seleccionada sigue disponible aunque no carguen las demás. */ })
      .finally(() => { if (active) setClientAccountsBusy(false); });
    return () => { active = false; };
  }, [selected?.id, rows]);
  async function auditCartera() {
    if (auditBusy) return;
    setAuditBusy(true);
    setAudit(null);
    try {
      const response = await fetch("/api/dashboard/cartera/operacion", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ operacion: "auditar_integridad", datos: {} }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible auditar la cartera.");
      setAudit(payload.data || {});
    } catch (error) {
      setAudit({ error: error.message || "No fue posible auditar la cartera." });
    } finally { setAuditBusy(false); }
  }
  return <section className="module-panel cartera-module">
    <div className="module-heading">
      <div>
        <p className="eyebrow">COBROS Y SEGUIMIENTO</p>
        <h2>Cartera</h2>
        <span className="record-count">Abre una cuenta para registrar un abono o preparar un recordatorio manual.</span>
      </div>
      <div className="cartera-heading-actions"><div className="cartera-total"><span>Saldo visible</span><strong>{formatCurrency(totalPendiente)}</strong></div><button type="button" className="secondary-action cartera-audit-button" onClick={auditCartera} disabled={auditBusy}>{auditBusy ? "Auditando…" : "Auditar integridad"}</button></div>
    </div>
    <div className="cartera-kpis">
      <div><span>Por cobrar</span><strong>{formatCurrency(totalPendiente)}</strong><small>{cuentasPendientes} cuenta(s) pendiente(s)</small></div>
      <div><span>Abonos registrados</span><strong>{formatCurrency(totalAbonado)}</strong><small>En las cuentas visibles</small></div>
      <div><span>Cuentas visibles</span><strong>{filtered.length}</strong><small>Ordenadas por saldo</small></div>
    </div>
    <div className="cartera-toolbar">
      <label className="table-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar cliente, celular o pedido" /></label>
      <button type="button" className={`cartera-filters-toggle${filtersOpen ? " is-open" : ""}`} onClick={() => setFiltersOpen((open) => !open)} aria-expanded={filtersOpen}><Filter size={16} /> Filtros{fechaDesde || fechaHasta || showPaid ? " activos" : ""}</button>
      <button type="button" className="route-refresh" onClick={() => onRefresh?.()}>Actualizar</button>
    </div>
    {filtersOpen && <div className="cartera-filter-panel"><label className="cartera-date-filter">Desde<input type="date" value={fechaDesde} onChange={(event) => setFechaDesde(event.target.value)} /></label><label className="cartera-date-filter">Hasta<input type="date" value={fechaHasta} onChange={(event) => setFechaHasta(event.target.value)} /></label><label className="cartera-toggle"><input type="checkbox" checked={showPaid} onChange={(event) => setShowPaid(event.target.checked)} /> Incluir pagadas</label><button type="button" className="text-action" onClick={() => { setFechaDesde(""); setFechaHasta(""); setShowPaid(false); }}>Limpiar filtros</button></div>}
    <div className="cartera-list-heading"><span>{filtered.length} cuenta(s)</span><small>Toca un cliente para ver toda su cartera activa y registrar movimientos.</small></div>
    {audit && <section className={`cartera-audit-result${audit.error ? " is-error" : audit.resumen?.estado === "OK" ? " is-ok" : " is-warning"}`} aria-live="polite">
      {audit.error ? <strong>{audit.error}</strong> : <><div><p className="eyebrow">AUDITORÍA DE INTEGRIDAD</p><h3>{audit.resumen?.estado === "OK" ? "Cartera consistente" : "Se encontraron alertas"}</h3><span>{audit.resumen?.cuentas || 0} cuentas · {audit.resumen?.movimientos_aplicados || 0} abonos aplicados · {audit.resumen?.alertas || 0} alerta(s)</span></div>{(audit.alertas || []).length > 0 && <div className="cartera-audit-alerts">{audit.alertas.map((item, index) => <div key={`${item.codigo}-${item.referencia}-${index}`}><b>{item.codigo}</b><span>{item.detalle}</span></div>)}</div>}</>}
    </section>}
    <div className="cartera-cards">
      {filtered.map((row) => {
        const saldo = Number(row.saldo_pendiente || 0);
        const pagada = saldo <= 0 || /pagada/i.test(String(row.estado || ""));
        return <SwipeableCarteraCard key={row.id || `${row.pedido}-${row.venta}`} row={row} cardClassName={pagada ? " is-paid" : ""} onOpen={(item) => { setQuickCashId(""); setSelected(item); }} onQuickCash={(item) => { setQuickCashId(item.id); setSelected(item); }}>
          <div className="cartera-card-top">
            <div><b>{row.cliente || "Cliente sin nombre"}</b><small>{formatPhoneVisible(row.telefono) || "Teléfono no registrado"}</small></div>
            <span className={`cartera-state${pagada ? " paid" : " pending"}`}>{pagada ? "Pagada" : "Pendiente"}</span>
          </div>
          <div className="cartera-card-values">
            <span><small>Valor de la venta</small><b>{formatCurrency(row.valor || 0)}</b></span>
            <span><small>Abonos</small><b>{formatCurrency(row.abonos || 0)}</b></span>
            <span className="cartera-card-balance"><small>Saldo pendiente</small><strong>{formatCurrency(saldo)}</strong></span>
          </div>
          <div className="cartera-card-foot"><span>Pedido {row.pedido || "—"}</span><span>{row.fecha || "Sin fecha"}</span></div>
        </SwipeableCarteraCard>;
      })}
      {!filtered.length && <div className="cartera-empty"><CircleDollarSign size={24} /><b>No hay cuentas para este filtro.</b><span>Prueba con otro cliente o incluye las cuentas pagadas.</span></div>}
    </div>
    {selected && <CarteraDrawer cartera={selected} clientAccounts={clientAccounts} clientAccountsBusy={clientAccountsBusy} quickCash={quickCashId === selected.id} onSelectAccount={(account) => { setQuickCashId(""); setSelected(account); }} onClose={() => { setQuickCashId(""); setSelected(null); }} onChanged={() => { setQuickCashId(""); setSelected(null); onRefresh?.({ notificar: false }); }} />}
  </section>;
}

function CarteraDrawer({ cartera, clientAccounts = [], clientAccountsBusy = false, onSelectAccount, onClose, onChanged, quickCash = false }) {
  const saldo = Number(cartera.saldo_pendiente || 0);
  const [valor, setValor] = useState(quickCash ? String(saldo) : "");
  const [observacion, setObservacion] = useState("");
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [receptorEfectivo, setReceptorEfectivo] = useState("ADMINISTRACION");
  const [repartidorId, setRepartidorId] = useState("");
  const [repartidores, setRepartidores] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [recordatorio, setRecordatorio] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [movimientosError, setMovimientosError] = useState("");
  useEffect(() => {
    if (!quickCash) return;
    setMetodoPago("EFECTIVO");
    setValor(String(saldo));
    setReceptorEfectivo("ADMINISTRACION");
    setRepartidorId("");
  }, [quickCash, cartera.id, saldo]);
  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  useEffect(() => {
    let active = true;
    fetch("/api/dashboard/rutas/operacion", {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ operacion: "listar_repartidores", datos: {} }),
    })
      .then(async (response) => ({ response, payload: await response.json().catch(() => ({})) }))
      .then(({ response, payload }) => {
        if (active && response.ok && payload.ok) setRepartidores((payload.data || {}).repartidores || []);
      })
      .catch(() => { /* El cobro por administración sigue disponible. */ });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    fetch("/api/dashboard/cartera/operacion", {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ operacion: "consultar_movimientos", datos: { cartera_id: cartera.id } }),
    })
      .then(async (response) => ({ response, payload: await response.json().catch(() => ({})) }))
      .then(({ response, payload }) => {
        if (!active) return;
        if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible consultar los movimientos.");
        setMovimientos(payload.data?.movimientos || []);
      })
      .catch((error) => { if (active) setMovimientosError(error.message || "No fue posible consultar los movimientos."); });
    return () => { active = false; };
  }, [cartera.id]);
  async function registrar(monto) {
    if (!Number.isFinite(monto) || monto <= 0 || monto > saldo) {
      const mensaje = "Indica un abono mayor a cero y no superior al saldo pendiente.";
      setStatus(mensaje); notificarAdmin(mensaje, "warning"); return;
    }
    if (metodoPago === "EFECTIVO" && receptorEfectivo === "REPARTIDOR" && !repartidorId) {
      const mensaje = "Selecciona el repartidor que recibió el efectivo.";
      setStatus(mensaje); notificarAdmin(mensaje, "warning"); return;
    }
    setBusy(true); setStatus("Registrando abono…");
    try {
      const response = await fetch("/api/dashboard/cartera/operacion", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ operacion: "registrar_abono", datos: {
          cartera_id: cartera.id, valor: monto, observacion,
          operacion_cartera_id: createOperationId("MCA-ADMIN"),
          metodo_pago: metodoPago,
          repartidor_id: metodoPago === "EFECTIVO" && receptorEfectivo === "REPARTIDOR" ? repartidorId : "",
        } }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || `No fue posible registrar el abono (HTTP ${response.status}).`);
      const nuevoSaldo = Number(payload.data?.saldo_pendiente || 0);
      const trazabilidad = metodoPago === "TRANSFERENCIA"
        ? " Registrado como transferencia; no suma caja del repartidor."
        : receptorEfectivo === "REPARTIDOR"
          ? " Registrado en la caja del repartidor seleccionado."
          : " Registrado bajo administración; no suma caja de reparto.";
      const mensaje = (nuevoSaldo === 0 ? "Pago total registrado. La cuenta quedó pagada." : `Abono registrado. Nuevo saldo: ${formatCurrency(nuevoSaldo)}.`) + trazabilidad;
      setStatus(mensaje); notificarAdmin(mensaje, "success");
      onChanged?.();
    } catch (error) {
      const mensaje = error.message || "No fue posible registrar el abono.";
      setStatus(mensaje); notificarAdmin(mensaje, "error");
    } finally { setBusy(false); }
  }
  function confirmarPagoTotal() {
    confirmarAccionAdmin({
      titulo: "Confirmar pago total",
      mensaje: `Registrar ${formatCurrency(saldo)} como pago total de ${cartera.cliente || "este cliente"}? Esta operación actualiza la cartera y la venta.`,
      confirmar: "Registrar pago total",
      onConfirm: () => registrar(saldo),
    });
  }
  async function prepararRecordatorio() {
    setBusy(true); setStatus("Preparando recordatorio…");
    try {
      const response = await fetch("/api/dashboard/cartera/operacion", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ operacion: "preparar_recordatorio", datos: {
          cartera_id: cartera.id,
          operacion_recordatorio_id: createOperationId("REC-ADMIN"),
        } }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || `No fue posible preparar el recordatorio (HTTP ${response.status}).`);
      setRecordatorio(payload.data?.recordatorio || null);
      setStatus("Recordatorio preparado. El sistema no lo envió ni lo dejó en cola.");
    } catch (error) {
      const mensaje = error.message || "No fue posible preparar el recordatorio.";
      setStatus(mensaje); notificarAdmin(mensaje, "error");
    } finally { setBusy(false); }
  }
  const phoneHref = formatPhoneHref(cartera.telefono);
  const whatsappRecordatorioUrl = recordatorio?.telefono && recordatorio?.mensaje
    ? `https://wa.me/57${String(recordatorio.telefono).replace(/^57/, "")}?text=${encodeURIComponent(recordatorio.mensaje)}`
    : "";
  return <div className="drawer-backdrop" role="presentation" onMouseDown={onClose}>
    <aside className="drawer cartera-drawer" role="dialog" aria-modal="true" aria-label="Detalle de cartera" onMouseDown={(event) => event.stopPropagation()}>
      <button type="button" className="drawer-close" onClick={onClose} aria-label="Cerrar"><X size={21} /></button>
      <p className="eyebrow">{quickCash ? "COBRO RÁPIDO EN EFECTIVO" : "CUENTA POR COBRAR"}</p><h2>{cartera.cliente || "Cliente sin nombre"}</h2>
      <p className="drawer-subtitle">Venta {cartera.venta || "—"} · Pedido {cartera.pedido || "—"}</p>
      {clientAccounts.length > 1 && <section className="cartera-client-summary"><div><span>Cartera activa del cliente</span><strong>{formatCurrency(clientAccounts.reduce((total, item) => total + Number(item.saldo_pendiente || 0), 0))}</strong><small>{clientAccounts.length} cuentas pendientes{clientAccountsBusy ? " · Actualizando…" : ""}</small></div><div className="cartera-client-accounts">{clientAccounts.map((cuenta) => <button type="button" key={cuenta.id} className={cuenta.id === cartera.id ? "is-active" : ""} onClick={() => onSelectAccount?.(cuenta)}><span>{cuenta.pedido || "Cuenta"}</span><b>{formatCurrency(cuenta.saldo_pendiente || 0)}</b></button>)}</div></section>}
      <div className="cartera-balance"><span>Saldo pendiente</span><strong>{formatCurrency(saldo)}</strong><small>Abonos acumulados: {formatCurrency(cartera.abonos || 0)}</small></div>
      <div className="detail-list cartera-detail-list">
        <div><span>Teléfono</span>{phoneHref ? <a href={phoneHref} target="_blank" rel="noreferrer">{formatPhoneVisible(cartera.telefono)}</a> : <b>No registrado</b>}</div>
        <div><span>Fecha de venta</span><b>{cartera.fecha || "—"}</b></div>
        <div><span>Estado</span><b>{cartera.estado || "Pendiente"}</b></div>
        {cartera.motivo && <div><span>Origen</span><b>{cartera.motivo}</b></div>}
      </div>
      <section className="cartera-history">
        <p className="eyebrow">TRAZABILIDAD</p><h3>Abonos registrados</h3>
        {movimientos.map((movimiento) => <div className="cartera-history-row" key={movimiento.id}>
          <div><b>{formatCurrency(movimiento.valor || 0)}</b><small>{movimiento.fecha_recepcion || "Fecha no registrada"}</small></div>
          <div><span>{String(movimiento.metodo_pago || "").toLowerCase() === "transferencia" ? "Transferencia" : "Efectivo"}</span><small>{movimiento.receptor_rol === "REPARTIDOR" ? "Recibió repartidor" : "Recibió administración"}</small></div>
        </div>)}
        {!movimientos.length && !movimientosError && <p className="cartera-history-empty">Aún no hay abonos registrados para esta cuenta.</p>}
        {movimientosError && <p className="cartera-history-empty error">{movimientosError}</p>}
      </section>
      {saldo > 0 && <form className="admin-order-form cartera-payment-form" onSubmit={(event) => { event.preventDefault(); registrar(Number(valor)); }}>
        <h3>{quickCash ? "Confirma el pago total" : "Registrar abono"}</h3>
        <label>Valor recibido<input type="number" inputMode="decimal" min="1" max={saldo} step="0.01" value={valor} onChange={(event) => setValor(event.target.value)} placeholder={`Máximo ${formatCurrency(saldo)}`} required /></label>
        <label>Medio de pago<select value={metodoPago} onChange={(event) => setMetodoPago(event.target.value)}><option value="EFECTIVO">Efectivo</option><option value="TRANSFERENCIA">Transferencia</option></select></label>
        {metodoPago === "EFECTIVO" ? <section className="cartera-cash-receiver">
          <p>¿Quién recibió el efectivo?</p>
          <label><input type="radio" name="receptor-efectivo" checked={receptorEfectivo === "ADMINISTRACION"} onChange={() => setReceptorEfectivo("ADMINISTRACION")} /> Administración <small>No se suma a la caja del repartidor.</small></label>
          <label><input type="radio" name="receptor-efectivo" checked={receptorEfectivo === "REPARTIDOR"} onChange={() => setReceptorEfectivo("REPARTIDOR")} /> Repartidor <small>Se suma a su caja desde hoy.</small></label>
          {receptorEfectivo === "REPARTIDOR" && <label>Repartidor que recibió<select value={repartidorId} onChange={(event) => setRepartidorId(event.target.value)} required><option value="">Seleccionar repartidor…</option>{repartidores.map((repartidor) => <option key={repartidor.usuario_operativo_id} value={repartidor.usuario_operativo_id}>{repartidor.nombre}</option>)}</select></label>}
        </section> : <p className="cartera-payment-note">La transferencia queda trazada en Cartera y no aumenta la caja de ningún repartidor.</p>}
        <label>Observación<input value={observacion} onChange={(event) => setObservacion(event.target.value)} placeholder="Opcional" /></label>
        <button type="submit" className="primary-action" disabled={busy}>{busy ? "Registrando…" : "Registrar abono"}</button>
        <button type="button" className="secondary-action" disabled={busy} onClick={confirmarPagoTotal}>Registrar pago total {formatCurrency(saldo)}</button>
      </form>}
      {saldo > 0 && <section className="cartera-reminder">
        <p className="eyebrow">RECORDATORIO</p>
        <h3>Mensaje manual por WhatsApp</h3>
        <p>Prepararlo no envía mensajes, no activa el bot y no crea una cola. Revísalo y, si estás de acuerdo, envíalo tú desde WhatsApp.</p>
        {!recordatorio ? <button type="button" className="secondary-action" disabled={busy} onClick={prepararRecordatorio}>Preparar recordatorio</button> : <>
          <textarea readOnly value={recordatorio.mensaje} aria-label="Vista previa del recordatorio" />
          <a className="primary-action cartera-whatsapp-link" href={whatsappRecordatorioUrl} target="_blank" rel="noreferrer">Abrir WhatsApp y enviar manualmente</a>
        </>}
      </section>}
      {status && <p className="action-status">{status}</p>}
    </aside>
  </div>;
}

function OperationalModule({ active, data, onRefresh, deliveryOnly = false, pushOrderId = "", onPushOrderOpened, orderQuery = {}, onOrderQueryChange, routeQuery = {}, onRouteQueryChange }) {
  const config = moduleConfig[active];
  const [tab, setTab] = useState(config?.tabs?.[0]);
  const [inventoryPolicy, setInventoryPolicy] = useState("TODAS");
  const [orderDateFilter, setOrderDateFilter] = useState(orderQuery.scope || "PROXIMAS");
  const [orderProductFilter, setOrderProductFilter] = useState(orderQuery.productoCodigo || "");
  const [orderProductMode, setOrderProductMode] = useState(orderQuery.productoModo || "TODOS");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedWaitlist, setSelectedWaitlist] = useState(null);
  const [bulkAction, setBulkAction] = useState("marcar_preparacion");
  const [bulkDate, setBulkDate] = useState("");
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const pagination = data.paginacion || {};
  useEffect(() => {
    if (active !== "pedidos") return;
    setOrderDateFilter(orderQuery.scope || "PROXIMAS");
    setOrderProductFilter(orderQuery.productoCodigo || "");
    setOrderProductMode(orderQuery.productoModo || "TODOS");
    setTab(orderQuery.estado ? pedidoTabEstado(orderQuery.estado) : "Todos");
  }, [active, orderQuery.scope, orderQuery.estado, orderQuery.productoCodigo, orderQuery.productoModo, orderQuery.offset]);
  if (!config) return null;
  if (active === "rutas")
    return (
      <section className="module-panel operational-panel">
        <div className="module-heading">
          <div>
            <p className="eyebrow">GUÍA DE REPARTO</p>
            <h2>{deliveryOnly ? "Mi ruta asignada" : "Rutas operativas"}</h2>
            <span className="record-count">
              {deliveryOnly
                ? "Tus paradas asignadas para hoy"
                : "Pedidos confirmados listos para organizar y entregar"}
            </span>
          </div>
          <div className="route-heading-tools">
            <div className="heading-tools">
              <Truck size={16} />
              <span>{data.total_rutas ?? (data.rutas || []).length} paradas {data.total_rutas === (data.rutas || []).length ? "cargadas" : "en este filtro"}</span>
            </div>
            <button
              type="button"
              className="route-refresh"
              onClick={onRefresh}
            >
              Actualizar ruta
            </button>
          </div>
        </div>
        {deliveryOnly && <RouteDailyCashCard summary={data.caja_diaria} />}
        <RouteGuide
          rows={data.rutas || []}
          onRefresh={onRefresh}
          deliveryOnly={deliveryOnly}
          defaultRouteOrigin={data.origen_ruta_predefinido}
          routeQuery={routeQuery}
          onRouteQueryChange={onRouteQueryChange}
        />
        {deliveryOnly && <DeferredModule><AdminPushNotifications deliveryOnly /></DeferredModule>}
      </section>
    );
  const sourceRows =
    tab === "Movimientos"
      ? data.movimientos || []
      : tab === "Cartera"
        ? data.cartera || []
        : data[config.key] || [];
  const pedidoRowsForTab = sourceRows;
  const filteredRows =
    active === "pedidos"
      ? pedidoRowsForTab
        : active === "rutas" && tab !== "Todas"
          ? sourceRows.filter((row) => row.estado === tab)
          : active === "inventario" && inventoryPolicy !== "TODAS"
            ? sourceRows.filter(
                (row) =>
                  String(row.politica || "").toUpperCase() === inventoryPolicy,
              )
            : sourceRows;
  const dateRows = filteredRows;
  const productFilterOptions = (() => {
    if (active !== "pedidos") return [];
    const options = new Map();
    for (const row of dateRows) {
      for (const item of row.productos || []) {
        const codigo = String(item.codigo || "").trim();
        if (!codigo || options.has(codigo)) continue;
        options.set(codigo, {
          codigo,
          producto: String(item.producto || codigo).trim(),
          presentacion: String(item.presentacion || "").trim(),
        });
      }
    }
    return [...options.values()].sort((left, right) =>
      `${left.producto} ${left.presentacion}`.localeCompare(
        `${right.producto} ${right.presentacion}`,
        "es",
      ),
    );
  })();
  const productFilteredRows = dateRows;
  const rows =
    active === "pedidos"
      ? [...productFilteredRows].sort((left, right) => {
          const difference =
            adminDateKey(left.fecha) - adminDateKey(right.fecha);
          return orderDateFilter === "PROXIMAS" ? difference : -difference;
        })
      : dateRows;
  const pedidosVisiblesTotal = active === "pedidos"
    ? productFilteredRows.reduce((total, row) => total + Number(row.total || 0), 0)
    : 0;
  const productosPendientes = (() => {
    if (active !== "pedidos") return [];
    const agrupados = new Map();
    for (const pedido of productFilteredRows) {
      for (const item of pedido.productos_pendientes || []) {
        const codigo = String(item.codigo || "").trim();
        const producto = String(item.producto || codigo || "Producto").trim();
        const presentacion = String(item.presentacion || "").trim();
        const unidad = String(item.unidad || "unidades").trim();
        const clave = `${codigo}|${presentacion}|${unidad}`;
        const existente = agrupados.get(clave) || {
          codigo,
          producto,
          presentacion,
          unidad,
          cantidad: 0,
        };
        existente.cantidad += Number(item.cantidad_pendiente || 0);
        agrupados.set(clave, existente);
      }
    }
    return [...agrupados.values()].sort((left, right) =>
      `${left.producto} ${left.presentacion} ${left.codigo}`.localeCompare(
        `${right.producto} ${right.presentacion} ${right.codigo}`,
        "es",
      ),
    );
  })();
  const columns =
    tab === "Movimientos"
      ? [
          { accessorKey: "fecha", header: "Fecha" },
          { accessorKey: "producto", header: "Producto" },
          { accessorKey: "tipo", header: "Movimiento" },
          { accessorKey: "cantidad", header: "Cantidad" },
          { accessorKey: "referencia", header: "Referencia" },
        ]
      : tab === "Cartera"
        ? [
            { accessorKey: "cliente", header: "Cliente" },
            { accessorKey: "pedido", header: "Pedido" },
            { accessorKey: "saldo", header: "Saldo" },
            { accessorKey: "vencimiento", header: "Vencimiento" },
            { accessorKey: "estado", header: "Estado" },
          ]
        : config.columns;
  async function executeBulk() {
    if (!selectedIds.length) return;
    if (bulkAction === "cancelar") {
      confirmarAccionAdmin({
        titulo: "Cancelar pedidos",
        mensaje: `¿Cancelar ${selectedIds.length} pedido(s) y liberar su disponibilidad?`,
        confirmar: `Cancelar ${selectedIds.length} pedido(s)`,
        peligro: true,
        onConfirm: ejecutarOperacionMasiva,
      });
      return;
    }
    return ejecutarOperacionMasiva();
  }
  async function ejecutarOperacionMasiva() {
    if (bulkAction === "reprogramar" && !bulkDate) {
      setBulkStatus("Selecciona una nueva fecha.");
      notificarAdmin("Selecciona una nueva fecha.", "warning");
      return;
    }
    setBulkBusy(true);
    setBulkStatus("Aplicando acción a los pedidos…");
    try {
      const response = await fetch("/api/dashboard/pedidos/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: "operacion_masiva",
          datos: {
            operacion_id: createOperationId("ADM-BULK"),
            accion: bulkAction,
            pedidos: selectedIds,
            fecha_entrega: bulkDate,
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(
          payload.mensaje ||
            `No fue posible aplicar la operación (HTTP ${response.status}).`,
        );
      const mensaje = payload.mensaje || "Operación aplicada correctamente.";
      setBulkStatus(mensaje);
      notificarAdmin(mensaje, "success");
      setSelectedIds([]);
      onRefresh?.({ notificar: false });
    } catch (error) {
      const mensaje = error.message || "No fue posible aplicar la operación.";
      setBulkStatus(mensaje);
      notificarAdmin(mensaje, "error");
    } finally {
      setBulkBusy(false);
    }
  }
  return (
    <section className={`module-panel operational-panel${active === "pedidos" ? " orders-workbench" : ""}`}>
      {active === "pedidos" ? (
        <header className="orders-workbench-header">
          <div>
            <p className="eyebrow">OPERACIÓN DIARIA</p>
            <h2>Pedidos</h2>
            <span>Gestiona lo que se entrega y prepara durante el día.</span>
          </div>
          <div className="orders-workbench-overview" aria-label="Resumen de pedidos visibles">
            <span><b>{pagination.total ?? rows.length}</b> pedidos</span>
            <strong>{formatCurrency(pedidosVisiblesTotal)}</strong>
            <button type="button" className="primary-action" onClick={() => setManualOpen(true)}>Nuevo pedido</button>
          </div>
        </header>
      ) : <div className="module-heading">
        <div>
          <p className="eyebrow">CONSULTA OPERATIVA</p>
          <h2>{config.title}</h2>
          <span className="record-count">
            {active === "inventario"
              ? "Todos los productos y presentaciones"
              : active === "pedidos"
                ? `${pagination.total ?? rows.length} pedidos encontrados · selecciona uno para gestionarlo`
                : "Fuente operativa"}
          </span>
        </div>
        <div className="heading-tools">
          <Search size={16} />
          <span>Buscar y ordenar</span>
        </div>
      </div>}
      {active === "pedidos" ? <div className="order-status-tabs" role="tablist" aria-label="Filtrar pedidos por estado">
        {config.tabs.map((value) => (
          <button
            className={tab === value ? "active" : ""}
            key={value}
            role="tab"
            aria-selected={tab === value}
            onClick={() => {
              setTab(value);
              onOrderQueryChange?.({ estado: pedidoEstadoServidor(value) });
            }}
          >
            {value}
          </button>
        ))}
      </div> : <div className="module-tabs">
            {config.tabs.map((value) => (
              <button
                className={tab === value ? "module-tab active" : "module-tab"}
                key={value}
                onClick={() => {
                  setTab(value);
                  if (active === "pedidos")
                    onOrderQueryChange?.({ estado: pedidoEstadoServidor(value) });
                }}
          >
            {value}
          </button>
        ))}
      </div>}
      {active === "pedidos" && (
        <>
          <section className="orders-filter-bar" aria-label="Filtros de pedidos">
          <div className="order-date-filters" role="group" aria-label="Filtrar pedidos por fecha">
            {[
              ["TODAS", "Todos"],
              ["HOY", "Hoy"],
              ["MANANA", "Mañana"],
              ["SEMANA", "Esta semana"],
              ["PROXIMAS", "Próximas"],
            ].map(([value, label]) => (
              <button
                type="button"
                className={orderDateFilter === value ? "active" : ""}
                key={value}
                onClick={() => {
                  setOrderDateFilter(value);
                  onOrderQueryChange?.({ scope: value });
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="order-product-filters" role="group" aria-label="Filtrar pedidos por producto">
            <label>
              <span>Producto</span>
              <select
                value={orderProductFilter}
                onChange={(event) => {
                  const codigo = event.target.value;
                  const modo = codigo ? orderProductMode : "TODOS";
                  setOrderProductFilter(codigo);
                  if (!codigo) setOrderProductMode("TODOS");
                  onOrderQueryChange?.({ productoCodigo: codigo, productoModo: modo });
                }}
              >
                <option value="">Todos los productos</option>
                {productFilterOptions.map((item) => (
                  <option value={item.codigo} key={item.codigo}>
                    {[item.producto, item.presentacion].filter(Boolean).join(" · ")}
                  </option>
                ))}
              </select>
            </label>
            {orderProductFilter && <div className="order-product-mode" role="group" aria-label="Modo del filtro de producto">
              {[["SIN", "Sin"], ["CON", "Con"]].map(([value, label]) => (
                <button
                  type="button"
                  className={orderProductMode === value ? "active" : ""}
                  key={value}
                  onClick={() => {
                    setOrderProductMode(value);
                    onOrderQueryChange?.({ productoModo: value });
                  }}
                >
                  {label}
                </button>
              ))}
            </div>}
          </div>
          </section>
          {productosPendientes.length > 0 && <section className="delivery-requirements" aria-live="polite">
            <div className="delivery-requirements-heading">
              <div>
                <p className="eyebrow">PREPARACIÓN Y ENTREGA</p>
                <h3>Productos pendientes de entregar</h3>
              </div>
              <span>{productosPendientes.length} presentaciones requeridas</span>
            </div>
            <div className="delivery-requirements-list">
                {productosPendientes.map((item) => (
                  <div className="delivery-requirement" key={`${item.codigo}|${item.presentacion}|${item.unidad}`}>
                    <span>
                      <strong>{item.producto}</strong>
                      {(item.presentacion || item.codigo) && (
                        <small>
                          {[item.presentacion, item.codigo].filter(Boolean).join(" · ")}
                        </small>
                      )}
                    </span>
                    <b>
                      {Number(item.cantidad || 0).toLocaleString("es-CO", {
                        maximumFractionDigits: 3,
                      })}{" "}
                      {item.unidad}
                    </b>
                  </div>
                ))}
            </div>
          </section>}
          {false && selectedIds.length > 0 && <div className="bulk-toolbar orders-bulk-toolbar">
            <strong>{selectedIds.length} seleccionados</strong>
            <button
              type="button"
              className="secondary-action orders-merge-action"
              disabled={selectedIds.length < 2 || bulkBusy || mergeBusy}
              onClick={() => { setMergeStatus(""); setMergeOpen(true); }}
            >
              Unificar pedidos
            </button>
            <select
              value={bulkAction}
              onChange={(event) => setBulkAction(event.target.value)}
            >
              <option value="marcar_preparacion">Marcar en preparación</option>
              <option value="registrar_entrega">Registrar entrega</option>
              <option value="reprogramar">Reprogramar</option>
              <option value="cancelar">Cancelar y liberar</option>
            </select>
            {bulkAction === "reprogramar" && (
              <input
                type="date"
                value={bulkDate}
                onChange={(event) => setBulkDate(event.target.value)}
              />
            )}
            <button
              type="button"
              className="secondary-action"
              disabled={!selectedIds.length || bulkBusy}
              onClick={executeBulk}
            >
              {bulkBusy ? "Aplicando…" : "Aplicar a seleccionados"}
            </button>
            {bulkStatus && <span className="action-status">{bulkStatus}</span>}
          </div>}
        </>
      )}
      {active === "inventario" && tab === "Saldos" && (
        <div className="inventory-filter">
          <label>
            Mostrar por política
            <select
              value={inventoryPolicy}
              onChange={(event) => setInventoryPolicy(event.target.value)}
            >
              <option value="TODAS">Todas las políticas</option>
              <option value="INVENTARIO_ESTATICO">Inventario estático</option>
              <option value="CAPACIDAD_DIARIA">Capacidad diaria</option>
              <option value="CALENDARIO">Calendario</option>
              <option value="LOTE_VARIABLE">Lote variable</option>
            </select>
          </label>
          <span>
            La disponibilidad se decide según la política de cada presentación.
          </span>
        </div>
      )}
      {active === "inventario" && tab === "Saldos" ? (
        <InventoryOverview rows={rows} />
      ) : active === "pedidos" ? (
        <>
          <OrderCardList
            rows={rows}
            onRowSelect={setSelectedOrder}
          />
          {pagination.total > pagination.limite && (
            <div className="orders-server-pagination orders-pagination-bottom" aria-label="Paginación de pedidos">
              <span>Mostrando {Math.min((pagination.offset || 0) + 1, pagination.total)}–{Math.min((pagination.offset || 0) + rows.length, pagination.total)} de {pagination.total} pedidos</span>
              <div>
                <button type="button" className="secondary-action" disabled={!pagination.tiene_anterior} onClick={() => onOrderQueryChange?.({ offset: Math.max(0, (pagination.offset || 0) - (pagination.limite || 50)) })}>Anterior</button>
                <button type="button" className="secondary-action" disabled={!pagination.tiene_siguiente} onClick={() => onOrderQueryChange?.({ offset: (pagination.offset || 0) + (pagination.limite || 50) })}>Siguiente</button>
              </div>
            </div>
          )}
        </>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          onRowSelect={
            active === "pedidos"
              ? setSelectedOrder
              : active === "lista_espera"
                ? setSelectedWaitlist
                : undefined
          }
          selectable={active === "pedidos"}
          onSelectionChange={setSelectedIds}
        />
      )}
      {selectedWaitlist && (
        <WaitlistEntryDrawer
          entry={selectedWaitlist}
          onClose={() => setSelectedWaitlist(null)}
          onChanged={() => onRefresh?.({ notificar: false })}
        />
      )}
      {active === "pedidos" && pushOrderId && (
        <PushOrderFocus
          orderId={pushOrderId}
          rows={sourceRows}
          onOpen={(order) => {
            setTab("Todos");
            setOrderDateFilter("TODAS");
            setSelectedOrder(order);
            onPushOrderOpened?.();
          }}
        />
      )}
      {selectedOrder && (
        <AdminOrderDrawer
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onChanged={onRefresh}
        />
      )}
      {manualOpen && (
        <ManualOrderModal
          onClose={() => setManualOpen(false)}
          onCreated={() => {
            setManualOpen(false);
            onRefresh?.();
          }}
        />
      )}
    </section>
  );
}

function UnifyOrdersModal({ orders, busy, status, onClose, onConfirm }) {
  const total = orders.reduce((sum, order) => sum + Number(order.total || order.Valor || 0), 0);
  const normalized = (value) => String(value || "").trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("es-CO").replace(/\s+/g, " ");
  const first = orders[0] || {};
  const same = (values) => new Set(values.map(normalized)).size <= 1;
  const sameClient = same(orders.map((order) => order.ClienteId || order.cliente));
  const sameAddress = same(orders.map((order) => order.DireccionEntrega || order.direccion));
  const sameDate = same(orders.map((order) => order.fecha || order["Fecha entrega"]));
  const sameService = same(orders.map((order) => order.ServicioEntrega || order.servicio_entrega || "RUTA_AM"));
  const compatible = orders.length >= 2 && sameClient && sameAddress && sameDate && sameService;
  const products = new Map();
  for (const order of orders) {
    for (const line of order.productos || []) {
      const key = `${line.codigo || ""}|${line.presentacion || ""}|${line.producto || ""}`;
      const current = products.get(key) || { ...line, cantidad: 0 };
      current.cantidad += Number(line.cantidad_solicitada || line.cantidad || 0);
      products.set(key, current);
    }
  }
  return <div className="drawer-backdrop order-unify-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="order-unify-modal" role="dialog" aria-modal="true" aria-labelledby="unify-orders-title" onMouseDown={(event) => event.stopPropagation()}>
      <button type="button" className="drawer-close" disabled={busy} onClick={onClose} aria-label="Cerrar"><X size={21} /></button>
      <p className="eyebrow">UNA SOLA PARADA</p>
      <h2 id="unify-orders-title">Unificar pedidos</h2>
      <p className="order-unify-description">Los pedidos originales, sus reservas y pagos se conservan. El repartidor los verá como una misma entrega.</p>
      <div className="order-unify-summary">
        <div><span>Cliente</span><b>{first.cliente || first.Cliente || "—"}</b></div>
        <div><span>Entrega</span><b>{first.fecha || first["Fecha entrega"] || "—"}</b></div>
        <div><span>Dirección</span><b>{first.DireccionEntrega || first.direccion || "—"}</b></div>
        <div><span>Total conjunto</span><strong>{formatCurrency(total)}</strong></div>
      </div>
      <section className="order-unify-orders">
        <h3>{orders.length} pedidos seleccionados</h3>
        {orders.map((order) => <div key={order.id || order["Id Pedido"]}>
          <span>{order.id || order["Id Pedido"]}</span><b>{formatCurrency(order.total || order.Valor || 0)}</b>
        </div>)}
      </section>
      <section className="order-unify-products">
        <h3>Productos que recibirá el cliente</h3>
        {[...products.values()].map((line) => <div key={`${line.codigo}-${line.presentacion}`}>
          <b>{Number(line.cantidad || 0)} {line.unidad || ""}</b><span>{[line.producto, line.presentacion].filter(Boolean).join(" · ")}</span>
        </div>)}
      </section>
      {!compatible && <p className="order-unify-warning">Estos pedidos no tienen el mismo cliente, dirección, fecha o servicio. No se pueden unificar.</p>}
      {status && <p className="action-status">{status}</p>}
      <footer>
        <button type="button" className="secondary-action" disabled={busy} onClick={onClose}>Cancelar</button>
        <button type="button" className="primary-action" disabled={!compatible || busy} onClick={onConfirm}>{busy ? "Unificando…" : "Confirmar unificación"}</button>
      </footer>
    </section>
  </div>;
}

function OrderCardList({ rows, onRowSelect }) {
  return (
    <section className="order-list" aria-label="Pedidos filtrados">
      {rows.length > 0 && <div className="order-list-heading" aria-hidden="true">
        <span />
        <span>Cliente y fecha de entrega</span>
        <span>Estado</span>
        <span>Total</span>
        <span />
      </div>}
      {rows.length ? rows.map((row) => {
        const id = String(row.id || row["Id Pedido"] || "").trim();
        const statusKey = pedidoTabEstado(row.estado).toLowerCase().replaceAll(" ", "-");
        return (
          <article
            className={`order-row order-row-${statusKey}`}
            key={id || `${row.cliente}-${row.fecha}`}
            onClick={() => onRowSelect?.(row)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") onRowSelect?.(row);
            }}
            role="button"
            tabIndex={0}
          >
            <div className="order-row-client">
              <strong>{row.cliente || "Cliente sin nombre"}</strong>
              <small>{row.fecha || "Fecha no registrada"}</small>
              {Number(row.cantidad_pedidos_unificados || row.CantidadPedidosUnificados || 0) > 1 && (
                <small className="order-unified-note">
                  {row.cantidad_pedidos_unificados || row.CantidadPedidosUnificados} pedidos unificados · una sola entrega
                </small>
              )}
            </div>
            <span className={`order-status-badge ${statusKey}`}>{row.estado || "Pendiente"}</span>
            <strong className="order-row-total">{formatCurrency(row.total || 0)}</strong>
            <span className="order-row-arrow" aria-hidden="true">›</span>
          </article>
        );
      }) : (
        <div className="order-card-empty">
          <strong>No hay pedidos en esta vista</strong>
          <span>Prueba otro estado, fecha o filtro de producto.</span>
        </div>
      )}
    </section>
  );
}

function DataTable({
  columns,
  rows,
  onRowSelect,
  selectable = false,
  onSelectionChange,
}) {
  return (
    <Suspense fallback={<div className="table-loading">Cargando tabla…</div>}>
      <AdminWidgets
        type="table"
        columns={columns}
        rows={rows}
        onRowSelect={onRowSelect}
        selectable={selectable}
        onSelectionChange={onSelectionChange}
      />
    </Suspense>
  );
}

function InventoryOverview({ rows }) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("TODOS");
  const visible = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("es-CO");
    return [...(rows || [])]
      .filter((row) => {
        const disponible = Number(row.disponible);
        const minimo = Number(row.minimo);
        const estado = String(row.estado || "").toUpperCase();
        const status = estado.includes("INACT")
          ? "INACTIVO"
          : !Number.isFinite(disponible) || disponible <= 0
            ? "AGOTADO"
            : Number.isFinite(minimo) && minimo > 0 && disponible <= minimo
              ? "BAJO"
              : "DISPONIBLE";
        if (statusFilter !== "TODOS" && status !== statusFilter) return false;
        return !needle || [row.producto, row.presentacion, row.codigo]
          .some((value) => String(value || "").toLocaleLowerCase("es-CO").includes(needle));
      })
      .sort((left, right) => {
        const leftAvailable = Number(left.disponible);
        const rightAvailable = Number(right.disponible);
        return (Number.isFinite(leftAvailable) ? leftAvailable : -1) - (Number.isFinite(rightAvailable) ? rightAvailable : -1);
      });
  }, [query, rows, statusFilter]);
  const summary = useMemo(() => {
    const source = rows || [];
    const statuses = source.reduce((result, row) => {
      const disponible = Number(row.disponible);
      const minimo = Number(row.minimo);
      const estado = String(row.estado || "").toUpperCase();
      const status = estado.includes("INACT")
        ? "INACTIVO"
        : !Number.isFinite(disponible) || disponible <= 0
          ? "AGOTADO"
          : Number.isFinite(minimo) && minimo > 0 && disponible <= minimo
            ? "BAJO"
            : "DISPONIBLE";
      result[status] = (result[status] || 0) + 1;
      return result;
    }, {});
    return { total: source.length, ...statuses };
  }, [rows]);
  function itemStatus(item) {
    const disponible = Number(item.disponible);
    const minimo = Number(item.minimo);
    const estado = String(item.estado || "").toUpperCase();
    if (estado.includes("INACT")) return { key: "INACTIVO", label: "Inactivo" };
    if (!Number.isFinite(disponible) || disponible <= 0) return { key: "AGOTADO", label: "Agotado" };
    if (Number.isFinite(minimo) && minimo > 0 && disponible <= minimo) return { key: "BAJO", label: "Saldo bajo" };
    return { key: "DISPONIBLE", label: "Disponible" };
  }
  function policyLabel(policy) {
    return ({
      INVENTARIO_ESTATICO: "Stock físico",
      CAPACIDAD_DIARIA: "Capacidad diaria",
      CALENDARIO: "Calendario",
      LOTE_VARIABLE: "Lote variable",
    })[String(policy || "").toUpperCase()] || policy || "Sin política";
  }
  function displayNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString("es-CO", { maximumFractionDigits: 3 }) : "—";
  }
  return <section className="inventory-overview" aria-label="Resumen visual de inventario">
    <div className="inventory-summary-cards">
      <div><span>Presentaciones</span><strong>{summary.total || 0}</strong><small>En la vista actual</small></div>
      <div className="is-good"><span>Disponibles</span><strong>{summary.DISPONIBLE || 0}</strong><small>Saldo operativo suficiente</small></div>
      <div className="is-warning"><span>Saldo bajo</span><strong>{summary.BAJO || 0}</strong><small>Revisar antes de preparar</small></div>
      <div className="is-danger"><span>Agotadas</span><strong>{summary.AGOTADO || 0}</strong><small>No disponibles ahora</small></div>
    </div>
    <div className="inventory-overview-toolbar">
      <label className="table-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar producto, presentación o código" /></label>
      <div className="inventory-status-filters" role="group" aria-label="Filtrar estado de inventario">
        {[['TODOS', 'Todos'], ['DISPONIBLE', 'Disponibles'], ['BAJO', 'Saldo bajo'], ['AGOTADO', 'Agotados'], ['INACTIVO', 'Inactivos']].map(([value, label]) => <button type="button" key={value} className={statusFilter === value ? "active" : ""} onClick={() => setStatusFilter(value)}>{label}</button>)}
      </div>
    </div>
    <div className="inventory-overview-heading"><span>{visible.length} presentación(es) visibles</span><small>El saldo operativo no mezcla stock físico con capacidad diaria.</small></div>
    {visible.length ? <div className="inventory-card-grid">{visible.map((item) => {
      const status = itemStatus(item);
      const policy = String(item.politica || "").toUpperCase();
      const isStock = policy === "INVENTARIO_ESTATICO";
      return <article className={`inventory-card inventory-card-${status.key.toLowerCase()}`} key={item.codigo || `${item.producto}-${item.presentacion}`}>
        <header className="inventory-card-header"><div><h3>{item.producto || "Producto sin nombre"}</h3><p>{item.presentacion || "Presentación no especificada"}</p></div><span className={`inventory-status-pill ${status.key.toLowerCase()}`}>{status.label}</span></header>
        <div className="inventory-card-code"><span>Código</span><b>{item.codigo_visual || item.codigo || "—"}</b><span className="inventory-policy-pill">{policyLabel(item.politica)}</span></div>
        <div className="inventory-available"><span>{isStock ? "Saldo disponible" : "Disponible según política"}</span><strong>{displayNumber(item.disponible)} <small>{item.unidad || "unidades"}</small></strong></div>
        <div className="inventory-metrics">
          <div><span>{isStock ? "Existencias" : "Base registrada"}</span><b>{displayNumber(item.existencias)}</b></div>
          <div><span>Reservado</span><b>{displayNumber(item.reservado)}</b></div>
          <div><span>{isStock ? "Punto de reorden" : "Fuente"}</span><b>{isStock ? displayNumber(item.minimo) : (item.fuente || "ERP")}</b></div>
        </div>
        <footer className="inventory-card-footer"><span>Costo unitario <b>{item.costo == null ? "—" : formatCurrency(item.costo)}</b></span><span>Valor en stock <b>{item.valor_inventario == null ? "No aplica" : formatCurrency(item.valor_inventario)}</b></span></footer>
      </article>;
    })}</div> : <div className="inventory-overview-empty"><Package size={26} /><strong>No hay presentaciones para este filtro.</strong><span>Prueba otra búsqueda o cambia el estado seleccionado.</span></div>}
  </section>;
}

function OperationalConfiguration({ products }) {
  const capacityDayOptions = [
    [0, "Lun"],
    [1, "Mar"],
    [2, "Mié"],
    [3, "Jue"],
    [4, "Vie"],
    [5, "Sáb"],
    [6, "Dom"],
  ];
  const [selectedCode, setSelectedCode] = useState("");
  const [selectedCanonicalId, setSelectedCanonicalId] = useState("");
  const [form, setForm] = useState({
    policy: "",
    availability: "",
    capacity: "",
    catalogVisible: false,
    unit: "",
    base: "",
    maximum: "",
    active: false,
    date: "",
    days: [],
    capacityDays: [],
  });
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const selected = products.find((item) => item.codigo === selectedCode);
  const canonicalGroups = Object.values(
    products.reduce((groups, item) => {
      const id = item.producto_canonico_id || item.codigo;
      if (!groups[id])
        groups[id] = {
          id,
          producto: item.producto,
          capacidad: item.capacidad || "",
          presentations: [],
        };
      groups[id].presentations.push(item);
      if (!groups[id].capacidad && item.capacidad)
        groups[id].capacidad = item.capacidad;
      return groups;
    }, {}),
  );
  const selectedCanonical = canonicalGroups.find(
    (item) => item.id === selectedCanonicalId,
  );
  const capacityGroups = canonicalGroups.filter((group) =>
    group.presentations.some((item) => item.politica === "CAPACIDAD_DIARIA"),
  );
  function selectProduct(event) {
    const code = event.target.value;
    const item = products.find((row) => row.codigo === code);
    setSelectedCode(code);
    setStatus("");
    setForm({
      policy: item?.politica || "",
      availability: item?.disponibilidad || "",
      capacity: item?.capacidad || code,
      catalogVisible: item?.visible === true || item?.visible === "true",
      unit: "",
      base: "",
      maximum: "",
      active: false,
      date: "",
      days: [],
      capacityDays: [],
    });
  }
  function selectCanonical(event) {
    const id = event.target.value;
    const group = canonicalGroups.find((item) => item.id === id);
    setSelectedCanonicalId(id);
    setStatus("");
    setForm((current) => ({
      ...current,
      capacity: group?.capacidad || "",
      unit: current.unit,
      base: current.base,
      maximum: current.maximum,
    }));
  }
  function update(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }
  function toggleDay(day) {
    setForm((current) => ({
      ...current,
      days: current.days.includes(day)
        ? current.days.filter((value) => value !== day)
        : [...current.days, day].sort(),
    }));
  }
  function toggleCapacityDay(day) {
    setForm((current) => ({
      ...current,
      capacityDays: current.capacityDays.includes(day)
        ? current.capacityDays.filter((value) => value !== day)
        : [...current.capacityDays, day].sort(),
    }));
  }
  async function send(path, body) {
    setBusy(true);
    setStatus("");
    try {
      const response = await fetch(path, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(
          payload.mensaje ||
            `No fue posible completar la operación (HTTP ${response.status}).`,
        );
      setStatus(payload.mensaje || "Configuración guardada correctamente.");
      return payload;
    } catch (error) {
      setStatus(error.message || "No fue posible guardar la configuración.");
    } finally {
      setBusy(false);
    }
  }
  async function saveAvailability(event) {
    event.preventDefault();
    if (!selectedCode) return;
    const cambios = {
      politica_disponibilidad: form.policy,
      disponibilidad: form.availability,
      catalogo_visible: form.catalogVisible,
    };
    if (form.days.length)
      cambios.calendario_disponibilidad = {
        dias: form.days,
        lead_time_dias: 0,
        hora_corte: "20:00",
      };
    await send("/api/dashboard/productos/operacion", {
      operacion: "configurar_disponibilidad",
      datos: { codigo: selectedCode, cambios },
    });
  }
  async function saveCanonicalSource(event) {
    event.preventDefault();
    if (!selectedCanonical || !form.capacity.trim()) {
      setStatus(
        "Selecciona un producto con capacidad diaria y escribe su fuente compartida.",
      );
      return;
    }
    await send("/api/dashboard/productos/operacion", {
      operacion: "configurar_capacidad_canonica",
      datos: {
        producto_canonico_id: selectedCanonical.id,
        codigo_capacidad: form.capacity.trim(),
      },
    });
  }
  async function saveCapacity(event) {
    event.preventDefault();
    if (
      !selectedCanonical ||
      !form.capacity.trim() ||
      !form.unit ||
      !form.base ||
      !form.maximum
    ) {
      setStatus(
        "Completa producto, código de capacidad, unidad y límites explícitos.",
      );
      return;
    }
    await send("/api/dashboard/capacidad/operacion", {
      operacion: "guardar_configuracion",
      datos: {
        codigo_producto: form.capacity.trim(),
        producto: selectedCanonical.producto,
        unidad_capacidad: form.unit,
        capacidad_base: Number(form.base),
        capacidad_maxima: Number(form.maximum),
        activo: form.active,
        dias_operacion: form.capacityDays,
      },
    });
  }
  async function openDay(event) {
    event.preventDefault();
    if (!selectedCanonical || !form.capacity.trim() || !form.date) {
      setStatus("Selecciona producto, código de capacidad y fecha.");
      return;
    }
    await send("/api/dashboard/capacidad/operacion", {
      operacion: "abrir_dia",
      datos: { codigo_capacidad: form.capacity.trim(), fecha: form.date },
    });
  }
  const selectableProducts = products.filter(
    (item) => String(item.disponibilidad || "").toUpperCase() !== "INACTIVO",
  );
  return (
    <section className="operational-config">
      <div className="module-panel config-intro">
        <p className="eyebrow">OPERACIÓN CONTROLADA</p>
        <h2>Presentaciones, cupos y calendario</h2>
        <p>
          La disponibilidad se configura por presentación. El cupo diario
          pertenece al producto canónico y se comparte entre sus presentaciones.
        </p>
      </div>
      <div className="module-panel config-panel">
        <div className="config-fields">
          <label>
            Presentación: disponibilidad y calendario
            <select value={selectedCode} onChange={selectProduct} required>
              <option value="">Selecciona una presentación</option>
              {selectableProducts.map((item) => (
                <option key={item.codigo} value={item.codigo}>
                  {item.producto || item.codigo}
                  {item.presentacion_descripcion
                    ? ` · ${item.presentacion_descripcion}`
                    : item.presentacion
                      ? ` · ${item.presentacion}`
                      : ""}
                </option>
              ))}
            </select>
          </label>
          {selected && (
            <span className="config-current">
              Estado actual: {selected.disponibilidad || "sin configurar"} ·
              Política: {selected.politica || "sin configurar"}
            </span>
          )}
        </div>
        <form className="config-form" onSubmit={saveAvailability}>
          <h3>Disponibilidad comercial</h3>
          <div className="config-grid">
            <label>
              Política
              <select
                value={form.policy}
                onChange={(event) => update("policy", event.target.value)}
                required
              >
                <option value="">Selecciona</option>
                <option>CAPACIDAD_DIARIA</option>
                <option>INVENTARIO_ESTATICO</option>
                <option>CALENDARIO</option>
                <option>LOTE_VARIABLE</option>
                <option>NO_CONFIGURADA</option>
              </select>
            </label>
            <label>
              Estado
              <select
                value={form.availability}
                onChange={(event) => update("availability", event.target.value)}
                required
              >
                <option value="">Selecciona</option>
                <option>DISPONIBLE</option>
                <option>AGOTADO</option>
                <option>PROGRAMADO</option>
                <option>NO_CONFIGURADO</option>
                <option>INACTIVO</option>
              </select>
            </label>
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={form.catalogVisible}
                onChange={(event) =>
                  update("catalogVisible", event.target.checked)
                }
              />{" "}
              Visible en catálogo
            </label>
          </div>
          <fieldset>
            <legend>Días de calendario: días de entrega programables</legend>
            <div className="day-options">
              {[
                [0, "Lun"],
                [1, "Mar"],
                [2, "Mié"],
                [3, "Jue"],
                [4, "Vie"],
                [5, "Sáb"],
                [6, "Dom"],
              ].map(([day, label]) => (
                <label key={day}>
                  <input
                    type="checkbox"
                    checked={form.days.includes(day)}
                    onChange={() => toggleDay(day)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          <button type="submit" disabled={busy || !selectedCode}>
            {busy ? "Guardando..." : "Guardar disponibilidad"}
          </button>
        </form>
        <div className="config-fields">
          <label>
            Producto: cupo compartido
            <select value={selectedCanonicalId} onChange={selectCanonical}>
              <option value="">
                Selecciona un producto con capacidad diaria
              </option>
              {capacityGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.producto} · {group.presentations.length}{" "}
                  presentación(es)
                </option>
              ))}
            </select>
          </label>
          {selectedCanonical && (
            <span className="config-current">
              Comparten el cupo:{" "}
              {selectedCanonical.presentations
                .map((item) => item.presentacion_descripcion || item.codigo)
                .join(" · ")}
            </span>
          )}
        </div>
        <form className="config-form" onSubmit={saveCanonicalSource}>
          <h3>Fuente de capacidad compartida</h3>
          <div className="config-grid">
            <label>
              Código de capacidad
              <input
                value={form.capacity}
                onChange={(event) => update("capacity", event.target.value)}
                placeholder="Ej.: CAP-LECHE-VACA"
              />
            </label>
          </div>
          <button type="submit" disabled={busy || !selectedCanonical}>
            {busy ? "Guardando..." : "Guardar fuente compartida"}
          </button>
        </form>
        <form className="config-form" onSubmit={saveCapacity}>
          <h3>Capacidad explícita</h3>
          <div className="config-grid">
            <label>
              Unidad
              <input
                value={form.unit}
                onChange={(event) => update("unit", event.target.value)}
                placeholder="litros, paquetes..."
              />
            </label>
            <label>
              Base
              <input
                type="number"
                min="0"
                value={form.base}
                onChange={(event) => update("base", event.target.value)}
              />
            </label>
            <label>
              Máxima
              <input
                type="number"
                min="0"
                value={form.maximum}
                onChange={(event) => update("maximum", event.target.value)}
              />
            </label>
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) => update("active", event.target.checked)}
              />{" "}
              Configuración activa
            </label>
          </div>
          <button type="submit" disabled={busy || !selectedCanonical}>
            {busy ? "Guardando..." : "Guardar capacidad"}
          </button>
        </form>
        <form className="config-form" onSubmit={openDay}>
          <h3>Abrir fecha de capacidad</h3>
          <div className="config-grid">
            <label>
              Código de capacidad
              <input
                value={form.capacity}
                onChange={(event) => update("capacity", event.target.value)}
                required
              />
            </label>
            <label>
              Fecha
              <input
                type="date"
                value={form.date}
                onChange={(event) => update("date", event.target.value)}
                required
              />
            </label>
          </div>
          <button type="submit" disabled={busy || !selectedCanonical}>
            {busy ? "Guardando..." : "Abrir fecha"}
          </button>
        </form>
        {status && <p className="action-status">{status}</p>}
      </div>
    </section>
  );
}

function RecurringCapacityConfiguration({ products }) {
  const [selected, setSelected] = useState("");
  const [days, setDays] = useState([]);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const groups = Object.values(
    products.reduce((result, item) => {
      if (item.politica !== "CAPACIDAD_DIARIA") return result;
      const id = item.producto_canonico_id || item.codigo;
      if (!result[id])
        result[id] = { id, name: item.producto, code: item.capacidad || "" };
      return result;
    }, {}),
  );
  const options = [
    [0, "Lunes"],
    [1, "Martes"],
    [2, "Miércoles"],
    [3, "Jueves"],
    [4, "Viernes"],
    [5, "Sábado"],
    [6, "Domingo"],
  ];
  function toggle(day) {
    setDays((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day].sort(),
    );
  }
  async function save(event) {
    event.preventDefault();
    const group = groups.find((item) => item.id === selected);
    if (!group?.code || !days.length) {
      setStatus("Selecciona el producto y al menos un día de apertura.");
      return;
    }
    setBusy(true);
    setStatus("");
    try {
      const response = await fetch("/api/dashboard/capacidad/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operacion: "programar_aperturas",
          datos: { codigo_producto: group.code, dias_operacion: days },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(
          payload.mensaje ||
            `No fue posible guardar la programación (HTTP ${response.status}).`,
        );
      setStatus(
        "Programación guardada. Las fechas se abrirán automáticamente según estos días.",
      );
    } catch (error) {
      setStatus(error.message || "No fue posible guardar la programación.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="module-panel config-panel">
      <div className="config-fields">
        <p className="eyebrow">APERTURA AUTOMÁTICA</p>
        <h2>Programar días de capacidad</h2>
        <p>
          El sistema abrirá cupos solo en los días elegidos. No cambia la
          disponibilidad de las presentaciones.
        </p>
      </div>
      <form className="config-form" onSubmit={save}>
        <label>
          Producto con capacidad compartida
          <select
            value={selected}
            onChange={(event) => {
              setSelected(event.target.value);
              setDays([]);
              setStatus("");
            }}
            required
          >
            <option value="">Selecciona un producto</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name} · {group.code}
              </option>
            ))}
          </select>
        </label>
        <fieldset>
          <legend>Días de entrega</legend>
          <div className="day-options">
            {options.map(([day, label]) => (
              <label key={day}>
                <input
                  type="checkbox"
                  checked={days.includes(day)}
                  onChange={() => toggle(day)}
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
        <button type="submit" disabled={busy}>
          {busy ? "Guardando..." : "Guardar programación"}
        </button>
        {status && <p className="action-status">{status}</p>}
      </form>
    </section>
  );
}

function DetailDrawer({ item, onClose }) {
  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className="drawer" onClick={(event) => event.stopPropagation()}>
        <button className="drawer-close" onClick={onClose}>
          <X size={18} />
        </button>
        <p className="eyebrow">DETALLE</p>
        <h2>{item.id || item.producto || item.referencia || item.ruta}</h2>
        <div className="detail-list">
          {Object.entries(item).map(([key, value]) => (
            <div key={key}>
              <span>{key.replaceAll("_", " ")}</span>
              <b>{formatCell(value, key)}</b>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

function liquidationProductLabel(item) {
  const name = visibleProductName(item?.producto, item?.presentacion, item?.codigo || item?.codigo_producto);
  const presentation = String(item?.presentacion || "").trim();
  // Las presentaciones específicas de compra ya están expresadas en el
  // nombre comercial; no repetimos gramaje en esas tarjetas.
  if (/^(Queso (pequeño|mediano|grande)|Esparcible)$/.test(name)) return name;
  return presentation ? `${name} · ${presentation}` : name;
}

function formatQuantity(value) {
  return Number(value || 0).toLocaleString("es-CO", { maximumFractionDigits: 3 });
}

function aggregateLiquidationProducts(sales, purchases, catalog = []) {
  const groups = new Map();
  const allowedCodes = new Set(catalog.map((item) => String(item?.codigo || "").trim()).filter(Boolean));
  function add(item, kind) {
    const code = String(item?.codigo || item?.codigo_producto || "").trim();
    if (!allowedCodes.has(code)) return;
    const label = liquidationProductLabel(item);
    const unit = String(item?.unidad || item?.unidad_venta || "unidades").trim() || "unidades";
    const key = code || `${label.toLowerCase()}|${unit.toLowerCase()}`;
    const current = groups.get(key) || {
      key, codigo: code, producto: String(item?.producto || "Producto sin nombre"),
      presentacion: String(item?.presentacion || ""), unidad: unit,
      vendido: 0, comprado: 0, valorVenta: 0, costoCompra: 0,
    };
    const quantity = Number(item?.cantidad || 0) || 0;
    if (kind === "venta") {
      current.vendido += quantity;
      current.valorVenta += Number(item?.valor || 0) || 0;
    } else {
      current.comprado += quantity;
      current.costoCompra += Number(item?.valor || 0) || quantity * (Number(item?.costo_unitario || 0) || 0);
    }
    groups.set(key, current);
  }
  catalog.forEach((item) => add(item, "catalogo"));
  (sales || []).forEach((sale) => (sale.productos || []).forEach((item) => add(item, "venta")));
  (purchases || []).forEach((expense) => (expense.items || []).forEach((item) => add(item, "compra")));
  return Array.from(groups.values())
    .map((item) => ({ ...item, diferencia: Number((item.comprado - item.vendido).toFixed(3)) }))
    .sort((left, right) => liquidationProductLabel(left).localeCompare(liquidationProductLabel(right), "es"));
}

function DailyLiquidation({ data = {}, date, onDateChange }) {
  const [responsible, setResponsible] = useState("");
  // La liquidación conserva el cierre completo del día, pero su lectura
  // operativa permite separar domicilios de ventas en sitio.
  const [salesChannel, setSalesChannel] = useState("RUTA");
  const [liquidation, setLiquidation] = useState(null);
  const [liquidationBusy, setLiquidationBusy] = useState(false);
  const [liquidationMessage, setLiquidationMessage] = useState("");
  const [operationId, setOperationId] = useState(() => createOperationId("LIQUIDACION"));
  const [showSalesDetail, setShowSalesDetail] = useState(false);
  // Las APIs de estadísticas pueden devolver una colección vacía o un valor
  // nulo cuando no hay registros. Normalizamos aquí el contrato de lectura
  // para que una fecha sin datos no desmonte todo el panel.
  const salesDays = Array.isArray(data.ventas) ? data.ventas : [];
  const financialDays = Array.isArray(data.finanzas_por_dia) ? data.finanzas_por_dia : [];
  const saleDay = salesDays.find((day) => String(day?.fecha || "").slice(0, 10) === date) || { ventas: [], total: 0, cantidad_ventas: 0 };
  const allSaleRows = Array.isArray(saleDay.ventas) ? saleDay.ventas : [];
  const saleRows = allSaleRows.filter((sale) => salesChannel === "TODOS" || salesChannelCode(sale) === salesChannel);
  const visibleSaleTotal = saleRows.reduce((total, sale) => total + Number(sale.valor_total || 0), 0);
  const financialDay = financialDays.find((day) => String(day?.fecha || "").slice(0, 10) === date) || {};
  const expenseRows = Array.isArray(financialDay.gastos) ? financialDay.gastos : [];
  const hasStandActivity = allSaleRows.some((sale) => salesChannelCode(sale) === "PRESENCIAL") || expenseRows.some((expense) => expenseSourceCode(expense) === "STAND");
  // Liquidar día comienza en Domicilios. Stand solo entra al selector y a sus
  // cifras cuando realmente hay actividad de ese origen en la fecha.
  useEffect(() => {
    if (!hasStandActivity && salesChannel !== "RUTA") setSalesChannel("RUTA");
  }, [hasStandActivity, salesChannel]);
  const visibleExpenseRows = expenseRows.filter((expense) => salesChannel === "TODOS" || expenseSourceCode(expense) === (salesChannel === "PRESENCIAL" ? "STAND" : "DOMICILIOS"));
  const visibleExpenseTotal = visibleExpenseRows.reduce((total, expense) => total + (Number(expense.valor) || 0), 0);
  const paymentTotals = saleRows.reduce((summary, sale) => {
    const state = paymentVisualClass(sale);
    summary[state] += Number(sale.valor_total || 0);
    return summary;
  }, { efectivo: 0, transferencia: 0, pendiente: 0 });
  // Los recaudos se reciben en esta fecha, pero pertenecen a ventas previas;
  // nunca se suman a paymentTotals ni al total vendido.
  // Una liquidación ya guardada debe conservar exactamente su fotografía.
  // Para días abiertos se usa la lectura en vivo; los cierres anteriores no
  // se recalculan ni se alteran por abonos posteriores.
  const snapshotReceivables = liquidation?.Fotografia?.resumen?.recaudo_cartera;
  const receivablesToday = liquidation
    ? (snapshotReceivables && typeof snapshotReceivables === "object" ? snapshotReceivables : {})
    : financialDay.recaudos_cartera || {};
  const receivableCash = Number(receivablesToday?.efectivo?.valor || 0);
  const receivableCourierCash = Number(receivablesToday?.efectivo?.repartidor || 0);
  const receivableTransfer = Number(receivablesToday?.transferencia?.valor || 0);
  const receivableTotal = Number(receivablesToday?.total || 0);
  const purchases = visibleExpenseRows.filter((expense) => String(expense?.tipo || "").toUpperCase() === "COMPRA_ADICIONAL");
  // La cifra que se concilia contra la caja es estrictamente efectivo: una
  // transferencia de gastos no reduce el dinero físico del repartidor.
  const expenseTotalsBySource = expenseRows.reduce((summary, expense) => {
    const payment = String(expense?.metodo_pago || "EFECTIVO").trim().toUpperCase();
    if (payment && payment !== "EFECTIVO") return summary;
    summary[expenseSourceCode(expense)] += Number(expense.valor) || 0;
    return summary;
  }, { DOMICILIOS: 0, STAND: 0 });
  const derivedSettlement = allSaleRows.reduce((summary, sale) => {
    const source = salesChannelCode(sale) === "PRESENCIAL" ? "STAND" : "DOMICILIOS";
    const value = Number(sale.valor_total) || 0;
    summary[source].ventas_totales += value;
    const payment = paymentVisualClass(sale);
    if (payment === "efectivo") summary[source].ventas_efectivo += value;
    else if (payment === "transferencia") summary[source].transferencias += value;
    else summary[source].cartera_nueva += value;
    return summary;
  }, {
    DOMICILIOS: { ventas_totales: 0, ventas_efectivo: 0, transferencias: 0, cartera_nueva: 0, cartera_recuperada_efectivo: receivableCourierCash, gastos: expenseTotalsBySource.DOMICILIOS },
    STAND: { ventas_totales: 0, ventas_efectivo: 0, transferencias: 0, cartera_nueva: 0, cartera_recuperada_efectivo: 0, gastos: expenseTotalsBySource.STAND },
  });
  Object.values(derivedSettlement).forEach((source) => {
    const efectivoRecibido = source.ventas_efectivo + source.cartera_recuperada_efectivo;
    source.efectivo_recibido = efectivoRecibido;
    source.valor_a_consignar = Math.max(0, efectivoRecibido - source.gastos);
    source.saldo_gastos_por_compensar = Math.max(0, source.gastos - efectivoRecibido);
    source.efectivo_para_recibir = source.valor_a_consignar;
  });
  const savedSettlement = liquidation?.Fotografia?.resumen?.liquidacion_efectivo || data.resumen?.liquidacion_efectivo || {};
  const normalizeSettlement = (source) => {
    const row = source || {};
    const efectivoRecibido = Number(row.efectivo_recibido ?? (Number(row.ventas_efectivo || 0) + Number(row.cartera_recuperada_efectivo || 0)));
    const gastos = Number(row.gastos || 0);
    const valorAConsignar = Number(row.valor_a_consignar ?? Math.max(0, efectivoRecibido - gastos));
    const saldoGastos = Number(row.saldo_gastos_por_compensar ?? Math.max(0, gastos - efectivoRecibido));
    return { ...row, efectivo_recibido: efectivoRecibido, valor_a_consignar: valorAConsignar, saldo_gastos_por_compensar: saldoGastos, efectivo_para_recibir: valorAConsignar };
  };
  const domicilioSettlement = normalizeSettlement(savedSettlement.DOMICILIOS || derivedSettlement.DOMICILIOS);
  const standSettlement = normalizeSettlement(savedSettlement.STAND || derivedSettlement.STAND);
  const purchaseCatalog = Array.isArray(data.catalogo_compras) ? data.catalogo_compras : [];
  // La conciliación y el cierre no se filtran: deben conservar la fotografía
  // completa del día, independientemente de cómo se consulte el detalle.
  const groupedProducts = useMemo(() => aggregateLiquidationProducts(allSaleRows, purchases, purchaseCatalog), [allSaleRows, purchases, purchaseCatalog]);
  useEffect(() => {
    let alive = true;
    setLiquidation(null);
    setLiquidationMessage("");
    setResponsible("");
    setOperationId(createOperationId("LIQUIDACION"));
    if (!date) return () => { alive = false; };
    fetch("/api/dashboard/liquidacion/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "consultar", datos: { fecha: date } }) })
      .then((response) => response.json().catch(() => ({})))
      .then((payload) => { if (alive && payload.ok) setLiquidation(payload.data?.liquidada ? payload.data.liquidacion : null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [date]);
  async function saveLiquidation() {
    if (!responsible || liquidation || liquidationBusy) return;
    setLiquidationBusy(true); setLiquidationMessage("");
    try {
      const response = await fetch("/api/dashboard/liquidacion/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "guardar", datos: { fecha: date, responsable: responsible, operacion_id: operationId } }) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible guardar la liquidación.");
      setLiquidation(payload.data?.liquidacion || null);
      setLiquidationMessage(payload.mensaje || "Día liquidado y guardado.");
    } catch (error) { setLiquidationMessage(error.message || "No fue posible guardar la liquidación."); }
    finally { setLiquidationBusy(false); }
  }
  return <section className="daily-liquidation">
    <header className="module-panel daily-liquidation-intro">
      <div><p className="eyebrow">LIQUIDACIÓN DIARIA</p><h2>{liquidation ? "Día liquidado" : "Revisa y liquida un día"}</h2><p>{liquidation ? `Liquidado por ${liquidation.ResponsableNombre || liquidation.responsable_name || "responsable registrado"}. La fotografía quedó protegida.` : "Selecciona el responsable y guarda una fotografía definitiva de las ventas y gastos de este día."}</p></div>
      <label>Fecha a revisar<input type="date" value={date || ""} onChange={(event) => onDateChange?.(event.target.value)} /></label>
    </header>
    <section className="daily-liquidation-summary" aria-label="Resumen preliminar de liquidación">
      <article><span>{salesChannelTitle(salesChannel)}</span><strong>{formatCurrency(visibleSaleTotal)}</strong><small>{saleRows.length} venta{saleRows.length === 1 ? "" : "s"}</small></article>
      <article className="cash"><span>Ventas en efectivo</span><strong>{formatCurrency(paymentTotals.efectivo)}</strong><small>Confirmado</small></article>
      <article className="transfer"><span>Ventas por transferencia</span><strong>{formatCurrency(paymentTotals.transferencia)}</strong><small>Confirmada</small></article>
      <article className="pending"><span>Pendiente</span><strong>{formatCurrency(paymentTotals.pendiente)}</strong><small>Por cobrar</small></article>
      <article className="pending"><span>Gastos de domicilios</span><strong>{formatCurrency(expenseTotalsBySource.DOMICILIOS)}</strong><small>Registrados en el día</small></article>
      <article className="pending"><span>Gastos de stand</span><strong>{formatCurrency(expenseTotalsBySource.STAND)}</strong><small>Registrados en el día</small></article>
    </section>
    <section className="daily-liquidation-receive-card" aria-label="Liquidación de efectivo de domicilios">
      <div><p className="eyebrow">DOMICILIOS</p><h3>Recibir al repartidor</h3><small>Solo efectivo físico: ventas, cartera recuperada y gastos de domicilios.</small></div>
      <div className="daily-liquidation-receive-grid">
        <div><span>Ventas domicilio</span><strong>{formatCurrency(domicilioSettlement.ventas_totales)}</strong></div>
        <div className="transfer"><span>Transferencias</span><strong>− {formatCurrency(domicilioSettlement.transferencias)}</strong></div>
        <div className="pending"><span>Cartera nueva</span><strong>− {formatCurrency(domicilioSettlement.cartera_nueva)}</strong></div>
        <div><span>+ Cartera recuperada en efectivo</span><strong>{formatCurrency(domicilioSettlement.cartera_recuperada_efectivo)}</strong></div>
        <div className="expense"><span>− Gastos de domicilios</span><strong>{formatCurrency(domicilioSettlement.gastos)}</strong></div>
        <div className="total"><span>Valor a recibir del repartidor</span><strong>{formatCurrency(domicilioSettlement.valor_a_consignar)}</strong></div>
      </div>
      {domicilioSettlement.saldo_gastos_por_compensar > 0 && <p className="daily-liquidation-shortfall">Los gastos en efectivo superan lo recibido por {formatCurrency(domicilioSettlement.saldo_gastos_por_compensar)}. Requiere revisión administrativa.</p>}
    </section>
    <section className="daily-liquidation-receive-card stand" aria-label="Liquidación de efectivo del stand">
      <div><p className="eyebrow">STAND</p><h3>Liquidación del punto de venta</h3><small>El efectivo del stand se liquida aparte y sus gastos no afectan Domicilios.</small></div>
      <div className="daily-liquidation-receive-grid">
        <div><span>Ventas en sitio</span><strong>{formatCurrency(standSettlement.ventas_totales)}</strong></div>
        <div className="transfer"><span>Transferencias</span><strong>− {formatCurrency(standSettlement.transferencias)}</strong></div>
        <div className="pending"><span>Cartera nueva</span><strong>− {formatCurrency(standSettlement.cartera_nueva)}</strong></div>
        <div className="expense"><span>− Gastos de stand</span><strong>{formatCurrency(standSettlement.gastos)}</strong></div>
        <div className="total"><span>Valor a recibir del stand</span><strong>{formatCurrency(standSettlement.valor_a_consignar)}</strong></div>
      </div>
      {standSettlement.saldo_gastos_por_compensar > 0 && <p className="daily-liquidation-shortfall">Los gastos en efectivo superan lo recibido por {formatCurrency(standSettlement.saldo_gastos_por_compensar)}. Requiere revisión administrativa.</p>}
    </section>
    <section className="module-panel daily-liquidation-receivables" aria-label="Recaudos de cartera del día">
      <header><div><p className="eyebrow">RECAUDOS DE CARTERA</p><h3>Dinero recibido de ventas anteriores</h3></div><small>Se informa separado: no aumenta las ventas del día.</small></header>
      <div className="daily-liquidation-summary">
        <article className="cash"><span>Efectivo recibido</span><strong>{formatCurrency(receivableCash)}</strong><small>{Number(receivablesToday?.efectivo?.operaciones || 0)} abono(s)</small></article>
        <article className="transfer"><span>Transferencia recibida</span><strong>{formatCurrency(receivableTransfer)}</strong><small>{Number(receivablesToday?.transferencia?.operaciones || 0)} abono(s)</small></article>
        <article><span>Total recuperado</span><strong>{formatCurrency(receivableTotal)}</strong><small>{Number(receivablesToday?.operaciones || 0)} movimiento(s)</small></article>
      </div>
    </section>
    <section className={`module-panel daily-liquidation-action${liquidation ? " is-closed" : ""}`}>
      <div><p className="eyebrow">CIERRE DEL DÍA</p><strong>{liquidation ? "Liquidación guardada" : "Responsable obligatorio"}</strong><small>{liquidation ? `Estado: ${liquidation.Estado || liquidation.status || "LIQUIDADO"}` : "Elige quién realizó la liquidación para guardarla."}</small></div>
      {!liquidation && <><label>¿Quién liquidó?<select value={responsible} onChange={(event) => setResponsible(event.target.value)}><option value="">Seleccionar responsable</option><option value="DANIEL_FELIPE">Daniel Felipe</option><option value="SEBASTIAN">Sebastián</option></select></label><button type="button" className="primary-action" disabled={!responsible || liquidationBusy} onClick={saveLiquidation}>{liquidationBusy ? "Guardando…" : "Guardar y liquidar día"}</button></>}
      {liquidationMessage && <p className="action-status">{liquidationMessage}</p>}
    </section>
    <section className="module-panel daily-liquidation-products">
      <header><div><p className="eyebrow">CONCILIACIÓN DE PRODUCTOS</p><h3>Vendido contra comprado</h3></div><small>{salesChannelTitle(salesChannel)} · cambia el origen en el detalle de ventas.</small></header>
      {groupedProducts.length ? <div className="daily-liquidation-table-wrap"><table className="daily-liquidation-table"><thead><tr><th>Producto</th><th>Vendido</th><th>Comprado</th><th>Diferencia</th></tr></thead><tbody>{groupedProducts.map((item) => <tr key={item.key}><td><strong>{liquidationProductLabel(item)}</strong></td><td>{formatQuantity(item.vendido)}</td><td>{formatQuantity(item.comprado)}</td><td className={item.diferencia === 0 ? "balanced" : item.diferencia > 0 ? "surplus" : "shortage"}>{item.diferencia > 0 ? "+" : ""}{formatQuantity(item.diferencia)}</td></tr>)}</tbody></table></div> : <p className="empty-state">No hay productos vendidos ni compras diarias registradas en esta fecha.</p>}
    </section>
    <section className="module-panel daily-liquidation-sales">
      <header><div><p className="eyebrow">DETALLE BAJO DEMANDA</p><h3>{showSalesDetail ? salesChannelTitle(salesChannel) : "Detalle de domicilios"}</h3></div><button type="button" className="secondary-action" aria-expanded={showSalesDetail} onClick={() => setShowSalesDetail((visible) => !visible)}>{showSalesDetail ? "Ocultar detalle" : "Mostrar detalle de domicilios"}</button></header>
      {showSalesDetail && <><SalesChannelSelector value={salesChannel} onChange={setSalesChannel} includeStand={hasStandActivity} />
      {saleRows.length ? <div className="daily-liquidation-sale-list">{saleRows.map((sale) => { const paymentClass = paymentVisualClass(sale); return <article className={`sale-record payment-${paymentClass}`} key={sale.venta_id || sale.pedido_id}><div><div className="sale-record-head"><div><strong>{sale.cliente || "Cliente sin nombre"}</strong><small>{sale.pedido_id || sale.venta_id} · {sale.etiqueta_canal || salesChannelTitle(salesChannelCode(sale))}</small></div><div className="sale-record-payment"><b>{formatCurrency(sale.valor_total)}</b><span className={`payment-badge payment-badge-${paymentClass}`}>{sale.etiqueta_pago_visual || "Efectivo"}</span></div></div><ul>{(Array.isArray(sale.productos) ? sale.productos : []).map((item, index) => <li key={`${item.codigo || item.producto}-${index}`}><span>{formatQuantity(item.cantidad)} · {liquidationProductLabel(item)}</span><b>{formatCurrency(item.valor)}</b></li>)}</ul></div></article>; })}</div> : <p className="empty-state">No hay ventas registradas en este canal para esta fecha.</p>}</>}
    </section>
  </section>;
}

function Statistics({ data = {}, range = {}, onRangeChange }) {
  const [tab, setTab] = useState("ventas");
  const [showCosts, setShowCosts] = useState(false);
  const [supplements, setSupplements] = useState({});
  const [loadingSupplement, setLoadingSupplement] = useState(false);
  // La operación diaria empieza por domicilios. El punto de venta solo se
  // incorpora al detalle cuando la persona lo selecciona explícitamente.
  const [salesChannel, setSalesChannel] = useState("RUTA");
  const [shareStatus, setShareStatus] = useState("");
  const rangeKey = `${range?.fecha_desde || ""}:${range?.fecha_hasta || ""}`;
  const tabs = [
    "ventas",
    "conversion",
    "productos",
    "clientes",
    "operacion",
    "inventario",
    "pagos",
  ];
  const tabLabels = {
    ventas: "Ventas",
    conversion: "Conversión",
    productos: "Productos",
    clientes: "Clientes",
    operacion: "Operación",
    inventario: "Inventario",
    pagos: "Pagos y cartera",
  };
  const requiredSupplements = useMemo(() => {
    const required = new Set();
    if (tab === "conversion") required.add("analitica");
    if (["operacion", "inventario", "pagos"].includes(tab)) required.add("operativas");
    if (tab === "ventas" && showCosts) {
      required.add("finanzas");
      required.add("costos");
    }
    return [...required];
  }, [tab, showCosts]);

  useEffect(() => {
    setSupplements({});
  }, [rangeKey]);

  useEffect(() => {
    const missing = requiredSupplements.filter((section) => !supplements[section]);
    if (!missing.length) return undefined;
    let active = true;
    setLoadingSupplement(true);
    Promise.all(missing.map(async (section) => [section, await readStatisticsSupplement(section, range)]))
      .then((entries) => {
        if (!active) return;
        setSupplements((current) => ({ ...current, ...Object.fromEntries(entries) }));
      })
      .catch(() => {
        // La pantalla base ya está disponible. La capa de datos opcional se
        // puede volver a solicitar al cambiar de pestaña, sin desmontarla.
      })
      .finally(() => {
        if (active) setLoadingSupplement(false);
      });
    return () => { active = false; };
  }, [rangeKey, range, requiredSupplements, supplements]);

  const viewData = useMemo(
    () => Object.assign({}, data, ...Object.values(supplements)),
    [data, supplements],
  );
  const ensureFullReport = async () => {
    const sections = ["operativas", "finanzas", "analitica", "costos"];
    const missing = sections.filter((section) => !supplements[section]);
    if (!missing.length) return viewData;
    setLoadingSupplement(true);
    try {
      const entries = await Promise.all(
        missing.map(async (section) => [section, await readStatisticsSupplement(section, range)]),
      );
      const extra = Object.fromEntries(entries);
      setSupplements((current) => ({ ...current, ...extra }));
      return Object.assign({}, viewData, ...Object.values(extra));
    } finally {
      setLoadingSupplement(false);
    }
  };
  const money = (value) =>
    value == null ? "—" : `$${Number(value).toLocaleString("es-CO")}`;
  // Todas las cifras que acompañan el detalle obedecen al mismo canal. Así,
  // al abrir Estadísticas no se mezcla punto de venta con domicilios.
  const channelDays = filterSalesDaysByChannel(viewData.ventas || [], salesChannel);
  const channelSummary = summarizeSalesDays(channelDays);
  const channelLabel = salesChannelTitle(salesChannel);
  const cards = [
    [channelLabel, money(channelSummary.total)],
    ["Ventas", channelSummary.quantity],
    ["Ticket promedio", money(channelSummary.ticket)],
    ["Producto destacado", channelSummary.topProduct],
  ];
  return (
    <>
      <div className="statistics-interactive statistics-module">
      <section className="filter-bar stats-filter statistics-command-bar">
        <div>
          <p className="eyebrow">ANÁLISIS DEL NEGOCIO</p>
          <h2>Decisiones con datos del ERP.</h2>
        </div>
        <div className="stats-date-range" aria-label="Rango de ventas">
          <label>
            Desde
            <input
              type="date"
              value={range?.fecha_desde || ""}
              max={range?.fecha_hasta || undefined}
              onChange={(event) => onRangeChange?.((actual) => ({ ...actual, fecha_desde: event.target.value }))}
            />
          </label>
          <label>
            Hasta
            <input
              type="date"
              value={range?.fecha_hasta || ""}
              min={range?.fecha_desde || undefined}
              onChange={(event) => onRangeChange?.((actual) => ({ ...actual, fecha_hasta: event.target.value }))}
            />
          </label>
        </div>
        <div className="stats-actions">
          <button type="button" className="secondary-action" onClick={async () => {
            try {
              await ensureFullReport();
              window.print();
            } catch (_error) {
              setShareStatus("No fue posible preparar el informe completo.");
            }
          }} title="Abrir el informe listo para guardar como PDF">
            <Download size={16} /> Descargar PDF
          </button>
          <button type="button" className="secondary-action" onClick={async () => {
            try {
              const reportData = await ensureFullReport();
              const texto = buildStatisticsReportText(reportData, range, showCosts);
              if (navigator.share) await navigator.share({ title: "Informe de Titina", text: texto });
              else if (navigator.clipboard) await navigator.clipboard.writeText(texto);
              setShareStatus(navigator.share ? "Informe compartido." : "Resumen copiado para compartir.");
            } catch (error) {
              if (error?.name !== "AbortError") setShareStatus("No fue posible compartir el informe.");
            }
          }} title="Compartir resumen">
            <Share2 size={16} /> Compartir
          </button>
          {shareStatus && <small className="stats-share-status">{shareStatus}</small>}
        </div>
      </section>
      <div className="cards stats-cards statistics-kpi-grid">
        {cards.map(([label, value]) => (
          <KpiCard
            key={label}
            label={label}
            value={value}
            trend={DEV_BYPASS_AUTH ? "Disponible en mock" : "Fuente ERP"}
            icon={BarChart3}
          />
        ))}
      </div>
      <div className="tabs statistics-tabs">
        {tabs.map((value) => (
          <button
            className={tab === value ? "tab active" : "tab"}
            key={value}
            onClick={() => setTab(value)}
          >
            {tabLabels[value]}
          </button>
        ))}
      </div>
      {tab === "ventas" && (
        <label className="stats-cost-toggle">
          <input type="checkbox" checked={showCosts} onChange={(event) => setShowCosts(event.target.checked)} />
          Mostrar costos reales y gastos diarios
        </label>
      )}
      {tab === "ventas" && salesChannel === "TODOS" && <SalesChannelSummary channels={viewData.canales_ventas || []} />}
      <section className="module-panel stats-content statistics-content-card">
        <div className="module-heading">
          <div>
            <p className="eyebrow">{tabLabels[tab].toUpperCase()}</p>
            <h2>
              {tab === "ventas"
                ? "Comparación temporal"
                : `Detalle de ${tabLabels[tab].toLowerCase()}`}
            </h2>
          </div>
          <span className="record-count">
            {loadingSupplement ? "Actualizando detalle…" : DEV_BYPASS_AUTH ? "Datos de desarrollo" : "Lectura ERP"}
          </span>
        </div>
        {tab === "ventas" ? (
          <SalesAnalysis data={viewData} showCosts={showCosts} salesChannel={salesChannel} onSalesChannelChange={setSalesChannel} />
        ) : tab === "conversion" ? (
          <WebAnalyticsFunnel data={viewData} />
        ) : tab === "productos" ? (
          <ProductStatistics data={viewData.productos || {}} />
        ) : tab === "clientes" ? (
          <CustomerStatistics data={viewData.clientes || {}} />
        ) : tab === "operacion" ? (
          <OperationStatistics data={viewData.operacion || {}} />
        ) : tab === "inventario" ? (
          <InventoryStatistics data={viewData.inventario || {}} />
        ) : tab === "pagos" ? (
          <PaymentsStatistics pagos={viewData.pagos || {}} cartera={viewData.cartera || {}} cajaPresencial={viewData.caja_presencial || {}} />
        ) : (
          <AnalysisTable tab={tab} data={viewData} />
          )}
      </section>
      </div>
      <StatisticsReport data={viewData} range={range} showCosts={showCosts} />
    </>
  );
}

function reportRows(rows, mapRow) {
  const source = Array.isArray(rows) ? rows : [];
  return source.length ? source.map(mapRow) : [["Sin datos", "—"]];
}

function SalesChannelSummary({ channels = [] }) {
  const rows = Array.isArray(channels) ? channels : [];
  const total = rows.reduce((value, channel) => value + Number(channel.valor_ventas || 0), 0);
  const quantity = rows.reduce((value, channel) => value + Number(channel.cantidad_ventas || 0), 0);
  const channelClass = (channel) => String(channel?.canal || "").toUpperCase() === "PRESENCIAL" ? "onsite" : "delivery";
  return <section className="sales-channel-summary" aria-label="Ventas por canal">
    <header><div><p className="eyebrow">CANALES DE VENTA</p><h3>Ventas separadas por origen</h3></div><small>El total general suma domicilios y ventas en sitio.</small></header>
    <div>
      {rows.map((channel) => <article className={`sales-channel-card sales-channel-${channelClass(channel)}`} key={channel.canal}>
        <strong><i aria-hidden="true" />{channel.etiqueta}</strong>
        <b>{formatCurrency(channel.valor_ventas)}</b>
        <span>{channel.cantidad_ventas || 0} venta{Number(channel.cantidad_ventas || 0) === 1 ? "" : "s"} · Ticket {formatCurrency(channel.ticket_promedio)}</span>
        <small>Efectivo {formatCurrency(channel.efectivo)} · Transferencias {formatCurrency(channel.transferencias)}</small>
      </article>)}
      <article className="sales-channel-card sales-channel-total">
        <strong><i aria-hidden="true" />Ventas totales</strong>
        <b>{formatCurrency(total)}</b>
        <span>{quantity} venta{quantity === 1 ? "" : "s"} en el período</span>
        <small>Domicilios + ventas en sitio</small>
      </article>
    </div>
  </section>;
}

function ReportTable({ title, note = "", rows = [] }) {
  return <section className="statistics-report-section">
    <header><h3>{title}</h3>{note && <small>{note}</small>}</header>
    <div className="statistics-report-table">
      {rows.map(([label, value], index) => <div key={`${label}-${index}`}>
        <span>{label}</span><b>{value}</b>
      </div>)}
    </div>
  </section>;
}

function getRecentPaymentVisualSummary(days) {
  const summary = {
    efectivo: { ventas: 0, valor: 0 },
    transferencia: { ventas: 0, valor: 0 },
    pendiente: { ventas: 0, valor: 0 },
  };
  (days || []).filter((day) => isRecentPaymentVisualDate(day.fecha)).forEach((day) => {
    (day.ventas || []).forEach((sale) => {
      const state = paymentVisualClass(sale);
      summary[state].ventas += 1;
      summary[state].valor += Number(sale.valor_total || 0);
    });
  });
  return summary;
}

function buildStatisticsReportText(data, range, showCosts) {
  const period = `${formatSalesDate(range?.fecha_desde)} a ${formatSalesDate(range?.fecha_hasta)}`;
  const products = data.productos?.top_por_unidades || [];
  const customers = data.clientes?.top_por_valor || [];
  const operation = data.operacion?.resumen || {};
  const payments = data.pagos?.resumen || {};
  const receivables = data.cartera?.resumen || {};
  const posCash = data.caja_presencial?.resumen || {};
  const funnel = data.embudo_web || {};
  const recentPayments = getRecentPaymentVisualSummary(data.ventas || []);
  const lines = [
    `Informe Titina · ${period}`,
    `Ventas: ${formatCurrency(data.ventas_periodo)}`,
    `Pedidos: ${data.pedidos_periodo ?? 0}`,
    `Ticket promedio: ${formatCurrency(data.ticket_promedio)}`,
    ...(data.canales_ventas || []).map((channel) => `${channel.etiqueta}: ${formatCurrency(channel.valor_ventas)} · ${channel.cantidad_ventas || 0} ventas · Efectivo ${formatCurrency(channel.efectivo)} · Transferencias ${formatCurrency(channel.transferencias)}`),
    "",
    "Pagos últimos 8 días:",
    `  Efectivo: ${formatCurrency(recentPayments.efectivo.valor)} · ${recentPayments.efectivo.ventas} ventas`,
    `  Transferencia: ${formatCurrency(recentPayments.transferencia.valor)} · ${recentPayments.transferencia.ventas} ventas`,
    `  Pendiente: ${formatCurrency(recentPayments.pendiente.valor)} · ${recentPayments.pendiente.ventas} ventas`,
    "",
    "Productos más vendidos:",
    ...(products.length ? products.slice(0, 5).map((product, index) => `  ${index + 1}. ${product.producto || "Producto"} · ${Number(product.unidades_vendidas || 0).toLocaleString("es-CO", { maximumFractionDigits: 3 })} ${product.unidad || "unidades"}`) : ["  Sin datos en este período"]),
    "",
    "Clientes por valor comprado:",
    ...(customers.length ? customers.slice(0, 5).map((customer, index) => `  ${index + 1}. ${customer.cliente || "Cliente sin identificar"} · ${formatCurrency(customer.valor_comprado)}`) : ["  Sin datos en este período"]),
    `Clientes con una sola compra: ${(data.clientes?.una_compra || []).length}`,
    "",
    `Operación: ${operation.entregados || 0} entregados · ${operation.pendientes || 0} pendientes · ${operation.parciales || 0} parciales`,
    `Inventario: ${data.inventario?.resumen?.movimientos || 0} movimientos · ${data.inventario?.resumen?.entradas || 0} entradas · ${data.inventario?.resumen?.despachos || 0} despachos`,
    `Pagos aplicados: ${formatCurrency(payments.valor_recibido)} · Cartera pendiente actual: ${formatCurrency(receivables.saldo_pendiente_actual)}`,
    `Caja presencial: ${posCash.sesiones || 0} turno(s) · Efectivo esperado ${formatCurrency(posCash.efectivo_esperado)} · Transferencias ${formatCurrency(posCash.transferencias)} · Diferencia ${formatCurrency(posCash.diferencia_acumulada)}`,
    `Conversión catálogo → confirmación: ${funnel.tasa_catalogo_a_confirmacion == null ? "Sin datos" : `${funnel.tasa_catalogo_a_confirmacion}%`}`,
  ];
  if (showCosts) {
    const costReport = data.informe_costos_reales || {};
    const financial = data.finanzas_por_dia || [];
    const costs = (costReport.dias || []).reduce((total, day) => total + Number(day.costo_retorno_total || 0), 0);
    const expenses = financial.reduce((total, day) => total + Number(day.gastos_totales || 0), 0);
    lines.push(`Retorno de capital: ${formatCurrency(costs)} · Gastos: ${formatCurrency(expenses)}`);
    (costReport.dias || []).forEach((day) => {
      lines.push(formatSalesDate(day.fecha));
      (day.clientes || []).forEach((sale) => {
        lines.push(`  ${sale.cliente || "Cliente sin identificar"}`);
        (sale.productos || []).forEach((product) => {
          const productLabel = formatFinancialProduct(product);
          const returnValue = product.costo_retorno == null ? "" : formatCurrency(product.costo_retorno);
          lines.push(`    ${productLabel} · Precio venta ${formatFinancialSalePrice(product)} · Total ${formatCurrency(product.valor_venta)} · Retorno ${returnValue} · Utilidad ${formatCurrency(product.utilidad)}`);
        });
      });
    });
  }
  lines.push("Fuente: ERP Titina · Informe administrativo");
  return lines.join("\n");
}

function StatisticsReport({ data, range, showCosts }) {
  const productRows = reportRows(data.productos?.top_por_unidades, (row) => [
    `${row.producto || "Producto"}${row.presentacion ? ` · ${row.presentacion}` : ""}`,
    `${Number(row.unidades_vendidas || 0).toLocaleString("es-CO", { maximumFractionDigits: 3 })} ${row.unidad || "unidades"}`,
  ]);
  const customerRows = reportRows(data.clientes?.top_por_valor, (row) => [
    row.cliente || "Cliente sin identificar", formatCurrency(row.valor_comprado),
  ]);
  const operationRows = reportRows(data.operacion?.por_estado, (row) => [row.estado, row.pedidos || 0]);
  const inventoryRows = reportRows(data.inventario?.por_tipo, (row) => [
    `${row.tipo} · ${row.movimientos || 0} registros`,
    `${Number(row.cantidad || 0).toLocaleString("es-CO", { maximumFractionDigits: 3 })} · ${formatCurrency(row.valor)}`,
  ]);
  const paymentRows = reportRows(data.pagos?.por_medio, (row) => [
    `${row.metodo} · ${row.operaciones || 0} operaciones`, formatCurrency(row.valor_recibido),
  ]);
  const funnel = data.embudo_web || {};
  const financial = data.finanzas_por_dia || [];
  const costs = financial.reduce((total, day) => total + Number(day.costo_real_ventas || 0), 0);
  const expenses = financial.reduce((total, day) => total + Number(day.gastos_totales || 0), 0);
  const receivables = data.cartera?.resumen || {};
  const posCash = data.caja_presencial?.resumen || {};
  const recentPayments = getRecentPaymentVisualSummary(data.ventas || []);
  const recentPaymentRows = [
    ["Efectivo", `${formatCurrency(recentPayments.efectivo.valor)} · ${recentPayments.efectivo.ventas} ventas`],
    ["Transferencia", `${formatCurrency(recentPayments.transferencia.valor)} · ${recentPayments.transferencia.ventas} ventas`],
    ["Pendiente", `${formatCurrency(recentPayments.pendiente.valor)} · ${recentPayments.pendiente.ventas} ventas`],
  ];
  const channelRows = reportRows(data.canales_ventas, (channel) => [
    `${channel.etiqueta} · ${channel.cantidad_ventas || 0} ventas`,
    `${formatCurrency(channel.valor_ventas)} · Ef. ${formatCurrency(channel.efectivo)} · Transf. ${formatCurrency(channel.transferencias)}`,
  ]);
  const posCashRows = reportRows(data.caja_presencial?.sesiones, (session) => [
    `${formatSalesDate(session.fecha_apertura)} · ${session.estado || "—"}`,
    `Inicial ${formatCurrency(session.efectivo_inicial)} · Ef. ${formatCurrency(session.efectivo_ventas)} · Transf. ${formatCurrency(session.transferencias)} · Dif. ${session.diferencia == null ? "—" : formatCurrency(session.diferencia)}`,
  ]);
  return <article className="statistics-report" aria-label="Informe de estadísticas listo para PDF">
    <header className="statistics-report-header">
      <div><p>INFORME ADMINISTRATIVO</p><h1>Titina</h1><strong>Estadísticas del negocio</strong></div>
      <div><span>Período</span><b>{formatSalesDate(range?.fecha_desde)} — {formatSalesDate(range?.fecha_hasta)}</b><small>Generado el {new Date().toLocaleDateString("es-CO")}</small></div>
    </header>
    <section className="statistics-report-kpis">
      <StatisticsMetric label="Ventas" value={formatCurrency(data.ventas_periodo)} />
      <StatisticsMetric label="Pedidos" value={data.pedidos_periodo ?? 0} />
      <StatisticsMetric label="Ticket promedio" value={formatCurrency(data.ticket_promedio)} />
      <StatisticsMetric label="Cartera pendiente" value={formatCurrency(receivables.saldo_pendiente_actual)} detail="Fotografía actual" />
    </section>
    <div className="statistics-report-grid">
      <ReportTable title="Productos más vendidos" note="Por unidades" rows={productRows} />
      <ReportTable title="Clientes con mayor compra" note="Por valor comprado" rows={customerRows} />
      <ReportTable title="Operación" note="Por fecha de entrega" rows={operationRows} />
      <ReportTable title="Inventario" note="Movimientos del período" rows={inventoryRows} />
      <ReportTable title="Pagos aplicados" note="Por medio de pago" rows={paymentRows} />
      <ReportTable title="Pagos últimos 8 días" note="Efectivo, transferencia y pendiente" rows={recentPaymentRows} />
      <ReportTable title="Canales de venta" note="Rutas y punto de venta" rows={channelRows} />
      <ReportTable title="Caja presencial" note={`${posCash.abiertas || 0} abierta(s) · ${posCash.cerradas || 0} cerrada(s)`} rows={posCashRows} />
      <ReportTable title="Cartera" rows={[
        ["Saldo inicial", formatCurrency(receivables.saldo_inicial)],
        ["Generada", formatCurrency(receivables.cartera_generada)],
        ["Recuperada", formatCurrency(receivables.cartera_recuperada)],
        ["Cierre estimado", formatCurrency(receivables.saldo_estimado_cierre)],
      ]} />
      <ReportTable title="Pedidos Web" note="Conversión del período" rows={[
        ["Sesiones con actividad", funnel.sesiones_con_actividad ?? 0],
        ["Pedidos confirmados", funnel.pedidos_confirmados ?? 0],
        ["Catálogo a confirmación", funnel.tasa_catalogo_a_confirmacion == null ? "—" : `${funnel.tasa_catalogo_a_confirmacion}%`],
      ]} />
      {showCosts && <ReportTable title="Costos y gastos" note="Incluidos por selección del informe" rows={[
        ["Retorno de capital", formatCurrency((data.informe_costos_reales?.dias || []).reduce((total, day) => total + Number(day.costo_retorno_total || 0), 0))],
        ["Gastos reales", formatCurrency(expenses)],
        ["Resultado operativo", formatCurrency(Number(data.ventas_periodo || 0) - costs - expenses)],
      ]} />}
    </div>
    {showCosts && <RealCostSalesReport report={data.informe_costos_reales} financialDays={financial} salesDays={data.ventas || []} printMode />}
    <footer>Fuente: ERP Titina. Operación se calcula por fecha de entrega; inventario por movimiento; pagos por fecha efectiva; cartera pendiente es una fotografía actual.</footer>
  </article>;
}

function SalesAnalysis({ data, showCosts, salesChannel = "RUTA", onSalesChannelChange }) {
  data = data || {};
  const visibleDays = filterSalesDaysByChannel(data.ventas || [], salesChannel);
  const daily = visibleDays.slice().reverse().map((day) => ({
    dia: String(day.fecha || "").slice(5),
    valor: Number(day.total || 0),
  }));
  return (
    <Suspense
      fallback={
        <div className="stats-grid">
          <div className="chart-box">Cargando análisis…</div>
        </div>
      }
    >
      <AdminWidgets
        type="sales"
        daily={daily}
        comparison={salesChannel === "TODOS" ? (data.ventas_comparacion || []) : []}
        showComparison={salesChannel === "TODOS"}
        formatCurrency={formatCurrency}
      />
      {showCosts ? (
        <RealCostSalesReport report={data.informe_costos_reales} financialDays={data.finanzas_por_dia || []} salesDays={data.ventas || []} salesChannel={salesChannel} channelControl={<SalesChannelSelector value={salesChannel} onChange={onSalesChannelChange} />} />
      ) : (
        <SalesDetail days={data.ventas || []} financialDays={data.finanzas_por_dia || []} showCosts={false} salesChannel={salesChannel} channelControl={null} />
      )}
    </Suspense>
  );
}

function formatFinancialProduct(product) {
  const quantity = Number(product?.cantidad || 0).toLocaleString("es-CO", { maximumFractionDigits: 3 });
  const presentation = String(product?.presentacion || "").trim();
  return `${quantity} ${product?.producto || "Producto sin nombre"}${presentation ? ` · ${presentation}` : ""}`;
}

function formatFinancialSalePrice(product) {
  return formatCurrency(product?.precio_unitario || 0);
}

function RealCostSalesReport({ report, financialDays, salesDays = [], printMode = false, salesChannel = "RUTA", channelControl = null }) {
  const sourceDays = Array.isArray(report?.dias) ? report.dias : [];
  const financialByDate = new Map((financialDays || []).map((day) => [day.fecha, day]));
  const salesDayOrder = new Map((salesDays || []).map((day, index) => [day.fecha, index]));
  const identifier = (value) => String(value || "").trim().toUpperCase();
  const channelBySale = new Map();
  (salesDays || []).forEach((day) => (day.ventas || []).forEach((sale) => {
    const channel = salesChannelCode(sale);
    [sale.venta_id, sale.pedido_id].map(identifier).filter(Boolean).forEach((key) => channelBySale.set(key, channel));
  }));
  const salesOrderByDate = new Map((salesDays || []).map((day) => {
    const order = new Map();
    (day.ventas || []).forEach((sale, index) => {
      // The normal report is the source of truth for sequence. Accept both
      // identifiers because the cost snapshot and the sales report can use
      // different names for the same sale/order.
      [sale.venta_id, sale.pedido_id].map(identifier).filter(Boolean).forEach((key) => {
        if (!order.has(key)) order.set(key, index);
      });
    });
    return [day.fecha, order];
  }));
  const days = sourceDays
    .map((day) => ({
      ...day,
      clientes: (day.clientes || []).filter((sale) =>
        salesChannel === "TODOS" || (channelBySale.get(identifier(sale.venta_id)) || channelBySale.get(identifier(sale.pedido_id)) || "RUTA") === salesChannel
      ),
    }))
    .filter((day) => day.clientes.length > 0)
    .sort((left, right) =>
      (salesDayOrder.get(left.fecha) ?? Number.MAX_SAFE_INTEGER) -
      (salesDayOrder.get(right.fecha) ?? Number.MAX_SAFE_INTEGER)
    );
  if (!days.length) {
    return <p className="empty-state">No hay líneas con costo confirmado en este rango de fechas.</p>;
  }
  return <section className={printMode ? "real-cost-report print-only-cost-report" : "real-cost-report"}>
    {!printMode && channelControl}
    <header className="real-cost-report-heading">
      <div>
        <p className="eyebrow">COSTOS REALES DE VENTAS</p>
        <h3>Retorno de capital y utilidad por venta</h3>
      </div>
      <small>Precio venta · Retorno · Utilidad<br />El retorno queda vacío en productos de operación diaria.</small>
    </header>
    {Number(report?.ventas_pendientes_configuracion || 0) > 0 && (
      <p className="statistics-data-note">Hay {report.ventas_pendientes_configuracion} venta(s) sin costo configurado; no se inventan valores.</p>
    )}
    <div className="real-cost-days">
      {days.map((day) => {
        const financial = financialByDate.get(day.fecha);
        // Los gastos pertenecen al día, no a un canal. Solo aparecen cuando
        // la persona decidió incluir ambos tipos de venta.
        const expenses = salesChannel === "TODOS" ? (financial?.gastos || []) : [];
        const clientOrder = salesOrderByDate.get(day.fecha) || new Map();
        const clients = (day.clientes || []).slice().sort((left, right) =>
          (clientOrder.get(identifier(left.venta_id)) ?? clientOrder.get(identifier(left.pedido_id)) ?? Number.MAX_SAFE_INTEGER) -
          (clientOrder.get(identifier(right.venta_id)) ?? clientOrder.get(identifier(right.pedido_id)) ?? Number.MAX_SAFE_INTEGER)
        );
        const dailySalesTotal = clients.reduce(
          (total, sale) => total + Number(sale.valor_total_venta || 0), 0
        );
        const dailyReturnTotal = clients.reduce((total, sale) => total + (sale.productos || []).reduce(
          (partial, product) => partial + Number(product.costo_retorno || 0), 0
        ), 0);
        const dailyProfitTotal = clients.reduce((total, sale) => total + (sale.productos || []).reduce(
          (partial, product) => partial + Number(product.utilidad || 0), 0
        ), 0);
        return <article className="real-cost-day" key={day.fecha}>
          <header>
            <div><p className="eyebrow">{formatSalesDate(day.fecha)}</p><strong>{clients.length} venta{clients.length === 1 ? "" : "s"}</strong></div>
            <div className="real-cost-day-totals"><span>Ventas <b>{formatCurrency(dailySalesTotal)}</b></span><span>Retorno <b>{formatCurrency(dailyReturnTotal)}</b></span><span>Utilidad <b>{formatCurrency(dailyProfitTotal)}</b></span></div>
          </header>
          <div className="real-cost-customers">
            {clients.map((sale) => <section className="real-cost-customer" key={sale.venta_id}>
              <header><strong>{sale.cliente || "Cliente sin identificar"}</strong><small>Venta total {formatCurrency(sale.valor_total_venta)}</small></header>
              <ul>
                {(sale.productos || []).map((product, index) => <li key={`${product.codigo}-${index}`}>
                  <span className="real-cost-product">{formatFinancialProduct(product)}</span>
                  <b>{formatFinancialSalePrice(product)}</b>
                  <b className={product.costo_retorno == null ? "real-cost-empty" : ""}>{product.costo_retorno == null ? "" : formatCurrency(product.costo_retorno)}</b>
                  <b className="real-cost-profit">{formatCurrency(product.utilidad)}</b>
                </li>)}
              </ul>
            </section>)}
          </div>
          {expenses.length > 0 && <footer className="real-cost-expenses">Gastos reales: <b>{formatCurrency(financial.gastos_totales)}</b> · {expenses.map((expense) => expense.descripcion || expense.tipo).filter(Boolean).join(" · ")}</footer>}
        </article>;
      })}
    </div>
  </section>;
}

function RankingList({ title, detail, rows, valueLabel, formatValue }) {
  if (!rows.length)
    return <section className="statistics-ranking"><header><div><h3>{title}</h3><p>{detail}</p></div></header><p className="empty-state">No hay datos para este rango de fechas.</p></section>;
  return <section className="statistics-ranking">
    <header><div><h3>{title}</h3><p>{detail}</p></div><span>Top {rows.length}</span></header>
    <ol>
      {rows.map((row, index) => <li key={`${row.codigo || row.cliente_id || row.cliente || "fila"}-${index}`}>
        <span className="statistics-rank">{index + 1}</span>
        <div>
          <strong>{row.producto || row.cliente || "Sin identificar"}</strong>
          <small>{row.presentacion || `${row.cantidad_ventas || 0} compra${Number(row.cantidad_ventas || 0) === 1 ? "" : "s"}`}</small>
        </div>
        <b>{formatValue(row)}</b>
      </li>)}
    </ol>
    {valueLabel && <small className="statistics-ranking-note">{valueLabel}</small>}
  </section>;
}

function ProductStatistics({ data }) {
  const summary = data.resumen || {};
  const byUnits = data.top_por_unidades || [];
  const byValue = data.top_por_valor || [];
  return <div className="statistics-detail-grid">
    <section className="statistics-intro-card">
      <p className="eyebrow">PRODUCTOS VENDIDOS</p>
      <h3>Qué se vendió en el período</h3>
      <div className="statistics-mini-kpis">
        <span><b>{summary.presentaciones_vendidas || 0}</b> presentaciones vendidas</span>
        <span><b>{formatCurrency(summary.valor_lineas_vendidas || 0)}</b> valor de productos</span>
        <span><b>{summary.ventas_sin_detalle || 0}</b> ventas sin detalle</span>
      </div>
    </section>
    <div className="statistics-ranking-grid">
      <RankingList
        title="Más vendidos"
        detail="Ordenados por unidades vendidas."
        rows={byUnits}
        valueLabel="Cada presentación se cuenta por separado."
        formatValue={(row) => `${Number(row.unidades_vendidas || 0).toLocaleString("es-CO", { maximumFractionDigits: 3 })} ${row.unidad || "unidades"}`}
      />
      <RankingList
        title="Mayor facturación"
        detail="Ordenados por valor vendido."
        rows={byValue}
        valueLabel="No incluye transporte ni domicilio."
        formatValue={(row) => formatCurrency(row.valor_vendido || 0)}
      />
    </div>
  </div>;
}

function CustomerStatistics({ data }) {
  const summary = data.resumen || {};
  const oneTime = data.una_compra || [];
  const oneTimeColumns = [
    { accessorKey: "cliente", header: "Cliente" },
    { accessorKey: "valor_comprado", header: "Compra" },
    { accessorKey: "ticket_promedio", header: "Ticket" },
    { accessorKey: "ultima_compra", header: "Última compra" },
  ];
  return <div className="statistics-detail-grid">
    <section className="statistics-intro-card">
      <p className="eyebrow">CLIENTES DEL PERÍODO</p>
      <h3>Frecuencia y valor de compra</h3>
      <div className="statistics-mini-kpis">
        <span><b>{summary.clientes_identificados || 0}</b> clientes identificados</span>
        <span><b>{summary.clientes_recurrentes || 0}</b> recurrentes</span>
        <span><b>{summary.clientes_una_compra || 0}</b> con una compra</span>
      </div>
      {summary.ventas_sin_cliente > 0 && <p className="statistics-data-note">{summary.ventas_sin_cliente} venta(s) no se pudieron asociar a un cliente.</p>}
    </section>
    <div className="statistics-ranking-grid">
      <RankingList
        title="Clientes que más compran"
        detail="Ordenados por valor total comprado."
        rows={data.top_por_valor || []}
        formatValue={(row) => formatCurrency(row.valor_comprado || 0)}
      />
      <RankingList
        title="Ticket promedio más alto"
        detail="Promedio por compra en el período."
        rows={data.top_por_ticket || []}
        formatValue={(row) => formatCurrency(row.ticket_promedio || 0)}
      />
    </div>
    <section className="statistics-one-time">
      <header><div><p className="eyebrow">OPORTUNIDAD DE RECOMPRA</p><h3>Clientes con una sola compra</h3></div><span>{oneTime.length}</span></header>
      {oneTime.length ? <DataTable columns={oneTimeColumns} rows={oneTime} /> : <p className="empty-state">No hay clientes con una única compra en este rango.</p>}
    </section>
  </div>;
}

function StatisticsMetric({ label, value, detail = "" }) {
  return <div className="statistics-metric"><span>{label}</span><b>{value}</b>{detail && <small>{detail}</small>}</div>;
}

function OperationStatistics({ data }) {
  const summary = data.resumen || {};
  const columns = [
    { accessorKey: "estado", header: "Estado" },
    { accessorKey: "pedidos", header: "Pedidos" },
  ];
  return <div className="statistics-detail-grid">
    <section className="statistics-intro-card"><p className="eyebrow">OPERACIÓN POR FECHA DE ENTREGA</p><h3>Pedidos programados en el período</h3><div className="statistics-metrics-grid">
      <StatisticsMetric label="Programados" value={summary.pedidos_programados || 0} />
      <StatisticsMetric label="Entregados" value={summary.entregados || 0} />
      <StatisticsMetric label="Parciales" value={summary.parciales || 0} />
      <StatisticsMetric label="Pendientes" value={summary.pendientes || 0} />
      <StatisticsMetric label="No entregados" value={summary.no_entregados || 0} />
      <StatisticsMetric label="Cancelados" value={summary.cancelados || 0} />
    </div></section>
    <section className="statistics-one-time"><header><div><p className="eyebrow">ESTADOS REALES</p><h3>Resultado logístico</h3></div></header><DataTable columns={columns} rows={data.por_estado || []} /></section>
  </div>;
}

function InventoryStatistics({ data }) {
  const summary = data.resumen || {};
  const columns = [
    { accessorKey: "tipo", header: "Movimiento" },
    { accessorKey: "movimientos", header: "Registros" },
    { accessorKey: "cantidad", header: "Cantidad" },
    { accessorKey: "valor", header: "Valor" },
  ];
  return <div className="statistics-detail-grid">
    <section className="statistics-intro-card"><p className="eyebrow">MOVIMIENTOS DE INVENTARIO</p><h3>Actividad física registrada en el período</h3><div className="statistics-metrics-grid">
      <StatisticsMetric label="Movimientos" value={summary.movimientos || 0} />
      <StatisticsMetric label="Entradas" value={Number(summary.entradas || 0).toLocaleString("es-CO", { maximumFractionDigits: 3 })} />
      <StatisticsMetric label="Despachos" value={Number(summary.despachos || 0).toLocaleString("es-CO", { maximumFractionDigits: 3 })} />
      <StatisticsMetric label="Mermas" value={Number(summary.mermas || 0).toLocaleString("es-CO", { maximumFractionDigits: 3 })} />
    </div><p className="statistics-data-note">El saldo actual se consulta en Inventario; esta pestaña muestra movimientos ocurridos dentro del rango.</p></section>
    <section className="statistics-one-time"><header><div><p className="eyebrow">TRAZABILIDAD</p><h3>Movimientos por tipo</h3></div></header><DataTable columns={columns} rows={data.por_tipo || []} /></section>
  </div>;
}

function PaymentsStatistics({ pagos, cartera, cajaPresencial }) {
  const payment = pagos.resumen || {};
  const receivables = cartera.resumen || {};
  const collection = cartera.recaudos || {};
  const collectionRows = [
    { metodo: "Efectivo", ...(collection.efectivo || {}) },
    { metodo: "Transferencia", ...(collection.transferencia || {}) },
  ];
  const collectionColumns = [
    { accessorKey: "metodo", header: "Recaudo" },
    { accessorKey: "operaciones", header: "Abonos" },
    { accessorKey: "valor", header: "Recibido", cell: ({ getValue }) => formatCurrency(getValue()) },
    { accessorKey: "repartidor", header: "Recibido por repartidor", cell: ({ getValue }) => formatCurrency(getValue()) },
    { accessorKey: "administracion", header: "Recibido por administración", cell: ({ getValue }) => formatCurrency(getValue()) },
  ];
  const paymentColumns = [
    { accessorKey: "metodo", header: "Medio de pago" },
    { accessorKey: "operaciones", header: "Operaciones" },
    { accessorKey: "valor_recibido", header: "Valor recibido" },
  ];
  const cash = cajaPresencial?.resumen || {};
  const cashColumns = [
    { accessorKey: "fecha_apertura", header: "Apertura" },
    { accessorKey: "estado", header: "Estado" },
    { accessorKey: "efectivo_inicial", header: "Inicial", cell: ({ getValue }) => formatCurrency(getValue()) },
    { accessorKey: "efectivo_ventas", header: "Ventas efectivo", cell: ({ getValue }) => formatCurrency(getValue()) },
    { accessorKey: "transferencias", header: "Transferencias", cell: ({ getValue }) => formatCurrency(getValue()) },
    { accessorKey: "efectivo_esperado", header: "Esperado", cell: ({ getValue }) => formatCurrency(getValue()) },
    { accessorKey: "efectivo_contado", header: "Contado", cell: ({ getValue }) => getValue() == null ? "—" : formatCurrency(getValue()) },
    { accessorKey: "diferencia", header: "Diferencia", cell: ({ getValue }) => getValue() == null ? "—" : formatCurrency(getValue()) },
  ];
  return <div className="statistics-detail-grid">
    <section className="statistics-intro-card"><p className="eyebrow">PAGOS EFECTIVOS DEL PERÍODO</p><h3>Dinero aplicado y pendiente de validar</h3><div className="statistics-metrics-grid">
      <StatisticsMetric label="Recibido" value={formatCurrency(payment.valor_recibido || 0)} />
      <StatisticsMetric label="Aplicados" value={payment.operaciones_aplicadas || 0} />
      <StatisticsMetric label="Pendiente validación" value={formatCurrency(payment.pendiente_validacion || 0)} />
    </div></section>
    <section className="statistics-one-time"><header><div><p className="eyebrow">POR MEDIO</p><h3>Pagos aplicados</h3></div></header><DataTable columns={paymentColumns} rows={pagos.por_medio || []} /></section>
    <section className="statistics-intro-card"><p className="eyebrow">CARTERA</p><h3>Evolución del saldo</h3><div className="statistics-metrics-grid">
      <StatisticsMetric label="Saldo inicial" value={formatCurrency(receivables.saldo_inicial || 0)} />
      <StatisticsMetric label="Generada" value={formatCurrency(receivables.cartera_generada || 0)} />
      <StatisticsMetric label="Recuperada" value={formatCurrency(receivables.cartera_recuperada || 0)} />
      <StatisticsMetric label="Cierre estimado" value={formatCurrency(receivables.saldo_estimado_cierre || 0)} />
      <StatisticsMetric label="Pendiente actual" value={formatCurrency(receivables.saldo_pendiente_actual || 0)} detail="Fotografía actual" />
    </div></section>
    <section className="statistics-one-time receivable-collections"><header><div><p className="eyebrow">RECAUDOS DE CARTERA</p><h3>Dinero recibido de ventas anteriores</h3></div><small>No se suma como venta nueva.</small></header><div className="statistics-metrics-grid"><StatisticsMetric label="Efectivo recuperado" value={formatCurrency(collection?.efectivo?.valor || 0)} /><StatisticsMetric label="Transferencia recuperada" value={formatCurrency(collection?.transferencia?.valor || 0)} /><StatisticsMetric label="Total recuperado" value={formatCurrency(collection?.total || 0)} /><StatisticsMetric label="Abonos" value={collection?.operaciones || 0} /></div><DataTable columns={collectionColumns} rows={collectionRows} /></section>
    <section className="statistics-intro-card pos-cash-statistics"><p className="eyebrow">CAJA PRESENCIAL</p><h3>Arqueos del punto de venta</h3><div className="statistics-metrics-grid">
      <StatisticsMetric label="Turnos" value={cash.sesiones || 0} />
      <StatisticsMetric label="Efectivo esperado" value={formatCurrency(cash.efectivo_esperado || 0)} />
      <StatisticsMetric label="Transferencias" value={formatCurrency(cash.transferencias || 0)} />
      <StatisticsMetric label="Diferencia acumulada" value={formatCurrency(cash.diferencia_acumulada || 0)} />
    </div><p className="statistics-data-note">Abiertas: {cash.abiertas || 0} · Cerradas: {cash.cerradas || 0}. No se mezcla con la caja de repartidores.</p></section>
    <section className="statistics-one-time pos-cash-history"><header><div><p className="eyebrow">HISTORIAL DE CAJA</p><h3>Aperturas y cierres del punto físico</h3></div></header><DataTable columns={cashColumns} rows={cajaPresencial?.sesiones || []} /></section>
  </div>;
}

function WeeklyFinancialSummary({ weeks }) {
  if (!Array.isArray(weeks) || !weeks.length) return null;
  return <section className="weekly-financial-summary">
    <div className="weekly-financial-heading">
      <div><p className="eyebrow">CIERRE SEMANAL</p><h3>Domingo a sábado</h3></div>
      <small>CMV congelado al abrir cada ruta · compras adicionales separadas</small>
    </div>
    {weeks.map((week) => <article key={week.fecha_desde}>
      <header><strong>{formatSalesDate(week.fecha_desde)} — {formatSalesDate(week.fecha_hasta)}</strong><b>{week.resultado_operativo == null ? "Costo pendiente" : formatCurrency(week.resultado_operativo)}</b></header>
      <div className="weekly-financial-grid">
        <span>Ventas <b>{formatCurrency(week.ventas_totales)}</b></span>
        <span>CMV real <b>{formatCurrency(week.costo_real_ventas)}</b></span>
        <span>CMV presupuestado <b>{formatCurrency(week.cmv_presupuestado)}</b></span>
        <span>Compras adicionales <b>{formatCurrency(week.compras_adicionales)}</b></span>
        <span>Gastos reales <b>{formatCurrency(week.gastos_totales)}</b></span>
        <span className={week.variacion_compras_cmv > 0 ? "over" : "ok"}>Variación compras/CMV <b>{week.variacion_compras_cmv == null ? "—" : formatCurrency(week.variacion_compras_cmv)}</b></span>
      </div>
    </article>)}
  </section>;
}

function isRecentPaymentVisualDate(value) {
  const text = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const firstVisibleDate = new Date(today);
  // Tres días contando hoy: desde hoy menos dos días.
  firstVisibleDate.setDate(today.getDate() - 2);
  return new Date(`${text}T00:00:00`) >= firstVisibleDate;
}

function paymentVisualClass(sale) {
  const state = String(sale?.pago_visual || "").toLowerCase();
  return ["efectivo", "transferencia", "pendiente"].includes(state) ? state : "efectivo";
}

function SalesPaymentSummary({ days }) {
  const summary = getRecentPaymentVisualSummary(days);
  const total = Object.values(summary).reduce((count, item) => count + item.ventas, 0);
  if (!total) return null;
  return <section className="sales-payment-summary" aria-label="Resumen de pagos de los últimos tres días">
    <header><div><p className="eyebrow">PAGOS RECIENTES</p><h3>Últimos 3 días</h3></div><small>{total} venta{total === 1 ? "" : "s"} clasificadas</small></header>
    <div>
      <article className="payment-summary-item payment-summary-cash"><span>Efectivo confirmado</span><b>{formatCurrency(summary.efectivo.valor)}</b><small>{summary.efectivo.ventas} venta{summary.efectivo.ventas === 1 ? "" : "s"} · recibido</small></article>
      <article className="payment-summary-item payment-summary-transfer"><span>Transferencia confirmada</span><b>{formatCurrency(summary.transferencia.valor)}</b><small>{summary.transferencia.ventas} venta{summary.transferencia.ventas === 1 ? "" : "s"} · dinero recibido</small></article>
      <article className="payment-summary-item payment-summary-pending"><span>Pago pendiente</span><b>{formatCurrency(summary.pendiente.valor)}</b><small>{summary.pendiente.ventas} venta{summary.pendiente.ventas === 1 ? "" : "s"} · por cobrar</small></article>
    </div>
  </section>;
}

function salesChannelCode(sale) {
  return String(sale?.canal || "RUTA").trim().toUpperCase() === "PRESENCIAL" ? "PRESENCIAL" : "RUTA";
}

function filterSalesDaysByChannel(days, channel) {
  return (days || []).map((day) => {
    const ventas = (day.ventas || []).filter((sale) => channel === "TODOS" || salesChannelCode(sale) === channel);
    return {
      ...day,
      ventas,
      cantidad_ventas: ventas.length,
      total: ventas.reduce((total, sale) => total + Number(sale.valor_total || 0), 0),
    };
  }).filter((day) => day.ventas.length > 0);
}

function summarizeSalesDays(days) {
  const summary = { total: 0, quantity: 0, ticket: 0, topProduct: "—" };
  const products = new Map();
  (days || []).forEach((day) => {
    (day.ventas || []).forEach((sale) => {
      summary.quantity += 1;
      summary.total += Number(sale.valor_total || 0);
      (sale.productos || []).forEach((product) => {
        const name = String(product?.producto || "").trim();
        if (!name) return;
        products.set(name, (products.get(name) || 0) + Number(product?.cantidad || 0));
      });
    });
  });
  summary.ticket = summary.quantity ? summary.total / summary.quantity : 0;
  const top = [...products.entries()].sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0], "es"))[0];
  summary.topProduct = top?.[0] || "—";
  return summary;
}

function salesChannelTitle(channel) {
  if (channel === "PRESENCIAL") return "Ventas en sitio";
  if (channel === "TODOS") return "Todas las ventas";
  return "Ventas a domicilio";
}

function SalesChannelSelector({ value, onChange, compact = false, includeStand = true }) {
  const options = [
    ["RUTA", "Domicilios"],
    ["PRESENCIAL", "Ventas en sitio"],
    ["TODOS", "Incluir ambas"],
  ].filter(([channel]) => includeStand || channel === "RUTA");
  return <div className={`sales-channel-selector${compact ? " sales-channel-selector-compact" : ""}`} role="group" aria-label="Mostrar detalle de ventas por origen">
    <span>{compact ? "Canal" : "Mostrar detalle"}</span>
    {options.map(([channel, label]) => <button
      key={channel}
      type="button"
      className={value === channel ? "active" : ""}
      aria-pressed={value === channel}
      onClick={() => onChange(channel)}
    >{label}</button>)}
  </div>;
}

function SalesDetail({ days, financialDays, showCosts, salesChannel = "RUTA", channelControl = null }) {
  const [dayChannels, setDayChannels] = useState({});
  const financialByDate = new Map(financialDays.map((day) => [day.fecha, day]));
  const salesByDate = new Map(days.map((day) => [day.fecha, day]));
  const dates = Array.from(new Set(showCosts ? [...salesByDate.keys(), ...financialByDate.keys()] : [...salesByDate.keys()])).sort().reverse();
  if (!dates.length)
    return <p className="empty-state">No hay ventas registradas en este rango de fechas.</p>;
  return (
    <div className="sales-detail">
      {channelControl}
      <SalesPaymentSummary days={days} />
      {dates.map((fecha) => {
        const day = salesByDate.get(fecha) || { fecha, cantidad_ventas: 0, total: 0, ventas: [] };
        const dayChannel = dayChannels[fecha] || "RUTA";
        const daySales = (day.ventas || []).filter((sale) => dayChannel === "TODOS" || salesChannelCode(sale) === dayChannel);
        const dayTotal = daySales.reduce((total, sale) => total + Number(sale.valor_total || 0), 0);
        return <article className="sales-day" key={day.fecha}>
          <header>
            <div>
              <p className="eyebrow">{formatSalesDate(day.fecha)}</p>
              <strong>{daySales.length} venta{daySales.length === 1 ? "" : "s"}</strong>
            </div>
            <div className="sales-day-total"><b>{formatCurrency(dayTotal)}</b><SalesChannelSelector compact value={dayChannel} onChange={(next) => setDayChannels((current) => ({ ...current, [fecha]: next }))} /></div>
          </header>
          <div className="sales-list">
            {daySales.map((sale) => {
              const visiblePaymentVisual = isRecentPaymentVisualDate(day.fecha);
              const paymentClass = paymentVisualClass(sale);
              return <div className={`sale-record${visiblePaymentVisual ? ` payment-${paymentClass}` : ""}`} key={`${day.fecha}-${sale.venta_id}`}>
                <div className="sale-record-head">
                  <div><strong>{sale.cliente}</strong><small>{sale.pedido_id || sale.venta_id} · {sale.etiqueta_canal || (salesChannelCode(sale) === "PRESENCIAL" ? "Ventas en sitio" : "Domicilios")}</small></div>
                  <div className="sale-record-payment"><b>{formatCurrency(sale.valor_total)}</b>{visiblePaymentVisual && <span className={`payment-badge payment-badge-${paymentClass}`}>{sale.etiqueta_pago_visual || "Efectivo"}</span>}</div>
                </div>
                {sale.productos?.length ? (
                  <ul>
                    {sale.productos.map((product, index) => (
                      <li key={`${product.codigo}-${index}`}>
                        <span>{product.cantidad} {product.unidad} · {product.producto}{product.presentacion ? ` · ${product.presentacion}` : ""}</span>
                        <b>{formatCurrency(product.valor)}</b>
                      </li>
                    ))}
                  </ul>
                ) : <p className="sale-products-missing">Venta histórica sin detalle de productos.</p>}
              </div>;
            })}
            {!daySales.length && <p className="sale-products-missing">No hubo ventas de este canal en este día.</p>}
          </div>
          {showCosts && <DailyFinancialSummary data={financialByDate.get(day.fecha)} />}
        </article>;
      })}
    </div>
  );
}

function DailyFinancialSummary({ data }) {
  const expenses = data?.gastos || [];
  if (!data)
    return <div className="daily-financial-summary"><span>Costos reales</span><b>Sin información financiera para este día.</b></div>;
  return (
    <div className="daily-financial-summary">
      <div><span>Ventas totales</span><b>{formatCurrency(data.ventas_totales)}</b></div>
      <div><span>Costos reales de ventas</span><b>{data.costos_completos ? formatCurrency(data.costo_real_ventas) : "Pendiente de confirmar"}</b></div>
      <div><span>Gastos totales</span><b>{formatCurrency(data.gastos_totales)}</b></div>
      <div className={data.resultado_operativo == null ? "pending" : data.resultado_operativo < 0 ? "negative" : "positive"}>
        <span>Resultado operativo</span><b>{data.resultado_operativo == null ? "Pendiente de costos" : formatCurrency(data.resultado_operativo)}</b>
      </div>
      {data.ventas_con_costo_pendiente > 0 && <p>{data.ventas_con_costo_pendiente} venta(s) con costo pendiente de confirmar.</p>}
      {expenses.length > 0 && <ul className="daily-expenses">
        {expenses.map((expense) => <li key={expense.gasto_id}>
          <span>{expense.tipo === "COMPRA_ADICIONAL" ? "Compra adicional" : expense.tipo} · {expense.descripcion}
            {expense.items?.length ? `: ${expense.items.map((item) => `${item.cantidad} ${visibleProductName(item.producto, item.presentacion, item.codigo || item.codigo_producto)}`).join(", ")}` : ""}
          </span><b>{formatCurrency(expense.valor)}</b>
        </li>)}
      </ul>}
    </div>
  );
}

function formatSalesDate(value) {
  const date = new Date(`${String(value || "").slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? String(value || "Fecha sin registrar") : date.toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" });
}

function AnalysisTable({ tab, data }) {
  const rows = data[tab] || [];
  const columns = rows.length
    ? Object.keys(rows[0]).map((key) => ({
        accessorKey: key,
        header: key.replaceAll("_", " "),
      }))
    : [];
  return <DataTable columns={columns} rows={rows} />;
}

function WebAnalyticsFunnel({ data }) {
  const funnel = data.embudo_web || {};
  const period = data.periodo_analitica || {};
  const periodLabel =
    period.fecha_desde && period.fecha_hasta
      ? `${period.fecha_desde} a ${period.fecha_hasta}`
      : "Últimos 30 días";
  const steps = [
    ["Sesiones con actividad", funnel.sesiones_con_actividad],
    ["Catálogos consultados", funnel.catalogos_consultados],
    ["Fichas consultadas", funnel.fichas_consultadas],
    ["Carritos iniciados", funnel.carritos_iniciados],
    ["Checkouts iniciados", funnel.checkouts_iniciados],
    ["Pedidos confirmados", funnel.pedidos_confirmados],
    ["Carritos sin confirmar", funnel.carritos_sin_confirmar],
    ["Checkouts sin confirmar", funnel.checkouts_sin_confirmar],
  ].map(([etapa, sesiones]) => ({ etapa, sesiones: sesiones ?? 0 }));
  const rates = [
    ["Catálogo → carrito", funnel.tasa_catalogo_a_carrito],
    ["Carrito → confirmación", funnel.tasa_carrito_a_confirmacion],
    ["Catálogo → confirmación", funnel.tasa_catalogo_a_confirmacion],
  ].map(([indicador, porcentaje]) => ({
    indicador,
    porcentaje: porcentaje == null ? "—" : `${porcentaje}%`,
  }));
  return (
    <div className="analytics-funnel">
      <div className="stats-grid">
        <div className="chart-box">
          <h3>Embudo de Pedidos Web</h3>
          <DataTable
            columns={[
              { accessorKey: "etapa", header: "Etapa" },
              { accessorKey: "sesiones", header: "Sesiones" },
            ]}
            rows={steps}
          />
        </div>
        <div className="chart-box">
          <h3>Conversión</h3>
          <DataTable
            columns={[
              { accessorKey: "indicador", header: "Indicador" },
              { accessorKey: "porcentaje", header: "Resultado" },
            ]}
            rows={rates}
          />
        </div>
      </div>
      <div className="chart-box">
        <h3>Productos más consultados</h3>
        <DataTable
          columns={[
            { accessorKey: "codigo_producto", header: "Producto" },
            { accessorKey: "sesiones", header: "Sesiones" },
          ]}
          rows={data.productos_mas_consultados || []}
        />
      </div>
      <p className="chart-caption">
        Período: {periodLabel}. Las cifras son agregadas por sesión y no
        incluyen teléfonos, direcciones ni contenido de carritos.
      </p>
    </div>
  );
}

function InventoryActions({ inventory, onRefresh }) {
  const operableInventory = inventory.filter(
    (item) =>
      String(item.politica || "").toUpperCase() === "INVENTARIO_ESTATICO",
  );
  const [operation, setOperation] = useState("registrar_entrada");
  const [code, setCode] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [observation, setObservation] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const selectedInventory = operableInventory.find(
    (item) => String(item.codigo || item.producto) === code,
  );

  function selectPresentation(nextCode) {
    setCode(nextCode);
    const selected = operableInventory.find(
      (item) => String(item.codigo || item.producto) === nextCode,
    );
    const referenceCost = selected?.costo_unitario_referencia;
    setUnitCost(
      referenceCost === null ||
        referenceCost === undefined ||
        referenceCost === ""
        ? ""
        : String(referenceCost),
    );
    setStatus("");
  }

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setStatus("");
    try {
      const datos = {
        codigo: code,
        cantidad: Number(quantity),
        operacion_id: createOperationId("DASH"),
        observacion: observation,
      };
      if (operation === "registrar_entrada")
        datos.costo_unitario = unitCost === "" ? undefined : Number(unitCost);
      const { response, payload } = await enviarOperacionInventario({
        operacion: operation,
        datos,
      });
      if (!response.ok || !payload?.ok) {
        const mensaje =
          payload?.mensaje ||
            `No fue posible completar la operación (HTTP ${response.status}).`;
        setStatus(mensaje);
        notificarAdmin(mensaje, "error");
        return;
      }
      const mensaje = "Operación registrada correctamente.";
      setStatus(mensaje);
      notificarAdmin(mensaje, "success");
      setCode("");
      setQuantity("");
      setUnitCost("");
      setObservation("");
      void onRefresh?.({ notificar: false });
    } catch (error) {
      const mensaje = `No fue posible conectar con inventario${error?.message ? `: ${error.message}` : "."}`;
      setStatus(mensaje);
      notificarAdmin(mensaje, "error");
    } finally {
      setBusy(false);
    }
  }

  async function refreshInventory() {
    setStatus("Actualizando inventario…");
    setBusy(true);
    try {
      const resultado = await onRefresh?.({ notificar: false });
      const mensaje =
        resultado?.ok === false
          ? resultado.mensaje || "No fue posible actualizar el inventario."
          : "Inventario actualizado.";
      setStatus(mensaje);
      notificarAdmin(mensaje, resultado?.ok === false ? "error" : "success");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="inventory-actions" onSubmit={submit}>
      <div>
        <p className="eyebrow">GESTIÓN AUTORIZADA</p>
        <h2>Actualizar inventario</h2>
        <small>Solo presentaciones de inventario estático</small>
      </div>
      <label>
        Operación
        <select
          value={operation}
          onChange={(event) => setOperation(event.target.value)}
        >
          <option value="registrar_entrada">Registrar entrada</option>
          <option value="ajustar">Ajustar saldo</option>
          <option value="registrar_devolucion">Registrar devolución</option>
          <option value="registrar_merma">Registrar merma</option>
        </select>
      </label>
      <label>
        Producto o presentación
        <select
          value={code}
          onChange={(event) => selectPresentation(event.target.value)}
          required
        >
          <option value="">Selecciona un producto</option>
          {operableInventory.map((item) => (
            <option
              key={item.codigo || item.producto}
              value={item.codigo || item.producto}
            >
              {item.producto || item.codigo}
              {item.presentacion ? ` · ${item.presentacion}` : ""}
            </option>
          ))}
        </select>
      </label>
      <label>
        Cantidad
        <input
          type="number"
          min="0.001"
          step="0.001"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
          required
        />
      </label>
      {operation === "registrar_entrada" && (
        <label>
          Costo unitario
          <input
            type="number"
            min="0"
            step="0.01"
            value={unitCost}
            onChange={(event) => setUnitCost(event.target.value)}
            placeholder="Costo de compra"
          />
          <small>
            {selectedInventory?.costo_unitario_referencia != null
              ? "Precargado desde la presentación; puedes ajustarlo para esta compra."
              : "Ingresa el costo de esta compra."}
          </small>
        </label>
      )}
      <label>
        Observación
        <input
          value={observation}
          onChange={(event) => setObservation(event.target.value)}
        />
      </label>
      <button type="submit" disabled={busy || operableInventory.length === 0}>
        {busy ? "Guardando operación…" : "Registrar operación"}
      </button>
      <button
        type="button"
        className="inventory-refresh"
        disabled={busy}
        onClick={refreshInventory}
      >
        Actualizar tabla
      </button>
      {operableInventory.length === 0 && (
        <span className="action-status">
          No hay presentaciones de inventario estático configuradas.
        </span>
      )}
      {status && (
        <span
          className={`action-status inventory-operation-status ${status.startsWith("Operación registrada") ? "success" : status.startsWith("Actualizando") || status.startsWith("Guardando") ? "progress" : "error"}`}
          role="status"
          aria-live="polite"
        >
          {status}
        </span>
      )}
    </form>
  );
}

function RouteExpensesModule({ expenses, onRefresh }) {
  const total = expenses.reduce((sum, expense) => sum + (Number(expense.valor) || 0), 0);
  return <section className="module-panel route-expenses-module">
    <div className="route-expenses-hero">
      <div className="route-expenses-hero-copy"><span className="route-expenses-kicker">OPERACIÓN DEL DÍA</span><h2>Mis gastos</h2><p>Registra aquí la compra diaria y consulta lo que ya quedó guardado.</p></div>
      <div className="route-expenses-kpi"><span>Total registrado hoy</span><strong>{formatCurrency(total)}</strong><small>{expenses.length} movimiento{expenses.length === 1 ? "" : "s"}</small></div>
    </div>
    <RouteExpensePanel onChanged={onRefresh} />
    <ExpenseHistoryBySource expenses={expenses} title="Movimientos registrados" subtitle="Solo aparecen gastos asociados a tu usuario." />
  </section>;
}

function RouteExpensePanel({ onChanged }) {
  const [budget, setBudget] = useState(null);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState({});
  const [expenseSource, setExpenseSource] = useState("DOMICILIOS");
  const [operationId, setOperationId] = useState(() => createOperationId("RUTA-COMPRA"));
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [otherDescription, setOtherDescription] = useState("");
  const [otherValueInput, setOtherValueInput] = useState("");
  const [otherSource, setOtherSource] = useState("DOMICILIOS");
  const [otherOperationId, setOtherOperationId] = useState(() => createOperationId("RUTA-OTRO"));
  const [otherStatus, setOtherStatus] = useState("");
  const [otherBusy, setOtherBusy] = useState(false);
  const today = todayRouteKey();
  const products = categories.flatMap((category) => category.productos || []);
  const cartItems = products.filter((product) => Number(cart[product.codigo]) > 0).map((product) => ({
    ...product, cantidad: Number(cart[product.codigo]), subtotal: Number(cart[product.codigo]) * Number(product.costo_unitario || 0),
  }));
  const total = cartItems.reduce((sum, item) => sum + item.subtotal, 0);
  async function loadBudget() {
    const response = await fetch("/api/dashboard/gastos/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "presupuesto_ruta", datos: { fecha: today } }) });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible consultar el CMV diario.");
    setBudget(payload.data || null);
  }
  async function loadCatalog() {
    setLoadingCatalog(true);
    const response = await fetch("/api/dashboard/gastos/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "catalogo_compra_ruta", datos: {} }) });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible cargar los productos de compra.");
    setCategories(payload.data?.categorias || []);
  }
  useEffect(() => {
    // El CMV es una lectura informativa. El catálogo y la compra no pueden
    // quedar esperando su cálculo ni heredar un error de costos de pedidos.
    loadCatalog()
      .catch((error) => setStatus(error.message))
      .finally(() => setLoadingCatalog(false));
    loadBudget().catch(() => setBudget(null));
  }, []);
  function changeQuantity(code, delta) {
    setCart((current) => {
      const next = Math.max(0, (Number(current[code]) || 0) + delta);
      if (!next) {
        const { [code]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [code]: next };
    });
  }
  async function submit(event) {
    event.preventDefault();
    if (!cartItems.length) return;
    setBusy(true); setStatus("");
    try {
      const datos = {
        operacion_id: operationId, fecha: today, fuente_gasto: expenseSource,
        items: cartItems.map((item) => ({ codigo_producto: item.codigo, cantidad: item.cantidad })),
      };
      const response = await fetch("/api/dashboard/gastos/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "registrar_compra_ruta", datos }) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible registrar el gasto.");
      setStatus(payload.mensaje || "Gasto registrado.");
      setCart({}); setOperationId(createOperationId("RUTA-COMPRA"));
      // Refrescar el indicador en segundo plano no debe retener una compra ya
      // guardada; el historial sí se actualiza por el flujo normal del módulo.
      loadBudget().catch(() => setBudget(null));
      await onChanged?.({ notificar: false });
    } catch (error) { setStatus(error.message || "No fue posible registrar el gasto."); }
    finally { setBusy(false); }
  }
  async function submitOther(event) {
    event.preventDefault();
    const value = parseMoneyInput(otherValueInput);
    if (!otherDescription.trim() || !Number.isFinite(value) || value <= 0) {
      setOtherStatus("Escribe una descripción y un valor mayor que cero.");
      return;
    }
    setOtherBusy(true); setOtherStatus("");
    try {
      const response = await fetch("/api/dashboard/gastos/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "registrar_otro_ruta", datos: { operacion_id: otherOperationId, fecha: today, fuente_gasto: otherSource, descripcion: otherDescription.trim(), valor: value, metodo_pago: "Efectivo" } }) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible registrar el otro gasto.");
      setOtherStatus(payload.mensaje || "Otro gasto registrado.");
      setOtherDescription(""); setOtherValueInput(""); setOtherOperationId(createOperationId("RUTA-OTRO"));
      await onChanged?.({ notificar: false });
    } catch (error) { setOtherStatus(error.message || "No fue posible registrar el otro gasto."); }
    finally { setOtherBusy(false); }
  }
  const cmv = Number(budget?.presupuesto?.limite_cmv || 0);
  const accumulated = Number(budget?.compras_acumuladas || 0);
  const cmvLoaded = Boolean(budget);
  const cmvComplete = cmvLoaded ? budget?.cmv_completo !== false : null;
  const missingCosts = Array.isArray(budget?.productos_sin_costo) ? budget.productos_sin_costo : [];
  const variation = Math.round((accumulated - cmv) * 100) / 100;
  const cmvOrigin = budget?.origen_cmv === "HISTORICO_CONGELADO" ? "Registro histórico" : "Estimación de los pedidos de hoy";
  return <section className="route-expense-panel">
    <div className="route-expense-panel-heading"><div><p className="eyebrow">NUEVA COMPRA</p><h3>Registra lo que compraste</h3><p>Elige una fuente y agrega los productos. El pago se registra siempre en efectivo.</p></div><span className="route-expense-panel-badge">Compra única del día</span></div>
    <section className={`route-cmv-summary ${cmvComplete === false ? "incomplete" : ""}`} aria-live="polite">
      <header><div><p className="eyebrow">CMV INFORMATIVO</p><h4>Referencia de los pedidos de hoy</h4></div><small>{cmvLoaded ? cmvOrigin : "Calculando en segundo plano"}</small></header>
      <div className="route-cmv-metrics">
        <div><span>CMV {cmvComplete === false ? "parcial" : "estimado"}</span><strong>{cmvLoaded ? formatCurrency(cmv) : "—"}</strong></div>
        <div><span>Compras registradas</span><strong>{formatCurrency(accumulated)}</strong></div>
        <div className={variation > 0 ? "above" : ""}><span>{cmvComplete === false ? "Diferencia parcial" : "Diferencia compras / CMV"}</span><strong>{cmvLoaded ? `${variation > 0 ? "+" : ""}${formatCurrency(variation)}` : "—"}</strong></div>
      </div>
      {cmvComplete === false && <div className="route-cmv-note"><strong>CMV incompleto</strong><span>Faltan costos en: {missingCosts.join(" · ") || "algunas presentaciones"}. Puedes registrar la compra normalmente.</span></div>}
      {!cmvLoaded && <div className="route-cmv-note neutral"><span>No es necesario esperar este cálculo para guardar tu compra.</span></div>}
    </section>
    <form onSubmit={submit} className="route-purchase-form">
      <label className="route-purchase-source expense-source-selector">
        Esta compra corresponde a
        <select value={expenseSource} onChange={(event) => setExpenseSource(event.target.value)} disabled={busy || loadingCatalog}>
          <option value="DOMICILIOS">Domicilios</option>
          <option value="STAND">Stand</option>
        </select>
        <small>Todos los productos seleccionados quedarán asociados a esta fuente.</small>
      </label>
      <div className="route-purchase-catalog" aria-busy={loadingCatalog}>
        {loadingCatalog ? <p>Cargando productos disponibles…</p> : categories.map((category) => <section key={category.id} className="route-purchase-category"><h4>{category.nombre}</h4><div>{category.productos.map((product) => <button type="button" key={product.codigo} className={cart[product.codigo] ? "selected" : ""} onClick={() => changeQuantity(product.codigo, 1)}><strong>{visibleProductName(product.producto, product.presentacion, product.codigo)}</strong><small>{productHasCompactPresentation(product.producto, product.presentacion, product.codigo) ? "" : product.presentacion}</small><span>{formatCurrency(product.costo_unitario)}</span>{cart[product.codigo] ? <b>{cart[product.codigo]} seleccionado{Number(cart[product.codigo]) === 1 ? "" : "s"}</b> : null}</button>)}</div></section>)}
        {!loadingCatalog && !categories.length && <p>No hay productos de compra diaria activos y con costo configurado.</p>}
      </div>
      <aside className="route-purchase-cart">
        <div><p className="eyebrow">CARRITO</p><h4>Compra en efectivo</h4></div>
        {cartItems.length ? <ul>{cartItems.map((item) => <li key={item.codigo}><div><strong>{visibleProductName(item.producto, item.presentacion, item.codigo)}</strong><small>{productHasCompactPresentation(item.producto, item.presentacion, item.codigo) ? "" : item.presentacion} · {formatCurrency(item.costo_unitario)} c/u</small></div><div className="route-purchase-quantity"><button type="button" aria-label={`Quitar una unidad de ${visibleProductName(item.producto, item.presentacion, item.codigo)}`} onClick={() => changeQuantity(item.codigo, -1)}>−</button><b>{item.cantidad}</b><button type="button" aria-label={`Agregar una unidad de ${visibleProductName(item.producto, item.presentacion, item.codigo)}`} onClick={() => changeQuantity(item.codigo, 1)}>+</button></div><strong>{formatCurrency(item.subtotal)}</strong></li>)}</ul> : <p className="route-purchase-empty">Selecciona los productos que compraste.</p>}
        <div className="route-purchase-total"><span>Total de la compra</span><strong>{formatCurrency(total)}</strong></div>
        <button className="primary-action" disabled={busy || loadingCatalog || !cartItems.length}>{busy ? "Guardando compra…" : "Guardar compra del día"}</button>
      </aside>
    </form>
    <form onSubmit={submitOther} className="route-other-expense-form">
      <div className="route-other-expense-heading"><div><p className="eyebrow">GASTO EXTRAORDINARIO</p><h3>Otro gasto</h3><p>Úsalo para una compra o imprevisto que no hace parte de los productos diarios.</p></div><span className="route-expense-panel-badge">Efectivo</span></div>
      <section className="route-other-expense-card" aria-label="Tarjeta para registrar otro gasto">
        <div className="route-other-expense-card-title"><span aria-hidden="true">+</span><div><strong>Otro gasto</strong><small>No modifica inventario, capacidad ni CMV.</small></div></div>
        <fieldset className="route-other-source-options" disabled={otherBusy}><legend>¿A qué operación corresponde?</legend><label className={otherSource === "DOMICILIOS" ? "selected" : ""}><input type="radio" name="other-expense-source" value="DOMICILIOS" checked={otherSource === "DOMICILIOS"} onChange={(event) => setOtherSource(event.target.value)} />Domicilios</label><label className={otherSource === "STAND" ? "selected" : ""}><input type="radio" name="other-expense-source" value="STAND" checked={otherSource === "STAND"} onChange={(event) => setOtherSource(event.target.value)} />Stand</label></fieldset>
        <div className="route-other-expense-fields">
          <label>¿Qué se compró o pagó?<input value={otherDescription} onChange={(event) => setOtherDescription(event.target.value)} placeholder="Ej. bolsa, reparación, hielo" disabled={otherBusy} required /></label>
          <label>Valor pagado<span className="route-money-input"><b>$</b><input inputMode="numeric" value={otherValueInput} onChange={(event) => setOtherValueInput(formatMoneyInput(event.target.value))} placeholder="0" disabled={otherBusy} required /></span></label>
        </div>
      </section>
      <button className="primary-action" type="submit" disabled={otherBusy}>{otherBusy ? "Guardando otro gasto…" : "Guardar otro gasto"}</button>
      {otherStatus && <p className="action-status" role="status" aria-live="polite">{otherStatus}</p>}
    </form>
    {status && <p className="action-status">{status}</p>}
  </section>;
}

function ExpenseActions({ onChanged, expenses = [] }) {
  const [type, setType] = useState("FLETE");
  const [expenseSource, setExpenseSource] = useState("DOMICILIOS");
  const [value, setValue] = useState("");
  const [date, setDate] = useState(operationalDateKey());
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [approvalBusy, setApprovalBusy] = useState("");
  const [items, setItems] = useState([{ producto: "", cantidad: "", unidad: "unidad", costo_unitario: "" }]);
  const isAdditionalPurchase = type === "COMPRA_ADICIONAL";
  const purchaseTotal = items.reduce((total, item) => total + (Number(item.cantidad) || 0) * (Number(item.costo_unitario) || 0), 0);
  function updateItem(index, field, nextValue) {
    setItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: nextValue } : item));
  }
  async function approveExpense(expenseId) {
    setApprovalBusy(expenseId);
    try {
      const response = await fetch("/api/dashboard/gastos/operacion", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ operacion: "aprobar", datos: { gasto_id: expenseId } }) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.mensaje || "No fue posible aprobar el gasto.");
      setStatus(payload.mensaje || "Gasto aprobado.");
      onChanged?.({ notificar: false });
    } catch (error) { setStatus(error.message || "No fue posible aprobar el gasto."); }
    finally { setApprovalBusy(""); }
  }
  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setStatus("");
    try {
      const response = await fetch("/api/dashboard/gastos/operacion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          datos: {
            operacion_id: createOperationId("DASH"),
            tipo: type,
            // Una compra completa pertenece a una sola operación. La fuente
            // se define aquí, antes de agregar productos, nunca por línea.
            fuente_gasto: expenseSource,
            valor: isAdditionalPurchase ? Number(purchaseTotal.toFixed(2)) : Number(value),
            fecha: date,
            descripcion,
            items: isAdditionalPurchase ? items.map((item) => ({
              producto: item.producto, cantidad: Number(item.cantidad), unidad: item.unidad,
              costo_unitario: Number(item.costo_unitario),
            })) : [],
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      setStatus(
        response.ok && payload.ok
          ? "Gasto registrado correctamente."
          : payload.mensaje ||
              `No fue posible completar el gasto (HTTP ${response.status}).`,
      );
      if (response.ok && payload.ok) {
        setValue("");
        setDescription("");
        setItems([{ producto: "", cantidad: "", unidad: "unidad", costo_unitario: "" }]);
        onChanged?.({ notificar: false });
      }
    } catch {
      setStatus("No fue posible enviar el gasto. Inténtalo nuevamente.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
    <form className="inventory-actions expense-actions" onSubmit={submit}>
      <div>
        <p className="eyebrow">GESTIÓN AUTORIZADA</p>
        <h2>Registrar gasto</h2>
      </div>
      <label className="expense-source-selector">
        Este gasto corresponde a
        <select value={expenseSource} onChange={(event) => setExpenseSource(event.target.value)} disabled={busy}>
          <option value="DOMICILIOS">Domicilios</option>
          <option value="STAND">Stand</option>
        </select>
        <small>{isAdditionalPurchase ? "Todos los productos de esta compra quedarán asociados a esta fuente." : "La fuente se aplica a este gasto completo."}</small>
      </label>
      <label>
        Tipo
        <select value={type} onChange={(event) => setType(event.target.value)}>
          <option>FLETE</option>
          <option>OPERATIVO</option>
          <option>SERVICIO</option>
          <option>OTRO</option>
          <option value="COMPRA_ADICIONAL">COMPRA ADICIONAL</option>
        </select>
      </label>
      {!isAdditionalPurchase && <label>
        Valor
        <input
          type="number"
          min="0.01"
          step="0.01"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          required
        />
      </label>}
      <label>
        Fecha
        <input
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
          required
        />
      </label>
      {isAdditionalPurchase && <div className="expense-items-editor">
        <div><strong>Productos comprados</strong><small>Solo se registra para análisis; no actualiza inventario ni capacidad.</small></div>
        {items.map((item, index) => <div className="expense-item-row" key={index}>
          <input value={item.producto} onChange={(event) => updateItem(index, "producto", event.target.value)} placeholder="Producto, ej. Leche" required />
          <input type="number" min="0.001" step="0.001" value={item.cantidad} onChange={(event) => updateItem(index, "cantidad", event.target.value)} placeholder="Cantidad" required />
          <input value={item.unidad} onChange={(event) => updateItem(index, "unidad", event.target.value)} placeholder="Unidad" required />
          <input type="number" min="0.01" step="0.01" value={item.costo_unitario} onChange={(event) => updateItem(index, "costo_unitario", event.target.value)} placeholder="Costo unitario" required />
          {items.length > 1 && <button type="button" className="secondary-action" onClick={() => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))}>Quitar</button>}
        </div>)}
        <div className="expense-items-total"><button type="button" className="secondary-action" onClick={() => setItems((current) => [...current, { producto: "", cantidad: "", unidad: "unidad", costo_unitario: "" }])}>Agregar producto</button><strong>Total: {formatCurrency(purchaseTotal)}</strong></div>
      </div>}
      <label>
        Descripción
        <input
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          required
        />
      </label>
      <button type="submit" disabled={busy}>
        {busy ? "Guardando..." : "Registrar gasto"}
      </button>
      {status && <span className="action-status">{status}</span>}
    </form>
    <ExpenseHistoryBySource expenses={expenses} />
    {expenses.filter((expense) => expense.estado === "PENDIENTE_APROBACION").length > 0 && <section className="pending-expense-approvals">
      <p className="eyebrow">EXCESOS DE CMV</p><h3>Compras pendientes de aprobación</h3>
      {expenses.filter((expense) => expense.estado === "PENDIENTE_APROBACION").map((expense) => <div key={expense.id}>
        <span><b>{expense.descripcion}</b><small>{expense.fecha} · {formatCurrency(expense.valor)}</small></span>
        <button type="button" className="primary-action" disabled={approvalBusy === expense.id} onClick={() => approveExpense(expense.id)}>{approvalBusy === expense.id ? "Aprobando…" : "Aprobar exceso"}</button>
      </div>)}
    </section>}
    </>
  );
}

function expenseSourceCode(expense) {
  return String(expense?.fuente_gasto || "DOMICILIOS").trim().toUpperCase() === "STAND" ? "STAND" : "DOMICILIOS";
}

function ExpenseHistoryBySource({ expenses = [], title = "Historial separado por operación", subtitle = "" }) {
  const today = operationalDateKey();
  const todayExpenses = expenses.filter((expense) => String(expense?.fecha || "").slice(0, 10) === today && String(expense?.estado || "").toUpperCase() !== "ANULADO");
  const groups = [
    { id: "DOMICILIOS", label: "Domicilios", description: "Compras y gastos de las entregas" },
    { id: "STAND", label: "Stand", description: "Compras y gastos del punto de venta" },
  ].map((source) => {
    const rows = todayExpenses.filter((expense) => expenseSourceCode(expense) === source.id);
    return { ...source, rows, total: rows.reduce((sum, expense) => sum + (Number(expense.valor) || 0), 0) };
  });
  const total = groups.reduce((sum, group) => sum + group.total, 0);
  return <section className="expense-source-history" aria-label="Gastos de hoy por fuente">
    <header className="expense-source-heading">
      <div><p className="eyebrow">GASTOS DE HOY</p><h3>{title}</h3><small>{subtitle || formatSalesDate(today)}</small></div>
      <strong>{formatCurrency(total)}</strong>
    </header>
    <div className="expense-source-summary">
      {groups.map((group) => <article key={group.id} className={`expense-source-card ${group.id.toLowerCase()}`}>
        <span>{group.label}</span><strong>{formatCurrency(group.total)}</strong><small>{group.rows.length} gasto{group.rows.length === 1 ? "" : "s"}</small>
      </article>)}
    </div>
    <div className="expense-source-groups">
      {groups.map((group) => <section key={group.id} className="expense-source-group">
        <header><div><h4>{group.label}</h4><small>{group.description}</small></div><strong>{formatCurrency(group.total)}</strong></header>
        {group.rows.length ? <ul>{group.rows.map((expense) => <li key={expense.gasto_id || expense.id || expense.operacion_id}>
          <div><strong>{expense.tipo === "COMPRA_ADICIONAL" ? "Compra diaria" : expense.tipo === "OTRO" ? "Otro gasto" : expense.tipo}</strong>{expense.items?.length ? <em>{expense.items.map((item) => `${item.cantidad} · ${visibleProductName(item.producto, item.presentacion, item.codigo || item.codigo_producto)}`).join(" · ")}</em> : <small>{expense.descripcion || "Sin descripción"}</small>}</div>
          <b>{formatCurrency(expense.valor)}</b>
        </li>)}</ul> : <p>No hay gastos registrados para {group.label.toLowerCase()} hoy.</p>}
      </section>)}
    </div>
  </section>;
}

function createOperationId(prefix) {
  if (globalThis.crypto?.randomUUID)
    return `${prefix}-${globalThis.crypto.randomUUID()}`;
  const bytes = new Uint32Array(2);
  if (globalThis.crypto?.getRandomValues)
    globalThis.crypto.getRandomValues(bytes);
  else {
    bytes[0] = Date.now();
    bytes[1] = Math.floor(Math.random() * 0xffffffff);
  }
  return `${prefix}-${Date.now().toString(36)}-${bytes[0].toString(36)}${bytes[1].toString(36)}`;
}
function formatCurrency(value) {
  return `$${Number(value || 0).toLocaleString("es-CO")}`;
}

function formatMoneyInput(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits ? Number(digits).toLocaleString("es-CO") : "";
}

function parseMoneyInput(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits ? Number(digits) : 0;
}

const PURCHASE_DISPLAY_NAME_BY_CODE = Object.freeze({
  PRE_7EB804FDCB964F258B71007583042BB6: "Esparcible",
  PRE_99FF0DCC96C64CDBB5F988273606159A: "Queso pequeño",
  PRE_C21D67B4993F4AD88B4792159FF93137: "Queso mediano",
  Q01: "Queso grande",
});

function purchaseProductCodeKey(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replaceAll("-", "_");
}

function visibleProductName(product, presentation = "", productCode = "") {
  const name = String(product || "Producto sin nombre").trim();
  const normalized = name.toLocaleLowerCase("es-CO");
  const detail = String(presentation || "").toLocaleLowerCase("es-CO");
  const code = purchaseProductCodeKey(productCode);
  if (normalized === "leche cruda de vaca") return "Leche";
  if (normalized === "mantequilla de vaca") return "Mantequilla";
  if (normalized === "cuajada campesina") return "Cuajada";
  if (normalized === "quesito campesino") {
    // El código pertenece a la presentación concreta y queda guardado en
    // expense_items. Nunca dependemos del texto libre para distinguir los
    // tamaños, así los gastos históricos conservan su nombre correcto.
    if (PURCHASE_DISPLAY_NAME_BY_CODE[code]) return PURCHASE_DISPLAY_NAME_BY_CODE[code];
    // Compatibilidad para registros muy antiguos que no tuvieran código.
    if (/\b170\s*g\b/.test(detail)) return "Queso pequeño";
    if (/\b350\s*g\b/.test(detail)) return "Queso mediano";
    if (/\b500\s*g\b/.test(detail)) return "Queso grande";
    if (/queso\s+esparcible|esparcible/.test(detail)) return "Esparcible";
    // Nunca etiquetamos como esparcible una presentación desconocida.
    return "Quesito campesino";
  }
  return name;
}

function productHasCompactPresentation(product, presentation = "", productCode = "") {
  const normalized = String(product || "").trim().toLocaleLowerCase("es-CO");
  if (normalized === "quesito campesino") {
    return visibleProductName(product, presentation, productCode) !== "Quesito campesino";
  }
  return /^(Unidad de (170|350|500)\s*g|170\s*g|350\s*g|500\s*g)$/i.test(String(presentation || "").trim());
}

function isOrderProductSelectable(product) {
  const normalized = (value) => String(value ?? "").trim().toLocaleLowerCase("es-CO");
  const active = normalized(product?.activo ?? product?.Activo);
  const commercialState = normalized(product?.estado_comercial ?? product?.EstadoComercial);
  const availability = normalized(product?.disponibilidad ?? product?.Disponibilidad);
  if (active && !["si", "sí", "s", "true", "1", "activo", "activa"].includes(active)) return false;
  if (["inactivo", "inactiva", "no configurado", "no configurada"].includes(commercialState)) return false;
  if (["inactivo", "inactiva", "no configurado", "no configurada"].includes(availability)) return false;
  return Boolean(String(product?.codigo || product?.Codigo || "").trim());
}

function orderProductLabel(product) {
  const rawName = String(product?.producto || product?.Producto || product?.nombre || product?.Nombre || product?.codigo || "Producto").trim();
  const baseName = visibleProductName(rawName, product?.presentacion_descripcion || product?.PresentacionDescripcion || product?.presentacion || product?.Presentacion, product?.codigo || product?.Codigo || product?.codigo_producto || product?.CodigoProducto);
  let detail = String(product?.presentacion_descripcion || product?.PresentacionDescripcion || product?.presentacion || product?.Presentacion || "").trim();
  const soup = String(product?.servicio_entrega || product?.ServicioEntrega || "").toUpperCase() === "RUTA_SOPAS_MEDIODIA";
  if (soup && /^(porci[oó]n)$/i.test(detail)) return baseName;
  if (!detail) return baseName;

  detail = detail
    .replace(/^\s*[x×]\s*/i, "")
    .replace(/\b(?:unidad(?:es)?|paquete|empaque|botella|bolsa|envase|canasta|cubeta|bandeja|frasco|caja)\s*(?:de\s*)?\d+(?:[.,]\d+)?\s*(?:g|gr|gramos?|kg|ml|cl|l|litros?|libra(?:s)?|lb)\b/gi, " ")
    .replace(/\b\d+(?:[.,]\d+)?\s*(?:g|gr|gramos?|kg|ml|cl|l|litros?|libra(?:s)?|lb)\b/gi, " ")
    .replace(/\bx\s*\d+(?:[.,]\d+)?(?:\s+\w+)?\b/gi, " ")
    .replace(/\b(?:unidad(?:es)?|paquete|empaque|botella|bolsa|envase|canasta|cubeta|bandeja|frasco|caja)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^(?:de|del)\s+/i, "");

  const normalizedBase = baseName.toLocaleLowerCase("es-CO");
  const normalizedRaw = rawName.toLocaleLowerCase("es-CO");
  const normalizedDetail = detail.toLocaleLowerCase("es-CO");
  if (!detail || normalizedBase.includes(normalizedDetail)) return baseName;
  if (normalizedDetail.startsWith(`${normalizedRaw} `)) detail = detail.slice(rawName.length).trim();
  const firstNameWord = rawName.split(/\s+/, 1)[0]?.toLocaleLowerCase("es-CO");
  if (firstNameWord && detail.toLocaleLowerCase("es-CO").startsWith(`${firstNameWord} `)) detail = detail.slice(firstNameWord.length).trim();
  return detail ? `${baseName} ${detail}` : baseName;
}

function deliveryServiceLabel(productOrService) {
  // La categoría Sopas ya comunica la ruta. Repetir el servicio en cada
  // producto alarga el selector y no aporta una decisión al administrador.
  return "";
}

function OrderProductCards({ products, selectedCode, lines = [], onSelect, onAdd, disabled = false }) {
  const selectedQuantities = new Map(lines.map((line) => [String(line.codigo), Number(line.cantidad || 0)]));
  const groups = Array.from(
    products.filter(isOrderProductSelectable).reduce((map, product) => {
      const key = String(product.categoria || product.Categoria || "Otros");
      const list = map.get(key) || [];
      list.push(product);
      map.set(key, list);
      return map;
    }, new Map()),
  );
  return (
    <div className="order-product-cards" aria-label="Productos disponibles">
      {groups.map(([category, items]) => (
        <section className="order-product-card-group" key={category}>
          <h4>{categoryLabel(category)}</h4>
          <div className="order-product-card-grid">
            {items.map((product) => {
              const code = product.codigo || product.Codigo;
              const name = orderProductLabel(product);
              const price = Number(product.precio ?? product.precio_venta ?? product.Precio ?? 0);
              const selectedQuantity = selectedQuantities.get(String(code)) || 0;
              const selected = selectedQuantity > 0 || String(selectedCode || "") === String(code);
              return (
                <button
                  type="button"
                  className={`order-product-card${selected ? " is-selected" : ""}`}
                  key={code}
                  onClick={() => {
                    onSelect(product);
                    onAdd(product);
                  }}
                  disabled={disabled}
                  aria-pressed={selected}
                >
                  <span className="order-product-card-copy">
                    <b>{name}</b>
                  </span>
                  <strong>{price > 0 ? formatCurrency(price) : "Precio por peso"}</strong>
                  {selectedQuantity > 0 && (
                    <span className="order-product-card-selected">
                      {selectedQuantity} seleccionado{selectedQuantity === 1 ? "" : "s"}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function OrderDetailFloatingButton({ lines }) {
  if (!lines.length) return null;
  const selectedUnits = lines.reduce((sum, line) => sum + Number(line.cantidad || 0), 0);
  return (
    <button
      type="button"
      className="order-product-detail-floating"
      aria-label={`Abrir pedido, ${selectedUnits} producto${selectedUnits === 1 ? "" : "s"} seleccionado${selectedUnits === 1 ? "" : "s"}`}
      onClick={(event) => {
        const summary = event.currentTarget.closest("form")?.querySelector('[aria-label="Resumen editable del pedido"]');
        summary?.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
    >
      <span>Pedido</span><b>{selectedUnits}</b>
    </button>
  );
}

function EditableOrderSummary({ lines, products, setLines, priceFor, title = "Resumen del pedido", totalLabel = "Total estimado", extra = null, totalExtra = 0 }) {
  const total = lines.reduce((sum, line) => sum + Number(priceFor(line, products) || 0) * Number(line.cantidad || 0), 0) + Number(totalExtra || 0);
  const selectedUnits = lines.reduce((sum, line) => sum + Number(line.cantidad || 0), 0);
  const changeQuantity = (code, delta) => setLines((current) => current
    .map((line) => line.codigo === code ? { ...line, cantidad: Number(line.cantidad || 0) + delta } : line)
    .filter((line) => Number(line.cantidad || 0) > 0));
  if (!lines.length) return null;
  return (
    <div className="manual-lines" aria-label="Resumen editable del pedido">
      <div className="manual-lines-heading"><b>{title}</b><span>{selectedUnits} producto{selectedUnits === 1 ? "" : "s"} seleccionado{selectedUnits === 1 ? "" : "s"}</span></div>
      {lines.map((line) => {
        const price = Number(priceFor(line, products) || 0);
        return (
          <div className="manual-line-card" key={line.codigo}>
            <span><b>{line.nombre}</b><small>{formatCurrency(price)} c/u</small></span>
            <div className="manual-line-quantity" aria-label={`Cantidad de ${line.nombre}`}>
              <button type="button" onClick={() => changeQuantity(line.codigo, -1)} aria-label={`Disminuir ${line.nombre}`}>−</button>
              <b>{line.cantidad}</b>
              <button type="button" onClick={() => changeQuantity(line.codigo, 1)} aria-label={`Aumentar ${line.nombre}`}>+</button>
            </div>
            <strong>{formatCurrency(price * Number(line.cantidad || 0))}</strong>
            <button type="button" onClick={() => setLines((current) => current.filter((item) => item.codigo !== line.codigo))}>Quitar</button>
          </div>
        );
      })}
      {extra}
      <div className="manual-order-total grand"><span>{totalLabel}</span><b>{formatCurrency(total)}</b></div>
    </div>
  );
}

function visibleRepartidorName(value) {
  return String(value || "").trim() === "Repartidor 1" ? "David Romero" : String(value || "");
}

function formatPhoneVisible(value) {
  let phone = String(value || "").trim().replace(/^wa:/i, "").replace(/\s+/g, "");
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("57"))
    return `+57 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  if (digits.length === 10 && digits.startsWith("3"))
    return `+57 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  return phone;
}
function formatPhoneHref(value) {
  const digits = String(value || "").replace(/^wa:/i, "").replace(/\D/g, "");
  if (!digits) return "";
  return `https://wa.me/${digits.startsWith("57") ? digits : `57${digits}`}`;
}
function formatCell(value, key = "") {
  if (value == null) return "—";
  if (
    [
      "total",
      "monto",
      "valor",
      "saldo_pendiente",
      "abonos",
      "ingresos",
      "costo",
      "valor_inventario",
    ].includes(key)
  )
    return formatCurrency(value);
  return String(value);
}
function availabilityFailureMessage(payload) {
  const data = payload?.data || {};
  const product = String(data.producto || "este producto").trim();
  const available =
    data.disponible ??
    data.cantidad_disponible ??
    data.capacidad_disponible ??
    data.litros_disponibles;
  const requested =
    data.solicitado ?? data.cantidad_solicitada ?? data.litros_solicitados;
  if (product && product !== "este producto") {
    const detail = Number.isFinite(Number(available))
      ? ` En este momento hay ${available} disponible${Number(available) === 1 ? "" : "s"}${Number.isFinite(Number(requested)) ? ` para ${requested} solicitada${Number(requested) === 1 ? "" : "s"}` : ""}.`
      : ".";
    return `${product} ya no está disponible para esa entrega.${detail} Puedes quitarlo o dejarlo en lista de espera.`;
  }
  return "Alguno de los productos ya no está disponible para esa entrega. Vuelve al carrito para ajustarlo.";
}

const PEDIDOS_WEB_PRODUCTS = [
  {
    code: "LECHE-VACA-1L",
    category: "Leches",
    name: "Leche cruda de vaca",
    presentation: "1 litro",
    price: 4500,
    availability: "Disponible",
    image: "",
  },
  {
    code: "LECHE-COCO-500",
    category: "Leches",
    name: "Leche de coco",
    presentation: "500 ml",
    price: 18500,
    availability: "Disponible",
    image: "",
  },
  {
    code: "LECHE-CABRA-1L",
    category: "Leches",
    name: "Leche de cabra",
    presentation: "1 litro",
    price: 13000,
    availability: "Disponible",
    image: "",
  },
  {
    code: "LECHE-SOYA-1L",
    category: "Leches",
    name: "Leche de soya",
    presentation: "1 litro",
    price: 15000,
    availability: "Agotado",
    image: "",
  },
  {
    code: "AREPAS-5",
    category: "Panadería",
    name: "Arepas",
    presentation: "5 arepas",
    price: 2700,
    availability: "Disponible",
    image: "",
  },
  {
    code: "PAN-250",
    category: "Panadería",
    name: "Pan",
    presentation: "250 g",
    price: 7000,
    availability: "Disponible",
    image: "Pan.jpg",
  },
  {
    code: "PANDEQUESO-5",
    category: "Panadería",
    name: "Pandequeso",
    presentation: "5 pandequesos",
    price: 5000,
    availability: "Disponible",
    image: "",
  },
  {
    code: "PAN-CHOCHA-500",
    category: "Panadería",
    name: "Pan chocha",
    presentation: "500 g · sábados",
    price: 14000,
    availability: "Programado",
    delivery: "sábado",
    image: "",
  },
  {
    code: "CUAJADA-500",
    category: "Lácteos",
    name: "Cuajada campesina",
    presentation: "500 g",
    price: 12000,
    availability: "Disponible",
    delivery: "martes y sábado",
    image: "",
  },
  {
    code: "QUESITO-500",
    category: "Lácteos",
    name: "Quesito campesino",
    presentation: "500 g",
    price: 12000,
    availability: "Disponible",
    delivery: "martes y sábado",
    image: "Quesito campesino.jpg",
  },
  {
    code: "MANTEQUILLA-250",
    category: "Lácteos",
    name: "Mantequilla de vaca",
    presentation: "250 g",
    price: 12000,
    availability: "Disponible",
    image: "Mantequilla.png",
  },
  {
    code: "HUEVOS-30",
    category: "Huevos",
    name: "Huevos campesinos",
    presentation: "30 huevos grandes",
    price: 18000,
    availability: "Disponible",
    image: "",
  },
  {
    code: "PP-500",
    category: "Despensa",
    name: "Panela pulverizada",
    presentation: "500 g",
    price: 7000,
    availability: "Disponible",
    image: "",
  },
  {
    code: "PANELA-GRANDE-PAR",
    category: "Despensa",
    name: "Panela grande",
    presentation: "Par de 2 unidades",
    price: 8000,
    availability: "Disponible",
    image: "",
  },
  {
    code: "PANELA-PEQUENA-6",
    category: "Despensa",
    name: "Panela pequeña",
    presentation: "6 unidades",
    price: 7000,
    availability: "Disponible",
    image: "",
  },
  {
    code: "MIEL-500",
    category: "Despensa",
    name: "Miel pura",
    presentation: "500 g",
    price: 22000,
    availability: "Disponible",
    image: "",
  },
  {
    code: "ACEITE-COCO-250",
    category: "Coco",
    name: "Aceite de coco",
    presentation: "250 g",
    price: 20000,
    availability: "Disponible",
    image: "",
  },
  {
    code: "COCO-001",
    category: "Coco",
    name: "Coco natural entero",
    presentation: "1 coco mediano",
    price: 9000,
    availability: "Disponible",
    image: "",
  },
  {
    code: "LIMONADA-COCO-250",
    category: "Coco",
    name: "Limonada de coco",
    presentation: "250 ml",
    price: 4800,
    availability: "Disponible",
    image: "",
  },
  {
    code: "CAFE-COCO-100",
    category: "Coco",
    name: "Café de coco",
    presentation: "100 g",
    price: 4800,
    availability: "Disponible",
    image: "",
  },
  {
    code: "SIROPE-100",
    category: "Bebidas tradicionales",
    name: "Sirope",
    presentation: "100 g",
    price: 1800,
    availability: "Disponible",
    image: "",
  },
  {
    code: "POLLO-001",
    category: "Carnes",
    name: "Pollo campesino",
    presentation: "1 pollo · 7–9 kg",
    price: 0,
    availability: "Por reservar",
    image: "",
  },
];

function ProductImage({ product, priority = false }) {
  const [failed, setFailed] = useState(false);
  const [usingOriginal, setUsingOriginal] = useState(false);
  const rawImage = String(product.image || "").trim();
  const originalSrc = rawImage
    ? /^https?:\/\//i.test(rawImage)
      ? rawImage
      : `https://ddsmofsvionaucypmvsb.supabase.co/storage/v1/object/public/product-media/${rawImage
          .replace(/^\/+/, "")
          .replace(/^product-media\//i, "")
          .split("/")
          .map(encodeURIComponent)
          .join("/")}`
    : "";
  const optimizedSrc = originalSrc
    ? originalSrc.replace(
        "/storage/v1/object/public/",
        "/storage/v1/render/image/public/",
      ) + "?width=640&quality=72&resize=contain"
    : "";
  const src = usingOriginal ? originalSrc : optimizedSrc;
  const srcSet =
    !usingOriginal && optimizedSrc
      ? [
          [320, optimizedSrc.replace("width=640", "width=320")],
          [640, optimizedSrc],
          [960, optimizedSrc.replace("width=640", "width=960")],
        ]
          .map(([width, url]) => `${url} ${width}w`)
          .join(", ")
      : undefined;
  if (!src || failed)
    return (
      <div className="product-image placeholder">
        <span>T</span>
        <small>Foto próximamente</small>
      </div>
    );
  return (
    <img
      className="product-image"
      src={src}
      srcSet={srcSet}
      sizes="(max-width: 650px) 46vw, (max-width: 1100px) 30vw, 280px"
      alt={product.name}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding="async"
      onError={() => {
        if (!usingOriginal && originalSrc) setUsingOriginal(true);
        else setFailed(true);
      }}
    />
  );
}

function productFromErp(product) {
  if (!product?.codigo || !product?.producto) return null;
  const action = String(product.accion || "PENDIENTE").toUpperCase();
  const policy = String(
    product.politica_disponibilidad || product.politica || "",
  ).toUpperCase();
  const days =
    Array.isArray(product.dias_entrega)
      ? product.dias_entrega
      : policy === "CAPACIDAD_DIARIA" && Array.isArray(product.dias_operacion)
      ? product.dias_operacion
      : Array.isArray(product.calendario_disponibilidad?.days)
        ? product.calendario_disponibilidad.days
        : Array.isArray(product.dias_operacion)
          ? product.dias_operacion
          : [];
  const dayLabels = {
    0: "lunes",
    1: "martes",
    2: "miércoles",
    3: "jueves",
    4: "viernes",
    5: "sábado",
    6: "domingo",
    MONDAY: "lunes",
    TUESDAY: "martes",
    WEDNESDAY: "miércoles",
    THURSDAY: "jueves",
    FRIDAY: "viernes",
    SATURDAY: "sábado",
    SUNDAY: "domingo",
  };
  const deliveryDays = [
    ...new Set(
      days.map(
        (day) =>
          dayLabels[String(day).toUpperCase()] ||
          dayLabels[Number(day)] ||
          String(day).toLowerCase(),
      ),
    ),
  ];
  // Un calendario vacío no significa automáticamente "toda la semana".
  // Esa etiqueta solo es segura para inventario estático (o productos legacy
  // sin política), mientras que capacidad/lote deben conservar su restricción.
  const unrestricted =
    deliveryDays.length === 0 && (!policy || policy === "INVENTARIO_ESTATICO");
  const scheduleLabel = String(product.etiqueta_entrega || "").trim() ||
    (product.entrega_toda_la_semana === true || deliveryDays.length === 7
      ? "Entrega toda la semana"
      : deliveryDays.length === 1
      ? `Solo los ${deliveryDays[0]}`
      : deliveryDays.length > 1
        ? `Disponible ${deliveryDays.slice(0, -1).join(", ")} y ${deliveryDays[deliveryDays.length - 1]}`
        : unrestricted
          ? "Entrega toda la semana"
          : "");
  const availability =
    {
      AGREGAR: "Disponible",
      PROGRAMAR: scheduleLabel || "Disponible en fecha programada",
      RESERVAR: "Disponible para reservar",
      LISTA_ESPERA: "Sin disponibilidad",
      NO_DISPONIBLE: "No disponible",
    }[action] || "Disponibilidad por confirmar";
  const defaultLabel =
    {
      AGREGAR: "Agregar",
      PROGRAMAR: "Agregar",
      RESERVAR: "Solicitar reserva",
      LISTA_ESPERA: "Avisarme cuando haya disponibilidad",
      NO_DISPONIBLE: "No disponible",
    }[action] || "Disponibilidad por confirmar";
  return {
    code: product.codigo,
    canonicalProductId:
      product.producto_canonico_id ||
      product.catalog_product_id ||
      product.codigo,
    canonicalName: product.nombre_producto_canonico || product.producto,
    category: product.categoria || "Otros",
    categoryOrder: Number(product.orden_familia ?? product.category_order ?? 999),
    name: product.producto,
    presentation:
      product.presentacion_descripcion ||
      product.presentacion ||
      product.unidad_venta ||
      "Presentación por confirmar",
    price: Number(product.precio || 0),
    availability,
    availabilityAction: action,
    canAdd: product.puede_agregar_carrito === true,
    actionLabel: product.etiqueta_accion || defaultLabel,
    availabilityMessage: product.mensaje_disponibilidad || "",
    suggestedDeliveryDate: product.fecha_entrega || "",
    pricingStatus: product.pricing_status || "CONFIRMADO",
    image:
      product.foto ||
      product.Foto ||
      product.photo_url ||
      product.Imagen ||
      product.image_url ||
      "",
    ficha:
      product.ficha && typeof product.ficha === "object" ? product.ficha : {},
    delivery: scheduleLabel,
    // Solo estos productos tienen una explicación narrativa de disponibilidad
    // aprobada para el ícono contextual. La ficha técnica sigue siendo
    // consultable para cualquier producto por separado.
    infoText: product.beneficios || "",
    product3d: product3dFor(product.codigo),
  };
}

// El navegador agrupa únicamente presentaciones ya resueltas por el catálogo
// canónico. No combina precios, inventario ni disponibilidad.
function groupProductPresentations(products) {
  const grouped = new Map();
  for (const product of products) {
    const id = String(product.canonicalProductId || product.code);
    const group = grouped.get(id) || {
      id,
      category: product.category,
      categoryOrder: product.categoryOrder,
      name: product.canonicalName || product.name,
      presentations: [],
    };
    group.presentations.push(product);
    grouped.set(id, group);
  }
  return Array.from(grouped.values());
}

function ProductPresentationCard({
  group,
  onAdd,
  onWaitlist,
  onSelect,
  requestingCode,
}) {
  const initial =
    group.presentations.find((item) => item.canAdd) || group.presentations[0];
  const [selectedCode, setSelectedCode] = useState(initial?.code || "");
  const [presentationPickerOpen, setPresentationPickerOpen] = useState(false);
  const product =
    group.presentations.find((item) => item.code === selectedCode) || initial;
  if (!product) return null;
  const unavailable = ["NO_DISPONIBLE", "LISTA_ESPERA"].includes(
    product.availabilityAction,
  );
  const photoProduct =
    group.presentations.find((item) => item.image) || initial;
  const prices = group.presentations
    .map((item) => Number(item.price))
    .filter((price) => price > 0);
  const uniquePrices = [...new Set(prices)];
  const lowestPrice = prices.length ? Math.min(...prices) : 0;
  const priceLabel =
    lowestPrice > 0
      ? uniquePrices.length > 1
        ? `Desde ${formatCurrency(lowestPrice)}`
        : formatCurrency(lowestPrice)
      : "Precio por peso";
  const addOrChoose = () =>
    group.presentations.length > 1
      ? setPresentationPickerOpen(true)
      : onAdd(product);
  return (
    <article
      className={`product-card ${unavailable ? "unavailable" : ""}`}
      onClick={() => onSelect(product)}
    >
      <ProductImage product={photoProduct} />
      <div className="product-card-body">
        <div
          className="catalog-availability-line"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "6px",
            width: "fit-content",
          }}
        >
          <span
            className={`catalog-availability-badge ${product.availability.toLowerCase().replaceAll(" ", "-")}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              minHeight: "26px",
              padding: "4px 9px",
              border: "1px solid #b9d8c1",
              borderRadius: "999px",
              color: "#28754e",
              background: "#e4f1e6",
              fontSize: "11px",
              fontWeight: 750,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {product.availability}
          </span>
          <ProductInfoButton product={product} />
        </div>
        <small className="catalog-delivery catalog-delivery-slot">
          {product.delivery || "\u00a0"}
        </small>
        <h2>{group.name}</h2>
        <p
          className={
            group.presentations.length > 1
              ? "presentation presentation-count"
              : "presentation"
          }
        >
          {group.presentations.length > 1
            ? `${group.presentations.length} presentaciones disponibles`
            : product.presentation}
        </p>
        <strong className="product-price">{priceLabel}</strong>
        <button
          className="product-detail-link"
          onClick={(event) => {
            event.stopPropagation();
            onSelect(product);
          }}
        >
          Ver ficha del producto
        </button>
        <ProductAction
          product={product}
          onAdd={addOrChoose}
          onWaitlist={onWaitlist}
          requesting={requestingCode === product.code}
        />
        <div className="product-card-ar-slot">
          <ProductArAction product={product} />
        </div>
      </div>
      {presentationPickerOpen && (
        <div
          className="presentation-picker-backdrop"
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
            setPresentationPickerOpen(false);
          }}
        >
          <section
            className="presentation-picker"
            role="dialog"
            aria-modal="true"
            aria-label={`Presentaciones de ${group.name}`}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="product-info-close"
              onClick={() => setPresentationPickerOpen(false)}
              aria-label="Cerrar"
            >
              ×
            </button>
            <p className="eyebrow">ELIGE TU PRESENTACIÓN</p>
            <h3>{group.name}</h3>
            {group.presentations.map((item) => (
              <div className="presentation-choice" key={item.code}>
                <div>
                  <b>{item.presentation}</b>
                  <small>
                    {item.price > 0
                      ? formatCurrency(item.price)
                      : "Precio por peso"}
                  </small>
                </div>
                <ProductAction
                  product={item}
                  onAdd={(chosen) => {
                    onAdd(chosen);
                    setPresentationPickerOpen(false);
                  }}
                  onWaitlist={(chosen) => {
                    onWaitlist(chosen);
                    setPresentationPickerOpen(false);
                  }}
                  requesting={requestingCode === item.code}
                />
              </div>
            ))}
          </section>
        </div>
      )}
    </article>
  );
}

function categoryLabel(value) {
  const raw = String(value || "Otros");
  const normalized = raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es-CO")
    .trim();
  if (normalized === "lacteos") return "Derivados lácteos";
  return raw
    .toLocaleLowerCase("es-CO")
    .replace(
      /(^|[\s-])([a-záéíóúñ])/g,
      (_, prefix, letter) => `${prefix}${letter.toLocaleUpperCase("es-CO")}`,
    );
}

function fichaLabel(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

function fichaValue(value) {
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (Array.isArray(value))
    return value.map((item) => fichaValue(item)).join(" · ");
  if (value && typeof value === "object")
    return Object.entries(value)
      .map(([key, item]) => `${fichaLabel(key)}: ${fichaValue(item)}`)
      .join(" · ");
  return String(value);
}

function ProductAction({
  product,
  onAdd,
  onWaitlist,
  onReserve = onWaitlist,
  onClose,
  checking = false,
  requesting = false,
}) {
  const action = String(
    product.availabilityAction || "PENDIENTE",
  ).toUpperCase();
  const verifying = checking || action === "VERIFICANDO";
  const waitlist = action === "LISTA_ESPERA";
  const reserve = action === "RESERVAR";
  const canAdd =
    product.canAdd === true ||
    (DEV_BYPASS_AUTH && product.availability === "Disponible");
  const enabled = !verifying && !requesting && (canAdd || waitlist || reserve);
  const label = requesting
    ? "Registrando solicitud…"
    : verifying
      ? "Verificando disponibilidad…"
      : product.actionLabel ||
        (canAdd
          ? "Agregar"
          : reserve
            ? "Solicitar reserva"
            : waitlist
              ? "Avisarme cuando haya disponibilidad"
              : "Disponibilidad por confirmar");
  return (
    <button
      className={`product-action ${waitlist ? "waitlist-action" : ""} ${reserve ? "reserve-action" : ""}`}
      disabled={!enabled}
      onClick={(event) => {
        event.stopPropagation();
        if (waitlist) onWaitlist(product);
        else if (reserve) onReserve(product);
        else if (canAdd) onAdd(product);
        onClose?.();
      }}
    >
      {label}
    </button>
  );
}

function ProductInfoButton({ product }) {
  const [open, setOpen] = useState(false);
  const infoText = product.infoText || product.availabilityMessage || "";
  if (!infoText) return null;
  return (
    <>
      <button
        type="button"
        className="product-info-button catalog-info-button"
        aria-label={`Información de ${product.name}`}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        i
      </button>
      {open && (
        <div
          className="product-info-backdrop"
          role="presentation"
          onClick={() => setOpen(false)}
        >
          <section
            className="product-info-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={`Información de ${product.name}`}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="product-info-close"
              aria-label="Cerrar información"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
            <p className="eyebrow">PARA TENER EN CUENTA</p>
            <h3>{product.name}</h3>
            <p>{infoText}</p>
            <button
              type="button"
              className="product-info-confirm"
              onClick={() => setOpen(false)}
            >
              Entendido
            </button>
          </section>
        </div>
      )}
    </>
  );
}

let modelViewerLoadPromise;
function ensureModelViewer() {
  if (customElements.get("model-viewer")) return Promise.resolve();
  if (modelViewerLoadPromise) return modelViewerLoadPromise;
  modelViewerLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.type = "module";
    script.src =
      "https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("No se pudo cargar el visor."));
    document.head.appendChild(script);
  });
  return modelViewerLoadPromise;
}

function ProductArAction({ product }) {
  const model = product.product3d;
  const modelViewerRef = useRef(null);
  const [arMessage, setArMessage] = useState("");
  useEffect(() => {
    if (model?.arModelUrl)
      ensureModelViewer().catch(() =>
        setArMessage(
          "La realidad aumentada no está disponible en este momento.",
        ),
      );
  }, [model?.arModelUrl]);
  useEffect(() => {
    const viewer = modelViewerRef.current;
    if (!viewer) return undefined;
    function onArStatus(event) {
      const status = event.detail?.status;
      if (status === "session-started")
        setArMessage("Mueve el teléfono para ubicar el producto.");
      if (status === "object-placed")
        setArMessage("Producto colocado sobre la superficie.");
      if (status === "failed")
        setArMessage(
          "No se pudo iniciar la realidad aumentada en este dispositivo.",
        );
    }
    viewer.addEventListener("ar-status", onArStatus);
    return () => viewer.removeEventListener("ar-status", onArStatus);
  }, []);
  if (
    !model?.arModelUrl ||
    String(product.code || "").startsWith("PAN-250") ||
    String(product.name || "")
      .trim()
      .toLocaleLowerCase("es-CO") === "pan"
  )
    return null;
  async function activateAR() {
    setArMessage("");
    if (!modelViewerRef.current?.activateAR) {
      setArMessage(
        "La realidad aumentada no está disponible en este dispositivo.",
      );
      return;
    }
    try {
      await modelViewerRef.current.activateAR();
    } catch {
      setArMessage(
        "La realidad aumentada no está disponible en este dispositivo.",
      );
    }
  }
  return (
    <div className="product-ar-card-action">
      <model-viewer
        ref={modelViewerRef}
        className="product-ar-card-model"
        src={model.arModelUrl}
        ios-src={model.iosModelUrl || undefined}
        alt={`Modelo para realidad aumentada de ${product.name}`}
        ar
        ar-modes="webxr scene-viewer quick-look"
        ar-placement="floor"
        ar-scale="fixed"
        camera-controls
        touch-action="pan-y"
        shadow-intensity="1"
        exposure="1"
      />
      <button
        type="button"
        className="product-ar-button product-ar-card-button"
        onClick={(event) => {
          event.stopPropagation();
          activateAR();
        }}
      >
        Ver sobre la mesa
      </button>
      {arMessage && (
        <small className="product-ar-message" role="status">
          {arMessage}
        </small>
      )}
    </div>
  );
}

function canAddToCart(product) {
  return (
    product.canAdd === true ||
    (DEV_BYPASS_AUTH && product.availability === "Disponible")
  );
}

function fechaEntregaVisible(value) {
  const fecha = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return "Fecha por confirmar";
  const [year, month, day] = fecha.split("-").map(Number);
  return new Intl.DateTimeFormat("es-CO", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(year, month - 1, day));
}

function agruparCarritoPorEntrega(rows) {
  const grupos = new Map();
  for (const product of rows) {
    const fecha = String(product.suggestedDeliveryDate || "").trim();
    const key = fecha || "sin-fecha";
    if (!grupos.has(key)) grupos.set(key, { fecha, productos: [] });
    grupos.get(key).productos.push(product);
  }
  return Array.from(grupos.values()).sort((left, right) => {
    if (!left.fecha) return 1;
    if (!right.fecha) return -1;
    return left.fecha.localeCompare(right.fecha);
  });
}

function importeVisible(product, quantity) {
  return product.pricingStatus === "PENDIENTE_PESO_PRECIO"
    ? "Peso y precio por confirmar"
    : formatCurrency(product.price * quantity);
}

function MisPedidosVisual({ onClose }) {
  const pedidosDemo = [
    {
      pedido: "PED-DEMO-041",
      estado: "Pendiente",
      estado_operativo: "Confirmado, pendiente de reparto",
      fecha_entrega: "2026-08-31",
      valor: 42000,
      producto: "2 arepas · 1 quesito campesino · 1 leche cruda",
      puede_editarse: true,
      puede_cancelarse: true,
    },
    {
      pedido: "PED-DEMO-042",
      estado: "Pendiente",
      estado_operativo: "Confirmado, pendiente de reparto",
      fecha_entrega: "2026-09-05",
      valor: 14000,
      producto: "1 pan chocha",
      puede_editarse: true,
      puede_cancelarse: true,
    },
    {
      pedido: "PED-DEMO-038",
      estado: "Entregado",
      fecha_entrega: "2026-08-25",
      valor: 28500,
      producto: "2 leches · 1 panela pequeña",
    },
  ];
  const [tab, setTab] = useState("Activos");
  const [selected, setSelected] = useState(null);
  const [state, setState] = useState(DEV_BYPASS_AUTH ? "ready" : "loading");
  const [error, setError] = useState("");
  const [data, setData] = useState(() =>
    DEV_BYPASS_AUTH
      ? { pedidos: pedidosDemo, entradas_lista_espera: [] }
      : { pedidos: [], entradas_lista_espera: [] },
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [detailState, setDetailState] = useState("idle");
  const [detailMessage, setDetailMessage] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editObservation, setEditObservation] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [waitlistCancelTarget, setWaitlistCancelTarget] = useState(null);
  const [waitlistCancelState, setWaitlistCancelState] = useState("idle");

  useEffect(() => {
    if (DEV_BYPASS_AUTH) return undefined;
    let alive = true;
    const controller = new AbortController();
    fetch("/api/pedidos-web/mis-pedidos", {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok)
          throw new Error(
            payload.mensaje || "No pudimos consultar tus pedidos ahora.",
          );
        if (alive) {
          const respuesta = payload.data || {
            pedidos: [],
            entradas_lista_espera: [],
          };
          setData(respuesta);
          setState("ready");
        }
      })
      .catch((reason) => {
        if (alive && reason.name !== "AbortError") {
          setError(reason.message);
          setState("error");
        }
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [refreshKey]);

  const pedidos = Array.isArray(data.pedidos) ? data.pedidos : [];
  const enEspera = Array.isArray(data.entradas_lista_espera)
    ? data.entradas_lista_espera
    : [];
  const esHistorico = (pedido) =>
    String(pedido.clasificacion_operativa || "").toUpperCase() === "HISTORIAL";
  // PedidosService entrega el orden canónico: más reciente a más antiguo.
  // Esta vista únicamente filtra y presenta, sin volver a interpretar el
  // orden de negocio.
  const visibles =
    tab === "Historial"
      ? pedidos.filter(esHistorico)
      : pedidos.filter((pedido) => !esHistorico(pedido));
  const fechaVisible = (fecha) => {
    const texto = String(fecha || "").slice(0, 10);
    if (!texto) return "Fecha de entrega por confirmar";
    const parsed = new Date(`${texto}T12:00:00`);
    return Number.isNaN(parsed.getTime())
      ? texto
      : new Intl.DateTimeFormat("es-CO", {
          weekday: "long",
          day: "numeric",
          month: "long",
        }).format(parsed);
  };
  const estadoVisible = (pedido) =>
    typeof pedido === "string"
      ? pedido
      : String(pedido?.estado_operativo || "Estado por actualizar");
  const puedeEditar = (pedido) => pedido?.puede_editarse === true;
  const puedeCancelar = (pedido) => pedido?.puede_cancelarse === true;

  async function abrirDetalle(pedido) {
    setSelected({ ...pedido, detalle: [] });
    setDetailState("loading");
    setDetailMessage("");
    setConfirmCancel(false);
    if (DEV_BYPASS_AUTH) {
      setDetailState("ready");
      return;
    }
    try {
      const response = await fetch("/api/pedidos-web/mis-pedidos/gestionar", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accion: "detalle", pedido_id: pedido.pedido }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || "No pudimos abrir este pedido.");
      const detalle = payload.data || {};
      const pedidoDetalleSinNormalizar = {
        ...pedido,
        ...(detalle.pedido || {}),
        detalle: Array.isArray(detalle.detalle) ? detalle.detalle : [],
      };
      const pedidoDetalle = pedidoDetalleSinNormalizar;
      setSelected(pedidoDetalle);
      setEditAddress(String(pedidoDetalle.direccion_entrega || ""));
      setEditObservation(String(pedidoDetalle.observaciones || ""));
      setDetailState("ready");
    } catch (reason) {
      setDetailMessage(reason.message);
      setDetailState("error");
    }
  }

  async function guardarEntrega() {
    if (!selected || detailState === "saving") return;
    if (!editAddress.trim() && !editObservation.trim()) {
      setDetailMessage("Escribe una dirección u observación para guardar.");
      return;
    }
    if (DEV_BYPASS_AUTH) {
      setDetailMessage(
        "Vista de desarrollo: los cambios reales se guardan con un enlace válido.",
      );
      return;
    }
    setDetailState("saving");
    setDetailMessage("");
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch("/api/pedidos-web/mis-pedidos/gestionar", {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accion: "actualizar_entrega",
          pedido_id: selected.pedido,
          direccion: editAddress,
          observacion: editObservation,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || "No pudimos actualizar el pedido.");
      const actualizado = payload.data?.pedido_actualizado || {};
      setSelected((current) => ({ ...current, ...actualizado }));
      setDetailMessage("Listo, guardé la observación y los datos de entrega.");
      setDetailState("ready");
      setRefreshKey((current) => current + 1);
    } catch (reason) {
      setDetailMessage(
        reason.name === "AbortError"
          ? "La actualización está tardando más de lo esperado. Verifica el pedido antes de volver a intentarlo."
          : reason.message,
      );
      setDetailState("error");
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async function cancelarPedido() {
    if (!selected || detailState === "saving") return;
    if (!confirmCancel) {
      setConfirmCancel(true);
      setDetailMessage(
        "¿Seguro que deseas cancelar este pedido? Esta acción libera su disponibilidad.",
      );
      return;
    }
    if (DEV_BYPASS_AUTH) {
      setDetailMessage(
        "Vista de desarrollo: la cancelación real se habilita con un enlace válido.",
      );
      return;
    }
    setDetailState("saving");
    setDetailMessage("");
    try {
      const response = await fetch("/api/pedidos-web/mis-pedidos/gestionar", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accion: "cancelar",
          pedido_id: selected.pedido,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || "No pudimos cancelar el pedido.");
      setSelected((current) => ({
        ...current,
        estado: "Cancelado",
        estado_operativo: "Cancelado",
        clasificacion_operativa: "HISTORIAL",
        puede_editarse: false,
        puede_cancelarse: false,
      }));
      setDetailMessage(
        "Tu pedido fue cancelado. La disponibilidad quedó liberada.",
      );
      setDetailState("ready");
      setConfirmCancel(false);
      setRefreshKey((current) => current + 1);
    } catch (reason) {
      setDetailMessage(reason.message);
      setDetailState("error");
    }
  }

  async function cancelarSolicitudEspera() {
    if (!waitlistCancelTarget || waitlistCancelState === "saving") return;
    setWaitlistCancelState("saving");
    try {
      const response = await fetch("/api/pedidos-web/lista-espera/cancelar", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lista_espera_id: waitlistCancelTarget.lista_espera_id,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(
          payload.mensaje || "No pudimos retirar esta solicitud.",
        );
      setData((current) => ({
        ...current,
        entradas_lista_espera: (current.entradas_lista_espera || []).filter(
          (entry) =>
            entry.lista_espera_id !== waitlistCancelTarget.lista_espera_id,
        ),
      }));
      setWaitlistCancelTarget(null);
      setWaitlistCancelState("idle");
    } catch (reason) {
      setWaitlistCancelState("error");
      setDetailMessage(reason.message);
    }
  }

  return (
    <div className="my-orders-backdrop">
      {selected && detailState === "saving" && (
        <div
          className="my-order-saving-overlay"
          role="status"
          aria-live="polite"
        >
          <span className="loading-dot" /> Titina está guardando tus cambios…
        </div>
      )}
      <section
        className="my-orders-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Mis pedidos"
      >
        <button className="drawer-close" onClick={onClose} aria-label="Cerrar">
          <X size={18} />
        </button>
        <div className="my-orders-heading">
          <div>
            <p className="eyebrow">TU HISTORIA CON TITINA</p>
            <h2>Mis pedidos</h2>
            <p>Consulta tus pedidos y revisa cómo va cada entrega.</p>
          </div>
          {DEV_BYPASS_AUTH && (
            <span className="development-label">Vista de desarrollo</span>
          )}
        </div>
        <div className="my-orders-tabs">
          <button
            className={tab === "Activos" ? "active" : ""}
            onClick={() => setTab("Activos")}
          >
            Activos
          </button>
          <button
            className={tab === "Historial" ? "active" : ""}
            onClick={() => setTab("Historial")}
          >
            Historial
          </button>
          <button
            className={tab === "Espera" ? "active" : ""}
            onClick={() => setTab("Espera")}
          >
            En espera
          </button>
        </div>
        {state === "loading" && (
          <div className="my-orders-empty">
            <h3>Estamos buscando tus pedidos</h3>
            <p>Un momentico, ya te mostramos cómo van.</p>
          </div>
        )}
        {state === "error" && (
          <div className="my-orders-empty">
            <h3>No pudimos cargar tus pedidos</h3>
            <p>{error}</p>
          </div>
        )}
        {state === "ready" &&
          tab === "Espera" &&
          (enEspera.length ? (
            <div className="my-orders-list">
              {enEspera.map((entrada) => (
                <article
                  className="my-order-card"
                  key={
                    entrada.lista_espera_id ||
                    `${entrada.codigo_presentacion}-${entrada.fecha_ingreso}`
                  }
                >
                  <div>
                    <span className="my-order-id">SOLICITUD EN ESPERA</span>
                    <h3>
                      {entrada.codigo_presentacion || "Producto solicitado"}
                    </h3>
                    <p>
                      {entrada.tipo_solicitud === "RESERVA_LOTE_VARIABLE"
                        ? "Reserva pendiente de disponibilidad, peso y valor"
                        : "Te avisaremos cuando vuelva a estar disponible"}
                    </p>
                    <small>
                      {Number(entrada.cantidad || 1)} unidad(es) ·{" "}
                      {fechaVisible(entrada.fecha_solicitada)}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="my-order-cancel waitlist-cancel-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setWaitlistCancelState("idle");
                      setWaitlistCancelTarget(entrada);
                    }}
                  >
                    Retirar solicitud
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="my-orders-empty">
              <h3>No tienes solicitudes en espera</h3>
              <p>
                Cuando un producto no esté disponible, podrás pedir que Titina
                te avise.
              </p>
            </div>
          ))}
        {state === "ready" &&
          tab !== "Espera" &&
          (visibles.length ? (
            <div className="my-orders-list">
              {visibles.map((pedido) => (
                <button
                  className="my-order-card"
                  key={pedido.pedido}
                  onClick={() => abrirDetalle(pedido)}
                >
                  <div>
                    <span className="my-order-id">{pedido.pedido}</span>
                    <h3>{estadoVisible(pedido)}</h3>
                    <p>Entrega {fechaVisible(pedido.fecha_entrega)}</p>
                    <small>
                      {pedido.producto || "Productos por confirmar"}
                    </small>
                  </div>
                  <strong>{formatCurrency(Number(pedido.valor || 0))}</strong>
                </button>
              ))}
            </div>
          ) : (
            <div className="my-orders-empty">
              <h3>No tienes pedidos en esta sección</h3>
              <p>
                {tab === "Historial"
                  ? "Aquí verás los pedidos ya entregados o cancelados."
                  : "Cuando hagas un pedido, Titina lo mostrará aquí."}
              </p>
            </div>
          ))}
        {selected && (
          <div className="my-order-detail">
            <button
              className="drawer-close"
              onClick={() => {
                setSelected(null);
                setDetailMessage("");
              }}
              aria-label="Cerrar detalle"
            >
              <X size={18} />
            </button>
            <p className="eyebrow">DETALLE DEL PEDIDO</p>
            <h3>{selected.pedido}</h3>
            {detailState === "loading" ? (
              <div className="my-order-loading">
                <span className="loading-dot" /> Estamos consultando este
                pedido…
              </div>
            ) : (
              <>
                <p>
                  <b>{estadoVisible(selected)}</b>
                </p>
                <p>Entrega {fechaVisible(selected.fecha_entrega)}</p>
                <p>{selected.producto || "Productos por confirmar"}</p>
                {selected.detalle?.length > 0 && (
                  <div className="my-order-lines">
                    {selected.detalle.map((linea, index) => (
                      <p key={`${linea.producto}-${index}`}>
                        {linea.cantidad_solicitada} · {linea.producto}
                      </p>
                    ))}
                  </div>
                )}
                {selected.tipo_entrega && (
                  <p>Entrega: {selected.tipo_entrega}</p>
                )}
                {selected.hora_entrega_solicitada && (
                  <p>Hora solicitada: {selected.hora_entrega_solicitada}</p>
                )}
                <strong>
                  Total {formatCurrency(Number(selected.valor || 0))}
                </strong>
                {puedeEditar(selected) && (
                  <div className="my-order-actions">
                    <p className="eyebrow">AJUSTAR ENTREGA</p>
                    <label>
                      Dirección
                      <input
                        value={editAddress}
                        onChange={(event) => setEditAddress(event.target.value)}
                        placeholder="Dirección de entrega"
                      />
                    </label>
                    <label>
                      Observación
                      <textarea
                        value={editObservation}
                        onChange={(event) =>
                          setEditObservation(event.target.value)
                        }
                        rows="2"
                        placeholder="Ej. tocar en la puerta"
                      />
                    </label>
                    <button
                      type="button"
                      className="my-order-save"
                      disabled={detailState === "saving"}
                      onClick={guardarEntrega}
                    >
                      {detailState === "saving"
                        ? "Guardando…"
                        : "Guardar datos de entrega"}
                    </button>
                    <small>
                      Para cambiar productos, escríbele a Titina por WhatsApp.
                      Así cuidamos la disponibilidad de tu pedido.
                    </small>
                  </div>
                )}
                {!puedeEditar(selected) && selected.motivo_no_edicion && (
                  <p className="my-order-message">
                    {selected.motivo_no_edicion}
                  </p>
                )}
                {puedeCancelar(selected) && (
                  <div className="my-order-actions my-order-cancel-action">
                    <p className="eyebrow">CANCELAR PEDIDO</p>
                    <button
                      type="button"
                      className="my-order-cancel"
                      disabled={detailState === "saving"}
                      onClick={cancelarPedido}
                    >
                      {confirmCancel
                        ? "Sí, cancelar este pedido"
                        : "Cancelar pedido"}
                    </button>
                  </div>
                )}
                {detailMessage && (
                  <p
                    className={`my-order-message ${detailState === "error" ? "error" : ""}`}
                  >
                    {detailMessage}
                  </p>
                )}
              </>
            )}
          </div>
        )}
        {waitlistCancelTarget && (
          <div className="waitlist-confirm-backdrop" role="presentation">
            <section
              className="waitlist-confirm"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="waitlist-confirm-title"
            >
              <p className="eyebrow">SOLICITUD EN ESPERA</p>
              <h3 id="waitlist-confirm-title">¿Retirar esta solicitud?</h3>
              <p>
                Si la retiras, Titina ya no te avisará cuando haya
                disponibilidad de{" "}
                {waitlistCancelTarget.codigo_presentacion || "este producto"}.
              </p>
              {waitlistCancelState === "error" && (
                <p className="my-order-message error">
                  No pudimos retirar la solicitud. Inténtalo de nuevo.
                </p>
              )}
              <div className="waitlist-confirm-actions">
                <button
                  type="button"
                  onClick={() => setWaitlistCancelTarget(null)}
                >
                  Conservar solicitud
                </button>
                <button
                  type="button"
                  className="my-order-cancel"
                  disabled={waitlistCancelState === "saving"}
                  onClick={cancelarSolicitudEspera}
                >
                  {waitlistCancelState === "saving"
                    ? "Retirando…"
                    : "Sí, retirar solicitud"}
                </button>
              </div>
            </section>
          </div>
        )}
      </section>
    </div>
  );
}

class PedidosWebRuntimeBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    reportPedidosWebRuntimeIssue(
      "react_render",
      error,
      "pedidos_web_render",
      info?.componentStack || "",
    );
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="orders-web">
        <section className="catalog-loading catalog-error" role="alert">
          <b>No pudimos abrir pedidos</b>
          <span>
            Estamos revisando qué ocurrió. Tu carrito y tus datos no fueron
            modificados.
          </span>
          <button
            type="button"
            className="cart-continue"
            onClick={() => window.location.reload()}
          >
            Intentar de nuevo
          </button>
        </section>
      </main>
    );
  }
}

function PedidosWeb() {
  const [showMyOrders, setShowMyOrders] = useState(false);
  useEffect(() => {
    document.documentElement.dataset.titinaPedidosMounted = "1";
    return () => {
      delete document.documentElement.dataset.titinaPedidosMounted;
    };
  }, []);
  useEffect(() => {
    const onError = (event) =>
      reportPedidosWebRuntimeIssue(
        "window_error",
        event.error || event.message,
        "window_error",
      );
    const onRejection = (event) =>
      reportPedidosWebRuntimeIssue(
        "unhandled_rejection",
        event.reason,
        "unhandled_rejection",
      );
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
  return (
    <PedidosWebRuntimeBoundary>
      <PedidosWebCatalog />
      <button
        className="my-orders-trigger"
        type="button"
        onClick={() => setShowMyOrders(true)}
      >
        Mis pedidos
      </button>
      {showMyOrders && (
        <MisPedidosVisual onClose={() => setShowMyOrders(false)} />
      )}
    </PedidosWebRuntimeBoundary>
  );
}

function PedidosWebCatalog() {
  const traceIdRef = useRef(createPedidosWebTraceId());
  const traceHeaders = (includeJson = false) =>
    pedidosWebHeaders(traceIdRef.current, includeJson);
  const [products, setProducts] = useState(() =>
    DEV_BYPASS_AUTH ? PEDIDOS_WEB_PRODUCTS : [],
  );
  const [cart, setCart] = useState(() => {
    try {
      return normalizarCarritoCodigosHistoricos(JSON.parse(
        window.localStorage.getItem(
          DEV_BYPASS_AUTH
            ? "titina_pedidos_cart"
            : "titina_pedidos_cart_public",
        ) || "{}",
      ));
    } catch {
      return {};
    }
  });
  const [category, setCategory] = useState("Todas");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [cartNotice, setCartNotice] = useState("");
  const [voiceNotice, setVoiceNotice] = useState("");
  const [voiceResult, setVoiceResult] = useState(null);
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceState, setVoiceState] = useState("idle");
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voiceErrorCode, setVoiceErrorCode] = useState("");
  const [pwaInstallPrompt, setPwaInstallPrompt] = useState(null);
  const [pwaNotice, setPwaNotice] = useState("");
  const [pwaUpdateApply, setPwaUpdateApply] = useState(null);
  const [pwaInstalled, setPwaInstalled] = useState(() =>
    window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true,
  );
  const [voiceIntroVisible, setVoiceIntroVisible] = useState(false);
  const [reserveNotice, setReserveNotice] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [webSession, setWebSession] = useState(
    DEV_BYPASS_AUTH ? "development" : "loading",
  );
  const [webSessionError, setWebSessionError] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneState, setPhoneState] = useState("idle");
  const [phoneError, setPhoneError] = useState("");
  const [sessionRecovery, setSessionRecovery] = useState(false);
  const [catalogState, setCatalogState] = useState(
    DEV_BYPASS_AUTH ? "ready" : "loading",
  );
  const [webIdentity, setWebIdentity] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(DEV_BYPASS_AUTH);
  // El perfil es una lectura canónica de la identidad. No se permite abrir
  // checkout hasta recibirla: así un cliente existente no ve un formulario
  // vacío por una carrera entre catálogo y contexto.
  const [customerContextState, setCustomerContextState] = useState(
    DEV_BYPASS_AUTH ? "ready" : "loading",
  );
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [featuredConfig, setFeaturedConfig] = useState(null);
  const [superProductConfig, setSuperProductConfig] = useState(null);
  const [availabilityChecking, setAvailabilityChecking] = useState("");
  const [requestingCode, setRequestingCode] = useState("");
  const cartTouchedRef = useRef(false);
  const confirmationCompletedRef = useRef(false);
  const draftWriteQueueRef = useRef(Promise.resolve());
  const selectedProductRef = useRef(null);
  const voiceRecognitionRef = useRef(null);
  const voiceInterpretationRef = useRef(0);
  const voiceSessionRef = useRef(0);
  const voiceStateRef = useRef("idle");
  const voiceTranscriptBufferRef = useRef("");
  const voiceFinalHandledRef = useRef(false);
  const voiceListenTimeoutRef = useRef(null);
  const voiceTurnTimeoutRef = useRef(null);
  const clearVoiceTimeouts = () => {
    window.clearTimeout(voiceListenTimeoutRef.current);
    window.clearTimeout(voiceTurnTimeoutRef.current);
    voiceListenTimeoutRef.current = null;
    voiceTurnTimeoutRef.current = null;
  };
  const setVoicePhase = (phase) => {
    voiceStateRef.current = phase;
    setVoiceState(phase);
    setVoiceListening(phase === "listening");
  };
  useEffect(() => {
    if (!cartOpen && !checkoutOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (checkoutOpen) setCheckoutOpen(false);
      else setCartOpen(false);
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [cartOpen, checkoutOpen]);
  useEffect(() => {
    const onBeforeInstall = (event) => {
      event.preventDefault();
      setPwaInstallPrompt(event);
    };
    const onInstalled = () => {
      setPwaInstalled(true);
      setPwaInstallPrompt(null);
      setPwaNotice("Titina quedó instalada en tu dispositivo.");
    };
    const onUpdate = (event) => {
      if (typeof event.detail?.apply !== "function") return;
      setPwaUpdateApply(() => event.detail.apply);
      setPwaNotice("Hay una actualización de Titina lista para instalar.");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("titina-pedidos-pwa-update", onUpdate);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("titina-pedidos-pwa-update", onUpdate);
    };
  }, []);
  async function installTitinaPwa() {
    if (pwaInstallPrompt) {
      await pwaInstallPrompt.prompt();
      const choice = await pwaInstallPrompt.userChoice;
      setPwaInstallPrompt(null);
      setPwaNotice(choice?.outcome === "accepted"
        ? "Estamos instalando Titina en tu dispositivo."
        : "Puedes instalar Titina cuando quieras desde este botón.");
      return;
    }
    const isIos = /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
    setPwaNotice(isIos
      ? "En iPhone: toca Compartir, luego “Agregar a pantalla de inicio”."
      : "Abre el menú de tu navegador y elige “Instalar aplicación” o “Agregar a pantalla de inicio”.");
  }
  function applyTitinaPwaUpdate() {
    if (!pwaUpdateApply) return;
    pwaUpdateApply();
    setPwaNotice("Actualizando Titina…");
  }
  useEffect(() => {
    selectedProductRef.current = selectedProduct;
  }, [selectedProduct]);
  useEffect(() => () => {
    voiceSessionRef.current += 1;
    clearVoiceTimeouts();
    voiceRecognitionRef.current?.stop?.();
    voiceRecognitionRef.current = null;
    voiceInterpretationRef.current += 1;
  }, []);
  async function interpretarComandoVoz(transcript, sessionId) {
    const requestId = voiceInterpretationRef.current + 1;
    voiceInterpretationRef.current = requestId;
    setVoicePhase("processing");
    setVoiceNotice("Estoy entendiendo lo que necesitas.");
    reportPedidosWebRuntimeIssue("voice_turn_started", "voice transcript received", "voice");
    try {
      const response = await fetch("/api/pedidos-web/asistente/interpretar", {
        method: "POST",
        credentials: "include",
        headers: traceHeaders(true),
        body: JSON.stringify({ mensaje: transcript }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(payload.mensaje || "No pude interpretar esa solicitud.");
      if (voiceInterpretationRef.current !== requestId || voiceSessionRef.current !== sessionId) return;
      const data = payload.data || {};
      const iaEntrada = data.ia_entrada || {};
      const entidades = iaEntrada.entidades || {};
      const productQuery = String(entidades.producto || "").trim();
      const action = String(data.accion_web || "");
      const resolution = data.resolucion_producto || null;
      const normalizedQuery = productQuery.toLocaleLowerCase("es-CO");
      const localMatchingProducts = productQuery
        ? products.filter((item) =>
            [item.name, item.canonicalName, item.presentation].some((value) =>
              String(value || "").toLocaleLowerCase("es-CO").includes(normalizedQuery),
            ),
          )
        : [];
      const resolvedProduct = resolution?.producto
        ? {
            code: resolution.producto.codigo,
            name: resolution.producto.nombre,
            presentation: resolution.producto.presentacion,
            price: Number(resolution.producto.precio || 0),
            canAdd: resolution.producto.puede_agregar_carrito === true,
          }
        : null;
      const resolvedAlternatives = (resolution?.alternativas || data.resultados_producto || []).map((item) => ({
        code: item.codigo,
        name: item.nombre,
        presentation: item.presentacion,
        price: Number(item.precio || 0),
        canAdd: item.puede_agregar_carrito === true,
      }));
      const matchingProducts = iaEntrada.operacion === "precio" && resolvedAlternatives.length > 0
        ? resolvedAlternatives
        : resolvedProduct
          ? [resolvedProduct]
          : localMatchingProducts;
      if (action === "mostrar_catalogo" || action === "consultar_producto") {
        setCategory("Todas");
        setCatalogSearch(productQuery);
        setCartOpen(false);
      }
      if (action === "mostrar_ficha" && productQuery) {
        setCategory("Todas");
        setCatalogSearch(productQuery);
        const product = matchingProducts[0];
        if (product) setSelectedProduct(product);
      }
      const cartProduct = matchingProducts.find((item) => item.canAdd);
      let responseText = payload.texto_canonico || "Entendí tu solicitud.";
      if (iaEntrada.operacion === "precio" && resolvedAlternatives.length > 0) {
        const prices = resolvedAlternatives
          .filter((item) => item.canAdd && item.price > 0)
          .map((item) => `${item.presentation}: ${formatCurrency(item.price)}`)
          .join("; ");
        responseText = prices
          ? `Estos son los precios disponibles: ${prices}.`
          : "Encontré el producto, pero no tiene un precio confirmado disponible.";
      }
      let actionNotice = "";
      if (action === "agregar_al_carrito" && cartProduct) {
        addQuantity(cartProduct, Math.max(1, Number(entidades.cantidad) || 1), "incrementar");
      } else if (action === "eliminar_del_carrito" && cartProduct) {
        removeCompletely(cartProduct);
      } else if (action === "modificar_cantidad_carrito" && cartProduct) {
        const mode = String(entidades.modo_cantidad || "fijar");
        addQuantity(cartProduct, Math.max(1, Number(entidades.cantidad) || 1), mode);
      } else if (action === "abrir_carrito") {
        setVoiceResult(null);
        setCartOpen(true);
      } else if (
        ["agregar_al_carrito", "eliminar_del_carrito", "modificar_cantidad_carrito"].includes(action)
      ) {
        const reason = products.length === 0
          ? "El catálogo todavía está cargando. Inténtalo de nuevo en un momento."
          : productQuery
            ? `Entendí ${productQuery}, pero no encontré una presentación disponible para modificar.`
            : "Entendí la acción, pero necesito que me digas el producto.";
        actionNotice = reason;
        responseText = reason;
        reportPedidosWebRuntimeIssue("voice_action_unresolved", action, "voice");
      }
      const responseProducts = resolvedAlternatives.length > 0
        ? resolvedAlternatives
        : matchingProducts;
      setVoiceResult({
        text: responseText,
        operation: String(iaEntrada.operacion || ""),
        action,
        products: responseProducts.slice(0, 3),
        confirmed: ["agregar_al_carrito", "eliminar_del_carrito", "modificar_cantidad_carrito"].includes(action) && Boolean(cartProduct),
      });
      setVoiceNotice(actionNotice);
      reportPedidosWebRuntimeIssue("voice_turn_completed", action || "responder", "voice");
    } catch (error) {
      if (voiceInterpretationRef.current !== requestId || voiceSessionRef.current !== sessionId) return;
      // No copiamos la frase literal al buscador: una pregunta como “¿hay
      // arepas?” no es un término comercial y produciría un resultado vacío.
      setVoiceNotice(
        error.message || "No pude entenderte. Inténtalo nuevamente o busca escribiendo.",
      );
      setVoiceResult(null);
      reportPedidosWebRuntimeIssue("voice_turn_failed", error, "voice");
    } finally {
      if (voiceInterpretationRef.current === requestId && voiceSessionRef.current === sessionId)
        setVoicePhase("idle");
    }
  }
  function toggleVoiceSearch() {
    setVoiceIntroVisible(false);
    if (voiceStateRef.current !== "idle") {
      voiceSessionRef.current += 1;
      voiceInterpretationRef.current += 1;
      clearVoiceTimeouts();
      voiceRecognitionRef.current?.stop?.();
      voiceRecognitionRef.current = null;
      setVoicePhase("idle");
      setVoiceNotice("Escucha detenida. Puedes volver a hablar cuando quieras.");
      setVoiceErrorCode("");
      return;
    }
    const Recognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      setVoiceNotice(
        "Tu navegador no admite búsqueda por voz. Puedes buscar escribiendo el producto.",
      );
      return;
    }
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "microphone" }).then((permission) => {
        if (permission.state === "denied" && voiceStateRef.current === "idle") {
          setVoiceErrorCode("not-allowed");
          setVoiceNotice("El navegador bloqueó el micrófono. Permítelo en la configuración del sitio y vuelve a intentarlo.");
        }
      }).catch(() => {});
    }
    if (catalogState !== "ready") {
      setVoiceNotice("Estoy preparando el catálogo. Espera un momento y vuelve a intentarlo.");
      return;
    }
    const recognition = new Recognition();
    const sessionId = voiceSessionRef.current + 1;
    voiceSessionRef.current = sessionId;
    voiceTranscriptBufferRef.current = "";
    voiceFinalHandledRef.current = false;
    setVoiceResult(null);
    setVoiceTranscript("");
    setVoiceErrorCode("");
    setVoicePhase("starting");
    setVoiceNotice("Estoy activando el micrófono.");
    recognition.lang = "es-CO";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => {
      if (voiceSessionRef.current !== sessionId) return;
      voiceRecognitionRef.current = recognition;
      voiceTranscriptBufferRef.current = "";
      voiceFinalHandledRef.current = false;
      setVoicePhase("listening");
      setVoiceNotice("Te escucho. Dime qué necesitas.");
      reportPedidosWebRuntimeIssue("voice_listening", "recognition started", "voice");
      voiceListenTimeoutRef.current = window.setTimeout(() => {
        if (voiceSessionRef.current !== sessionId || voiceFinalHandledRef.current) return;
        voiceSessionRef.current += 1;
        recognition.abort();
        voiceRecognitionRef.current = null;
        setVoicePhase("idle");
        setVoiceNotice("No recibí audio en este momento. Revisa el micrófono e inténtalo de nuevo.");
        reportPedidosWebRuntimeIssue("voice_listen_timeout", "no audio received in 12 seconds", "voice");
      }, 12000);
    };
    recognition.onresult = (event) => {
      if (voiceSessionRef.current !== sessionId) return;
      const resultIndex = Number.isInteger(event.resultIndex)
        ? event.resultIndex
        : Math.max(0, (event.results?.length || 1) - 1);
      let finalTranscript = "";
      for (let index = resultIndex; index < (event.results?.length || 0); index += 1) {
        const result = event.results?.[index];
        const text = String(result?.[0]?.transcript || "").trim();
        if (!text) continue;
        voiceTranscriptBufferRef.current = text;
        window.clearTimeout(voiceListenTimeoutRef.current);
        voiceListenTimeoutRef.current = null;
        if (result.isFinal) finalTranscript = text;
      }
      const transcript = (finalTranscript || voiceTranscriptBufferRef.current)
        .trim()
        .replace(/[.!?]+$/, "");
      if (!finalTranscript || !transcript || voiceFinalHandledRef.current) {
        if (!finalTranscript && transcript) {
          setVoiceNotice("Te sigo escuchando…");
          window.clearTimeout(voiceTurnTimeoutRef.current);
          voiceTurnTimeoutRef.current = window.setTimeout(() => {
            if (voiceSessionRef.current !== sessionId || voiceFinalHandledRef.current) return;
            recognition.stop();
          }, 4000);
        }
        return;
      }
      voiceFinalHandledRef.current = true;
      setVoiceTranscript(transcript);
      setVoiceNotice("Te escuché. Estoy preparando la respuesta.");
      clearVoiceTimeouts();
      recognition.stop();
      interpretarComandoVoz(transcript, sessionId);
    };
    recognition.onerror = (event) => {
      if (voiceSessionRef.current !== sessionId) return;
      clearVoiceTimeouts();
      const message =
        event.error === "not-allowed"
          ? "No se concedió permiso para el micrófono. Puedes buscar escribiendo."
          : event.error === "audio-capture"
            ? "No puedo recibir audio del micrófono. Revisa que no esté en uso por otra aplicación."
            : event.error === "no-speech"
              ? "No escuché ninguna frase. Inténtalo de nuevo."
              : event.error === "aborted"
                ? "La escucha se detuvo. Puedes intentarlo otra vez."
                : "No pude entenderte. Inténtalo de nuevo o escribe el producto.";
      setVoiceErrorCode(event.error || "unknown");
      setVoicePhase("idle");
      setVoiceNotice(message);
      reportPedidosWebRuntimeIssue("voice_recognition_error", event.error || "unknown", "voice");
    };
    recognition.onend = () => {
      if (voiceSessionRef.current !== sessionId) return;
      clearVoiceTimeouts();
      voiceRecognitionRef.current = null;
      if (voiceStateRef.current === "listening" || voiceStateRef.current === "starting") {
        const fallbackTranscript = voiceTranscriptBufferRef.current.trim().replace(/[.!?]+$/, "");
        if (fallbackTranscript && !voiceFinalHandledRef.current) {
          voiceFinalHandledRef.current = true;
          setVoiceTranscript(fallbackTranscript);
          setVoiceNotice("Te escuché. Estoy preparando la respuesta.");
          interpretarComandoVoz(fallbackTranscript, sessionId);
        } else {
          setVoicePhase("idle");
          setVoiceNotice("No alcancé a escucharte. Toca el micrófono e inténtalo otra vez.");
        }
      }
      reportPedidosWebRuntimeIssue("voice_recognition_ended", "recognition ended", "voice");
    };
    try {
      recognition.start();
    } catch {
      setVoicePhase("idle");
      setVoiceErrorCode("start-failed");
      setVoiceNotice("No pude activar el micrófono. Puedes buscar escribiendo.");
      reportPedidosWebRuntimeIssue("voice_recognition_start_failed", "recognition start failed", "voice");
    }
  }
  useEffect(() => {
    if (DEV_BYPASS_AUTH) return undefined;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    const fragment = new URLSearchParams(
      window.location.hash.replace(/^#/, ""),
    );
    const linkToken = fragment.get("token") || "";
    const storedLinkToken = safeSessionGet("titina_pedidos_link_token");
    const reusableToken = linkToken || storedLinkToken;
    if (linkToken) safeSessionSet("titina_pedidos_link_token", linkToken);
    // El token vive en el fragmento para que no viaje al servidor ni quede en
    // logs HTTP. Se elimina de la barra inmediatamente después de leerlo.
    if (linkToken)
      window.history.replaceState(
        {},
        document.title,
        `${window.location.pathname}${window.location.search}`,
      );
    const endpoint = "/api/pedidos-web/session";
    let alive = true;
    const sessionRequest = (token) =>
      fetch(endpoint, {
        method: "POST",
        headers: traceHeaders(true),
        credentials: "include",
        signal: controller.signal,
        body: JSON.stringify({ token }),
      });
    const request = reusableToken
      ? sessionRequest(reusableToken)
      : fetch("/api/pedidos-web/me", {
          method: "POST",
          credentials: "include",
          signal: controller.signal,
          headers: traceHeaders(),
        });
    request
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok) {
          const failure = new Error(
            payload.mensaje ||
              (reusableToken
                ? `No fue posible validar el enlace (HTTP ${response.status})`
                : "Abre el enlace de pedidos que te compartió Titina."),
          );
          failure.status = response.status;
          throw failure;
        }
        if (!alive) return;
        setWebSession("active");
      })
      .catch(async (reason) => {
        if (!alive) return;
        if (!reusableToken) {
          setWebSession("anonymous");
          setWebSessionError(
            reason.name === "AbortError"
              ? "La verificación tardó más de lo esperado."
              : "",
          );
          setSessionRecovery(false);
          return;
        }
        safeSessionRemove("titina_pedidos_link_token");
        setWebSession("anonymous");
        setWebSessionError(
          reason.name === "AbortError"
            ? "La verificación tardó más de lo esperado."
            : "",
        );
        setSessionRecovery(false);
      });
    return () => {
      alive = false;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, []);
  async function iniciarConTelefono(event) {
    event.preventDefault();
    if (phoneState === "saving") return;
    setPhoneState("saving");
    setPhoneError("");
    try {
      const response = await fetch("/api/pedidos-web/acceso-telefono", {
        method: "POST",
        credentials: "include",
        headers: traceHeaders(true),
        body: JSON.stringify({ telefono: phone }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(
          payload.mensaje ||
            "No pudimos validar ese número. Revísalo e inténtalo de nuevo.",
        );
      safeSessionRemove("titina_pedidos_link_token");
      setPhoneState("idle");
      setPhoneError("");
      setWebSessionError("");
      setWebSession("active");
    } catch (error) {
      setPhoneState("error");
      setPhoneError(error.message || "No pudimos validar ese número.");
    }
  }
  useEffect(() => {
    window.localStorage.setItem(
      DEV_BYPASS_AUTH ? "titina_pedidos_cart" : "titina_pedidos_cart_public",
      JSON.stringify(cart),
    );
  }, [cart]);
  useEffect(() => {
    // La primera pintura sólo necesita el catálogo. El perfil, borrador y
    // lista de espera se recuperan en un efecto independiente para que una
    // conexión lenta a Supabase no esconda los productos.
    if (DEV_BYPASS_AUTH || !["active", "anonymous"].includes(webSession)) return undefined;
    let alive = true;
    const authenticated = webSession === "active";
    setDraftLoaded(!authenticated);
    setCatalogState("loading");
    setCustomerContextState(authenticated ? "loading" : "ready");
    fetch(authenticated ? "/api/pedidos-web/iniciar" : "/api/pedidos-web/catalogo-publico", {
      method: "POST",
      credentials: "include",
      headers: traceHeaders(),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok) {
          const failure = new Error(
            payload.mensaje || "No fue posible abrir pedidos.",
          );
          failure.status = response.status;
          throw failure;
        }
        if (!alive) return;
        const data = payload.data || {};
        const catalog = Array.isArray(data.productos) ? data.productos : [];
        const initialProducts = catalog.map(productFromErp).filter(Boolean);
        setProducts(initialProducts);
        setFeaturedConfig(data.sugeridos_catalogo || null);
        setSuperProductConfig(data.superproducto_popup || null);
        const identidadCanalId = String(data.identidad_canal_id || "").trim();
        if (authenticated)
          setWebIdentity((current) => ({
            ...(current || {}),
            identidadCanalId,
          }));
        else setWebIdentity(null);
        const cached = authenticated ? readDraftCache(identidadCanalId) : {};
        if (!cartTouchedRef.current && !confirmationCompletedRef.current) {
          setCart(normalizarCarritoCodigosHistoricos(cached));
          if (Object.keys(cached).length)
            cartTouchedRef.current = true;
        }
        if (!authenticated) setDraftLoaded(true);
        setCatalogState("ready");
      })
      .catch((error) => {
        if (alive) {
          setDraftLoaded(true);
          if (error.status === 401 && authenticated) {
            safeSessionRemove("titina_pedidos_link_token");
            setWebSession("anonymous");
            setWebSessionError("");
          } else {
            setCatalogState("error");
            setCustomerContextState("degraded");
            setCartNotice(error.message);
          }
        }
      });
    return () => {
      alive = false;
    };
  }, [webSession]);
  useEffect(() => {
    // El contexto es complementario: llega después de que el catálogo está
    // visible y no puede sobrescribir cambios que el cliente ya hizo.
    if (DEV_BYPASS_AUTH || webSession !== "active" || catalogState !== "ready") return undefined;
    let alive = true;
    fetch("/api/pedidos-web/contexto", {
      method: "POST",
      credentials: "include",
      headers: traceHeaders(),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok) {
          const failure = new Error(payload.mensaje || "No pudimos recuperar tus datos guardados.");
          failure.status = response.status;
          failure.code = payload.codigo || "";
          throw failure;
        }
        if (!alive) return;
        const data = payload.data || {};
        if (data.cliente)
          setWebIdentity((current) => ({ ...(current || {}), cliente: data.cliente }));
        setWaitlistEntries(Array.isArray(data.entradas_lista_espera) ? data.entradas_lista_espera : []);
        const restored = {};
        for (const item of data.borrador?.productos || []) {
          const code = LEGACY_CART_CODE_REPLACEMENTS[
            String(item.codigo || "").trim().toUpperCase()
          ] || String(item.codigo || "").trim().toUpperCase();
          const quantity = Number(item.cantidad);
          if (code && Number.isInteger(quantity) && quantity > 0) restored[code] = quantity;
        }
        if (!cartTouchedRef.current && !confirmationCompletedRef.current) setCart(restored);
        const identidadCanalId = String(data.identidad_canal_id || webIdentity?.identidadCanalId || "").trim();
        if (Object.keys(restored).length) cacheDraft(identidadCanalId, restored);
        setDraftLoaded(true);
        setCustomerContextState(Object.keys(data.contexto_parcial || {}).length ? "partial" : "ready");
      })
      .catch((error) => {
        if (!alive) return;
        setDraftLoaded(true);
        if (error.status === 401 || String(error.code || "").startsWith("PWEB-AUTH-")) {
          safeSessionRemove("titina_pedidos_link_token");
          setWebSession("anonymous");
          setWebSessionError("");
          return;
        }
        setCustomerContextState("degraded");
        setCartNotice("No pudimos recuperar algunos datos guardados. Puedes continuar y completarlos en la confirmación.");
      });
    return () => {
      alive = false;
    };
  }, [webSession, catalogState]);
  useEffect(() => {
    if (catalogState !== "ready" || !superProductConfig?.activo) return undefined;
    const code = String(superProductConfig.codigo || "").trim();
    const product = products.find((item) => item.code === code);
    const storageKey = `titina_superproducto_popup_seen:${code}`;
    const delaySeconds = Number(superProductConfig.delay_seconds || 5);
    if (!code || !product?.canAdd || safeSessionGet(storageKey)) return undefined;
    const timeout = window.setTimeout(() => {
      // No reemplazamos una ficha que el cliente abrió voluntariamente.
      if (selectedProductRef.current) return;
      safeSessionSet(storageKey, "1");
      setSelectedProduct({ ...product, superProductPopup: superProductConfig });
    }, Math.min(Math.max(delaySeconds, 1), 30) * 1000);
    return () => window.clearTimeout(timeout);
  }, [catalogState, products, superProductConfig]);
  useEffect(() => {
    // Una pulsación sobre el catálogo no puede perderse mientras el contexto
    // personal sigue cargando. El borrador remoto se concilia después, pero
    // la escritura del cambio del cliente debe encolarse de inmediato.
    if (DEV_BYPASS_AUTH || webSession !== "active" || !cartTouchedRef.current)
      return undefined;
    const identidadCanalId = String(webIdentity?.identidadCanalId || "").trim();
    const snapshot = Object.fromEntries(
      Object.entries(cart).filter(
        ([codigo, cantidad]) => String(codigo).trim() && Number(cantidad) > 0,
      ),
    );
    cacheDraft(identidadCanalId, snapshot);
    const guardar = async () => {
      try {
        const response = await fetch("/api/pedidos-web/borrador/guardar", {
          method: "POST",
          credentials: "include",
          keepalive: true,
          headers: traceHeaders(true),
          body: JSON.stringify({
            productos: Object.entries(snapshot).map(([codigo, cantidad]) => ({
              codigo,
              cantidad,
            })),
          }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
      } catch {
        // El cache local queda disponible para reconciliarse con el borrador
        // canónico cuando el cliente vuelva a abrir la tienda.
      }
    };
    // Serializar escrituras evita que una respuesta tardía restaure una versión
    // anterior del carrito después de varios toques seguidos.
    draftWriteQueueRef.current = draftWriteQueueRef.current.then(
      guardar,
      guardar,
    );
    return undefined;
  }, [cart, draftLoaded, webIdentity?.identidadCanalId, webSession]);
  useEffect(() => {
    if (confirmationCompletedRef.current)
      safeLocalRemove(draftCacheKey(webIdentity?.identidadCanalId));
  }, [cart, webIdentity?.identidadCanalId]);
  useEffect(() => {
    if (!cartNotice) return undefined;
    const timer = window.setTimeout(() => setCartNotice(""), 3600);
    return () => window.clearTimeout(timer);
  }, [cartNotice]);
  useEffect(() => {
    if (!voiceNotice) return undefined;
    const timer = window.setTimeout(() => setVoiceNotice(""), 3600);
    return () => window.clearTimeout(timer);
  }, [voiceNotice]);
  useEffect(() => {
    if (catalogState !== "ready") return undefined;
    setVoiceIntroVisible(true);
    const timer = window.setTimeout(() => setVoiceIntroVisible(false), 9000);
    return () => window.clearTimeout(timer);
  }, [catalogState]);
  const productGroups = groupProductPresentations(products);
  const categoryDefinitions = Array.from(
    productGroups.reduce((all, product) => {
      const name = String(product.category || "Otros");
      const current = all.get(name);
      if (!current || product.categoryOrder < current.order)
        all.set(name, { name, order: product.categoryOrder });
      return all;
    }, new Map()).values(),
  ).sort((left, right) => left.order - right.order || left.name.localeCompare(right.name, "es"));
  const categories = ["Todas", ...categoryDefinitions.map((item) => item.name)];
  const normalizedSearch = catalogSearch.trim().toLocaleLowerCase("es-CO");
  const visible = productGroups
    .filter((product) => category === "Todas" || product.category === category)
    .filter((product) => {
      if (!normalizedSearch) return true;
      return [
        product.name,
        product.category,
        ...product.presentations.map((item) => item.presentation),
      ].some((value) =>
        String(value || "")
          .toLocaleLowerCase("es-CO")
          .includes(normalizedSearch),
      );
    });
  const featuredIds = Array.isArray(featuredConfig?.productos)
    ? featuredConfig.productos.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const featuredRank = new Map(featuredIds.map((id, index) => [id, index]));
  const featured =
    featuredConfig?.activo === true && !normalizedSearch && category === "Todas"
      ? visible
          .filter((product) => featuredRank.has(String(product.id || product.canonicalProductId || "")))
          .sort((left, right) => featuredRank.get(String(left.id || left.canonicalProductId || "")) - featuredRank.get(String(right.id || right.canonicalProductId || "")))
      : [];
  // Sugeridos es un acceso rápido, no un filtro: cada producto sigue visible
  // dentro de su categoría para que el catálogo completo nunca tenga huecos.
  const catalogVisible = visible;
  const catalogByCategory = Array.from(
    catalogVisible.reduce((all, product) => {
      const name = String(product.category || "Otros");
      const section = all.get(name) || { name, order: product.categoryOrder, products: [] };
      section.order = Math.min(Number(section.order ?? 999), Number(product.categoryOrder ?? 999));
      section.products.push(product);
      all.set(name, section);
      return all;
    }, new Map()).values(),
  ).sort((left, right) => left.order - right.order || left.name.localeCompare(right.name, "es"));
  const cartRows = products
    .filter((product) => cart[product.code])
    .map((product) => ({ ...product, quantity: cart[product.code] }));
  const cartDeliveryGroups = agruparCarritoPorEntrega(cartRows);
  const cartProductIds = new Set(
    cartRows.map((product) =>
      String(product.canonicalProductId || product.code),
    ),
  );
  const cartCategories = new Set(
    cartRows
      .map((product) => String(product.category || "").trim())
      .filter(Boolean),
  );
  const cartSuggestions = cartRows.length
    ? productGroups
        .filter((group) =>
          cartCategories.has(String(group.category || "").trim()),
        )
        .filter((group) => !cartProductIds.has(String(group.id)))
        .map((group) => ({
          group,
          product: group.presentations.find((item) => item.canAdd) || null,
        }))
        .filter(({ product }) => product)
        .slice(0, 3)
    : [];
  const waitlistRows = waitlistEntries
    .map((entry) => ({
      ...entry,
      product: products.find(
        (product) => product.code === entry.codigo_presentacion,
      ),
    }))
    .reduce((rows, entry) => {
      const key = `${entry.codigo_presentacion || ""}:${entry.tipo_solicitud || entry.motivo || "LISTA_ESPERA"}`;
      const existing = rows.find(
        (row) =>
          `${row.codigo_presentacion || ""}:${row.tipo_solicitud || row.motivo || "LISTA_ESPERA"}` ===
          key,
      );
      if (existing)
        existing.cantidad =
          Number(existing.cantidad || 0) + Number(entry.cantidad || 0);
      else rows.push({ ...entry });
      return rows;
    }, []);
  const totalProducts = cartRows.length;
  const cartUnitCount = cartRows.reduce(
    (count, product) => count + Number(product.quantity || 0),
    0,
  );
  const total = cartRows.reduce(
    (sum, product) =>
      sum +
      (product.pricingStatus === "PENDIENTE_PESO_PRECIO"
        ? 0
        : product.price * cart[product.code]),
    0,
  );
  async function add(product) {
    addQuantity(product, 1, "incrementar");
  }
  function addQuantity(product, quantity, mode = "incrementar") {
    cartTouchedRef.current = true;
    const currentQuantity = Number(cart[product.code] || 0);
    const requestedQuantity = mode === "fijar"
      ? quantity
      : mode === "decrementar"
        ? currentQuantity - quantity
        : currentQuantity + quantity;
    setCart((current) => {
      const next = { ...current };
      if (requestedQuantity > 0) next[product.code] = requestedQuantity;
      else delete next[product.code];
      cacheDraft(webIdentity?.identidadCanalId, next);
      return next;
    });
    setCartNotice(
      mode === "fijar"
        ? `Dejé ${product.name} en cantidad ${requestedQuantity}.`
        : mode === "decrementar"
          ? `Disminuí ${product.name} en ${quantity}.`
          : `Agregué ${quantity} de ${product.name} a tu carrito.`,
    );
  }
  function removeCompletely(product) {
    cartTouchedRef.current = true;
    setCart((current) => {
      const next = { ...current };
      delete next[product.code];
      cacheDraft(webIdentity?.identidadCanalId, next);
      return next;
    });
    setCartNotice(`Retiré ${product.name} del carrito.`);
  }
  async function addToWaitlist(product) {
    if (requestingCode === product.code) return;
    setRequestingCode(product.code);
    if (DEV_BYPASS_AUTH) {
      setCartNotice(
        "Vista de desarrollo: la lista de espera se activa con un enlace válido.",
      );
      setRequestingCode("");
      return;
    }
    try {
      const idempotencyKey = `WEB-${crypto.randomUUID().replaceAll("-", "")}`;
      const response = await fetch("/api/pedidos-web/lista-espera", {
        method: "POST",
        credentials: "include",
        headers: traceHeaders(true),
        body: JSON.stringify({
          codigo: product.code,
          cantidad: 1,
          fecha_solicitada: product.suggestedDeliveryDate || undefined,
          idempotency_key: idempotencyKey,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(
          payload.mensaje || "No pudimos registrar la solicitud.",
        );
      const entrada = payload.data?.entrada;
      if (entrada?.lista_espera_id)
        setWaitlistEntries((current) => {
          const remaining = current.filter(
            (item) => item.lista_espera_id !== entrada.lista_espera_id,
          );
          return [...remaining, entrada];
        });
      const esReservaLote =
        String(product.availabilityAction || "").toUpperCase() === "RESERVAR";
      if (esReservaLote)
        setReserveNotice(
          `Recibí tu solicitud de reserva de ${product.name}. Te confirmaremos disponibilidad, peso y valor antes del pago y te escribiremos antes de enviarlo para confirmar que todavía lo necesitas.`,
        );
      else
        setCartNotice(
          `${product.name} quedó en lista de espera. Te avisaremos cuando vuelva a estar disponible.`,
        );
    } catch (error) {
      setCartNotice(error.message);
    } finally {
      setRequestingCode("");
    }
  }
  function remove(product) {
    cartTouchedRef.current = true;
    setCart((current) => {
      const next = {
        ...current,
        [product.code]: (current[product.code] || 0) - 1,
      };
      if (next[product.code] <= 0) delete next[product.code];
      cacheDraft(webIdentity?.identidadCanalId, next);
      return next;
    });
  }
  function openCheckout() {
    if (DEV_BYPASS_AUTH) {
      setCartNotice(
        "Carrito visual de demostración: la confirmación se habilitará en el siguiente sprint.",
      );
      return;
    }
    if (webSession === "active" && customerContextState === "expired") {
      setCartNotice(
        "Tu acceso ya no está activo. Abre nuevamente el enlace que te compartió Titina.",
      );
      return;
    }
    setCartOpen(false);
    setCheckoutOpen(true);
  }
  if (!DEV_BYPASS_AUTH && webSession === "loading")
    return (
      <main className="orders-web orders-auth-state">
        <section className="orders-auth-card">
          <span className="brand-mark">T</span>
          {webSession === "phone" ? (
            <>
              <p className="eyebrow">PEDIDOS EN LÍNEA</p>
              <h1>Hola, aquí estamos.</h1>
              <p>
                Escribe tu número de WhatsApp para cargar tu información y
                continuar tu pedido.
              </p>
              <form className="phone-access-form" onSubmit={iniciarConTelefono}>
                <label>
                  Número de WhatsApp
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder="321 123 4567"
                    required
                  />
                </label>
                <button
                  className="checkout-confirm"
                  type="submit"
                  disabled={phoneState === "saving"}
                >
                  {phoneState === "saving"
                    ? "Cargando tu información…"
                    : "Continuar"}
                </button>
                {phoneError && (
                  <p className="phone-access-error" role="alert">
                    {phoneError}
                  </p>
                )}
              </form>
              <small>
                Usaremos este número para encontrar tu información con Titina.
              </small>
            </>
          ) : (
            <>
              <h1>
                {webSession === "loading"
                  ? "Preparando tu pedido"
                  : "Tu acceso necesita renovarse"}
              </h1>
              <p>
                {webSession === "loading"
                  ? "Estamos verificando tu acceso seguro."
                  : webSessionError ||
                    "Vuelve a ingresar tu número de WhatsApp."}
              </p>
              {webSession === "loading" ? (
                <div className="auth-spinner" aria-label="Cargando" />
              ) : (
                <>
                  <p className="session-recovery-help">
                    Tu carrito permanece guardado en esta pestaña.
                  </p>
                  {sessionRecovery ? (
                    <button
                      className="checkout-confirm"
                      type="button"
                      onClick={() => window.location.reload()}
                    >
                      Reactivar acceso
                    </button>
                  ) : (
                    <button
                      className="checkout-confirm"
                      type="button"
                      onClick={() => setWebSession("phone")}
                    >
                      Ingresar con mi número
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </section>
      </main>
    );
  const catalogBody =
    catalogState === "loading" ? (
      <section className="catalog-loading" role="status" aria-live="polite">
        <div className="auth-spinner" />
        <div>
          <b>Cargando el catálogo de Titina</b>
          <span>Estamos preparando los productos para ti.</span>
        </div>
      </section>
    ) : catalogState === "error" ? (
      <section className="catalog-loading catalog-error" role="alert">
        <b>No pudimos cargar el catálogo</b>
        <span>Por favor, abre de nuevo el enlace que te compartió Titina.</span>
      </section>
    ) : catalogByCategory.length ? (
      <div className="catalog-category-list">
        {catalogByCategory.map((section) => (
          <section className="catalog-category" key={section.name} aria-labelledby={`catalog-category-${section.name.replaceAll(/[^a-z0-9]+/gi, "-")}`}>
            <header className="catalog-category-heading">
              <div>
                <p className="eyebrow">CATEGORÍA</p>
                <h3 id={`catalog-category-${section.name.replaceAll(/[^a-z0-9]+/gi, "-")}`}>{categoryLabel(section.name)}</h3>
              </div>
              <span>{section.products.length} {section.products.length === 1 ? "producto" : "productos"}</span>
            </header>
            <div className="orders-grid">
              {section.products.map((group) => (
                <ProductPresentationCard
                  key={group.id}
                  group={group}
                  onAdd={add}
                  onWaitlist={addToWaitlist}
                  onSelect={setSelectedProduct}
                  requestingCode={requestingCode}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    ) : (
      <section className="catalog-empty" role="status">
        <b>No encontré ese producto.</b>
        <span>
          Prueba con otro nombre o elige otra categoría, que Titina sigue aquí
          para ayudarte.
        </span>
      </section>
    );
  const customerName = String(webIdentity?.cliente?.nombre || "").trim();
  return (
    <main className="orders-web">
      <header className="orders-header">
        <div className="orders-brand">
          <span className="brand-mark">T</span>
          <div>
            <strong>Titina</strong>
            <small>
              {customerName
                ? `Hola, ${customerName}. Qué bueno tenerte por aquí.`
                : "Productos del campo para tu casa"}
            </small>
          </div>
        </div>
        {DEV_BYPASS_AUTH && (
          <div className="development-label">Vista de desarrollo</div>
        )}
        {!DEV_BYPASS_AUTH && webIdentity && (
          <div className="session-label">Acceso seguro</div>
        )}
        {!pwaInstalled && (
          <button className="pwa-install-trigger" type="button" onClick={installTitinaPwa}>
            Instalar app
          </button>
        )}
        <button
          className="cart-button"
          onClick={() => {
            setVoiceResult(null);
            setCartOpen(true);
          }}
          aria-label={`Ver carrito, ${totalProducts} productos`}
        >
          Carrito <b>{totalProducts}</b>
        </button>
      </header>
      {pwaNotice && (
        <div className="pwa-notice" role="status">
          <span>{pwaNotice}</span>
          {pwaUpdateApply && (
            <button type="button" onClick={applyTitinaPwaUpdate}>Actualizar ahora</button>
          )}
          <button type="button" aria-label="Cerrar aviso de aplicación" onClick={() => setPwaNotice("")}>×</button>
        </div>
      )}
      {cartNotice && (
        <div className="cart-toast" role="status" aria-live="polite">
          {cartNotice}
        </div>
      )}
      {voiceNotice && (
        <div className={`voice-assistant-notice${voiceState === "listening" ? " is-voice-active" : ""}${voiceState === "processing" ? " is-voice-processing" : ""}`} role="status" aria-live="polite">
          {voiceNotice}
          {voiceTranscript && voiceState !== "listening" && (
            <span className="voice-assistant-transcript">“{voiceTranscript}”</span>
          )}
          {voiceState === "idle" && voiceErrorCode && (
            <button type="button" className="voice-assistant-retry" onClick={toggleVoiceSearch}>
              Intentar de nuevo
            </button>
          )}
        </div>
      )}
      {voiceResult && !cartOpen && (
        <aside className="voice-assistant-result" role="status" aria-live="polite">
          <div className="voice-assistant-result-heading">
            <span className="voice-assistant-result-kicker">Titina</span>
            <button
              type="button"
              className="voice-assistant-result-close"
              aria-label="Cerrar respuesta de Titina"
              onClick={() => setVoiceResult(null)}
            >
              ×
            </button>
          </div>
          {voiceResult.confirmed && (
            <span className="voice-assistant-confirmed">Acción confirmada en el carrito</span>
          )}
          <p className="voice-assistant-result-text">{voiceResult.text}</p>
          {voiceResult.action === "instalar_pwa" && (
            <button type="button" className="pwa-install-confirm" onClick={installTitinaPwa}>
              Instalar Titina
            </button>
          )}
          {voiceResult.products.length > 0 && (
            <div className="voice-assistant-result-products">
              {voiceResult.products.map((product) => (
                <button
                  className={`voice-assistant-result-product${voiceResult.action === "agregar_al_carrito" && product.canAdd ? " is-addable" : ""}`}
                  key={product.code}
                  type="button"
                  disabled={voiceResult.action !== "agregar_al_carrito" || !product.canAdd}
                  onClick={() => {
                    if (voiceResult.action !== "agregar_al_carrito" || !product.canAdd) return;
                    addQuantity(product, 1, "incrementar");
                    setVoiceResult((current) => current ? { ...current, confirmed: true, text: `Agregué ${product.name} ${product.presentation ? `(${product.presentation}) ` : ""}a tu carrito.` } : current);
                  }}
                >
                  <span>
                    <strong>{product.name}</strong>
                    <small>{product.presentation}</small>
                  </span>
                  {voiceResult.operation === "precio" && (
                    <b>{formatCurrency(product.price)}</b>
                  )}
                  {voiceResult.action === "agregar_al_carrito" && product.canAdd && <em>Agregar</em>}
                </button>
              ))}
            </div>
          )}
        </aside>
      )}
      {voiceIntroVisible && !voiceListening && (
        <div className="voice-assistant-intro" role="status">
          <strong>Hola, soy Titina</strong>
          <span>
            Presiona aquí y habla conmigo. Te puedo guiar por la página usando
            solo tu voz.
          </span>
        </div>
      )}
      <div className="orders-web-floating-actions">
        <a
          className="whatsapp-floating-trigger"
          href="https://wa.me/573236340674?text=Hola%20Titina%2C%20necesito%20ayuda%20con%20mi%20pedido."
          target="_blank"
          rel="noreferrer"
          aria-label="Abrir WhatsApp de Titina"
          title="Hablar con Titina por WhatsApp"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M12 2.2a9.7 9.7 0 0 0-8.4 14.55L2.2 21.8l5.2-1.36A9.8 9.8 0 1 0 12 2.2Zm0 17.73a8 8 0 0 1-4.08-1.12l-.3-.18-3.08.8.82-3-.2-.31A8 8 0 1 1 12 19.93Zm4.39-5.97c-.24-.12-1.43-.71-1.65-.79-.22-.08-.38-.12-.54.12-.16.24-.62.79-.76.95-.14.16-.28.18-.52.06-1.4-.7-2.32-1.25-3.25-2.82-.24-.42.24-.39.69-1.3.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.47-.4-.4-.54-.41h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.69 2.58 4.1 3.62.57.25 1.02.4 1.37.51.58.18 1.1.15 1.51.09.46-.07 1.43-.59 1.63-1.16.2-.57.2-1.06.14-1.16-.06-.1-.22-.16-.46-.28Z" />
          </svg>
        </a>
        <button
          type="button"
          className={`voice-assistant-trigger${voiceState === "listening" ? " is-listening" : ""}${voiceState === "processing" || voiceState === "starting" ? " is-processing" : ""}`}
          aria-label={
            voiceState === "idle"
              ? "Iniciar búsqueda por voz"
              : voiceState === "processing"
                ? "Cancelar procesamiento de voz"
                : voiceState === "starting"
                  ? "Cancelar activación del micrófono"
                  : "Detener búsqueda por voz"
          }
          aria-pressed={voiceState !== "idle"}
          title={voiceState === "idle" ? "Buscar por voz" : "Detener voz"}
          onClick={toggleVoiceSearch}
        >
          <Mic size={22} aria-hidden="true" />
          <span>{voiceState === "listening" ? "Escuchando…" : voiceState === "processing" ? "Entendiendo…" : voiceState === "starting" ? "Activando…" : "Hablar"}</span>
        </button>
      </div>
      {reserveNotice && (
        <div className="reserve-alert-backdrop" role="presentation">
          <section
            className="reserve-alert"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="reserve-alert-title"
          >
            <p className="eyebrow">SOLICITUD RECIBIDA</p>
            <h2 id="reserve-alert-title">Reserva pendiente de confirmación</h2>
            <p>{reserveNotice}</p>
            <button
              type="button"
              className="reserve-alert-close"
              onClick={() => setReserveNotice("")}
            >
              Entendido
            </button>
          </section>
        </div>
      )}
      <section className="orders-hero">
        <p className="eyebrow">PEDIDOS EN LÍNEA</p>
        <h1>
          {customerName
            ? `Hola, ${customerName}`
            : "Elige lo que quieres llevar a casa"}
        </h1>
        <p>
          {customerName
            ? "Aquí puedes armar tu pedido con calma. Titina valida todo antes de confirmarlo."
            : "Revisa nuestras opciones, conoce su presentación y arma tu pedido con calma."}
        </p>
        <div className="order-steps">
          <span className="current">
            1 <b>Elige tus productos</b>
          </span>
          <i />{" "}
          <span>
            2 <b>Revisa tu pedido</b>
          </span>
          <i />{" "}
          <span>
            3 <b>Confirma</b>
          </span>
        </div>
      </section>
      <label className="catalog-search">
        <Search size={18} aria-hidden="true" />
        <span className="sr-only">Buscar productos</span>
        <input
          type="search"
          value={catalogSearch}
          onChange={(event) => setCatalogSearch(event.target.value)}
          placeholder="Busca leche, arepas, coco..."
          aria-label="Buscar productos"
        />
      </label>
      <nav className="category-tabs" aria-label="Categorías de productos">
        {categories.map((item) => (
          <button
            className={category === item ? "active" : ""}
            key={item}
            onClick={() => setCategory(item)}
          >
            {categoryLabel(item)}
          </button>
        ))}
      </nav>
      {featured.length > 0 && (
        <section
          className="catalog-featured"
          aria-labelledby="catalog-featured-title"
        >
          <div className="catalog-section-heading">
            <div>
              <p className="eyebrow">PARA EMPEZAR</p>
              <h2 id="catalog-featured-title">{featuredConfig?.titulo || "Recomendados para tu casa"}</h2>
            </div>
            <span>{featuredConfig?.subtitulo || "Los favoritos para el día a día"}</span>
          </div>
          <div className="orders-grid">
            {featured.map((group) => (
              <ProductPresentationCard
                key={group.id}
                group={group}
                onAdd={add}
                onWaitlist={addToWaitlist}
                onSelect={setSelectedProduct}
                requestingCode={requestingCode}
              />
            ))}
          </div>
        </section>
      )}
      <section
        className="catalog-results"
        aria-labelledby="catalog-results-title"
      >
        <div className="catalog-section-heading">
          <div>
            <p className="eyebrow">EXPLORA CON CALMA</p>
            <h2 id="catalog-results-title">Todo lo de Titina</h2>
          </div>
        </div>
        {catalogBody}
      </section>
      {selectedProduct && (
        <ProductDetail
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAdd={add}
          onWaitlist={addToWaitlist}
          requesting={requestingCode === selectedProduct.code}
          authenticated={webSession === "active"}
          traceId={traceIdRef.current}
          superProductPopup={selectedProduct.superProductPopup || null}
        />
      )}
      {cartOpen && (
        <div className="cart-backdrop" onClick={() => setCartOpen(false)}>
          <aside
            className="cart-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cart-drawer-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button className="drawer-close" onClick={() => setCartOpen(false)} aria-label="Cerrar carrito">
              <X size={18} />
            </button>
            <header className="cart-header">
              <div>
                <p className="eyebrow">TU PEDIDO</p>
                <h2 id="cart-drawer-title">Carrito</h2>
              </div>
              {cartRows.length > 0 && (
                <span className="cart-item-count" aria-label={`${cartUnitCount} productos en el carrito`}>
                  {cartUnitCount} producto{cartUnitCount === 1 ? "" : "s"}
                </span>
              )}
            </header>
            {cartRows.length === 0 ? (
              <section className="cart-empty-state">
                <Package size={28} aria-hidden="true" />
                <h3>Tu carrito está esperando</h3>
                <p>Agrega los productos que necesitas y aquí podrás revisar tu pedido.</p>
                <button type="button" onClick={() => setCartOpen(false)}>
                  Ver productos
                </button>
              </section>
            ) : (
              <>
                {cartDeliveryGroups.map((delivery) => (
                  <section
                    className="cart-delivery-group"
                    key={delivery.fecha || "sin-fecha"}
                  >
                    <h3 className="cart-delivery-date">
                      Entrega: {fechaEntregaVisible(delivery.fecha)}
                    </h3>
                    {delivery.productos.map((product) => (
                      <article className="cart-row" key={product.code}>
                        <div className="cart-item-image" aria-hidden="true">
                          <ProductImage product={product} />
                        </div>
                        <div className="cart-item-content">
                          <div className="cart-item-heading">
                            <div>
                              <b>{product.name}</b>
                              <small>{product.presentation}</small>
                            </div>
                            <button
                              type="button"
                              className="cart-item-remove"
                              onClick={() => removeCompletely(product)}
                              aria-label={`Quitar todos los ${product.name} del carrito`}
                            >
                              Quitar
                            </button>
                          </div>
                          {product.pricingStatus === "PENDIENTE_PESO_PRECIO" && (
                            <small className="cart-pricing-notice">Peso y valor final por confirmar</small>
                          )}
                          <div className="cart-item-actions">
                            <div className="quantity" aria-label={`Cantidad de ${product.name}`}>
                              <button
                                type="button"
                                onClick={() => remove(product)}
                                aria-label={`Disminuir ${product.name}`}
                              >
                                −
                              </button>
                              <span aria-live="polite">{cart[product.code]}</span>
                              <button
                                type="button"
                                onClick={() => add(product)}
                                aria-label={`Agregar ${product.name}`}
                              >
                                +
                              </button>
                            </div>
                            <strong className="cart-price">
                              {importeVisible(product, cart[product.code])}
                            </strong>
                          </div>
                        </div>
                      </article>
                    ))}
                  </section>
                ))}
              </>
            )}
            {cartSuggestions.length > 0 && (
              <section
                className="cart-suggestions"
                aria-labelledby="cart-suggestions-title"
              >
                <h3 id="cart-suggestions-title">También puede gustarte</h3>
                <p>Algunas opciones que combinan con lo que elegiste.</p>
                {cartSuggestions.map(({ group, product }) => (
                  <div className="cart-suggestion" key={group.id}>
                    <div>
                      <b>{group.name}</b>
                      <small>
                        {group.presentations.length > 1
                          ? `${group.presentations.length} presentaciones disponibles`
                          : product.presentation}{" "}
                        ·{" "}
                        {product.price > 0
                          ? formatCurrency(product.price)
                          : "Precio por peso"}
                      </small>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (group.presentations.length > 1) {
                          setCartOpen(false);
                          setSelectedProduct(product);
                        } else {
                          add(product);
                        }
                      }}
                    >
                      {group.presentations.length > 1
                        ? "Ver opciones"
                        : "Agregar"}
                    </button>
                  </div>
                ))}
              </section>
            )}
            {waitlistRows.length > 0 && (
              <section className="cart-waitlist">
                <h3>Solicitudes en espera</h3>
                <p>
                  Te avisaremos cuando estos productos vuelvan a estar
                  disponibles y te escribiremos antes de enviarlos para
                  confirmar que aún los necesitas.
                </p>
                {waitlistRows.map((entry) => (
                  <div
                    className="waitlist-row"
                    key={`${entry.codigo_presentacion}:${entry.tipo_solicitud || entry.motivo || "LISTA_ESPERA"}`}
                  >
                    <b>{entry.product?.name || entry.codigo_presentacion}</b>
                    <small>
                      {entry.cantidad} unidad(es) ·{" "}
                      {entry.tipo_solicitud === "RESERVA_LOTE_VARIABLE"
                        ? "reserva solicitada"
                        : "solicitud registrada"}
                    </small>
                  </div>
                ))}
              </section>
            )}
            {cartRows.length > 0 && (
              <footer className="cart-checkout-footer">
                <div className="cart-total">
                  <span>
                    {cartRows.some(
                      (product) =>
                        product.pricingStatus === "PENDIENTE_PESO_PRECIO",
                    )
                      ? "Total de productos con precio confirmado"
                      : "Subtotal de productos"}
                  </span>
                  <strong>{formatCurrency(total)}</strong>
                </div>
                <small className="cart-total-help">
                  El transporte y domicilio, si aplican, se muestran antes de confirmar.
                </small>
                <button
                  type="button"
                  className="cart-continue"
                  onClick={openCheckout}
                >
                  {DEV_BYPASS_AUTH ? "Ver resumen visual" : "Continuar con mi pedido"}
                </button>
                <small className="cart-note">
                  {DEV_BYPASS_AUTH
                    ? "Carrito visual con datos mock; todavía no confirma pedidos."
                    : "Titina valida disponibilidad, fecha y total antes de confirmar."}
                </small>
              </footer>
            )}
          </aside>
        </div>
      )}
      {checkoutOpen && (
        <CheckoutErrorBoundary onClose={() => setCheckoutOpen(false)}>
          <CheckoutPreview
            rows={cartRows}
            total={total}
            customer={webIdentity?.cliente || null}
            authenticated={webSession === "active"}
            customerContextState={customerContextState}
            traceId={traceIdRef.current}
            onAuthenticated={(identity) => {
              setWebIdentity((current) => ({ ...(current || {}), ...identity }));
              setWebSession("active");
            }}
            onClose={() => setCheckoutOpen(false)}
            onBackToCart={() => {
              setCheckoutOpen(false);
              setCartOpen(true);
            }}
            onConfirmed={() => {
              confirmationCompletedRef.current = true;
              cartTouchedRef.current = true;
              setCart({});
            }}
          />
        </CheckoutErrorBoundary>
      )}
    </main>
  );
}

class CheckoutErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    reportPedidosWebRuntimeIssue(
      "checkout_render",
      error,
      "checkout_preview_render",
      info?.componentStack || "",
    );
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="checkout-backdrop">
        <section className="checkout-panel checkout-fallback" role="alert">
          <p className="eyebrow">PEDIDO</p>
          <h2>No pudimos abrir la revisión</h2>
          <p>
            Vuelve al carrito e inténtalo de nuevo. Tu pedido no fue confirmado
            ni modificado.
          </p>
          <button
            className="checkout-confirm"
            type="button"
            onClick={this.props.onClose}
          >
            Volver al carrito
          </button>
        </section>
      </div>
    );
  }
}

function createCheckoutOperationId() {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.getRandomValues)
    throw new Error(
      "Tu navegador no puede preparar una confirmación segura. Actualízalo e inténtalo de nuevo.",
    );
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  return `WEB-${Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")}`;
}

function CheckoutPreview({
  rows,
  total,
  customer,
  authenticated = false,
  customerContextState = "ready",
  traceId,
  onAuthenticated,
  onClose,
  onBackToCart,
  onConfirmed,
}) {
  const [quote, setQuote] = useState(null);
  const [status, setStatus] = useState(
    DEV_BYPASS_AUTH ? "development" : "loading",
  );
  const [name, setName] = useState(() => String(customer?.nombre || ""));
  const [phone, setPhone] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(authenticated);
  const [resolvedCustomer, setResolvedCustomer] = useState(() => customer || null);
  const [profileMode, setProfileMode] = useState(() =>
    customer?.nombre && customer?.direccion ? "review" : "edit",
  );
  const [phoneLookupState, setPhoneLookupState] = useState("idle");
  const [address, setAddress] = useState(() =>
    String(customer?.direccion || ""),
  );
  const [neighborhood, setNeighborhood] = useState("");
  const [addressReference, setAddressReference] = useState("");
  const [municipality, setMunicipality] = useState("Supía, Caldas");
  const [destinations, setDestinations] = useState([]);
  const [destinationId, setDestinationId] = useState("");
  const [observation, setObservation] = useState("");
  const [deliveryType, setDeliveryType] = useState("Ruta normal");
  const [hour, setHour] = useState("08:00");
  const [location, setLocation] = useState(null);
  const [locationState, setLocationState] = useState("idle");
  const [deliveryNotice, setDeliveryNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [paymentCompletion, setPaymentCompletion] = useState(null);
  const [copiedPaymentField, setCopiedPaymentField] = useState("");
  const [quoteAttempt, setQuoteAttempt] = useState(0);
  const idempotencyKeyRef = useRef(null);
  const profileCustomer = resolvedCustomer || customer || null;
  const hasSavedProfile = Boolean(
    String(profileCustomer?.nombre || "").trim() &&
      String(profileCustomer?.direccion || "").trim(),
  );
  const checkoutIdentified = authenticated || phoneVerified;
  const profileLoading = Boolean(
    authenticated && customerContextState === "loading" && !profileCustomer,
  );
  const preferredDestinationId = String(
    profileCustomer?.ultimo_destino?.destino_entrega_id || "",
  ).trim();
  const preferredMunicipality = String(
    profileCustomer?.ultimo_destino?.municipio || "",
  ).trim();
  useEffect(() => {
    const nombre = String(customer?.nombre || "").trim();
    const direccion = String(customer?.direccion || "").trim();
    if (customer) {
      setResolvedCustomer(customer);
      setPhoneVerified(true);
      if (nombre && direccion)
        setProfileMode((current) => (current === "compact" ? current : "review"));
    }
    if (nombre) setName((actual) => (actual.trim() ? actual : nombre));
    if (direccion) setAddress((actual) => (actual.trim() ? actual : direccion));
  }, [customer?.nombre, customer?.direccion]);
  useEffect(() => {
    let active = true;
    fetch("/api/pedidos-web/destinos-entrega", { method: "POST", credentials: "include", headers: pedidosWebHeaders(traceId, true), body: "{}" })
      .then((response) => response.json())
      .then((payload) => {
        const list = payload?.data?.destinos || [];
        if (!active || !list.length) return;
        setDestinations(list);
        const preferred = list.find(
          (item) => item.id === preferredDestinationId && item.municipios?.includes(preferredMunicipality),
        );
        const selected = preferred || list[0];
        setDestinationId((value) => value || selected.id);
        setMunicipality((value) =>
          selected.municipios?.includes(value)
            ? value
            : (preferredMunicipality && selected.municipios?.includes(preferredMunicipality)
                ? preferredMunicipality
                : (selected.municipios?.[0] || value)),
        );
      })
      .catch(() => { if (active) setDestinations([]); });
    return () => { active = false; };
  }, [traceId, preferredDestinationId, preferredMunicipality]);
  const products = useMemo(
    () =>
      rows.map((product) => ({
        codigo: product.code,
        cantidad: cartQuantity(product),
      })),
    [rows],
  );
  const cartSignature = useMemo(
    () =>
      products
        .map((product) => `${product.codigo}:${product.cantidad}`)
        .sort()
        .join("|"),
    [products],
  );
  // La cotización de productos se ejecuta una sola vez al abrir la revisión.
  // Dirección, hora y observaciones pertenecen al cierre del pedido: no
  // pueden reiniciar capacidad/inventario por cada tecla del cliente.
  const quoteSignature = `${cartSignature}|${destinationId}|${municipality}|${deliveryType}`;

  useEffect(() => {
    if (DEV_BYPASS_AUTH || !rows.length) return undefined;
    let alive = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    setQuote(null);
    setResult(null);
    setStatus("loading");
    const quoteRequest = {
      productos: products,
      destino_entrega_id: destinationId || undefined,
      municipio_entrega: municipality || undefined,
      tipo_entrega: deliveryType,
    };
    const execute = () =>
      fetch(
        checkoutIdentified
          ? "/api/pedidos-web/cotizar"
          : "/api/pedidos-web/cotizar-publico",
        {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: pedidosWebHeaders(traceId, true),
        body: JSON.stringify(quoteRequest),
        },
      )
        .then(async (response) => {
          const payload = await response.json().catch(() => ({}));
          if (!response.ok || !payload.ok) {
            const tipo = String(payload.tipo_resultado || "");
            const mensaje =
              tipo === "cobertura_fuera"
                ? "Esa dirección está fuera de nuestra zona de entrega. Puedes ajustarla para continuar."
                : tipo === "cobertura_pendiente"
                  ? "Para confirmar la entrega, comparte tu ubicación o escribe una dirección en Supía, Caldas."
                  : payload.mensaje ||
                    payload.texto_canonico ||
                    payload.codigo ||
                    `No fue posible validar el carrito (HTTP ${response.status})`;
            throw new Error(mensaje);
          }
          if (alive) {
            setQuote({
              ...(payload.data || {}),
              _quoteSignature: quoteSignature,
            });
            setStatus("ready");
          }
        })
        .catch((error) => {
          if (alive) {
            setStatus("error");
            setResult(
              error.name === "AbortError"
                ? "Titina no pudo revisar los productos a tiempo. Tu carrito sigue igual; vuelve a intentarlo."
                : error.message,
            );
          }
        })
        .finally(() => window.clearTimeout(timeout));
    const debounceTimer = window.setTimeout(execute, 350);
    return () => {
      alive = false;
      window.clearTimeout(debounceTimer);
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [quoteSignature, checkoutIdentified, quoteAttempt, traceId]);

  function useLocation() {
    if (!navigator.geolocation) {
      setResult(
        "Tu navegador no permite compartir ubicación. Puedes escribir la dirección.",
      );
      return;
    }
    setLocation(null);
    setLocationState("requesting");
    setStatus("location");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitud: position.coords.latitude,
          longitud: position.coords.longitude,
        });
        setLocationState("ready");
        setStatus(DEV_BYPASS_AUTH ? "development" : "ready");
        setResult("Ubicación compartida y lista para confirmar la entrega.");
      },
      () => {
        setLocationState("error");
        setStatus(DEV_BYPASS_AUTH ? "development" : "ready");
        setResult(
          "No pudimos obtener tu ubicación. Puedes escribir la dirección.",
        );
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  }

  async function identifyCustomerByPhone() {
    if (!phone.trim()) {
      setResult("Escribe tu WhatsApp para encontrar tus datos o registrar el pedido.");
      return null;
    }
    setPhoneLookupState("loading");
    setResult(null);
    try {
      const access = await fetch("/api/pedidos-web/acceso-telefono", {
        method: "POST",
        credentials: "include",
        headers: pedidosWebHeaders(traceId, true),
        body: JSON.stringify({ telefono: phone }),
      });
      const accessPayload = await access.json().catch(() => ({}));
      if (!access.ok || !accessPayload.ok)
        throw new Error(accessPayload.mensaje || "No pudimos validar ese WhatsApp.");
      const context = await fetch("/api/pedidos-web/contexto", {
        method: "POST",
        credentials: "include",
        headers: pedidosWebHeaders(traceId),
      });
      const contextPayload = await context.json().catch(() => ({}));
      if (!context.ok || !contextPayload.ok)
        throw new Error(contextPayload.mensaje || "No pudimos recuperar tus datos guardados.");
      const nextCustomer = contextPayload.data?.cliente || null;
      const resolvedName = String(nextCustomer?.nombre || "").trim();
      const resolvedAddress = String(nextCustomer?.direccion || "").trim();
      setResolvedCustomer(nextCustomer);
      setPhoneVerified(true);
      if (resolvedName) setName(resolvedName);
      if (resolvedAddress) setAddress(resolvedAddress);
      setProfileMode(resolvedName && resolvedAddress ? "review" : "edit");
      onAuthenticated?.({
        identidadCanalId: String(contextPayload.data?.identidad_canal_id || "").trim(),
        ...(nextCustomer ? { cliente: nextCustomer } : {}),
      });
      setPhoneLookupState("ready");
      return nextCustomer;
    } catch (error) {
      setPhoneLookupState("error");
      setResult(error.message || "No pudimos validar ese WhatsApp.");
      return null;
    }
  }

  async function confirmOrder(event) {
    event.preventDefault();
    if (DEV_BYPASS_AUTH) {
      setResult(
        "Vista de desarrollo: la confirmación real se habilita al usar un enlace válido.",
      );
      return;
    }
    if (!checkoutIdentified) {
      await identifyCustomerByPhone();
      return;
    }
    if (hasSavedProfile && profileMode === "review") {
      setResult("Confirma si deseas usar los datos guardados o elige editar la información.");
      return;
    }
    const effectiveName = name.trim() || String(profileCustomer?.nombre || "").trim();
    const baseAddress = address.trim() || String(profileCustomer?.direccion || "").trim();
    const effectiveAddress = [
      baseAddress,
      neighborhood.trim() ? `Barrio o vereda: ${neighborhood.trim()}` : "",
      addressReference.trim() ? `Referencia: ${addressReference.trim()}` : "",
    ].filter(Boolean).join(" · ");
    if (!effectiveName) {
      setBusy(false);
      setResult("Escribe el nombre para registrar tu pedido.");
      return;
    }
    if (!effectiveAddress && !location) {
      setBusy(false);
      setResult("Escribe la dirección o comparte tu ubicación para continuar.");
      return;
    }
    try {
      if (!idempotencyKeyRef.current)
        idempotencyKeyRef.current = createCheckoutOperationId();
    } catch (error) {
      setResult(error.message);
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const response = await fetch("/api/pedidos-web/confirmar", {
        method: "POST",
        credentials: "include",
        headers: pedidosWebHeaders(traceId, true),
        body: JSON.stringify({
          productos: products,
          idempotency_key: idempotencyKeyRef.current,
          nombre: effectiveName,
          direccion: effectiveAddress,
          municipio_entrega: municipality,
          destino_entrega_id: destinationId || undefined,
          observaciones: observation,
          tipo_entrega: deliveryType,
          hora_entrega_solicitada:
            deliveryType === "Personalizada" ? hour : undefined,
          ubicacion_entrega: location || undefined,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) {
        const tipo = String(payload.tipo_resultado || "");
        const mensaje =
          tipo === "capacidad_insuficiente" ||
          tipo === "inventario_insuficiente"
            ? availabilityFailureMessage(payload)
            : tipo === "cobertura_fuera"
              ? "La dirección está fuera de la zona de entrega. Puedes ajustarla e intentarlo nuevamente."
              : tipo === "hora_entrega_fuera_de_rango" ||
                  tipo === "hora_entrega_invalida"
                ? "Para entrega a tu medida, solicita una hora entre las 7:00 a. m. y las 6:00 p. m."
                : String(
                    payload.texto_canonico || payload.mensaje || "",
                  ).trim() ||
                  `No pudimos confirmar el pedido todavía. Tu carrito sigue intacto; inténtalo nuevamente.${payload.codigo ? ` Referencia: ${payload.codigo}.` : ""}`;
        reportPedidosWebRuntimeIssue(
          "checkout_confirm_rejected",
          `${payload.codigo || "SIN-CODIGO"}:${tipo || "SIN-TIPO"}:HTTP-${response.status}`,
          "checkout_confirm_response",
        );
        setResult(mensaje);
        return;
      }
      setResult({
        mensaje: "Tu pedido quedó confirmado.",
        data: payload.data || {},
      });
      setQuote(payload.data || quote);
      onConfirmed?.();
    } catch {
      setResult("No pudimos confirmar el pedido. Inténtalo nuevamente.");
    } finally {
      setBusy(false);
    }
  }

  async function copyPaymentValue(field, value) {
    try {
      if (navigator.clipboard?.writeText)
        await navigator.clipboard.writeText(String(value));
      else {
        const input = document.createElement("textarea");
        input.value = String(value);
        input.setAttribute("readonly", "");
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
      }
      setCopiedPaymentField(field);
      window.setTimeout(
        () =>
          setCopiedPaymentField((current) =>
            current === field ? "" : current,
          ),
        1800,
      );
    } catch {
      setCopiedPaymentField("");
    }
  }

  async function selectPaymentMethod(method, confirmationId, isGroup) {
    setPaymentMethod(method);
    setPaymentError("");
    if (DEV_BYPASS_AUTH || !confirmationId) return;
    setPaymentSaving(true);
    try {
      const body = {
        metodo_pago: method === "transfer" ? "Transferencia" : "Efectivo",
      };
      if (isGroup) body.pedido_grupo_id = confirmationId;
      else body.pedido = confirmationId;
      const response = await fetch("/api/pedidos-web/pago", {
        method: "POST",
        credentials: "include",
        headers: pedidosWebHeaders(traceId, true),
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok)
        throw new Error(
          payload.texto_canonico ||
            payload.codigo ||
            "No pudimos registrar el medio de pago.",
        );
      const instrucciones = payload.data?.instrucciones_transferencia || null;
      setPaymentDetails(instrucciones);
      // La transferencia debe mostrar primero cuenta, llave y valor. El
      // mensaje de gracias no puede cubrir esos datos con otro modal.
      setPaymentCompletion(
        method === "cash" ? { method, instrucciones } : null,
      );
    } catch (error) {
      setPaymentError(
        error.message || "No pudimos registrar el medio de pago.",
      );
    } finally {
      setPaymentSaving(false);
    }
  }

  const quoteIsCurrent =
    status === "ready" && quote?._quoteSignature === quoteSignature;
  const immediateOption = quote?.opciones_entrega?.entrega_inmediata || null;
  const personalizedOption =
    quote?.opciones_entrega?.entrega_personalizada || null;
  const homeDeliveryOption = quote?.opciones_entrega?.domicilio || null;
  const deliveryOption =
    deliveryType === "Personalizada"
      ? personalizedOption
      : deliveryType === "Domicilio"
        ? homeDeliveryOption
      : deliveryType === "Entrega inmediata"
        ? immediateOption
        : quote?.opciones_entrega?.ruta_programada || null;
  const immediateAvailable =
    quoteIsCurrent && Boolean(immediateOption?.disponible);
  useEffect(() => {
    if (
      deliveryType === "Entrega inmediata" &&
      quoteIsCurrent &&
      !immediateOption?.disponible
    ) {
      setDeliveryType("Ruta normal");
    }
  }, [deliveryType, quoteIsCurrent, immediateOption?.disponible]);
  const totalProductos = quoteIsCurrent
    ? Number(quote?.valor_productos ?? quote?.valor ?? total)
    : Number(total);
  const selectedDestination = destinations.find((item) => item.id === destinationId) || null;
  // Mientras el servidor valida existencia, disponibilidad y fecha, el destino
  // ya elegido aporta su tarifa fija publicada. Es una estimación visible: la
  // cotización canónica sigue siendo la única que autoriza confirmar el pedido.
  const estimatedDestinationCharge = Number(
    selectedDestination?.valor_transporte ?? selectedDestination?.ValorTransporte ?? 0,
  );
  const recargo = quoteIsCurrent
    ? Number(
        quote?.recargo_entrega ??
          deliveryOption?.recargo_entrega ??
          estimatedDestinationCharge,
      )
    : estimatedDestinationCharge;
  const totalVisible = totalProductos + recargo;
  const effectiveNamePreview = name.trim() || String(profileCustomer?.nombre || "").trim();
  const effectiveAddressPreview = address.trim() || String(profileCustomer?.direccion || "").trim();
  const missingName = !effectiveNamePreview;
  const missingPhone = !checkoutIdentified && !phone.trim();
  const missingDelivery = !effectiveAddressPreview && !location;
  const deliveryPlaces = useMemo(() => {
    const municipalitiesByName = destinations.reduce((counts, destination) => {
      (destination.municipios || []).forEach((item) => {
        counts[item] = (counts[item] || 0) + 1;
      });
      return counts;
    }, {});

    return destinations.flatMap((destination) =>
      (destination.municipios || []).map((item) => ({
        key: `${destination.id}::${item}`,
        destinationId: destination.id,
        municipality: item,
        label:
          municipalitiesByName[item] > 1
            ? `${item} · ${destination.nombre}`
            : item,
      })),
    );
  }, [destinations]);
  const municipalityUnavailable = Boolean(selectedDestination && !selectedDestination.municipios?.includes(municipality));
  const deliverySummary = quote?.destino_entrega || selectedDestination || {};
  const deliverySummaryDate =
    deliverySummary.fecha_entrega ||
    deliverySummary.FechaEntrega ||
    deliverySummary.fechaEntrega ||
    rows.find((product) => product.suggestedDeliveryDate)?.suggestedDeliveryDate ||
    "Por confirmar";
  const deliverySummaryCharge = quoteIsCurrent
    ? Number(quote?.recargo_entrega || 0)
    : Number(deliverySummary.valor_transporte || deliverySummary.ValorTransporte || 0);
  const deliverySummaryText =
    deliverySummaryCharge > 0
      ? formatCurrency(deliverySummaryCharge)
      : deliveryType === "Ruta normal"
        ? "Incluido en ruta programada · 7:00 a. m. a 12:00 m."
        : "Incluido";
  const confirmDisabled =
    !rows.length ||
    busy ||
    locationState === "requesting" ||
    municipalityUnavailable ||
    (!DEV_BYPASS_AUTH && (!quoteIsCurrent || missingPhone || !checkoutIdentified || (checkoutIdentified && (missingName || missingDelivery)) || (hasSavedProfile && profileMode === "review")));
  const confirmLabel = busy
    ? "Confirmando…"
    : phoneLookupState === "loading"
      ? "Buscando tus datos…"
    : locationState === "requesting"
      ? "Preparando ubicación…"
      : !checkoutIdentified
        ? "Confirma tu WhatsApp para continuar"
        : hasSavedProfile && profileMode === "review"
          ? "Confirma tus datos guardados"
      : !DEV_BYPASS_AUTH && !quoteIsCurrent
        ? "Validando pedido…"
        : "Confirmar pedido";
  const deliveryLabel =
    deliveryType === "Entrega inmediata"
      ? "Entrega hoy"
      : deliveryType === "Personalizada"
        ? "Entrega a tu medida"
        : deliveryType === "Domicilio"
          ? "Domicilio"
        : "Ruta programada";
  function selectDeliveryType(nextType) {
    setDeliveryType(nextType);
    if (nextType === "Personalizada") {
      const valor = Number(personalizedOption?.recargo_entrega ?? 0);
      setDeliveryNotice(
        valor > 0
          ? `Entrega a tu medida agregada: ${formatCurrency(valor)}.`
          : "Entrega a tu medida seleccionada.",
      );
    } else if (nextType === "Domicilio") {
      setDeliveryNotice("Domicilio: $1.000 por pedido.");
      return;
    }
    setDeliveryNotice(
      nextType === "Entrega inmediata"
        ? "Entrega hoy seleccionada."
        : "Ruta programada seleccionada, sin recargo.",
    );
  }
  const confirmReason = busy
    ? "Titina está organizando tu pedido."
    : missingPhone
      ? "Escribe tu WhatsApp para encontrar tus datos y continuar."
      : !checkoutIdentified
        ? "Primero confirma tu WhatsApp para asociar el pedido correctamente."
      : hasSavedProfile && profileMode === "review"
        ? "Confirma si deseas usar los datos guardados o edítalos antes de continuar."
      : missingName
      ? "Escribe el nombre para registrar el pedido."
      : municipalityUnavailable
        ? "Selecciona un municipio incluido en el destino de entrega."
        : missingDelivery
          ? "Comparte tu ubicación o escribe la dirección de entrega."
          : !DEV_BYPASS_AUTH && !quoteIsCurrent
            ? status === "error"
              ? "No se pudo validar todavía. Revisa los datos e inténtalo de nuevo."
              : "Validando disponibilidad y fecha. El total mostrado es estimado."
            : "Titina validó los datos. Puedes confirmar tu pedido.";
  if (typeof result === "object" && result?.data) {
    const confirmationId =
      result.data.pedido_grupo_id ||
      result.data.pedido ||
      result.data.resumen_pedido?.pedido;
    const isGroup = Boolean(result.data.pedido_grupo_id);
    const paymentAmount =
      result.data.valor_total ?? result.data.valor ?? totalVisible;
    const paymentFields = paymentDetails
      ? [
          ["accountNumber", "Cuenta", paymentDetails.numero_cuenta],
          ["key", "Llave", paymentDetails.llave],
          ["amount", "Valor", formatCurrency(paymentAmount)],
        ]
      : [];
    return (
      <div className="checkout-backdrop">
        <section
          className="checkout-panel checkout-success"
          role="dialog"
          aria-modal="true"
          aria-label="Pedido confirmado"
        >
          <div className="checkout-steps">
            <span className="done">✓ Productos</span>
            <span className="done">✓ Confirmación</span>
            <span className="current">3 Listo</span>
          </div>
          <p className="eyebrow">PEDIDO CONFIRMADO</p>
          <h2>Listo, quedó en buenas manos.</h2>
          <p className="checkout-intro">
            Titina registró tu pedido y te acompañará por WhatsApp con la
            información de la entrega.
          </p>
          <div className="checkout-preview-fields" aria-label="Resumen de entrega confirmada">
            <div>
              <span>Lugar de entrega</span>
              <b>Entrega en {municipality || "Supía, Caldas"}</b>
            </div>
            <div>
              <span>Fecha de entrega</span>
              <b>{fechaEntregaVisible(deliverySummaryDate)}</b>
            </div>
          </div>
          {confirmationId && (
            <div className="checkout-confirmation-id">
              <span>Referencia de tu pedido</span>
              <b>{confirmationId}</b>
            </div>
          )}
          <section
            className="payment-choice"
            aria-labelledby="payment-choice-title"
          >
            <p className="eyebrow" id="payment-choice-title">
              ¿CÓMO DESEAS PAGAR?
            </p>
            <div className="payment-choice-buttons">
              <button
                type="button"
                disabled={paymentSaving}
                className={paymentMethod === "cash" ? "selected" : ""}
                onClick={() =>
                  selectPaymentMethod("cash", confirmationId, isGroup)
                }
              >
                Efectivo en la entrega
              </button>
              <button
                type="button"
                disabled={paymentSaving}
                className={paymentMethod === "transfer" ? "selected" : ""}
                onClick={() =>
                  selectPaymentMethod("transfer", confirmationId, isGroup)
                }
              >
                Transferencia Bancolombia
              </button>
            </div>
            {paymentSaving && (
              <p className="payment-help">
                Titina está registrando tu elección…
              </p>
            )}
            {paymentError && (
              <p className="checkout-result error">{paymentError}</p>
            )}
            {!paymentMethod && !paymentSaving ? (
              <p className="payment-help">
                Elige cómo deseas pagar para dejarlo registrado.
              </p>
            ) : null}
            {paymentMethod === "cash" && !paymentSaving ? (
              <p className="payment-help">
                Puedes pagar el total en efectivo cuando recibas tu pedido.
              </p>
            ) : null}
            {paymentMethod === "transfer" && !paymentSaving ? (
              paymentDetails ? (
                <div className="transfer-details">
                  <p>
                    Gracias por tu compra. Realiza la transferencia por el valor
                    exacto del pedido:
                  </p>
                  <div>
                    <span>Banco</span>
                    <b>{paymentDetails.banco}</b>
                  </div>
                  <div>
                    <span>Tipo de cuenta</span>
                    <b>{paymentDetails.tipo_cuenta}</b>
                  </div>
                  <div>
                    <span>Titular</span>
                    <b>{paymentDetails.titular}</b>
                  </div>
                  {paymentFields.map(([field, label, value]) => (
                    <div className="copy-payment-row" key={field}>
                      <span>{label}</span>
                      <b>{value}</b>
                      <button
                        type="button"
                        onClick={() => copyPaymentValue(field, value)}
                      >
                        {copiedPaymentField === field ? "Copiado" : "Copiar"}
                      </button>
                    </div>
                  ))}
                  <small>
                    Después de pagar, puedes enviar el comprobante por WhatsApp
                    para facilitar la validación.
                  </small>
                </div>
              ) : (
                <p className="payment-help">
                  Elige transferencia para cargar los datos de cuenta seguros.
                </p>
              )
            ) : null}
          </section>
          <button className="checkout-confirm" type="button" onClick={onClose}>
            Seguir viendo productos
          </button>
          {paymentCompletion && (
            <div className="payment-complete-backdrop" role="presentation">
              <section
                className="payment-complete"
                role="dialog"
                aria-modal="true"
                aria-label="Pago registrado"
              >
                <p className="eyebrow">PEDIDO LISTO</p>
                <h2>Gracias por tu pedido.</h2>
                <p>
                  Puedes pagar en efectivo cuando recibas tu pedido. Titina te
                  escribirá antes de la entrega.
                </p>
                <button
                  type="button"
                  className="checkout-confirm"
                  onClick={onClose}
                >
                  Volver al catálogo
                </button>
              </section>
            </div>
          )}
        </section>
      </div>
    );
  }
  return (
    <div className="checkout-backdrop">
      <section
        className="checkout-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-title"
      >
        <button className="drawer-close" onClick={onClose} aria-label="Cerrar">
          <X size={18} />
        </button>
        <button
          className="checkout-back"
          type="button"
          onClick={onBackToCart}
        >
          ← Volver al carrito
        </button>
        <div className="checkout-steps">
          <span className="done">✓ Productos</span>
          <span className="current">2 Confirmación</span>
          <span>3 Listo</span>
        </div>
        <p className="eyebrow">CONFIRMA TU PEDIDO</p>
        <h2 id="checkout-title">Revisa que todo esté bien</h2>
        <p className="checkout-intro">
          Titina revisa disponibilidad, entrega y total antes de confirmarlo.
        </p>
        <div className="checkout-summary">
          <div className="checkout-preview-fields" aria-label="Resumen de entrega">
            <div>
              <span>Lugar de entrega</span>
              <b>Entrega en {municipality || "Supía, Caldas"}</b>
            </div>
            <div>
              <span>Transporte y domicilio</span>
              <b>{deliverySummaryText}</b>
            </div>
            <div>
              <span>Fecha de entrega</span>
              <b>{fechaEntregaVisible(deliverySummaryDate)}</b>
            </div>
          </div>
          {quote?.entregas?.length > 1 ? (
            quote.entregas.map((delivery) => (
              <div className="delivery-group" key={delivery.fecha_entrega}>
                <h3>Entrega {fechaEntregaVisible(delivery.fecha_entrega)}</h3>
                {(delivery.productos || []).map((item, index) => (
                  <div
                    className="checkout-line"
                    key={`${delivery.fecha_entrega}-${item.codigo || index}`}
                  >
                    <span>
                      <b>{item.producto}</b>
                      <small>
                        {item.presentacion || item.unidad || ""} ·{" "}
                        {item.cantidad} unidad(es)
                      </small>
                    </span>
                    <strong>
                      {item.pricing_status === "PENDIENTE_PESO_PRECIO"
                        ? "Peso y precio por confirmar"
                        : formatCurrency(item.valor || 0)}
                    </strong>
                  </div>
                ))}
              </div>
            ))
          ) : rows.length ? (
            rows.map((product) => (
              <div className="checkout-line" key={product.code}>
                <span>
                  <b>{product.name}</b>
                  <small>
                    {cartLabel(product.presentation)} · {cartQuantity(product)}{" "}
                    unidad(es) · Entrega{" "}
                    {fechaEntregaVisible(product.suggestedDeliveryDate)}
                  </small>
                </span>
                <strong>
                  {importeVisible(product, cartQuantity(product))}
                </strong>
              </div>
            ))
          ) : (
            <p className="cart-empty">No hay productos para confirmar.</p>
          )}
          <div className="checkout-subtotal">
            <span>Productos</span>
            <strong>{formatCurrency(totalProductos)}</strong>
          </div>
          {recargo > 0 && (
            <div className="checkout-subtotal">
              <span>{deliveryLabel}</span>
              <strong>{formatCurrency(recargo)}</strong>
            </div>
          )}
          <div className="checkout-total">
            <span>
              {quoteIsCurrent ? "Total confirmado" : "Total estimado"}
            </span>
            <strong>{formatCurrency(totalVisible)}</strong>
          </div>
        </div>
        {!quoteIsCurrent && (
          <div className="checkout-quote-status" role="status">
            <p className="cart-note">{confirmReason}</p>
            {status === "error" && (
              <button
                type="button"
                className="checkout-retry"
                onClick={() => setQuoteAttempt((attempt) => attempt + 1)}
              >
                Reintentar validación
              </button>
            )}
          </div>
        )}
        {profileLoading ? (
          <section className="checkout-profile-loading" role="status" aria-live="polite">
            <div className="auth-spinner" aria-hidden="true" />
            <div>
              <b>Estamos preparando tus datos de entrega</b>
              <span>En un momento podrás confirmar sin volver a escribirlos.</span>
            </div>
          </section>
        ) : (
        <form className="checkout-form" onSubmit={confirmOrder}>
          {!checkoutIdentified && (
            <section className="checkout-phone-access" aria-label="Identificar cliente">
              <div>
                <b>¿Ya has pedido con Titina?</b>
                <span>Escribe tu WhatsApp y prepararemos tus datos para confirmar más rápido.</span>
              </div>
              <label>
                WhatsApp
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  placeholder="321 123 4567"
                />
              </label>
              <button
                type="button"
                className="checkout-phone-continue"
                disabled={phoneLookupState === "loading"}
                onClick={identifyCustomerByPhone}
              >
                {phoneLookupState === "loading" ? "Buscando tus datos…" : "Continuar"}
              </button>
              <small className="field-help">Lo usamos para asociar el pedido y mostrar tus datos guardados si ya existen.</small>
            </section>
          )}
          {checkoutIdentified && hasSavedProfile && profileMode === "review" && (
            <section className="checkout-saved-profile" aria-label="Datos guardados">
              <header>
                <p className="eyebrow">ENTREGA</p>
                <h3>¿Entregamos en esta dirección?</h3>
              </header>
              <div className="checkout-saved-address">
                <span>Dirección guardada</span>
                <b>{profileCustomer.direccion}</b>
                <small>Pedido para {profileCustomer.nombre}</small>
              </div>
              {profileCustomer?.ultimo_destino?.nombre && (
                <div className="checkout-saved-destination">
                  <span>Ruta habitual</span><b>{profileCustomer.ultimo_destino.nombre}</b>
                </div>
              )}
              <button type="button" className="checkout-use-profile" onClick={() => setProfileMode("compact")}>
                Sí, continuar con esta dirección
              </button>
              <button type="button" className="checkout-edit-profile" onClick={() => setProfileMode("edit")}>
                Usar otra dirección
              </button>
            </section>
          )}
          {checkoutIdentified && hasSavedProfile && profileMode === "compact" && (
            <section className="checkout-profile-ready" aria-label="Datos de entrega seleccionados">
              <div>
                <span>Entregar en</span>
                <b>{profileCustomer.direccion}</b>
                <small>Pedido para {profileCustomer.nombre}</small>
              </div>
              <button type="button" onClick={() => setProfileMode("edit")}>Cambiar</button>
            </section>
          )}
          {checkoutIdentified && (!hasSavedProfile || profileMode === "edit") && (
            <>
              <label>
                Nombre para registrar el pedido
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Tu nombre"
                />
              </label>
              <label>
                Dirección principal de entrega
                <textarea
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  placeholder={
                    location
                      ? "Ubicación compartida; puedes agregar una referencia"
                      : "Ej. Carrera 6 # 33-36, apartamento o punto de referencia"
                  }
                  rows="3"
                />
                <small className="field-help">
                  {location
                    ? "Ubicación compartida. Puedes agregar una referencia si lo deseas."
                    : "Escribe la dirección como la conoces. No necesitas un formato específico."}
                </small>
              </label>
            </>
          )}
          {checkoutIdentified && (!hasSavedProfile || profileMode === "edit") && (
            <label>
              Lugar de entrega
              <select
                value={destinationId && municipality ? `${destinationId}::${municipality}` : ""}
                onChange={(event) => {
                  const next = deliveryPlaces.find((item) => item.key === event.target.value);
                  if (!next) return;
                  setDestinationId(next.destinationId);
                  setMunicipality(next.municipality);
                }}
              >
                {!deliveryPlaces.length && <option value="">Supía, Caldas</option>}
                {deliveryPlaces.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
              </select>
              {selectedDestination?.descripcion_cobro && <small className="field-help">Entrega en {municipality}. {selectedDestination.descripcion_cobro}: {formatCurrency(Number(selectedDestination.valor_transporte || 0))}{selectedDestination.fecha_entrega ? ` · Entrega ${fechaEntregaVisible(selectedDestination.fecha_entrega)}` : ""}</small>}
            </label>
          )}
          {checkoutIdentified && hasSavedProfile && profileMode !== "edit" && (
            <section className="checkout-route-summary" aria-label="Ruta y horario de entrega">
              <div>
                <span>Destino</span>
                <b>{municipality || profileCustomer?.ultimo_destino?.municipio || "Supía, Caldas"}</b>
              </div>
              <div>
                <span>Horario</span>
                <b>Ruta programada · 7:00 a. m. a 12:00 m.</b>
              </div>
            </section>
          )}
          {checkoutIdentified && (
            <details className="checkout-extra-details">
              <summary>Modificar hora de entrega o añadir observación</summary>
              <div className="checkout-extra-details-body">
                <div className="address-detail-grid">
                  <label>
                    Barrio o vereda <em>opcional</em>
                    <input
                      value={neighborhood}
                      onChange={(event) => setNeighborhood(event.target.value)}
                      placeholder="Ej. La Pista"
                    />
                  </label>
                  <label>
                    Referencia para encontrar la casa <em>opcional</em>
                    <input
                      value={addressReference}
                      onChange={(event) => setAddressReference(event.target.value)}
                      placeholder="Ej. Frente al centro cívico"
                    />
                  </label>
                </div>
                <button
                  type="button"
                  className="location-button"
                  onClick={useLocation}
                >
                  Compartir mi ubicación
                </button>
                <label>
                  Entrega
                  <select
                    value={deliveryType}
                    onChange={(event) => selectDeliveryType(event.target.value)}
                  >
                    <option value="Ruta normal">Ruta programada</option>
                    <option value="Domicilio">Domicilio · $1.000</option>
                    <option value="Personalizada">Entrega a tu medida</option>
                    {immediateAvailable && (
                      <option value="Entrega inmediata">Entrega hoy</option>
                    )}
                  </select>
                  <small className="field-help">
                    {deliveryType === "Entrega inmediata"
                      ? `Entrega hoy entre ${immediateOption.hora_inicio || "7:00"} y ${immediateOption.hora_corte || "18:00"}. Recargo: ${formatCurrency(Number(immediateOption.recargo_entrega || 0))}.`
                      : deliveryType === "Personalizada"
                        ? "Entrega a tu medida: el recargo y la hora se validan con Titina."
                        : deliveryType === "Domicilio"
                          ? "Domicilio: $1.000 por pedido."
                        : immediateOption && !immediateOption.disponible
                          ? "Ruta programada: 7:00 AM a 12:00 M. La entrega hoy no aplica a todo este carrito."
                          : "Ruta programada: 7:00 AM a 12:00 M."}
                  </small>
                </label>
                {deliveryType === "Personalizada" && (
                  <label>
                    Hora solicitada
                    <input
                      type="time"
                      min="07:00"
                      max="18:00"
                      step="900"
                      value={hour}
                      onChange={(event) => setHour(event.target.value)}
                      required
                    />
                    <small className="field-help">
                      Por defecto solicitamos las 8:00 a. m.; puedes elegir una hora
                      entre las 7:00 a. m. y las 6:00 p. m.
                    </small>
                  </label>
                )}
                <label>
                  Observaciones para tu pedido
                  <textarea
                    value={observation}
                    onChange={(event) => setObservation(event.target.value)}
                    placeholder="Ej. tocar en la puerta, una referencia o algo que debamos tener en cuenta"
                    rows="3"
                  />
                </label>
              </div>
            </details>
          )}
          <button
            className="checkout-confirm"
            type="submit"
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </button>
          <small
            className={`checkout-confirm-help ${confirmDisabled ? "is-blocked" : "is-ready"}`}
            role="status"
          >
            {confirmReason}
          </small>
        </form>
        )}
        {typeof result === "string" && (
          <p
            className={`checkout-result ${result.startsWith("Ubicación compartida") ? "success" : "error"}`}
          >
            {result}
          </p>
        )}
        <small className="cart-note">
          Titina confirma la disponibilidad y la entrega antes de registrar tu
          pedido.
        </small>
      </section>
    </div>
  );
}

function cartQuantity(product) {
  // CheckoutPreview recibe filas ya filtradas; la cantidad se transporta en
  // la fila para no volver a interpretar el carrito en otra capa.
  return Number(product.quantity || product.selectedQuantity || 1);
}

function cartLabel(value) {
  return value;
}

function ProductDetail({
  product,
  onClose,
  onAdd,
  onWaitlist,
  requesting = false,
  authenticated = false,
  traceId = "sin-traza-cliente",
  superProductPopup = null,
}) {
  const [detail, setDetail] = useState(null);
  const [detailState, setDetailState] = useState("loading");
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setDetail(null);
    setDetailState("loading");
    fetch(authenticated ? "/api/pedidos-web/ficha" : "/api/pedidos-web/ficha-publica", {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
      headers: pedidosWebHeaders(traceId, true),
      body: JSON.stringify({ codigo: product.code }),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok)
          throw new Error(
            payload.mensaje ||
              payload.codigo ||
              "No fue posible cargar la ficha.",
          );
        return payload.data || {};
      })
      .then((data) => {
        if (!active) return;
        const ficha =
          data.ficha && typeof data.ficha === "object" ? data.ficha : {};
        setDetail({ ...product, ficha });
        setDetailState("ready");
      })
      .catch(() => {
        if (active) setDetailState("error");
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [product.code, traceId, authenticated]);
  const labels = {
    descripcion: "Sobre este producto",
    presentacion_descripcion: "Presentación",
    contenido_cantidad: "Contenido",
    contenido_unidad: "Unidad de contenido",
    unidades_empaque: "Unidades por empaque",
    peso_total_g: "Peso total",
    peso_unidad_g: "Peso por unidad",
    ingredientes: "Ingredientes",
    caracteristicas: "Características",
    origen: "Origen",
    productor: "Productor",
    conservacion: "Conservación",
    requiere_refrigeracion: "Refrigeración",
    vida_util_cerrado: "Duración cerrado",
    vida_util_abierto: "Duración abierto",
    recomendacion_consumo: "Cómo disfrutarlo",
    disponibilidad: "Disponibilidad",
  };
  const visibleProduct = detail || product;
  const technical = Object.entries(labels)
    .map(([key, label]) => [label, visibleProduct.ficha?.[key]])
    .filter(
      ([, value]) =>
        value !== null && value !== undefined && String(value).trim() !== "",
    );
  return (
    <div className="product-modal-backdrop" onClick={onClose}>
      <section
        className="product-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          className="drawer-close"
          onClick={onClose}
          aria-label="Cerrar ficha"
        >
          <X size={18} />
        </button>
        <ProductImage product={product} />
        <div className="product-modal-body">
          <p className="eyebrow">{superProductPopup ? "RECOMENDADO PARA TI" : "FICHA DEL PRODUCTO"}</p>
          {superProductPopup?.titulo && (
            <p className="super-product-title">{superProductPopup.titulo}</p>
          )}
          <div className="product-modal-title-row">
            <h2 id="product-modal-title">{product.name}</h2>
            <ProductInfoButton product={product} />
          </div>
          <p className="modal-price">
            {product.price > 0
              ? formatCurrency(product.price)
              : "Precio por peso"}{" "}
            · {product.presentation}
          </p>
          <p className="product-detail-intro">
            {superProductPopup?.descripcion || "Te cuento lo que tenemos confirmado de este producto."}
          </p>
          {detailState === "loading" && (
            <p className="product-detail-loading">
              Cargando la ficha del producto…
            </p>
          )}
          {detailState === "error" && (
            <p className="product-detail-loading error">
              Por ahora no pudimos cargar todos los detalles. Puedes seguir
              agregándolo a tu pedido.
            </p>
          )}
          {detailState === "ready" && technical.length > 0 && (
            <div className="technical-list">
              {technical.map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <b>{fichaValue(value)}</b>
                </div>
              ))}
            </div>
          )}
          <ProductAction
            product={product}
            onAdd={onAdd}
            onWaitlist={onWaitlist}
            onClose={onClose}
            requesting={requesting}
          />
        </div>
      </section>
    </div>
  );
}

const isOrdersWeb =
  window.location.pathname.startsWith("/pedidos") ||
  window.location.hostname.startsWith("pedidos.");
const isAdminPwa =
  !isOrdersWeb && window.location.hostname === "admin.somostitina.com";
const isPedidosPwa = isOrdersWeb && window.location.hostname.startsWith("pedidos.");

if (isAdminPwa && "serviceWorker" in navigator) {
  window.addEventListener(
    "load",
    () => {
      navigator.serviceWorker
        .register("/sw-admin.js", { scope: "/" })
        .then((registration) => {
          // El worker siempre se consulta sin caché en el servidor. Forzar la
          // revisión al abrir evita esperar el ciclo periódico del navegador.
          registration.update().catch(() => {});
          let reloadAfterApprovedUpdate = false;
          function announceUpdate() {
            if (!registration.waiting) return;
            const apply = () => {
              // Sólo una actualización aprobada por la persona usuaria puede
              // recargar el PWA. Una instalación automática no puede cortar
              // un inicio de sesión ni una operación abierta.
              reloadAfterApprovedUpdate = true;
              registration.waiting?.postMessage({
                type: "TITINA_ADMIN_SKIP_WAITING",
              });
            };
            window.__titinaAdminPwaUpdate = apply;
            window.dispatchEvent(
              new CustomEvent("titina-admin-pwa-update", { detail: { apply } }),
            );
          }
          if (registration.waiting) announceUpdate();
          registration.addEventListener("updatefound", () => {
            const installing = registration.installing;
            if (!installing) return;
            installing.addEventListener("statechange", () => {
              if (
                installing.state === "installed" &&
                navigator.serviceWorker.controller
              )
                announceUpdate();
            });
          });
          navigator.serviceWorker.addEventListener("controllerchange", () => {
            // Si el panel está en segundo plano, la siguiente apertura debe
            // mostrar siempre la publicación actual. Durante una interacción
            // visible se conserva la protección contra cortar una operación.
            if (reloadAfterApprovedUpdate || document.visibilityState !== "visible") window.location.reload();
          });
        })
        .catch(() => {
          /* La instalación PWA no bloquea el panel operativo. */
        });
    },
    { once: true },
  );
}
if (isPedidosPwa && "serviceWorker" in navigator) {
  window.addEventListener(
    "load",
    () => {
      navigator.serviceWorker
        .register("/sw-pedidos.js", { scope: "/" })
        .then((registration) => {
          const announceUpdate = () => {
            if (!registration.waiting) return;
            const apply = () =>
              registration.waiting?.postMessage({ type: "TITINA_PEDIDOS_SKIP_WAITING" });
            window.__titinaPedidosPwaUpdate = apply;
            window.dispatchEvent(new CustomEvent("titina-pedidos-pwa-update", { detail: { apply } }));
          };
          if (registration.waiting) announceUpdate();
          registration.addEventListener("updatefound", () => {
            const installing = registration.installing;
            if (!installing) return;
            installing.addEventListener("statechange", () => {
              if (installing.state === "installed" && navigator.serviceWorker.controller)
                announceUpdate();
            });
          });
          navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload());
        })
        .catch(() => {
          /* Pedidos mantiene su funcionamiento normal si PWA no está disponible. */
        });
    },
    { once: true },
  );
}

class DashboardRuntimeBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("Titina dashboard render error", error, info);
  }

  componentDidMount() {
    if (isAdminPwa) document.documentElement.dataset.titinaAdminMounted = "1";
  }

  render() {
    if (!this.state.error) return this.props.children;
    const detail = this.state.error?.message || "Error de ejecución no identificado";
    return <main className="dashboard-runtime-error" role="alert">
      <h1>No pudimos abrir este módulo</h1>
      <p>La información no fue modificada. El error técnico fue:</p>
      <code>{detail}</code>
      <p>Cierra y vuelve a abrir el panel para cargar la versión nueva.</p>
      <button type="button" onClick={() => window.location.reload()}>Recargar panel</button>
    </main>;
  }
}
createRoot(document.getElementById("root")).render(
  <StrictMode><DashboardRuntimeBoundary>{isOrdersWeb ? <PedidosWeb /> : <App />}</DashboardRuntimeBoundary></StrictMode>,
);
